/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * OmniClinicalBridge V2.0 - Enhanced Essential Medicines Database
 * ═══════════════════════════════════════════════════════════════════════════════
 * Based on WHO Essential Medicines List (EML) 2023
 * Adapted for African Healthcare Contexts (54 Countries)
 * 
 * OmniAlera Technologies Holdings Inc.
 * Delaware C-Corporation | EIN: 41-3038813
 * Author: Dr. Omokayode Abiola Aderolu
 * ═══════════════════════════════════════════════════════════════════════════════
 */

export interface EnhancedMedicine {
  id: string;
  genericName: string;
  brandNames: string[];
  atcCode: string;
  therapeuticClass: string;
  pharmacology: {
    mechanism: string;
    pharmacokinetics: {
      absorption: string;
      distribution: string;
      metabolism: string;
      elimination: string;
      halfLife: string;
    };
    pharmacodynamics: string;
  };
  indications: Array<{
    condition: string;
    evidenceLevel: 'A' | 'B' | 'C';
    recommendationStrength: 'Strong' | 'Moderate' | 'Weak';
    firstLine: boolean;
    notes?: string;
  }>;
  dosing: {
    adults: {
      standard: string;
      indicationSpecific?: Array<{ indication: string; dose: string; }>;
      maxDose: string;
      renalImpairment: {
        mild: string;
        moderate: string;
        severe: string;
        dialysis: string;
      };
      hepaticImpairment: string;
    };
    pediatric: {
      standard: string;
      weightBased: boolean;
      ageSpecific?: Array<{ age: string; dose: string; }>;
      maxDose: string;
      neonatal?: string;
    };
    elderly: string;
    routes: string[];
    administration: string[];
  };
  contraindications: {
    absolute: string[];
    relative: string[];
    precautions: string[];
  };
  adverseEffects: {
    common: Array<{ effect: string; frequency: string; management: string; }>;
    serious: Array<{ effect: string; frequency: string; management: string; warning?: string; }>;
    rare: string[];
  };
  interactions: {
    major: Array<{ drug: string; effect: string; mechanism: string; management: string; }>;
    moderate: Array<{ drug: string; effect: string; management: string; }>;
    food: string[];
  };
  pregnancy: {
    category: string;
    trimpiesterRisks: { first: string; second: string; third: string; };
    recommendation: string;
    lactation: { safe: boolean; notes: string; };
  };
  monitoring: {
    baseline: Array<{ test: string; reason: string; }>;
    ongoing: Array<{ test: string; frequency: string; target?: string; }>;
    therapeutic: { range?: string; timing?: string; };
  };
  storage: {
    temperature: string;
    light: string;
    humidity: string;
    specialInstructions: string;
    shelfLife: string;
  };
  africanContext: {
    availability: string;
    affordability: string;
    supplyChainNotes: string;
    localManufacturing: string;
    alternatives: string[];
    resistancePatterns?: string;
    endemicConsiderations?: string;
  };
  patientCounseling: {
    keyPoints: string[];
    commonMisconceptions: string[];
    adherenceTips: string[];
    warningSignsToReport: string[];
  };
  clinicalPearls: string[];
  references: string[];
  lastUpdated: string;
  medicalDisclaimer?: string;
}

export const ENHANCED_MEDICINES: EnhancedMedicine[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // 1. AMOXICILLIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'amoxicillin-001',
    genericName: 'Amoxicillin',
    brandNames: ['Amoxil', 'Trimox', 'Wymox', 'Moxatag', 'Ospamox'],
    atcCode: 'J01CA04',
    therapeuticClass: 'Aminopenicillin Antibiotic',
    
    pharmacology: {
      mechanism: `Amoxicillin is a bactericidal beta-lactam antibiotic that inhibits bacterial cell wall synthesis. It binds to penicillin-binding proteins (PBPs), particularly PBP1A, PBP1B, and PBP3, which are essential enzymes involved in the final stages of peptidoglycan synthesis. This binding prevents cross-linking of peptidoglycan chains, weakening the cell wall and leading to osmotic lysis and bacterial death. Amoxicillin has a broader spectrum than penicillin, with enhanced activity against gram-negative organisms due to better penetration through porins.`,
      pharmacokinetics: {
        absorption: 'Rapidly and well absorbed from GI tract (75-90% bioavailability). Food does not significantly affect absorption. Peak plasma concentrations reached in 1-2 hours.',
        distribution: 'Widely distributed to most body tissues and fluids including middle ear, sinuses, bronchial secretions, and bile. CSF penetration is poor unless meninges are inflamed (10-20% of serum levels). Protein binding is approximately 20%. Volume of distribution: 0.3-0.4 L/kg.',
        metabolism: 'Minimal hepatic metabolism (~10%). Most of the drug remains unchanged.',
        elimination: 'Primarily renal excretion (60-75% unchanged in urine via glomerular filtration and tubular secretion). Approximately 25-30% eliminated via non-renal routes including biliary excretion.',
        halfLife: '1-1.5 hours in adults with normal renal function. Prolonged to 7-20 hours in severe renal impairment (CrCl <10 mL/min). Neonates: 3-4 hours.'
      },
      pharmacodynamics: 'Time-dependent killing - efficacy correlates with time above MIC (T>MIC). Optimal bactericidal activity when free drug concentration exceeds MIC for 40-50% of dosing interval. Post-antibiotic effect is minimal against gram-negative organisms but moderate against streptococci.'
    },

    indications: [
      { condition: 'Community-acquired pneumonia (mild-moderate)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'High-dose for suspected resistant pneumococci' },
      { condition: 'Acute otitis media', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'High-dose (80-90mg/kg/day) for resistant pathogens' },
      { condition: 'Acute bacterial sinusitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Usually with clavulanate for better coverage' },
      { condition: 'Streptococcal pharyngitis/tonsillitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Alternative to penicillin V; better compliance' },
      { condition: 'H. pylori eradication', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Part of triple/quadruple therapy' },
      { condition: 'Urinary tract infection (uncomplicated)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Only if susceptibility confirmed; high resistance rates' },
      { condition: 'Dental infections/prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Pre-procedure prophylaxis for at-risk patients' },
      { condition: 'Lyme disease (early)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'For erythema migrans' },
      { condition: 'Infective endocarditis prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'For dental procedures in high-risk patients' },
      { condition: 'Typhoid fever (uncomplicated)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Only if susceptible; fluoroquinolones preferred in Africa' }
    ],

    dosing: {
      adults: {
        standard: '250-500mg every 8 hours OR 500-875mg every 12 hours',
        indicationSpecific: [
          { indication: 'Mild-moderate infections', dose: '500mg TDS or 875mg BD for 7-10 days' },
          { indication: 'Severe infections', dose: '875mg TDS or 1g TDS' },
          { indication: 'H. pylori eradication', dose: '1g BD for 14 days (with PPI + clarithromycin)' },
          { indication: 'Streptococcal pharyngitis', dose: '500mg BD or TDS for 10 days' },
          { indication: 'Dental prophylaxis', dose: '2g single dose 30-60 min before procedure' },
          { indication: 'Community-acquired pneumonia', dose: '500mg-1g TDS for 5-7 days' }
        ],
        maxDose: '3g/day (can use up to 6g/day in severe infections)',
        renalImpairment: {
          mild: 'CrCl 30-59: No adjustment needed',
          moderate: 'CrCl 10-29: 250-500mg every 12 hours',
          severe: 'CrCl <10: 250-500mg every 24 hours',
          dialysis: 'Give after dialysis session; supplement 250-500mg post-dialysis'
        },
        hepaticImpairment: 'No adjustment required; use with caution in severe hepatic disease'
      },
      pediatric: {
        standard: '25-50mg/kg/day divided every 8 hours OR 45-90mg/kg/day divided every 12 hours',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates 0-7 days', dose: '50mg/kg/day divided every 12 hours' },
          { age: 'Neonates 8-28 days', dose: '50-75mg/kg/day divided every 8 hours' },
          { age: 'Infants 1-3 months', dose: '20-30mg/kg/day divided every 12 hours' },
          { age: 'Children >3 months (mild-moderate)', dose: '25-45mg/kg/day divided every 8-12 hours' },
          { age: 'Children >3 months (severe/AOM)', dose: '80-90mg/kg/day divided every 12 hours' }
        ],
        maxDose: '3g/day',
        neonatal: '50mg/kg/day divided every 12 hours; increase to every 8 hours after first week'
      },
      elderly: 'No specific dose adjustment; monitor renal function',
      routes: ['Oral (capsules, tablets, suspension, dispersible tablets)', 'IV (as amoxicillin sodium)'],
      administration: [
        'Can be taken with or without food',
        'Suspension: Shake well before use; use calibrated measuring device',
        'Dispersible tablets: Dissolve in small amount of water before administration',
        'IV: Reconstitute with water for injection; give over 3-4 minutes or infuse over 30-60 minutes'
      ]
    },

    contraindications: {
      absolute: [
        'Known hypersensitivity to amoxicillin or any penicillin',
        'History of severe allergic reaction (anaphylaxis, angioedema) to any beta-lactam',
        'History of amoxicillin-associated cholestatic jaundice/hepatic dysfunction'
      ],
      relative: [
        'Cephalosporin allergy (1-2% cross-reactivity)',
        'Infectious mononucleosis (high risk of maculopapular rash - 90%)',
        'Cytomegalovirus infection (increased rash risk)',
        'Lymphocytic leukemia (increased rash risk)',
        'History of Clostridioides difficile infection'
      ],
      precautions: [
        'Renal impairment (dose adjustment required)',
        'Maintain adequate hydration to prevent crystalluria',
        'Prolonged use may result in superinfection',
        'May cause false-positive urine glucose tests (Benedict/Fehling)',
        'Use with caution in patients on anticoagulants (may increase INR)'
      ]
    },

    adverseEffects: {
      common: [
        { effect: 'Diarrhea', frequency: '10-20%', management: 'Usually self-limiting; consider probiotics; rule out C. diff if severe/persistent' },
        { effect: 'Nausea/Vomiting', frequency: '5-10%', management: 'Take with food; usually mild' },
        { effect: 'Rash (maculopapular)', frequency: '3-10%', management: 'Distinguish from hypersensitivity; may continue if non-allergic; very common with EBV' },
        { effect: 'Abdominal discomfort', frequency: '5-10%', management: 'Take with food' },
        { effect: 'Headache', frequency: '1-5%', management: 'Symptomatic treatment' }
      ],
      serious: [
        { effect: 'Anaphylaxis', frequency: '0.01-0.05%', management: 'STOP drug immediately; epinephrine, airway management, supportive care', warning: 'Can be fatal; more common with parenteral administration' },
        { effect: 'Clostridioides difficile-associated diarrhea (CDAD)', frequency: '0.1-1%', management: 'Stop amoxicillin; metronidazole or vancomycin for C. diff', warning: 'Can occur up to 2 months after treatment' },
        { effect: 'Stevens-Johnson Syndrome/TEN', frequency: '<0.01%', management: 'Immediate discontinuation; dermatology consult; supportive care', warning: 'Life-threatening; may require ICU' },
        { effect: 'Drug-induced liver injury', frequency: '<0.1%', management: 'Discontinue; monitor LFTs; usually reversible', warning: 'More common with amoxicillin-clavulanate' },
        { effect: 'Seizures', frequency: 'Rare', management: 'Stop drug; supportive care', warning: 'Usually with high doses in renal impairment or CNS disorders' },
        { effect: 'Interstitial nephritis', frequency: 'Rare', management: 'Discontinue; corticosteroids may help', warning: 'Usually reversible' }
      ],
      rare: [
        'Hemolytic anemia (Coombs positive)',
        'Thrombocytopenia',
        'Leukopenia/Agranulocytosis',
        'Crystalluria (high doses, dehydration)',
        'Black hairy tongue',
        'Tooth discoloration in children (with prolonged use)'
      ]
    },

    interactions: {
      major: [
        { drug: 'Methotrexate', effect: 'Increased methotrexate toxicity (myelosuppression, mucositis)', mechanism: 'Reduced renal tubular secretion of methotrexate', management: 'Avoid combination or monitor methotrexate levels closely; consider leucovorin rescue' },
        { drug: 'Warfarin', effect: 'Increased INR and bleeding risk', mechanism: 'Reduced gut flora vitamin K synthesis; possible CYP inhibition', management: 'Monitor INR closely; may need warfarin dose reduction' },
        { drug: 'Allopurinol', effect: 'Significantly increased risk of rash', mechanism: 'Unknown; possibly immunological', management: 'Monitor for rash; consider alternative urate-lowering therapy' }
      ],
      moderate: [
        { drug: 'Oral contraceptives', effect: 'Possible reduced contraceptive efficacy (controversial)', management: 'Use backup contraception during treatment and 7 days after' },
        { drug: 'Probenecid', effect: 'Increased amoxicillin levels and prolonged half-life', management: 'May be used therapeutically; monitor for adverse effects' },
        { drug: 'Mycophenolate', effect: 'Reduced mycophenolate levels', management: 'Monitor for rejection in transplant patients' },
        { drug: 'Aminoglycosides', effect: 'Synergistic bactericidal effect (enterococcal infections)', management: 'Used therapeutically for endocarditis; monitor renal function' }
      ],
      food: [
        'No significant food interactions',
        'Can be taken with or without food',
        'Avoid taking with fruit juice (may reduce absorption slightly)'
      ]
    },

    pregnancy: {
      category: 'B (FDA) / Compatible (WHO)',
      trimpiesterRisks: {
        first: 'No increased risk of major malformations in human studies',
        second: 'Safe; extensive use data',
        third: 'Safe; may use up to delivery'
      },
      recommendation: 'SAFE IN PREGNANCY. One of the preferred antibiotics during pregnancy. Extensive human data supports safety. No dose adjustment needed.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in small amounts (<1% of maternal dose). Generally considered compatible with breastfeeding. Monitor infant for diarrhea, candidiasis, or allergic reaction.'
      }
    },

    monitoring: {
      baseline: [
        { test: 'Allergy history', reason: 'Critical to identify penicillin/cephalosporin allergy' },
        { test: 'Renal function (if prolonged use)', reason: 'Dose adjustment in renal impairment' },
        { test: 'LFTs (if hepatic risk)', reason: 'Baseline for comparison if hepatotoxicity suspected' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: '48-72 hours', target: 'Improvement in symptoms' },
        { test: 'Stool (if diarrhea develops)', frequency: 'As needed', target: 'Rule out C. difficile' },
        { test: 'Renal function', frequency: 'Weekly if prolonged therapy', target: 'Stable renal function' },
        { test: 'CBC (prolonged therapy)', frequency: 'Every 1-2 weeks', target: 'No cytopenias' }
      ],
      therapeutic: {
        range: 'Not routinely monitored; MIC-guided dosing in severe infections',
        timing: 'Serum levels rarely needed clinically'
      }
    },

    storage: {
      temperature: 'Capsules/Tablets: Store below 25°C. Suspension: Store powder below 25°C; reconstituted suspension refrigerate (2-8°C) or room temperature depending on formulation.',
      light: 'Protect from light',
      humidity: 'Store in dry place; protect from moisture',
      specialInstructions: 'Reconstituted suspension: Use within 7-14 days depending on manufacturer. Discard unused suspension after course completion.',
      shelfLife: 'Tablets/Capsules: 2-3 years. Reconstituted suspension: 7-14 days.'
    },

    africanContext: {
      availability: 'WIDELY AVAILABLE. One of the most accessible antibiotics across Africa. Available at primary health centers, pharmacies, and community health posts. Multiple generic manufacturers.',
      affordability: 'VERY AFFORDABLE. Generic formulations cost <$0.50 USD for a 7-day course. Included in most national essential medicines lists and public sector procurement.',
      supplyChainNotes: 'Stable supply in most countries. Dispersible tablets preferred for pediatric use in areas with limited clean water. Suspension requires cold chain for storage after reconstitution in hot climates.',
      localManufacturing: 'Manufactured locally in several African countries including Nigeria, Kenya, South Africa, Egypt, and Morocco. WHO prequalified generics available.',
      alternatives: [
        'Ampicillin (similar spectrum, less bioavailable orally)',
        'Penicillin V (narrower spectrum, streptococcal infections)',
        'Erythromycin (if penicillin allergic)',
        'Azithromycin (if penicillin allergic)',
        'Cotrimoxazole (for susceptible respiratory infections)'
      ],
      resistancePatterns: 'Increasing resistance in S. pneumoniae (up to 30-40% in some regions), H. influenzae (beta-lactamase production), and E. coli (>50% resistance). Local antibiogram data essential. High-dose therapy may overcome some pneumococcal resistance.',
      endemicConsiderations: 'Used in syndromic management of respiratory infections. Part of WHO IMCI guidelines for pneumonia in children. Important for rheumatic fever prophylaxis (secondary prevention with benzathine penicillin preferred).'
    },

    patientCounseling: {
      keyPoints: [
        'Complete the entire course of antibiotics even if you feel better',
        'Take at regular intervals to maintain consistent blood levels',
        'Can be taken with or without food; take with food if stomach upset occurs',
        'Shake suspension well before each dose and use the measuring device provided',
        'Store suspension in refrigerator after mixing; discard after 14 days'
      ],
      commonMisconceptions: [
        'MYTH: "Stop taking antibiotics when you feel better" - TRUTH: This promotes resistance; complete the full course',
        'MYTH: "Antibiotics work for colds and flu" - TRUTH: These are viral; antibiotics do not help',
        'MYTH: "More expensive means more effective" - TRUTH: Generic amoxicillin is equally effective',
        'MYTH: "Rash always means allergy" - TRUTH: Some rashes are non-allergic; but report any rash to your doctor'
      ],
      adherenceTips: [
        'Set phone alarms for dose times',
        'Use a pill box to track doses',
        'Link doses to daily activities (e.g., meals)',
        'For children: mix suspension with small amount of food if needed',
        'Keep medication visible as a reminder'
      ],
      warningSignsToReport: [
        'Difficulty breathing or swallowing',
        'Swelling of face, lips, tongue, or throat',
        'Severe skin rash, blisters, or peeling',
        'Severe diarrhea (especially bloody or watery)',
        'Yellowing of skin or eyes',
        'Dark urine or pale stools',
        'Symptoms not improving after 48-72 hours'
      ]
    },

    clinicalPearls: [
      'High-dose amoxicillin (80-90mg/kg/day) can overcome intermediate-level pneumococcal resistance',
      'In infectious mononucleosis, 90%+ of patients develop a maculopapular rash - this is not a true allergy',
      'Amoxicillin has better oral bioavailability than ampicillin (90% vs 50%)',
      'For H. pylori, always combine with PPI and second antibiotic (clarithromycin or metronidazole)',
      'The beta-lactam/beta-lactamase inhibitor combination (amoxicillin-clavulanate) extends coverage to beta-lactamase producing organisms',
      'Time-dependent killing means frequency of dosing matters more than peak levels',
      'In renal impairment, extend interval rather than reducing individual doses to maintain T>MIC',
      'Cross-reactivity between penicillins and cephalosporins is lower than historically believed (1-2%)'
    ],

    references: [
      'WHO Model List of Essential Medicines - 23rd List (2023)',
      'Sanford Guide to Antimicrobial Therapy 2023',
      'UpToDate: Amoxicillin - Drug Information',
      'British National Formulary (BNF) 2023',
      'NICE Guidelines: Respiratory tract infections (self-limiting): prescribing antibiotics',
      'WHO IMCI Guidelines: Integrated Management of Childhood Illness',
      'African Society for Laboratory Medicine: Antimicrobial Resistance Guidelines'
    ],

    lastUpdated: '2026-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. CEFTRIAXONE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'ceftriaxone-002',
    genericName: 'Ceftriaxone',
    brandNames: ['Rocephin', 'Triaxone', 'Ceftriaxone Sodium', 'Oframax'],
    atcCode: 'J01DD04',
    therapeuticClass: 'Third-Generation Cephalosporin Antibiotic',
    
    pharmacology: {
      mechanism: `Ceftriaxone is a bactericidal third-generation cephalosporin that inhibits bacterial cell wall synthesis by binding to penicillin-binding proteins (PBPs). It has high affinity for PBP1, PBP2, and PBP3, preventing the final transpeptidation step of peptidoglycan synthesis. This results in cell wall weakening and osmotic lysis. Ceftriaxone has enhanced stability against beta-lactamases produced by gram-negative bacteria, providing extended-spectrum activity. It is particularly effective against Enterobacteriaceae but lacks activity against Pseudomonas aeruginosa (unlike ceftazidime).`,
      pharmacokinetics: {
        absorption: 'Not absorbed orally; must be given parenterally (IV or IM). Complete bioavailability with both routes. Peak serum concentrations: 2-3 hours after IM injection.',
        distribution: 'Widely distributed to tissues and body fluids. Excellent CSF penetration (5-15% of serum levels with uninflamed meninges; 15-40% with inflamed meninges). High biliary concentrations. Volume of distribution: 0.12-0.18 L/kg. Protein binding: 85-95% (saturable, decreases at higher concentrations).',
        metabolism: 'Not metabolized by the liver. No hepatic enzyme involvement.',
        elimination: 'Dual elimination: 40-65% excreted unchanged in urine; 35-45% excreted in bile (as active drug and inactive metabolites). Undergoes some intestinal degradation.',
        halfLife: '6-9 hours in adults (allows once-daily dosing). Neonates: 9-19 hours. Prolonged in severe renal impairment (up to 16 hours).'
      },
      pharmacodynamics: 'Time-dependent killing with a prolonged post-antibiotic effect (2-4 hours). Optimal efficacy when free drug concentration exceeds MIC for 50-70% of the dosing interval. Long half-life allows once or twice daily dosing while maintaining adequate T>MIC.'
    },

    indications: [
      { condition: 'Bacterial meningitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line empiric therapy; add ampicillin if Listeria suspected (age >50, immunocompromised)' },
      { condition: 'Severe community-acquired pneumonia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Often combined with macrolide for atypical coverage' },
      { condition: 'Sepsis/Bacteremia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Empiric therapy pending culture results' },
      { condition: 'Typhoid fever', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Especially for severe/complicated cases or MDR strains' },
      { condition: 'Gonorrhea (uncomplicated)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '500mg IM single dose (some guidelines recommend 250mg)' },
      { condition: 'Acute bacterial otitis media (severe)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'When oral therapy fails or not possible' },
      { condition: 'Pelvic inflammatory disease', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with doxycycline ± metronidazole' },
      { condition: 'Lyme disease (neurological/cardiac)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '2g daily for 14-28 days' },
      { condition: 'Spontaneous bacterial peritonitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '2g daily for 5-7 days' },
      { condition: 'Neonatal sepsis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with ampicillin; avoid in neonates with hyperbilirubinemia' }
    ],

    dosing: {
      adults: {
        standard: '1-2g IV/IM once daily',
        indicationSpecific: [
          { indication: 'Mild-moderate infections', dose: '1g IV/IM once daily' },
          { indication: 'Severe infections', dose: '2g IV once daily' },
          { indication: 'Meningitis', dose: '2g IV every 12 hours (4g/day)' },
          { indication: 'Gonorrhea', dose: '500mg IM single dose' },
          { indication: 'Typhoid fever', dose: '2g IV/IM once daily for 7-14 days' },
          { indication: 'Spontaneous bacterial peritonitis', dose: '2g IV once daily for 5-7 days' },
          { indication: 'Surgical prophylaxis', dose: '1-2g IV within 60 minutes of incision' }
        ],
        maxDose: '4g/day (meningitis); 2g/day for most other infections',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment required (due to biliary excretion)',
          severe: 'CrCl <10: Maximum 2g/day; monitor for accumulation',
          dialysis: 'Not significantly removed by hemodialysis; give after dialysis if scheduled'
        },
        hepaticImpairment: 'Combined hepatic and renal impairment: Maximum 2g/day and monitor'
      },
      pediatric: {
        standard: '50-100mg/kg/day IV/IM once daily or divided every 12 hours',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates (<14 days)', dose: '50mg/kg/day once daily' },
          { age: 'Neonates (14-28 days)', dose: '50-75mg/kg/day once daily' },
          { age: 'Infants and children (mild-moderate)', dose: '50-75mg/kg/day once daily' },
          { age: 'Infants and children (severe/meningitis)', dose: '100mg/kg/day divided every 12 hours' }
        ],
        maxDose: '4g/day (meningitis); 2g/day for most other infections',
        neonatal: '50mg/kg/day once daily. AVOID in jaundiced neonates or those receiving calcium-containing IV fluids.'
      },
      elderly: 'No specific dose adjustment; monitor for accumulation if combined hepatorenal impairment',
      routes: ['Intramuscular (IM)', 'Intravenous (IV) - bolus or infusion'],
      administration: [
        'IV bolus: Dilute 1g in 10mL sterile water; give slowly over 2-4 minutes',
        'IV infusion: Dilute in 50-100mL NS or D5W; infuse over 30 minutes',
        'IM injection: Reconstitute with lidocaine 1% to reduce pain (NOT for IV use)',
        'Do not mix with calcium-containing solutions (can precipitate)',
        'Administer at least 48 hours apart from calcium-containing IV fluids in neonates'
      ]
    },

    contraindications: {
      absolute: [
        'Known hypersensitivity to ceftriaxone or any cephalosporin',
        'History of severe allergic reaction (anaphylaxis) to any beta-lactam',
        'Neonates (<28 days) receiving calcium-containing IV solutions',
        'Hyperbilirubinemic neonates (displaces bilirubin from albumin)',
        'Premature neonates until corrected age of 41 weeks'
      ],
      relative: [
        'Penicillin allergy (1-2% cross-reactivity)',
        'History of gallbladder disease (risk of biliary sludge)',
        'Renal impairment with concurrent hepatic impairment',
        'History of GI disease, especially colitis'
      ],
      precautions: [
        'Prolonged use may result in superinfection including C. difficile',
        'Can cause biliary pseudolithiasis/sludge (usually reversible)',
        'Monitor for hemolytic anemia in prolonged use',
        'May cause false-positive urine glucose tests',
        'Can interfere with galactosemia newborn screening'
      ]
    },

    adverseEffects: {
      common: [
        { effect: 'Injection site pain/induration', frequency: '5-17% (IM)', management: 'Use lidocaine 1% for IM reconstitution; rotate injection sites' },
        { effect: 'Diarrhea', frequency: '3-10%', management: 'Usually self-limiting; rule out C. diff if severe/persistent' },
        { effect: 'Rash', frequency: '2-5%', management: 'Distinguish from hypersensitivity; may need to discontinue' },
        { effect: 'Eosinophilia', frequency: '3-8%', management: 'Usually benign; monitor' },
        { effect: 'Elevated liver enzymes', frequency: '3-5%', management: 'Usually transient; monitor LFTs if persistent' }
      ],
      serious: [
        { effect: 'Anaphylaxis', frequency: '0.01-0.1%', management: 'STOP immediately; epinephrine, airway management, supportive care', warning: 'Can be fatal; inquire about beta-lactam allergies' },
        { effect: 'Biliary pseudolithiasis/sludge', frequency: '1-5% (higher with high doses/prolonged use)', management: 'Usually reversible after stopping; rarely symptomatic', warning: 'More common in children and with high doses' },
        { effect: 'Clostridioides difficile colitis', frequency: '0.1-1%', management: 'Stop ceftriaxone; metronidazole or vancomycin for C. diff', warning: 'Can occur up to 2 months after treatment' },
        { effect: 'Immune-mediated hemolytic anemia', frequency: '<0.01%', management: 'Discontinue immediately; transfusion if severe; corticosteroids', warning: 'Can be fatal; more common with prolonged use' },
        { effect: 'Stevens-Johnson Syndrome/TEN', frequency: '<0.01%', management: 'Immediate discontinuation; dermatology consult; supportive care', warning: 'Life-threatening' },
        { effect: 'Ceftriaxone-calcium precipitates (neonates)', frequency: 'Rare but serious', management: 'NEVER mix or administer close to calcium-containing fluids', warning: 'Can be fatal in neonates; lung and kidney precipitates reported' }
      ],
      rare: [
        'Nephrotoxicity (especially with aminoglycosides)',
        'Pancreatitis',
        'Seizures (very high doses or renal impairment)',
        'Thrombocytopenia',
        'Neutropenia (prolonged use)',
        'Encephalopathy'
      ]
    },

    interactions: {
      major: [
        { drug: 'Calcium-containing IV solutions', effect: 'Formation of ceftriaxone-calcium precipitates', mechanism: 'Direct chemical incompatibility', management: 'NEVER mix; in neonates, separate by at least 48 hours; in adults, can give sequentially with line flush' },
        { drug: 'Warfarin', effect: 'Increased INR and bleeding risk', mechanism: 'Reduced vitamin K synthesis by gut flora; possible protein binding displacement', management: 'Monitor INR closely; may need warfarin dose reduction' },
        { drug: 'Aminoglycosides', effect: 'Increased nephrotoxicity risk; synergistic bactericidal effect', mechanism: 'Additive nephrotoxicity; enhanced bacterial killing', management: 'Useful therapeutically for synergy; monitor renal function closely' }
      ],
      moderate: [
        { drug: 'Oral contraceptives', effect: 'Possible reduced contraceptive efficacy', management: 'Use backup contraception during treatment' },
        { drug: 'Loop diuretics (furosemide)', effect: 'Possible increased nephrotoxicity', management: 'Monitor renal function' },
        { drug: 'Probenecid', effect: 'Increased ceftriaxone levels', management: 'Not clinically significant due to biliary elimination' },
        { drug: 'Ringer lactate solution', effect: 'Contains calcium - risk of precipitation', management: 'Do not use Ringer lactate as diluent; use NS or D5W' }
      ],
      food: [
        'No food interactions (parenteral administration)',
        'No dietary restrictions needed'
      ]
    },

    pregnancy: {
      category: 'B (FDA) / Compatible (WHO)',
      trimpiesterRisks: {
        first: 'No increased risk of major malformations in human studies',
        second: 'Safe; extensive use data',
        third: 'Safe; avoid immediately before delivery if possible (theoretical risk of bilirubin displacement in neonate)'
      },
      recommendation: 'SAFE IN PREGNANCY. Cephalosporins are among the safest antibiotics during pregnancy. Crosses placenta but no evidence of teratogenicity.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in very low concentrations (3-4% of maternal dose). Generally compatible with breastfeeding. Monitor infant for diarrhea or candidiasis.'
      }
    },

    monitoring: {
      baseline: [
        { test: 'Allergy history', reason: 'Critical to identify beta-lactam allergy' },
        { test: 'Renal function', reason: 'Dose adjustment if combined hepatorenal impairment' },
        { test: 'Hepatic function', reason: 'Baseline for biliary complications' },
        { test: 'CBC', reason: 'Baseline for hematologic monitoring' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: '48-72 hours', target: 'Improvement in signs/symptoms' },
        { test: 'Renal function', frequency: 'Every 2-3 days in prolonged therapy', target: 'Stable creatinine' },
        { test: 'LFTs', frequency: 'Weekly in prolonged therapy', target: 'No significant elevation' },
        { test: 'CBC', frequency: 'Weekly in prolonged therapy', target: 'No cytopenias' },
        { test: 'Abdominal ultrasound', frequency: 'If abdominal symptoms develop', target: 'Rule out biliary sludge' }
      ],
      therapeutic: {
        range: 'Routine TDM not required; may measure trough levels in severe infections (target: 1-4 mg/L)',
        timing: 'Trough level just before next dose if needed'
      }
    },

    storage: {
      temperature: 'Dry powder: Store below 25°C. Reconstituted solution: Stable for 6 hours at room temperature or 24 hours refrigerated.',
      light: 'Protect reconstituted solutions from direct light',
      humidity: 'Store dry powder in dry place',
      specialInstructions: 'Reconstituted solutions may darken but remain potent. Discard if particulate matter or discoloration observed. IM solutions with lidocaine must never be given IV.',
      shelfLife: 'Dry powder: 2-3 years. Reconstituted: 6-24 hours depending on storage conditions.'
    },

    africanContext: {
      availability: 'AVAILABLE AT INTERMEDIATE/ADVANCED FACILITIES. Found at district hospitals and above. May not be available at primary health centers. On national essential medicines lists.',
      affordability: 'MODERATE COST. Generic formulations: $0.50-2 USD per 1g vial. More expensive than first-line oral antibiotics but generally affordable in public sector.',
      supplyChainNotes: 'Stable as dry powder without cold chain. Reconstituted solutions require proper storage. Stock-outs can occur at peripheral facilities.',
      localManufacturing: 'Manufactured in several African countries. WHO prequalified generics available. Quality concerns with some local products - ensure quality-assured sources.',
      alternatives: [
        'Cefotaxime (similar spectrum, shorter half-life, more frequent dosing)',
        'Ampicillin + Gentamicin (for neonatal sepsis where ceftriaxone contraindicated)',
        'Chloramphenicol (meningitis alternative in resource-limited settings)',
        'Ciprofloxacin (for typhoid fever, not suitable for children)'
      ],
      resistancePatterns: 'Increasing resistance in Enterobacteriaceae (ESBL producers: 10-40% in many African hospitals). Widespread resistance in Salmonella typhi remains low but increasing. Local antibiogram data essential for empiric therapy.',
      endemicConsiderations: 'First-line for severe malaria with bacterial co-infection. Used empirically for severe pneumonia and sepsis. Important for typhoid fever management. Used in outbreaks of bacterial meningitis (meningococcal, pneumococcal).'
    },

    patientCounseling: {
      keyPoints: [
        'This medication must be given by injection (IV or IM)',
        'Complete the full course of treatment as prescribed',
        'IM injections may be painful; lidocaine helps reduce pain',
        'Report any signs of allergic reaction immediately',
        'Maintain good hydration during treatment'
      ],
      commonMisconceptions: [
        'MYTH: "IV antibiotics are always stronger than oral" - TRUTH: Route depends on severity; equivalent oral options exist for some infections',
        'MYTH: "One injection will cure my infection" - TRUTH: Most infections require multiple doses over several days',
        'MYTH: "I can take it at home without monitoring" - TRUTH: Injectable antibiotics often need clinical monitoring'
      ],
      adherenceTips: [
        'Attend all scheduled injection appointments',
        'Do not miss doses; consistent timing improves effectiveness',
        'If admitted, ensure doses are given on time',
        'Complete the full course even if feeling better'
      ],
      warningSignsToReport: [
        'Difficulty breathing, swelling of face/throat (allergic reaction)',
        'Severe or bloody diarrhea',
        'Skin rash, blisters, or peeling',
        'Abdominal pain (right upper quadrant)',
        'Yellowing of skin or eyes',
        'Unusual bleeding or bruising',
        'Symptoms not improving after 72 hours'
      ]
    },

    clinicalPearls: [
      'Once-daily dosing is possible due to long half-life and post-antibiotic effect',
      'NEVER co-administer with calcium in neonates - fatal precipitates have occurred',
      'For meningitis, high doses (4g/day) are needed to achieve adequate CSF concentrations',
      'Can be used IM in outpatient settings for gonorrhea, moderate infections',
      'Biliary sludge is usually asymptomatic and reversible after treatment completion',
      'In patients with combined hepatorenal impairment, cap dose at 2g/day',
      'Reconstitute IM injections with 1% lidocaine (NOT IV solutions) to reduce pain',
      'Consider ESBL risk in patients with recent hospitalization or antibiotic exposure',
      'Provides reliable gram-negative coverage but NO Pseudomonas activity'
    ],

    references: [
      'WHO Model List of Essential Medicines - 23rd List (2023)',
      'Sanford Guide to Antimicrobial Therapy 2023',
      'UpToDate: Ceftriaxone - Drug Information',
      'British National Formulary (BNF) 2023',
      'IDSA Guidelines: Healthcare-Associated Ventriculitis and Meningitis',
      'WHO Pocket Book of Hospital Care for Children (2nd Edition)',
      'NICE Guidelines: Meningitis (bacterial) and meningococcal septicaemia'
    ],

    lastUpdated: '2026-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. METRONIDAZOLE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'metronidazole-003',
    genericName: 'Metronidazole',
    brandNames: ['Flagyl', 'Metrogyl', 'Trichozole', 'Flagystatin'],
    atcCode: 'J01XD01',
    therapeuticClass: 'Nitroimidazole Antibiotic/Antiprotozoal',
    
    pharmacology: {
      mechanism: `Metronidazole is a bactericidal and antiprotozoal agent with a unique mechanism of action. The nitro group of the drug is reduced by ferredoxin (or ferredoxin-like electron transport proteins) in anaerobic organisms, producing cytotoxic intermediates that damage DNA by causing strand breaks and destabilizing the helix. This results in cell death. The selectivity for anaerobes comes from the low redox potential required for nitro group reduction, which only occurs in anaerobic or microaerophilic environments. Aerobic organisms lack the necessary electron transport proteins for activation.`,
      pharmacokinetics: {
        absorption: 'Well absorbed orally (nearly 100% bioavailability). Rectal bioavailability: 60-80%. IV provides 100% bioavailability. Peak concentrations in 1-2 hours.',
        distribution: 'Widely distributed to all body tissues and fluids, including CSF (80-100% of serum levels), abscess cavities, bone, and pelvic tissues. Crosses placenta. Volume of distribution: 0.5-1 L/kg. Protein binding: <20%.',
        metabolism: 'Hepatic metabolism via oxidation and glucuronidation (60-80%). Major metabolites: hydroxy-metronidazole (active, 30-65% activity of parent) and glucuronide conjugates.',
        elimination: 'Primarily renal: 60-80% of dose excreted in urine (20% as unchanged drug, remainder as metabolites). 6-15% excreted in feces.',
        halfLife: '6-8 hours in adults with normal hepatic function. Prolonged in severe hepatic impairment (up to 18 hours).'
      },
      pharmacodynamics: 'Concentration-dependent killing. Efficacy correlates with the ratio of peak concentration to MIC (Cmax/MIC). Prolonged post-antibiotic effect against anaerobes (2-4 hours).'
    },

    indications: [
      { condition: 'Anaerobic bacterial infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Intra-abdominal, pelvic, CNS infections' },
      { condition: 'Clostridioides difficile infection', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for non-severe CDI; vancomycin for severe' },
      { condition: 'Amoebiasis (intestinal and hepatic)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Follow with luminal agent (paromomycin/diloxanide)' },
      { condition: 'Giardiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Alternative: tinidazole (single dose)' },
      { condition: 'Bacterial vaginosis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Oral or intravaginal' },
      { condition: 'Trichomoniasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Treat sexual partners concurrently' },
      { condition: 'H. pylori eradication', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Part of triple/quadruple therapy (if clarithromycin resistant)' },
      { condition: 'Surgical prophylaxis (colorectal)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with cephalosporin' },
      { condition: 'Dental infections (severe)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Add to penicillin for anaerobic coverage' },
      { condition: 'Aspiration pneumonia', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'For anaerobic coverage in combination therapy' }
    ],

    dosing: {
      adults: {
        standard: '400-500mg every 8 hours OR 800mg every 12 hours',
        indicationSpecific: [
          { indication: 'Anaerobic infections', dose: '500mg IV/PO every 8 hours for 7-10 days' },
          { indication: 'C. difficile infection', dose: '500mg PO every 8 hours for 10-14 days' },
          { indication: 'Amoebiasis (intestinal)', dose: '750mg PO TDS for 7-10 days' },
          { indication: 'Amoebiasis (hepatic abscess)', dose: '500-750mg PO TDS for 7-10 days' },
          { indication: 'Giardiasis', dose: '250-500mg TDS for 5-7 days OR 2g single dose for 3 days' },
          { indication: 'Bacterial vaginosis', dose: '400-500mg BD for 7 days OR 2g single dose' },
          { indication: 'Trichomoniasis', dose: '2g single dose OR 500mg BD for 7 days' },
          { indication: 'H. pylori', dose: '500mg BD for 14 days (with PPI and other antibiotic)' },
          { indication: 'Surgical prophylaxis', dose: '500mg IV pre-operatively' }
        ],
        maxDose: '4g/day',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment required',
          severe: 'CrCl <10: Reduce dose by 50%; give every 12 hours',
          dialysis: 'Give after dialysis; supplement 50% of dose post-dialysis'
        },
        hepaticImpairment: 'Mild-moderate: Use with caution. Severe: Reduce dose by 50-67%; monitor for toxicity'
      },
      pediatric: {
        standard: '20-30mg/kg/day divided every 8 hours (max 2g/day)',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates', dose: '15mg/kg loading then 7.5mg/kg every 12-24 hours (age-dependent)' },
          { age: 'Infants/Children (amoebiasis)', dose: '35-50mg/kg/day divided TDS for 7-10 days' },
          { age: 'Infants/Children (giardiasis)', dose: '15-30mg/kg/day divided TDS for 5-7 days' }
        ],
        maxDose: '2g/day (amoebiasis: 2.4g/day)',
        neonatal: '15mg/kg loading dose then 7.5mg/kg; frequency based on gestational and postnatal age'
      },
      elderly: 'No specific adjustment; monitor for CNS effects; use cautiously in hepatic impairment',
      routes: ['Oral (tablets, suspension)', 'Intravenous', 'Rectal (suppositories)', 'Topical (gel, cream)', 'Intravaginal'],
      administration: [
        'Oral: Can be taken with or without food; take with food to reduce GI upset',
        'IV: Infuse over 30-60 minutes; do not give as bolus',
        'Avoid crushing extended-release tablets',
        'Intravaginal: Insert applicator at bedtime; avoid intercourse during treatment'
      ]
    },

    contraindications: {
      absolute: [
        'Known hypersensitivity to metronidazole or other nitroimidazoles',
        'First trimester of pregnancy (for non-life-threatening indications)',
        'Concurrent use of alcohol (disulfiram-like reaction)',
        'Concurrent use of disulfiram (within 2 weeks)'
      ],
      relative: [
        'Active CNS disease (may exacerbate)',
        'Blood dyscrasias',
        'Hepatic impairment (reduce dose)',
        'Cockayne syndrome (avoid - liver failure risk)'
      ],
      precautions: [
        'Avoid alcohol during treatment and for 48-72 hours after',
        'Prolonged use (>10 days) may cause peripheral neuropathy',
        'May exacerbate candidiasis',
        'Contains sodium in IV formulation (consider in sodium-restricted patients)'
      ]
    },

    adverseEffects: {
      common: [
        { effect: 'Metallic taste', frequency: '12-15%', management: 'Temporary; resolves after treatment; suggest sugar-free mints' },
        { effect: 'Nausea/Vomiting', frequency: '5-10%', management: 'Take with food; usually mild' },
        { effect: 'Headache', frequency: '5-10%', management: 'Simple analgesics' },
        { effect: 'Anorexia', frequency: '5-10%', management: 'Usually self-limiting' },
        { effect: 'Abdominal cramps', frequency: '5%', management: 'Take with food' },
        { effect: 'Dark urine', frequency: 'Variable', management: 'Harmless; due to metabolites; warn patients' }
      ],
      serious: [
        { effect: 'Peripheral neuropathy', frequency: '0.1-1% (with prolonged use)', management: 'STOP drug immediately; usually reversible but may take months', warning: 'Risk increases with cumulative dose; numbness/tingling in extremities' },
        { effect: 'CNS toxicity (encephalopathy, seizures)', frequency: '<0.1%', management: 'Discontinue; supportive care; usually reversible', warning: 'More common with high doses or prolonged use' },
        { effect: 'Disulfiram-like reaction with alcohol', frequency: 'Common if alcohol consumed', management: 'Avoid alcohol completely; supportive care', warning: 'Flushing, nausea, vomiting, tachycardia, hypotension' },
        { effect: 'Leukopenia', frequency: 'Rare', management: 'Monitor CBC; discontinue if severe', warning: 'Reversible on discontinuation' },
        { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Monitor LFTs; discontinue if significant elevation', warning: 'More common in those with underlying liver disease' }
      ],
      rare: [
        'Optic neuropathy',
        'Cerebellar dysfunction (ataxia, dysarthria)',
        'Stevens-Johnson Syndrome',
        'Pancreatitis',
        'QT prolongation',
        'Aseptic meningitis'
      ]
    },

    interactions: {
      major: [
        { drug: 'Alcohol', effect: 'Disulfiram-like reaction (flushing, nausea, vomiting, palpitations)', mechanism: 'Inhibition of acetaldehyde dehydrogenase', management: 'AVOID alcohol during treatment and 48-72 hours after completion' },
        { drug: 'Disulfiram', effect: 'Acute psychosis, confusion', mechanism: 'Combined effect on acetaldehyde metabolism and CNS', management: 'Do not use concurrently; wait 2 weeks between drugs' },
        { drug: 'Warfarin', effect: 'Increased INR and bleeding risk', mechanism: 'Inhibition of CYP2C9-mediated warfarin metabolism', management: 'Monitor INR closely; may need to reduce warfarin dose by 25-50%' },
        { drug: 'Lithium', effect: 'Increased lithium toxicity', mechanism: 'Reduced renal lithium clearance', management: 'Monitor lithium levels; may need dose reduction' }
      ],
      moderate: [
        { drug: 'Phenytoin', effect: 'Increased phenytoin levels and toxicity', management: 'Monitor phenytoin levels; adjust dose as needed' },
        { drug: 'Phenobarbital', effect: 'Decreased metronidazole levels', management: 'May need higher metronidazole dose' },
        { drug: 'Cyclosporine', effect: 'Increased cyclosporine levels', management: 'Monitor cyclosporine levels' },
        { drug: 'Busulfan', effect: 'Increased busulfan toxicity', management: 'Avoid combination if possible' }
      ],
      food: [
        'Alcohol: AVOID (disulfiram-like reaction)',
        'Food: Can reduce GI upset; does not significantly affect absorption',
        'Alcohol-containing products (mouthwashes, cough syrups): Avoid'
      ]
    },

    pregnancy: {
      category: 'B (FDA) / Use with caution in 1st trimester (WHO)',
      trimpiesterRisks: {
        first: 'Traditional advice to avoid; however, meta-analyses show no increased teratogenic risk. Avoid for non-life-threatening indications',
        second: 'Generally considered safe; extensive use data',
        third: 'Generally considered safe; may use up to delivery'
      },
      recommendation: 'AVOID IN FIRST TRIMESTER if possible for non-urgent indications. Can be used when benefits outweigh risks (e.g., severe amoebiasis, C. diff). Safe in second and third trimesters. Single-dose treatment for trichomoniasis is acceptable after first trimester.',
      lactation: {
        safe: false,
        notes: 'Excreted in breast milk in significant amounts (similar to maternal serum). Avoid breastfeeding during treatment and for 12-24 hours after last dose. May cause metallic taste in breast milk.'
      }
    },

    monitoring: {
      baseline: [
        { test: 'CBC', reason: 'Baseline if prolonged therapy planned' },
        { test: 'LFTs', reason: 'Baseline in patients with hepatic disease' },
        { test: 'Neurological assessment', reason: 'Baseline if CNS disease or prolonged therapy' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: '48-72 hours', target: 'Improvement in symptoms' },
        { test: 'CBC', frequency: 'Weekly if >10 days treatment', target: 'No leukopenia' },
        { test: 'LFTs', frequency: 'Weekly if >10 days or hepatic impairment', target: 'Stable enzymes' },
        { test: 'Neurological symptoms', frequency: 'Each visit if prolonged use', target: 'No neuropathy' }
      ],
      therapeutic: {
        range: 'Not routinely monitored',
        timing: 'N/A'
      }
    },

    storage: {
      temperature: 'Tablets: Store below 25°C. IV: Store at room temperature; protect from light during storage but not during administration.',
      light: 'IV solutions: Protect from direct light during storage (not required during infusion)',
      humidity: 'Protect from moisture',
      specialInstructions: 'IV solutions are light-sensitive but protection during infusion is not required. Discard unused IV solution.',
      shelfLife: 'Tablets: 3-5 years. IV: As per manufacturer (typically 2-3 years).'
    },

    africanContext: {
      availability: 'WIDELY AVAILABLE. One of the most accessible antibiotics across Africa. Available at primary health centers, pharmacies, and hospitals.',
      affordability: 'VERY AFFORDABLE. Generic formulations extremely low cost (<$0.50 USD for a course). Widely accessible to low-income populations.',
      supplyChainNotes: 'Stable supply. No cold chain required for tablets. IV requires proper storage.',
      localManufacturing: 'Manufactured locally in multiple African countries. Generic quality generally reliable.',
      alternatives: [
        'Tinidazole (similar spectrum, longer half-life, single-dose for some indications)',
        'Ornidazole (similar to tinidazole)',
        'Clindamycin (for anaerobic infections, if metronidazole not suitable)'
      ],
      resistancePatterns: 'Resistance in Helicobacter pylori increasing (30-50% in some African regions). Resistance in anaerobes remains low. Resistance in Trichomonas occasionally reported.',
      endemicConsiderations: 'Essential for endemic amoebiasis and giardiasis. Important component of H. pylori treatment where clarithromycin resistance is high. Used extensively for anaerobic coverage in surgical and obstetric settings.'
    },

    patientCounseling: {
      keyPoints: [
        'DO NOT drink alcohol during treatment and for 3 days after finishing',
        'Complete the entire course even if you feel better',
        'Take with food to reduce stomach upset',
        'Your urine may turn dark - this is harmless',
        'You may experience a metallic taste - this is temporary'
      ],
      commonMisconceptions: [
        'MYTH: "A little alcohol is okay" - TRUTH: Even small amounts can cause severe reaction',
        'MYTH: "It only treats stomach infections" - TRUTH: Effective against many different infections',
        'MYTH: "Dark urine means kidney damage" - TRUTH: This is a harmless effect of the medication'
      ],
      adherenceTips: [
        'Set alarms for dose times',
        'Take with meals to improve tolerance',
        'Read labels on products for hidden alcohol',
        'Complete full course for parasitic infections to prevent relapse'
      ],
      warningSignsToReport: [
        'Numbness or tingling in hands/feet (neuropathy)',
        'Confusion or seizures',
        'Severe abdominal pain',
        'Skin rash or itching',
        'Vision changes',
        'Symptoms not improving after 5-7 days'
      ]
    },

    clinicalPearls: [
      'The disulfiram-like reaction can occur even with alcohol in topical products and mouthwash',
      'For amoebiasis, ALWAYS follow with a luminal agent (paromomycin or diloxanide) to eradicate cysts',
      'Peripheral neuropathy risk increases with cumulative doses >40g - limit prolonged use',
      'In H. pylori therapy, metronidazole resistance can be overcome by increasing dose and duration',
      'Single-dose regimens (2g) for trichomoniasis have similar efficacy to 7-day courses',
      'Metronidazole has NO activity against aerobic bacteria - always combine for mixed infections',
      'Consider metronidazole for appendicitis, diverticulitis, and other intra-abdominal infections',
      'The metallic taste is due to the drug secretion in saliva, not just gut effects'
    ],

    references: [
      'WHO Model List of Essential Medicines - 23rd List (2023)',
      'Sanford Guide to Antimicrobial Therapy 2023',
      'UpToDate: Metronidazole - Drug Information',
      'British National Formulary (BNF) 2023',
      'IDSA/SHEA Guidelines: Clostridioides difficile Infection',
      'CDC Guidelines: Sexually Transmitted Infections Treatment',
      'WHO Guidelines: Treatment of Intestinal Parasites'
    ],

    lastUpdated: '2026-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. AZITHROMYCIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'azithromycin-004',
    genericName: 'Azithromycin',
    brandNames: ['Zithromax', 'Azithrocin', 'Zmax', 'Aziwok', 'Azee'],
    atcCode: 'J01FA10',
    therapeuticClass: 'Macrolide Antibiotic (Azalide)',
    
    pharmacology: {
      mechanism: `Azithromycin is a bacteriostatic macrolide antibiotic (azalide) that inhibits bacterial protein synthesis. It binds to the 50S ribosomal subunit near the peptidyl transferase center and blocks the translocation step of protein synthesis, preventing the growing peptide chain from moving to the next codon position. This reversibly inhibits bacterial growth. At high concentrations, it may be bactericidal against certain organisms. The azalide structure (15-membered ring) provides enhanced tissue penetration, longer half-life, and better gram-negative activity compared to erythromycin.`,
      pharmacokinetics: {
        absorption: 'Rapidly absorbed from GI tract (37-40% bioavailability). Food decreases rate but not extent of absorption for capsules; tablets can be taken with food. Peak serum concentrations in 2-3 hours.',
        distribution: 'Extensive tissue distribution - drug concentrates intracellularly (tissue levels 10-100x higher than serum). Highest concentrations in lungs, tonsils, prostate, and macrophages. Volume of distribution: 23-31 L/kg. Protein binding: 7-51% (concentration-dependent).',
        metabolism: 'Minimal hepatic metabolism. Parent drug is the main active form. CYP3A4 involvement is minor.',
        elimination: 'Primarily biliary excretion as unchanged drug (>50%). Transintestinal elimination also occurs. Only 6-12% excreted in urine unchanged.',
        halfLife: 'Serum: 11-14 hours after single dose, extending to 68-76 hours with multiple doses. Tissue half-life: 2-4 days. This allows short (3-5 day) courses to provide effective drug levels for up to 10 days.'
      },
      pharmacodynamics: 'Concentration-dependent killing with prolonged post-antibiotic effect (PAE) of 2-4 days. The AUC/MIC ratio best predicts efficacy. Tissue concentrations remain therapeutic for days after the last dose.'
    },

    indications: [
      { condition: 'Community-acquired pneumonia (mild-moderate)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Monotherapy for atypical coverage; add beta-lactam for CAP' },
      { condition: 'Acute bacterial sinusitis', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Second-line to amoxicillin' },
      { condition: 'Acute bronchitis/COPD exacerbation', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: true, notes: 'When bacterial infection confirmed' },
      { condition: 'Chlamydia trachomatis genital infection', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '1g single dose - high efficacy' },
      { condition: 'Chancroid', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '1g single dose' },
      { condition: 'Typhoid fever', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Alternative to fluoroquinolones' },
      { condition: 'Cholera', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '1g single dose; for outbreaks' },
      { condition: 'Travelers diarrhea', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '1g single dose or 500mg daily x 3 days' },
      { condition: 'Trachoma', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Mass drug administration programs' },
      { condition: 'MAC prophylaxis in HIV', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '1200mg weekly when CD4 <50' },
      { condition: 'Pertussis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Treatment and post-exposure prophylaxis' }
    ],

    dosing: {
      adults: {
        standard: '500mg on day 1, then 250mg once daily for days 2-5 (total 1.5g)',
        indicationSpecific: [
          { indication: 'Respiratory tract infections', dose: '500mg day 1, then 250mg days 2-5 (Z-pack)' },
          { indication: 'Community-acquired pneumonia', dose: '500mg daily for 3 days OR Z-pack' },
          { indication: 'Chlamydia/Chancroid', dose: '1g single dose' },
          { indication: 'Gonorrhea (combination therapy)', dose: '1-2g single dose with ceftriaxone' },
          { indication: 'Typhoid fever', dose: '1g daily for 5 days' },
          { indication: 'Cholera', dose: '1g single dose' },
          { indication: 'Travelers diarrhea', dose: '1g single dose or 500mg daily for 3 days' },
          { indication: 'Trachoma', dose: '1g single dose (mass administration)' },
          { indication: 'MAC prophylaxis', dose: '1200mg once weekly' },
          { indication: 'Pertussis', dose: '500mg day 1, then 250mg days 2-5' }
        ],
        maxDose: '2g/day (single doses up to 2g are used)',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment required',
          severe: 'Use with caution; no specific dose adjustment (limited renal excretion)',
          dialysis: 'Not significantly removed by dialysis'
        },
        hepaticImpairment: 'Caution in severe hepatic impairment (biliary excretion); avoid in severe liver disease'
      },
      pediatric: {
        standard: '10mg/kg on day 1 (max 500mg), then 5mg/kg daily for days 2-5 (max 250mg)',
        weightBased: true,
        ageSpecific: [
          { age: 'Infants <6 months', dose: 'Limited data; use with caution' },
          { age: 'Children (otitis media)', dose: '30mg/kg single dose OR 10mg/kg day 1, 5mg/kg days 2-5' },
          { age: 'Children (pharyngitis)', dose: '12mg/kg daily for 5 days (max 500mg/day)' },
          { age: 'Children (pneumonia)', dose: '10mg/kg day 1, then 5mg/kg days 2-5' },
          { age: 'Children (trachoma)', dose: '20mg/kg single dose (max 1g)' }
        ],
        maxDose: '500mg/day (single doses up to 30mg/kg for specific indications)',
        neonatal: 'Limited data; use only when benefits outweigh risks'
      },
      elderly: 'No dose adjustment; monitor for cardiac effects (QT prolongation)',
      routes: ['Oral (tablets, capsules, suspension)', 'Intravenous'],
      administration: [
        'Tablets: Can be taken with or without food',
        'Capsules: Take on empty stomach (1 hour before or 2 hours after food)',
        'Suspension: Shake well; can take with or without food',
        'IV: Infuse over at least 60 minutes (1mg/mL) or 3 hours (2mg/mL); never bolus'
      ]
    },

    contraindications: {
      absolute: [
        'Known hypersensitivity to azithromycin, erythromycin, or any macrolide/ketolide',
        'History of cholestatic jaundice/hepatic dysfunction with prior azithromycin use'
      ],
      relative: [
        'Known QT prolongation or history of torsades de pointes',
        'Concurrent use of drugs that prolong QT interval',
        'Electrolyte abnormalities (hypokalemia, hypomagnesemia)',
        'Clinically significant bradycardia or heart failure',
        'Myasthenia gravis (may exacerbate)',
        'Severe hepatic impairment'
      ],
      precautions: [
        'May cause prolonged cardiac repolarization (QT prolongation)',
        'Use cautiously in patients with hepatic impairment',
        'May exacerbate myasthenia gravis symptoms',
        'Superinfection may occur with prolonged use',
        'May mask or delay symptoms of incubating syphilis'
      ]
    },

    adverseEffects: {
      common: [
        { effect: 'Diarrhea', frequency: '5-10%', management: 'Usually mild and self-limiting; rule out C. diff if severe' },
        { effect: 'Nausea', frequency: '3-5%', management: 'Take with food (tablets); usually mild' },
        { effect: 'Abdominal pain', frequency: '3-5%', management: 'Usually transient' },
        { effect: 'Headache', frequency: '1-3%', management: 'Simple analgesics' }
      ],
      serious: [
        { effect: 'QT prolongation/Torsades de pointes', frequency: 'Rare', management: 'Avoid in high-risk patients; monitor ECG if risk factors', warning: 'FDA warning; risk factors: elderly, heart disease, electrolyte abnormalities, concurrent QT-prolonging drugs' },
        { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Discontinue if jaundice or elevated LFTs; usually reversible', warning: 'Can occur after completing course; cases of fulminant hepatitis reported' },
        { effect: 'Clostridioides difficile colitis', frequency: '<1%', management: 'Stop azithromycin; treat C. diff with metronidazole or vancomycin', warning: 'Can occur up to 2 months after treatment' },
        { effect: 'Allergic reactions/Anaphylaxis', frequency: '<0.1%', management: 'Discontinue; supportive care; epinephrine if anaphylaxis', warning: 'Cross-reactivity with erythromycin' },
        { effect: 'Infantile hypertrophic pyloric stenosis', frequency: 'Risk in neonates', management: 'Monitor infants <2 weeks; report projectile vomiting', warning: 'Increased risk in neonates, especially first 2 weeks of life' }
      ],
      rare: [
        'Hearing loss (usually reversible)',
        'Stevens-Johnson Syndrome',
        'Acute interstitial nephritis',
        'Pancreatitis',
        'Hemolytic anemia',
        'Thrombocytopenia'
      ]
    },

    interactions: {
      major: [
        { drug: 'QT-prolonging drugs (e.g., amiodarone, sotalol, quinidine, haloperidol)', effect: 'Additive QT prolongation; risk of torsades de pointes', mechanism: 'Combined effect on cardiac repolarization', management: 'Avoid combination or monitor ECG closely' },
        { drug: 'Nelfinavir', effect: 'Increased azithromycin levels', mechanism: 'Unknown (not CYP-mediated)', management: 'Monitor for azithromycin toxicity' },
        { drug: 'Warfarin', effect: 'Possible increased INR and bleeding', mechanism: 'Uncertain; gut flora effects', management: 'Monitor INR during and after treatment' }
      ],
      moderate: [
        { drug: 'Digoxin', effect: 'Increased digoxin levels', management: 'Monitor digoxin levels' },
        { drug: 'Cyclosporine', effect: 'Increased cyclosporine levels', management: 'Monitor cyclosporine levels' },
        { drug: 'Antacids (Al/Mg)', effect: 'Reduced azithromycin peak levels', management: 'Separate by 2 hours (for suspension)' },
        { drug: 'Theophylline', effect: 'Possible increased theophylline levels', management: 'Monitor theophylline levels' }
      ],
      food: [
        'Tablets: Can be taken with or without food',
        'Capsules: Take on empty stomach',
        'Suspension: Can be taken with or without food',
        'Avoid antacids within 2 hours of suspension'
      ]
    },

    pregnancy: {
      category: 'B (FDA) / Compatible (WHO)',
      trimpiesterRisks: {
        first: 'No increased risk of major malformations in human studies',
        second: 'Safe; extensive use data',
        third: 'Safe; may use up to delivery'
      },
      recommendation: 'SAFE IN PREGNANCY. Preferred macrolide during pregnancy (lower GI side effects than erythromycin, no cardiac risk like clarithromycin). First-line for chlamydia in pregnancy.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in low concentrations. Generally compatible with breastfeeding. Monitor infant for diarrhea or candidiasis. AVOID in neonates <2 weeks (pyloric stenosis risk).'
      }
    },

    monitoring: {
      baseline: [
        { test: 'Allergy history', reason: 'Identify macrolide allergy' },
        { test: 'ECG', reason: 'If risk factors for QT prolongation' },
        { test: 'Electrolytes', reason: 'Correct hypokalemia/hypomagnesemia before starting' },
        { test: 'LFTs', reason: 'If hepatic impairment suspected' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: '48-72 hours', target: 'Improvement in symptoms' },
        { test: 'ECG', frequency: 'If symptoms of arrhythmia', target: 'No QT prolongation' },
        { test: 'Stool', frequency: 'If diarrhea develops', target: 'Rule out C. difficile' }
      ],
      therapeutic: {
        range: 'Not routinely monitored',
        timing: 'N/A'
      }
    },

    storage: {
      temperature: 'Tablets: Store below 30°C. Suspension: Store dry powder below 30°C; reconstituted suspension at 5-30°C.',
      light: 'No special precautions',
      humidity: 'Protect from moisture',
      specialInstructions: 'Reconstituted suspension: Use within 10 days; discard any remaining after course. Shake well before use.',
      shelfLife: 'Tablets: 3 years. Reconstituted suspension: 10 days.'
    },

    africanContext: {
      availability: 'WIDELY AVAILABLE. Available at most health facilities and pharmacies. On national essential medicines lists.',
      affordability: 'AFFORDABLE. Generic formulations: <$1-2 USD for a standard course. Single-dose therapy is cost-effective.',
      supplyChainNotes: 'Stable supply in most countries. No cold chain required for tablets. Suspension powder is stable; reconstituted requires proper storage.',
      localManufacturing: 'Manufactured in several African countries. WHO prequalified generics available.',
      alternatives: [
        'Erythromycin (less well tolerated, more frequent dosing)',
        'Doxycycline (for respiratory and STI indications; not for children/pregnancy)',
        'Amoxicillin (for respiratory infections when atypical coverage not needed)'
      ],
      resistancePatterns: 'Increasing resistance in S. pneumoniae (20-40% in some regions), S. pyogenes, and N. gonorrhoeae. Gonococcal resistance limits use as monotherapy. S. typhi resistance variable.',
      endemicConsiderations: 'Critical for trachoma mass drug administration (MDA) programs. Used for cholera outbreaks. Important for typhoid fever, especially MDR strains. Used in community-based management of pneumonia.'
    },

    patientCounseling: {
      keyPoints: [
        'Complete the entire course even though it is short (usually 3-5 days)',
        'The drug continues working for several days after you finish taking it',
        'Take tablets with or without food; capsules on empty stomach',
        'Shake suspension well before each dose',
        'Store suspension properly and discard after 10 days'
      ],
      commonMisconceptions: [
        'MYTH: "Shorter course means weaker treatment" - TRUTH: The drug stays in your body for 10+ days',
        'MYTH: "Z-pack cures everything" - TRUTH: It only works for specific bacterial infections',
        'MYTH: "I can stop when I feel better after 2 days" - TRUTH: Complete the full course'
      ],
      adherenceTips: [
        'Short course aids adherence - complete all doses',
        'Take at same time daily',
        'Set a reminder for daily dose',
        'Keep track of days completed'
      ],
      warningSignsToReport: [
        'Palpitations, fainting, or irregular heartbeat',
        'Severe diarrhea (especially bloody or watery)',
        'Yellowing of skin or eyes',
        'Skin rash or allergic symptoms',
        'Hearing changes',
        'Symptoms not improving after completing course'
      ]
    },

    clinicalPearls: [
      'The long tissue half-life means 5 days of azithromycin provides 10+ days of therapeutic effect',
      'Single-dose therapy (1g) for chlamydia has >95% efficacy and ensures compliance',
      'For STIs, always consider co-treatment for possible co-infections (gonorrhea + chlamydia)',
      'QT prolongation risk is lower than erythromycin but still significant in at-risk patients',
      'Unlike erythromycin and clarithromycin, azithromycin has minimal CYP450 interactions',
      'In cholera epidemics, single-dose azithromycin reduces duration of illness and shedding',
      'Trachoma MDA programs have dramatically reduced blindness in endemic areas',
      'Not effective against Pseudomonas or Enterobacteriaceae',
      'Consider azithromycin resistance when empiric treatment for gonorrhea fails'
    ],

    references: [
      'WHO Model List of Essential Medicines - 23rd List (2023)',
      'Sanford Guide to Antimicrobial Therapy 2023',
      'UpToDate: Azithromycin - Drug Information',
      'British National Formulary (BNF) 2023',
      'CDC Guidelines: Sexually Transmitted Infections Treatment 2021',
      'IDSA/ATS Guidelines: Community-Acquired Pneumonia',
      'WHO Guidelines: Trachoma Mass Drug Administration'
    ],

    lastUpdated: '2026-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. CIPROFLOXACIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'ciprofloxacin-005',
    genericName: 'Ciprofloxacin',
    brandNames: ['Cipro', 'Ciproxin', 'Ciplox', 'Ciprobay'],
    atcCode: 'J01MA02',
    therapeuticClass: 'Fluoroquinolone Antibiotic',
    
    pharmacology: {
      mechanism: `Ciprofloxacin is a bactericidal fluoroquinolone antibiotic that inhibits bacterial DNA synthesis. It targets two essential bacterial enzymes: DNA gyrase (topoisomerase II) and topoisomerase IV. DNA gyrase is responsible for introducing negative supercoils in DNA to relieve torsional strain during replication, while topoisomerase IV is involved in decatenation of daughter chromosomes. By binding to the enzyme-DNA complex, ciprofloxacin prevents DNA replication, transcription, and repair, leading to rapid bacterial cell death. Gram-negative bacteria are killed primarily through DNA gyrase inhibition, while gram-positive coverage involves topoisomerase IV inhibition.`,
      pharmacokinetics: {
        absorption: 'Well absorbed orally (70-80% bioavailability). Peak serum concentrations in 1-2 hours. Food delays but does not significantly reduce absorption. Dairy products and divalent cations markedly reduce absorption.',
        distribution: 'Widely distributed to tissues and body fluids. High concentrations achieved in prostate, bone, lungs, and urinary tract. CSF penetration: 10% (inflamed meninges: up to 37%). Volume of distribution: 2-3 L/kg. Protein binding: 20-40%.',
        metabolism: 'Hepatic metabolism (~15%) to four metabolites with limited activity. CYP1A2 is partly involved but not a major pathway.',
        elimination: '40-50% excreted unchanged in urine via glomerular filtration and tubular secretion. 20-35% in feces (bile and transintestinal excretion). 15% as metabolites in urine.',
        halfLife: '3-5 hours in adults with normal renal function. Prolonged to 6-9 hours in severe renal impairment (CrCl <20 mL/min).'
      },
      pharmacodynamics: 'Concentration-dependent killing with a prolonged post-antibiotic effect (PAE) of 1-6 hours. The AUC/MIC ratio and Cmax/MIC ratio best predict efficacy. Target AUC24/MIC ≥125 for gram-negative infections.'
    },

    indications: [
      { condition: 'Complicated urinary tract infection', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Reserve for complicated UTI; avoid for uncomplicated cystitis' },
      { condition: 'Acute pyelonephritis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'When susceptibility confirmed or empiric when resistance is low' },
      { condition: 'Bacterial prostatitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Excellent prostate penetration' },
      { condition: 'Typhoid/Paratyphoid fever', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line in susceptible strains; avoid in children' },
      { condition: 'Travelers diarrhea', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose or 3-day course' },
      { condition: 'Infectious diarrhea (Shigella, Salmonella)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'When antibiotic indicated' },
      { condition: 'Intra-abdominal infections', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Combined with metronidazole' },
      { condition: 'Bone and joint infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Good bone penetration; long courses needed' },
      { condition: 'Pseudomonas infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'One of few oral anti-pseudomonal options' },
      { condition: 'Anthrax (post-exposure prophylaxis)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Bioterrorism preparedness' }
    ],

    dosing: {
      adults: {
        standard: '250-750mg every 12 hours (PO) OR 200-400mg every 8-12 hours (IV)',
        indicationSpecific: [
          { indication: 'Uncomplicated UTI', dose: '250mg BD for 3 days' },
          { indication: 'Complicated UTI/Pyelonephritis', dose: '500mg BD for 7-14 days' },
          { indication: 'Bacterial prostatitis', dose: '500mg BD for 28 days' },
          { indication: 'Typhoid fever', dose: '500mg BD for 7-14 days' },
          { indication: 'Travelers diarrhea', dose: '500mg single dose or 500mg BD for 1-3 days' },
          { indication: 'Intra-abdominal infections', dose: '400mg IV every 12 hours (with metronidazole)' },
          { indication: 'Bone/Joint infections', dose: '500-750mg BD for 4-6 weeks (or longer)' },
          { indication: 'Pseudomonal infections', dose: '400mg IV every 8 hours OR 750mg PO BD' },
          { indication: 'Anthrax (post-exposure)', dose: '500mg BD for 60 days' }
        ],
        maxDose: '1500mg/day PO; 1200mg/day IV',
        renalImpairment: {
          mild: 'CrCl 30-60: No adjustment',
          moderate: 'CrCl 10-29: 250-500mg every 12-18 hours',
          severe: 'CrCl <10: 250-500mg every 24 hours',
          dialysis: 'Give after dialysis; 250-500mg every 24 hours'
        },
        hepaticImpairment: 'No specific adjustment; use with caution in severe impairment'
      },
      pediatric: {
        standard: '10-20mg/kg/day divided every 12 hours (max 1.5g/day)',
        weightBased: true,
        ageSpecific: [
          { age: 'Children (UTI)', dose: '10-20mg/kg/day divided BD for 7-14 days' },
          { age: 'Cystic fibrosis', dose: '20-40mg/kg/day divided BD (max 2g/day)' },
          { age: 'Anthrax (post-exposure)', dose: '15mg/kg BD (max 500mg/dose) for 60 days' }
        ],
        maxDose: '1-1.5g/day (up to 2g in CF)',
        neonatal: 'Not recommended except for Pseudomonas infections without alternatives'
      },
      elderly: 'Reduce dose based on renal function; increased risk of tendon effects and CNS adverse events',
      routes: ['Oral (tablets, suspension)', 'Intravenous', 'Ophthalmic', 'Otic'],
      administration: [
        'Oral: Can take with or without food, but avoid taking with dairy alone',
        'AVOID taking with: Dairy products, calcium-fortified juice, antacids, iron, zinc (separate by 2-6 hours)',
        'IV: Infuse over 60 minutes; avoid rapid infusion',
        'Maintain adequate hydration to prevent crystalluria'
      ]
    },

    contraindications: {
      absolute: [
        'Known hypersensitivity to ciprofloxacin or any fluoroquinolone',
        'Concurrent use with tizanidine (CYP1A2 interaction)'
      ],
      relative: [
        'History of tendon disorders or tendon rupture with fluoroquinolone use',
        'Myasthenia gravis (may exacerbate)',
        'G6PD deficiency (hemolysis risk)',
        'History of QT prolongation or arrhythmias',
        'CNS disorders (seizures, encephalopathy)',
        'Pediatric patients (except specific indications)',
        'Elderly patients (increased toxicity risk)',
        'Aortic aneurysm or dissection risk'
      ],
      precautions: [
        'FDA Black Box Warning: Risk of tendinitis and tendon rupture',
        'FDA Black Box Warning: Peripheral neuropathy (may be irreversible)',
        'FDA Black Box Warning: CNS effects (seizures, psychosis)',
        'May cause QT prolongation; avoid in at-risk patients',
        'Photosensitivity - avoid excessive sun exposure',
        'May cause crystalluria - maintain adequate hydration',
        'Risk of aortic aneurysm and dissection (elderly, hypertension)'
      ]
    },

    adverseEffects: {
      common: [
        { effect: 'Nausea/Vomiting', frequency: '5-10%', management: 'Take with food (not dairy); usually mild' },
        { effect: 'Diarrhea', frequency: '2-5%', management: 'Usually self-limiting; rule out C. diff if severe' },
        { effect: 'Headache', frequency: '1-5%', management: 'Simple analgesics' },
        { effect: 'Dizziness', frequency: '1-3%', management: 'Caution with driving/operating machinery' },
        { effect: 'Rash', frequency: '1-2%', management: 'May need to discontinue if severe' }
      ],
      serious: [
        { effect: 'Tendinitis and tendon rupture', frequency: '0.1-0.5%', management: 'STOP immediately at first sign of tendon pain/swelling; rest affected area', warning: 'BLACK BOX WARNING - Risk increased with age >60, corticosteroids, kidney/heart/lung transplant' },
        { effect: 'Peripheral neuropathy', frequency: 'Rare', management: 'STOP immediately if symptoms occur; may be irreversible', warning: 'BLACK BOX WARNING - Paresthesias, pain, weakness; can occur within days of starting' },
        { effect: 'CNS effects (seizures, psychosis, confusion)', frequency: 'Rare', management: 'Discontinue; supportive care', warning: 'BLACK BOX WARNING - Use with caution in CNS disorders' },
        { effect: 'QT prolongation/Torsades de pointes', frequency: 'Rare', management: 'Avoid in at-risk patients; monitor ECG if risk factors', warning: 'Risk factors: Electrolyte abnormalities, cardiac disease, concurrent QT-prolonging drugs' },
        { effect: 'Clostridioides difficile colitis', frequency: '0.1-1%', management: 'Stop ciprofloxacin; treat C. diff appropriately', warning: 'Can occur weeks after treatment' },
        { effect: 'Photosensitivity', frequency: '1-5%', management: 'Avoid sun exposure; use sunscreen', warning: 'Can cause severe sunburn with moderate exposure' },
        { effect: 'Aortic aneurysm/dissection', frequency: 'Rare', management: 'Avoid in patients at risk; seek care for sudden severe pain', warning: 'FDA warning - Risk in elderly, hypertension, atherosclerosis' },
        { effect: 'Hypoglycemia', frequency: 'Rare', management: 'Monitor glucose in diabetics; stop if severe hypoglycemia', warning: 'Especially with concurrent sulfonylureas or insulin' }
      ],
      rare: [
        'Stevens-Johnson Syndrome',
        'Hepatotoxicity',
        'Interstitial nephritis',
        'Hemolytic anemia (G6PD deficiency)',
        'Crystalluria',
        'Achilles tendon rupture'
      ]
    },

    interactions: {
      major: [
        { drug: 'Tizanidine', effect: 'Markedly increased tizanidine levels causing hypotension, sedation', mechanism: 'Strong CYP1A2 inhibition by ciprofloxacin', management: 'CONTRAINDICATED - Never use together' },
        { drug: 'Theophylline', effect: 'Increased theophylline levels and toxicity', mechanism: 'CYP1A2 inhibition', management: 'Reduce theophylline dose by 30-40%; monitor levels' },
        { drug: 'Warfarin', effect: 'Increased INR and bleeding risk', mechanism: 'CYP1A2 inhibition of warfarin R-enantiomer', management: 'Monitor INR closely; may need warfarin dose reduction' },
        { drug: 'QT-prolonging drugs', effect: 'Additive QT prolongation; torsades risk', mechanism: 'Combined effect on cardiac repolarization', management: 'Avoid combination or monitor ECG closely' }
      ],
      moderate: [
        { drug: 'Antacids, Iron, Zinc, Calcium', effect: 'Dramatically reduced ciprofloxacin absorption (up to 90%)', management: 'Take ciprofloxacin 2 hours before or 6 hours after' },
        { drug: 'Dairy products', effect: 'Reduced ciprofloxacin absorption', management: 'Do not take with dairy alone; small amounts with meals okay' },
        { drug: 'Methotrexate', effect: 'Increased methotrexate levels and toxicity', management: 'Monitor methotrexate levels; watch for toxicity' },
        { drug: 'Cyclosporine', effect: 'Increased cyclosporine levels and nephrotoxicity', management: 'Monitor cyclosporine levels and renal function' },
        { drug: 'NSAIDs', effect: 'Increased risk of CNS stimulation and seizures', management: 'Use with caution; monitor for CNS effects' },
        { drug: 'Sulfonylureas', effect: 'Hypoglycemia risk', management: 'Monitor blood glucose closely' }
      ],
      food: [
        'Dairy products: Avoid taking with dairy alone (calcium chelation)',
        'Calcium-fortified foods: Avoid concurrent intake',
        'Caffeine: Reduced caffeine metabolism; may increase jitteriness',
        'Take with or without food (avoid dairy-only meals)'
      ]
    },

    pregnancy: {
      category: 'C (FDA) / Avoid if possible (WHO)',
      trimpiesterRisks: {
        first: 'Animal studies show cartilage damage in immature animals. Limited human data does not show increased malformation risk, but use only if no safer alternative',
        second: 'Same concerns; use only if benefits outweigh risks',
        third: 'Same concerns; theoretical risk of fetal cartilage damage'
      },
      recommendation: 'AVOID IF POSSIBLE. Fluoroquinolones are generally avoided in pregnancy due to theoretical risk of fetal cartilage damage (seen in animal studies). However, if no alternative exists for serious infections, benefits may outweigh risks. Use only when other options are unsuitable.',
      lactation: {
        safe: false,
        notes: 'Excreted in breast milk. AAP considers compatible but manufacturer recommends avoiding. May cause diarrhea, rash, or cartilage effects in infant. Consider pump and discard during treatment.'
      }
    },

    monitoring: {
      baseline: [
        { test: 'Renal function', reason: 'Dose adjustment in renal impairment' },
        { test: 'Blood glucose', reason: 'Baseline in diabetics' },
        { test: 'ECG', reason: 'If risk factors for QT prolongation' },
        { test: 'Electrolytes', reason: 'Correct abnormalities before starting' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: '48-72 hours', target: 'Improvement in symptoms' },
        { test: 'Tendon symptoms', frequency: 'Each visit', target: 'No pain/swelling' },
        { test: 'Neurological symptoms', frequency: 'Each visit', target: 'No neuropathy' },
        { test: 'Blood glucose', frequency: 'Regularly in diabetics', target: 'Stable control' },
        { test: 'Theophylline levels', frequency: 'If concurrent use', target: '10-20 mcg/mL' }
      ],
      therapeutic: {
        range: 'Not routinely monitored; AUC/MIC ratio >125 target for efficacy',
        timing: 'Levels rarely needed clinically'
      }
    },

    storage: {
      temperature: 'Tablets: Store below 30°C. IV: Store below 25°C; protect from light.',
      light: 'Protect IV solutions from light',
      humidity: 'Protect tablets from moisture',
      specialInstructions: 'Oral suspension: Reconstitute with water; use within 14 days; do not refrigerate.',
      shelfLife: 'Tablets: 3-5 years. IV: Per manufacturer. Suspension: 14 days after reconstitution.'
    },

    africanContext: {
      availability: 'WIDELY AVAILABLE. Available at most health facilities and pharmacies throughout Africa.',
      affordability: 'AFFORDABLE. Generic formulations: $1-3 USD for a standard course.',
      supplyChainNotes: 'Stable supply in most countries. Tablets have no special storage requirements.',
      localManufacturing: 'Widely manufactured locally. Quality varies - ensure quality-assured sources.',
      alternatives: [
        'Levofloxacin (similar spectrum, once-daily dosing)',
        'Ceftriaxone (for severe infections, parenteral)',
        'Azithromycin (for typhoid, travelers diarrhea)',
        'Trimethoprim-sulfamethoxazole (for UTI if susceptible)'
      ],
      resistancePatterns: 'Increasing fluoroquinolone resistance globally. E. coli UTI resistance 20-50% in many African countries. Salmonella typhi resistance emerging. N. gonorrhoeae resistance >50% in many areas. Always culture when possible.',
      endemicConsiderations: 'Important for typhoid fever (though resistance increasing). Used for travelers diarrhea and invasive diarrheal disease. Only oral anti-pseudomonal option. Reserve use to preserve efficacy - do NOT use for uncomplicated UTI.'
    },

    patientCounseling: {
      keyPoints: [
        'Do NOT take with dairy products, antacids, or iron/calcium supplements',
        'Take 2 hours before or 6 hours after products containing calcium, iron, or zinc',
        'Stay well hydrated and avoid excessive sun exposure',
        'Complete the full course of treatment',
        'Report any tendon pain, numbness/tingling, or mood changes immediately'
      ],
      commonMisconceptions: [
        'MYTH: "Ciprofloxacin is a good first choice for any UTI" - TRUTH: Reserve for complicated infections; overuse breeds resistance',
        'MYTH: "Taking with milk improves tolerance" - TRUTH: Milk prevents the drug from working',
        'MYTH: "Tendon problems are rare and always heal" - TRUTH: They can be serious and permanent'
      ],
      adherenceTips: [
        'Set alarms for doses (every 12 hours)',
        'Avoid dairy around dose times',
        'Keep hydrated throughout treatment',
        'Complete the full course even if feeling better'
      ],
      warningSignsToReport: [
        'Tendon pain, swelling, or stiffness (STOP drug immediately)',
        'Numbness, tingling, or burning in hands/feet (STOP drug immediately)',
        'Confusion, hallucinations, or mood changes',
        'Irregular heartbeat or fainting',
        'Severe diarrhea',
        'Severe skin rash or blisters',
        'Sudden severe pain in stomach, chest, or back'
      ]
    },

    clinicalPearls: [
      'DO NOT use for uncomplicated cystitis - contributes to resistance; use nitrofurantoin or TMP-SMX instead',
      'One of few oral options for Pseudomonas infections',
      'Excellent bone penetration makes it valuable for osteomyelitis',
      'Tendon rupture risk is highest in first 48 hours but can occur months after stopping',
      'The FDA black box warnings are real concerns - counsel all patients about tendon and nerve effects',
      'For typhoid, ciprofloxacin resistance is increasing globally - confirm susceptibility when possible',
      'Avoid fluoroquinolones in children except when no alternatives exist (arthropathy risk)',
      'Separate from all divalent and trivalent cations (Ca, Mg, Fe, Al, Zn) by at least 2 hours before or 6 hours after',
      'Consider in patients with severe penicillin allergy who need gram-negative coverage'
    ],

    references: [
      'WHO Model List of Essential Medicines - 23rd List (2023)',
      'FDA Drug Safety Communication: Fluoroquinolone Black Box Warnings',
      'Sanford Guide to Antimicrobial Therapy 2023',
      'UpToDate: Ciprofloxacin - Drug Information',
      'British National Formulary (BNF) 2023',
      'IDSA Guidelines: Uncomplicated Urinary Tract Infection',
      'CDC Guidelines: Typhoid Fever Treatment'
    ],

    lastUpdated: '2026-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  // ═══════════════════════════════════════════════════════════════════════════
  // 6. DOXYCYCLINE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'doxycycline-006',
    genericName: 'Doxycycline',
    brandNames: ['Vibramycin', 'Doryx', 'Doxycin', 'Monodox'],
    atcCode: 'J01AA02',
    therapeuticClass: 'Tetracycline Antibiotic',
    pharmacology: {
      mechanism: 'Inhibits bacterial protein synthesis by binding to 30S ribosomal subunit, preventing aminoacyl-tRNA attachment. Bacteriostatic with anti-inflammatory properties.',
      pharmacokinetics: {
        absorption: 'Well absorbed orally (90-100%). Absorption NOT significantly affected by food.',
        distribution: 'Widely distributed including respiratory tract, skin, prostate. CSF penetration 10-26%. Protein binding: 80-93%.',
        metabolism: 'Not extensively metabolized. Some hepatic inactivation.',
        elimination: 'Feces (40%) and urine (35-60% unchanged). Safe in renal impairment.',
        halfLife: '12-25 hours (average 18 hours)'
      },
      pharmacodynamics: 'Time-dependent killing with moderate post-antibiotic effect.'
    },
    indications: [
      { condition: 'Respiratory tract infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'CAP, COPD exacerbations' },
      { condition: 'Sexually transmitted infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Chlamydia, NGU, PID' },
      { condition: 'Malaria prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Alternative in chloroquine-resistant areas' },
      { condition: 'Rickettsial infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for typhus, spotted fevers' },
      { condition: 'Acne vulgaris', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Moderate to severe inflammatory acne' },
      { condition: 'Lyme disease', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for early disease' },
      { condition: 'Cholera', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Reduces duration and transmission' }
    ],
    dosing: {
      adults: {
        standard: '100mg twice daily or 200mg once daily',
        indicationSpecific: [
          { indication: 'Most infections', dose: '100mg BD x 7-14 days' },
          { indication: 'Chlamydia', dose: '100mg BD x 7 days' },
          { indication: 'Malaria prophylaxis', dose: '100mg daily, start 1-2 days before travel' },
          { indication: 'Acne', dose: '50-100mg daily x 3-6 months' }
        ],
        maxDose: '200mg/day for most indications',
        renalImpairment: {
          mild: 'No adjustment needed',
          moderate: 'No adjustment needed',
          severe: 'No adjustment needed - safe in renal impairment',
          dialysis: 'No supplemental dose required'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment'
      },
      pediatric: {
        standard: 'Children >8 years: 2-4mg/kg/day divided BD',
        weightBased: true,
        ageSpecific: [
          { age: '>8 years', dose: '2-4mg/kg/day divided BD' }
        ],
        maxDose: '200mg/day',
        neonatal: 'CONTRAINDICATED'
      },
      elderly: 'No specific adjustment; monitor for esophageal irritation',
      routes: ['Oral', 'IV'],
      administration: ['Take with full glass of water', 'Remain upright for 30 minutes after dose', 'Can take with food if GI upset']
    },
    contraindications: {
      absolute: ['Pregnancy (2nd and 3rd trimester)', 'Children under 8 years', 'Hypersensitivity to tetracyclines'],
      relative: ['First trimester pregnancy', 'Breastfeeding', 'Severe hepatic impairment'],
      precautions: ['Myasthenia gravis', 'SLE', 'Concurrent isotretinoin use']
    },
    adverseEffects: {
      common: [
        { effect: 'Nausea', frequency: '10-20%', management: 'Take with food' },
        { effect: 'Photosensitivity', frequency: '5-10%', management: 'Avoid sun exposure, use sunscreen' },
        { effect: 'Esophageal irritation', frequency: '5%', management: 'Take upright with full glass of water' },
        { effect: 'Diarrhea', frequency: '5-10%', management: 'Usually self-limiting' }
      ],
      serious: [
        { effect: 'Severe photosensitivity reactions', frequency: '<1%', management: 'Discontinue drug', warning: 'Can occur even with brief sun exposure' },
        { effect: 'Intracranial hypertension', frequency: 'Rare', management: 'Discontinue immediately', warning: 'Risk increased with isotretinoin' },
        { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Monitor LFTs, discontinue if elevated' }
      ],
      rare: ['Drug-induced lupus', 'Esophageal ulceration', 'Tooth discoloration (children)']
    },
    interactions: {
      major: [
        { drug: 'Isotretinoin', effect: 'Increased intracranial pressure', mechanism: 'Additive effect', management: 'AVOID combination' },
        { drug: 'Warfarin', effect: 'Enhanced anticoagulation', mechanism: 'Displacement from protein binding', management: 'Monitor INR closely' }
      ],
      moderate: [
        { drug: 'Antacids/Iron/Calcium', effect: 'Reduced doxycycline absorption', management: 'Separate by 2-3 hours' },
        { drug: 'Oral contraceptives', effect: 'Potentially reduced efficacy', management: 'Use backup contraception' }
      ],
      food: ['Dairy products may reduce absorption slightly but not significantly', 'Can take with food if GI upset']
    },
    pregnancy: {
      category: 'D',
      trimpiesterRisks: {
        first: 'Generally avoid - limited data',
        second: 'CONTRAINDICATED - tooth and bone effects',
        third: 'CONTRAINDICATED - tooth discoloration, bone growth inhibition'
      },
      recommendation: 'Avoid in pregnancy, especially after first trimester',
      lactation: { safe: false, notes: 'Excreted in breast milk; may affect infant teeth and bones' }
    },
    monitoring: {
      baseline: [
        { test: 'LFTs', reason: 'Baseline hepatic function' },
        { test: 'Pregnancy test', reason: 'Rule out pregnancy in women of childbearing age' }
      ],
      ongoing: [
        { test: 'Symptoms of photosensitivity', frequency: 'Each visit' },
        { test: 'Signs of esophageal irritation', frequency: 'Each visit' },
        { test: 'LFTs', frequency: 'If prolonged therapy >3 months' }
      ],
      therapeutic: { range: 'Not routinely monitored', timing: 'N/A' }
    },
    storage: {
      temperature: 'Store below 25°C',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Keep in original container',
      shelfLife: '2-3 years from manufacture'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO Essential Medicine',
      affordability: 'VERY AFFORDABLE - Generic widely available (<$0.50/course)',
      supplyChainNotes: 'Stable supply across most African countries',
      localManufacturing: 'Manufactured in several African countries including Kenya, Nigeria, South Africa',
      alternatives: ['Tetracycline', 'Minocycline'],
      resistancePatterns: 'Rising resistance in some STI pathogens. Monitor local data.',
      endemicConsiderations: 'Critical for malaria prophylaxis and rickettsial infections common in sub-Saharan Africa'
    },
    patientCounseling: {
      keyPoints: ['Take with full glass of water', 'Avoid lying down for 30 minutes after dose', 'Use sunscreen - increased sun sensitivity', 'Complete full course'],
      commonMisconceptions: ['Can take with dairy (actually okay with doxycycline unlike other tetracyclines)', 'Only for acne (has many uses)'],
      adherenceTips: ['Set daily reminders', 'Take at same time each day', 'Keep medication visible'],
      warningSignsToReport: ['Severe headache or vision changes', 'Difficulty swallowing', 'Severe skin reaction to sun', 'Signs of liver problems']
    },
    clinicalPearls: [
      'Unlike other tetracyclines, absorption NOT significantly affected by food or dairy',
      'Safe in renal impairment - no dose adjustment needed',
      'Drug of choice for rickettsial infections (typhus, spotted fevers)',
      'Effective malaria prophylaxis for chloroquine-resistant areas',
      'Anti-inflammatory properties useful in acne and rosacea'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'CDC Malaria Prevention Guidelines'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. GENTAMICIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'gentamicin-007',
    genericName: 'Gentamicin',
    brandNames: ['Garamycin', 'Gentak', 'Gentacin', 'Cidomycin'],
    atcCode: 'J01GB03',
    therapeuticClass: 'Aminoglycoside Antibiotic',
    pharmacology: {
      mechanism: 'Binds irreversibly to 30S ribosomal subunit, causing misreading of mRNA and inhibiting protein synthesis. Bactericidal and concentration-dependent.',
      pharmacokinetics: {
        absorption: 'Not absorbed orally. Given IV or IM.',
        distribution: 'Poor CNS penetration. Good renal cortex penetration. Vd: 0.2-0.3 L/kg.',
        metabolism: 'Not metabolized.',
        elimination: 'Renally excreted unchanged (>90%). Accumulates in renal failure.',
        halfLife: '2-3 hours (normal renal function), prolonged in renal impairment'
      },
      pharmacodynamics: 'Concentration-dependent killing with significant post-antibiotic effect (PAE) of 2-4 hours.'
    },
    indications: [
      { condition: 'Severe gram-negative infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Pseudomonas, Enterobacteriaceae' },
      { condition: 'Neonatal sepsis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with ampicillin - WHO recommended' },
      { condition: 'Endocarditis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Synergy with beta-lactams for enterococcal endocarditis' },
      { condition: 'Pelvic inflammatory disease', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Part of combination regimens' },
      { condition: 'Intra-abdominal infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Combined with metronidazole' }
    ],
    dosing: {
      adults: {
        standard: '5-7mg/kg IV once daily (extended-interval dosing)',
        indicationSpecific: [
          { indication: 'Once-daily dosing', dose: '5-7mg/kg IV q24h' },
          { indication: 'Traditional dosing', dose: '1-2.5mg/kg IV q8h' },
          { indication: 'Endocarditis synergy', dose: '1mg/kg IV q8h or 3mg/kg q24h' }
        ],
        maxDose: '7mg/kg/day',
        renalImpairment: {
          mild: 'Extend interval to q12-24h',
          moderate: 'Extend interval to q24-48h, reduce dose',
          severe: 'Extend interval to q48-72h, monitor levels',
          dialysis: 'Redose after dialysis; 1-2mg/kg post-HD'
        },
        hepaticImpairment: 'No adjustment needed'
      },
      pediatric: {
        standard: '2.5mg/kg IV q8h or 5-7.5mg/kg IV q24h',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates <7 days', dose: '4-5mg/kg IV q24-48h' },
          { age: 'Neonates >7 days', dose: '4-5mg/kg IV q24h' },
          { age: 'Children', dose: '2.5mg/kg IV q8h or 5-7.5mg/kg q24h' }
        ],
        maxDose: '7.5mg/kg/day',
        neonatal: '4-5mg/kg IV q24-48h depending on gestational age'
      },
      elderly: 'Use ideal body weight; more frequent monitoring required',
      routes: ['IV', 'IM', 'Intrathecal'],
      administration: ['IV infusion over 30-60 minutes', 'IM injection deep into large muscle']
    },
    contraindications: {
      absolute: ['Known aminoglycoside hypersensitivity', 'Concurrent neuromuscular blocking agents (relative)'],
      relative: ['Myasthenia gravis', 'Pre-existing renal impairment', 'Pre-existing hearing loss'],
      precautions: ['Concurrent nephrotoxic drugs', 'Concurrent ototoxic drugs', 'Dehydration']
    },
    adverseEffects: {
      common: [
        { effect: 'Nephrotoxicity', frequency: '10-25%', management: 'Monitor creatinine, hydration, use once-daily dosing' },
        { effect: 'Injection site reactions', frequency: '5%', management: 'Rotate injection sites' }
      ],
      serious: [
        { effect: 'Ototoxicity (vestibular and cochlear)', frequency: '2-5%', management: 'May be irreversible; discontinue at first sign', warning: 'Risk increases with duration and cumulative dose' },
        { effect: 'Neuromuscular blockade', frequency: '<1%', management: 'Calcium gluconate for reversal', warning: 'Risk with myasthenia gravis' },
        { effect: 'Acute kidney injury', frequency: '5-15%', management: 'Hydration, avoid other nephrotoxins', warning: 'Usually reversible' }
      ],
      rare: ['Hypersensitivity reactions', 'Blood dyscrasias']
    },
    interactions: {
      major: [
        { drug: 'Loop diuretics (furosemide)', effect: 'Increased ototoxicity', mechanism: 'Additive ototoxic effect', management: 'AVOID if possible; monitor hearing' },
        { drug: 'Vancomycin', effect: 'Increased nephrotoxicity', mechanism: 'Additive renal toxicity', management: 'Monitor renal function closely' },
        { drug: 'Neuromuscular blockers', effect: 'Prolonged paralysis', mechanism: 'Additive neuromuscular blockade', management: 'Use with extreme caution' }
      ],
      moderate: [
        { drug: 'NSAIDs', effect: 'Increased nephrotoxicity risk', management: 'Avoid concurrent use if possible' },
        { drug: 'Amphotericin B', effect: 'Additive nephrotoxicity', management: 'Monitor renal function daily' }
      ],
      food: ['No significant food interactions']
    },
    pregnancy: {
      category: 'D',
      trimpiesterRisks: {
        first: 'Potential ototoxicity to fetus',
        second: 'Risk of fetal ototoxicity',
        third: 'Risk of fetal ototoxicity and nephrotoxicity'
      },
      recommendation: 'Use only if clearly needed and no safer alternative',
      lactation: { safe: false, notes: 'Small amounts excreted; poorly absorbed orally by infant but avoid if possible' }
    },
    monitoring: {
      baseline: [
        { test: 'Serum creatinine/BUN', reason: 'Assess baseline renal function' },
        { test: 'Audiometry', reason: 'Baseline hearing assessment for prolonged therapy' }
      ],
      ongoing: [
        { test: 'Serum creatinine', frequency: 'Every 2-3 days' },
        { test: 'Drug levels (trough)', frequency: 'Before 3rd dose, then twice weekly', target: 'Trough <1 mcg/mL for once-daily; <2 mcg/mL for traditional' },
        { test: 'Drug levels (peak)', frequency: 'After 3rd dose if traditional dosing', target: 'Peak 5-10 mcg/mL for traditional dosing' }
      ],
      therapeutic: { range: 'Peak 5-10 mcg/mL (traditional), Trough <1 mcg/mL (once-daily)', timing: 'Trough: just before dose; Peak: 30 min after infusion' }
    },
    storage: {
      temperature: 'Store at 15-30°C',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Do not freeze',
      shelfLife: '2 years from manufacture'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO Essential Medicine',
      affordability: 'AFFORDABLE - Generic injectable widely available',
      supplyChainNotes: 'Requires cold chain; available at district hospital level and above',
      localManufacturing: 'Limited local manufacturing; mostly imported',
      alternatives: ['Amikacin', 'Tobramycin'],
      resistancePatterns: 'Generally preserved activity against gram-negatives in most African regions',
      endemicConsiderations: 'Critical for neonatal sepsis protocols across Africa. Once-daily dosing practical for resource-limited settings.'
    },
    patientCounseling: {
      keyPoints: ['Report any hearing changes immediately', 'Stay well hydrated', 'Report decreased urination'],
      commonMisconceptions: ['Can be taken orally (must be injected)'],
      adherenceTips: ['Complete full course as prescribed', 'Attend all follow-up appointments for monitoring'],
      warningSignsToReport: ['Ringing in ears or hearing loss', 'Dizziness or balance problems', 'Decreased urination', 'Muscle weakness']
    },
    clinicalPearls: [
      'Once-daily dosing is preferred - reduces nephrotoxicity while maintaining efficacy',
      'Post-antibiotic effect allows once-daily dosing despite short half-life',
      'Synergistic with beta-lactams against enterococci and streptococci',
      'Use ideal body weight for dosing in obese patients',
      'Therapeutic drug monitoring essential for courses >5 days'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'IDSA Guidelines for Aminoglycoside Use'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. COTRIMOXAZOLE (Trimethoprim-Sulfamethoxazole)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'cotrimoxazole-008',
    genericName: 'Cotrimoxazole',
    brandNames: ['Bactrim', 'Septrin', 'Cotrim', 'Septra'],
    atcCode: 'J01EE01',
    therapeuticClass: 'Sulfonamide/Trimethoprim Combination',
    pharmacology: {
      mechanism: 'Sequential blockade of folate synthesis. Sulfamethoxazole inhibits dihydropteroate synthase; trimethoprim inhibits dihydrofolate reductase. Bactericidal combination.',
      pharmacokinetics: {
        absorption: 'Well absorbed orally (85-90%).',
        distribution: 'Wide distribution including CSF (50% of serum). Protein binding: TMP 45%, SMX 65%.',
        metabolism: 'Hepatic metabolism of both components.',
        elimination: 'Renal excretion. TMP half-life 8-10h, SMX 9-11h.',
        halfLife: 'TMP: 8-10 hours, SMX: 9-11 hours'
      },
      pharmacodynamics: 'Bactericidal through sequential folate blockade. Time-dependent with concentration-dependent component.'
    },
    indications: [
      { condition: 'Pneumocystis jirovecii pneumonia (PCP)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Treatment and prophylaxis - cornerstone of HIV care' },
      { condition: 'Urinary tract infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'If local resistance <20%' },
      { condition: 'HIV prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Prevents PCP, toxoplasmosis, bacterial infections' },
      { condition: 'Toxoplasmosis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Treatment and secondary prophylaxis' },
      { condition: 'MRSA skin infections', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'CA-MRSA alternative' }
    ],
    dosing: {
      adults: {
        standard: '960mg (800/160) twice daily',
        indicationSpecific: [
          { indication: 'UTI', dose: '960mg BD x 3-5 days' },
          { indication: 'PCP treatment', dose: '15-20mg/kg/day TMP divided q6-8h x 21 days' },
          { indication: 'PCP prophylaxis', dose: '960mg daily or 3x/week' },
          { indication: 'HIV prophylaxis', dose: '960mg daily' }
        ],
        maxDose: '20mg/kg/day TMP component for PCP',
        renalImpairment: {
          mild: 'No adjustment',
          moderate: 'Reduce dose by 50%',
          severe: 'Avoid or reduce dose significantly',
          dialysis: 'Give dose after dialysis'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment'
      },
      pediatric: {
        standard: '4-6mg/kg TMP component BD',
        weightBased: true,
        ageSpecific: [
          { age: '>2 months', dose: '4-6mg/kg TMP BD' },
          { age: 'HIV prophylaxis', dose: 'Weight-based per WHO guidelines' }
        ],
        maxDose: '320mg TMP per dose',
        neonatal: 'Avoid in first 2 months of life (kernicterus risk)'
      },
      elderly: 'Increased risk of hyperkalemia and adverse effects; monitor closely',
      routes: ['Oral', 'IV'],
      administration: ['Take with full glass of water', 'Can take with or without food']
    },
    contraindications: {
      absolute: ['Sulfonamide allergy', 'Severe renal impairment (CrCl <15mL/min)', 'Megaloblastic anemia due to folate deficiency', 'Infants <2 months'],
      relative: ['G6PD deficiency', 'First trimester pregnancy', 'Severe hepatic impairment'],
      precautions: ['Elderly patients', 'Concurrent ACE inhibitors/ARBs', 'Folate deficiency']
    },
    adverseEffects: {
      common: [
        { effect: 'Nausea', frequency: '10-15%', management: 'Take with food' },
        { effect: 'Rash', frequency: '3-5%', management: 'Discontinue if severe' },
        { effect: 'Hyperkalemia', frequency: '5-10%', management: 'Monitor potassium, especially with ACE inhibitors' },
        { effect: 'Photosensitivity', frequency: '3%', management: 'Sun protection' }
      ],
      serious: [
        { effect: 'Stevens-Johnson syndrome/TEN', frequency: '<1%', management: 'Discontinue immediately', warning: 'Higher risk in HIV patients and slow acetylators' },
        { effect: 'Bone marrow suppression', frequency: '1-2%', management: 'Monitor CBC, give folinic acid', warning: 'Risk increases with prolonged use' },
        { effect: 'Severe hypersensitivity', frequency: '<1%', management: 'Discontinue, supportive care' }
      ],
      rare: ['Aseptic meningitis', 'Crystalluria', 'Hepatotoxicity']
    },
    interactions: {
      major: [
        { drug: 'Warfarin', effect: 'Markedly enhanced anticoagulation', mechanism: 'CYP2C9 inhibition', management: 'Monitor INR closely, reduce warfarin dose' },
        { drug: 'Methotrexate', effect: 'Increased methotrexate toxicity', mechanism: 'Reduced renal clearance, folate antagonism', management: 'AVOID combination if possible' }
      ],
      moderate: [
        { drug: 'ACE inhibitors/ARBs', effect: 'Hyperkalemia risk', management: 'Monitor potassium closely' },
        { drug: 'Phenytoin', effect: 'Increased phenytoin levels', management: 'Monitor phenytoin levels' }
      ],
      food: ['Maintain adequate hydration to prevent crystalluria']
    },
    pregnancy: {
      category: 'C (D near term)',
      trimpiesterRisks: {
        first: 'Potential teratogenicity (folate antagonist)',
        second: 'Relatively safer but monitor',
        third: 'Risk of neonatal kernicterus near term'
      },
      recommendation: 'Avoid in first trimester if possible; avoid near term',
      lactation: { safe: false, notes: 'Excreted in breast milk; avoid in infants with G6PD deficiency or jaundice' }
    },
    monitoring: {
      baseline: [
        { test: 'CBC', reason: 'Baseline blood counts' },
        { test: 'Renal function', reason: 'Assess for dose adjustment' },
        { test: 'Potassium', reason: 'Baseline electrolytes' },
        { test: 'G6PD screen', reason: 'If available, especially in endemic areas' }
      ],
      ongoing: [
        { test: 'CBC', frequency: 'Weekly for first month, then monthly' },
        { test: 'Potassium', frequency: 'Weekly initially if on ACE inhibitor' },
        { test: 'Renal function', frequency: 'Monthly with prolonged use' }
      ],
      therapeutic: { range: 'Not routinely monitored', timing: 'N/A' }
    },
    storage: {
      temperature: 'Store below 25°C',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'IV solution stable for 6 hours at room temperature',
      shelfLife: '3-5 years from manufacture'
    },
    africanContext: {
      availability: 'UNIVERSALLY AVAILABLE - Critical WHO Essential Medicine',
      affordability: 'VERY AFFORDABLE - Cornerstone of HIV care programs (<$1/month)',
      supplyChainNotes: 'Widely available through HIV/AIDS programs; stable supply',
      localManufacturing: 'Manufactured in multiple African countries',
      alternatives: ['Dapsone (for PCP prophylaxis)', 'Atovaquone'],
      resistancePatterns: 'High E. coli resistance (>50%) limits UTI use in many regions. PCP prophylaxis efficacy maintained.',
      endemicConsiderations: 'ESSENTIAL for HIV/AIDS management across Africa. Provides prophylaxis against PCP, toxoplasmosis, and bacterial infections.'
    },
    patientCounseling: {
      keyPoints: ['Drink plenty of water', 'Complete full course', 'Report any rash immediately', 'Take at regular intervals'],
      commonMisconceptions: ['Only for HIV patients (has many uses)', 'Safe to stop when feeling better'],
      adherenceTips: ['Use pill box', 'Set phone reminders', 'Link to daily routine'],
      warningSignsToReport: ['Skin rash or blistering', 'Sore throat or fever', 'Unusual bleeding or bruising', 'Severe diarrhea']
    },
    clinicalPearls: [
      'Cornerstone of HIV opportunistic infection prophylaxis in Africa',
      'Contains both antibacterial and antiprotozoal activity',
      'Folate supplementation may reduce hematologic toxicity',
      'Screen for G6PD deficiency if available - risk of hemolysis',
      'High E. coli resistance limits empiric UTI use in many regions'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines on Cotrimoxazole Prophylaxis',
      'DHHS Guidelines for Prevention of Opportunistic Infections'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 9. AMPICILLIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'ampicillin-009',
    genericName: 'Ampicillin',
    brandNames: ['Principen', 'Omnipen', 'Penbritin', 'Totacillin'],
    atcCode: 'J01CA01',
    therapeuticClass: 'Aminopenicillin Antibiotic',
    pharmacology: {
      mechanism: 'Inhibits bacterial cell wall synthesis by binding to penicillin-binding proteins (PBPs). Bactericidal. Extended spectrum includes some gram-negatives.',
      pharmacokinetics: {
        absorption: 'Oral bioavailability 30-55% (reduced by food). IV/IM preferred for serious infections.',
        distribution: 'Wide distribution. CSF penetration good with inflamed meninges (10-30%). Protein binding: 20%.',
        metabolism: 'Minimal hepatic metabolism.',
        elimination: 'Primarily renal (60-80% unchanged).',
        halfLife: '1-1.5 hours (prolonged in renal impairment)'
      },
      pharmacodynamics: 'Time-dependent killing. Efficacy correlates with time above MIC (>40-50%).'
    },
    indications: [
      { condition: 'Listeria meningitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Drug of choice - only beta-lactam with reliable Listeria activity' },
      { condition: 'Neonatal sepsis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with gentamicin - covers GBS, Listeria, enterococci' },
      { condition: 'Enterococcal infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Endocarditis, UTI (with gentamicin for synergy)' },
      { condition: 'Group B Streptococcus prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Intrapartum prophylaxis' },
      { condition: 'Bacterial meningitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Part of empiric regimen for Listeria coverage in elderly/immunocompromised' }
    ],
    dosing: {
      adults: {
        standard: '250-500mg PO q6h or 1-2g IV q4-6h',
        indicationSpecific: [
          { indication: 'Mild infections', dose: '250-500mg PO q6h' },
          { indication: 'Severe infections', dose: '1-2g IV q4-6h' },
          { indication: 'Meningitis', dose: '2g IV q4h' },
          { indication: 'GBS prophylaxis', dose: '2g IV then 1g IV q4h until delivery' }
        ],
        maxDose: '12g/day IV',
        renalImpairment: {
          mild: 'No adjustment',
          moderate: 'Extend interval to q6-8h',
          severe: 'Extend interval to q8-12h',
          dialysis: 'Give dose after dialysis'
        },
        hepaticImpairment: 'No adjustment needed'
      },
      pediatric: {
        standard: '50-100mg/kg/day divided q6h',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates <7 days', dose: '50mg/kg IV q12h' },
          { age: 'Neonates >7 days', dose: '50mg/kg IV q8h' },
          { age: 'Children', dose: '50-100mg/kg/day divided q6h' }
        ],
        maxDose: '200mg/kg/day for meningitis',
        neonatal: '50mg/kg IV q8-12h depending on age'
      },
      elderly: 'Adjust for renal function',
      routes: ['Oral', 'IV', 'IM'],
      administration: ['Oral: Take on empty stomach (1 hour before or 2 hours after meals)', 'IV: Infuse over 15-30 minutes']
    },
    contraindications: {
      absolute: ['Penicillin allergy', 'History of ampicillin-associated hepatitis'],
      relative: ['Cephalosporin allergy (10% cross-reactivity)', 'Infectious mononucleosis (high rash risk)'],
      precautions: ['Renal impairment', 'History of GI disease especially colitis']
    },
    adverseEffects: {
      common: [
        { effect: 'Diarrhea', frequency: '10-20%', management: 'Usually self-limiting; probiotics may help' },
        { effect: 'Rash', frequency: '5-10%', management: 'Distinguish from allergy; may continue if non-allergic' },
        { effect: 'Nausea/vomiting', frequency: '5%', management: 'Take with food if needed despite reduced absorption' }
      ],
      serious: [
        { effect: 'Anaphylaxis', frequency: '<0.05%', management: 'Epinephrine, supportive care', warning: 'Ask about penicillin allergy history' },
        { effect: 'C. difficile colitis', frequency: '1-2%', management: 'Discontinue, treat C. diff', warning: 'Risk with prolonged use' },
        { effect: 'Seizures', frequency: '<1%', management: 'Reduce dose, anticonvulsants', warning: 'High doses in renal impairment' }
      ],
      rare: ['Interstitial nephritis', 'Hemolytic anemia', 'Hepatitis']
    },
    interactions: {
      major: [
        { drug: 'Methotrexate', effect: 'Reduced methotrexate clearance, increased toxicity', mechanism: 'Competition for renal tubular secretion', management: 'Monitor methotrexate levels, consider alternative antibiotic' }
      ],
      moderate: [
        { drug: 'Allopurinol', effect: 'Increased rash risk', management: 'Monitor for rash' },
        { drug: 'Oral contraceptives', effect: 'Potentially reduced efficacy', management: 'Use backup contraception' },
        { drug: 'Warfarin', effect: 'May enhance anticoagulation', management: 'Monitor INR' }
      ],
      food: ['Food decreases absorption - take on empty stomach for oral dosing']
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'No known teratogenic risk',
        second: 'Generally safe',
        third: 'Safe; used for GBS prophylaxis'
      },
      recommendation: 'Safe to use in pregnancy; drug of choice for GBS prophylaxis',
      lactation: { safe: true, notes: 'Small amounts excreted; generally compatible with breastfeeding' }
    },
    monitoring: {
      baseline: [
        { test: 'Renal function', reason: 'Assess for dose adjustment' },
        { test: 'Allergy history', reason: 'Confirm no penicillin allergy' }
      ],
      ongoing: [
        { test: 'Signs of allergic reaction', frequency: 'Throughout therapy' },
        { test: 'Renal function', frequency: 'Weekly if prolonged IV therapy' },
        { test: 'CBC', frequency: 'Weekly if prolonged high-dose therapy' }
      ],
      therapeutic: { range: 'Not routinely monitored', timing: 'N/A' }
    },
    storage: {
      temperature: 'Store below 25°C (capsules); Reconstituted IV stable 1 hour at room temperature',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Use reconstituted solution within 1 hour',
      shelfLife: '2-3 years from manufacture (dry form)'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO Essential Medicine',
      affordability: 'AFFORDABLE - Generic widely available',
      supplyChainNotes: 'Stable supply; IV formulation requires proper storage',
      localManufacturing: 'Manufactured in several African countries',
      alternatives: ['Amoxicillin (better oral absorption)', 'Penicillin G'],
      resistancePatterns: 'High E. coli resistance limits empiric UTI/respiratory use. Retains activity against Listeria, enterococci, GBS.',
      endemicConsiderations: 'Critical for neonatal sepsis protocols. Essential for Listeria coverage in meningitis.'
    },
    patientCounseling: {
      keyPoints: ['Take oral form on empty stomach', 'Complete full course', 'Report any rash or allergic symptoms'],
      commonMisconceptions: ['All rashes mean allergy (maculopapular rash with EBV is not true allergy)'],
      adherenceTips: ['Set reminders for q6h dosing', 'Keep medication at room temperature'],
      warningSignsToReport: ['Difficulty breathing or swelling', 'Severe skin rash', 'Severe diarrhea', 'Signs of liver problems']
    },
    clinicalPearls: [
      'Drug of choice for Listeria - no other beta-lactam has reliable activity',
      'Maculopapular rash with EBV (mono) is NOT true allergy - occurs in 70-100% of cases',
      'Combine with aminoglycoside for enterococcal synergy',
      'Oral absorption poor (30-55%) - use amoxicillin for oral therapy when possible',
      'Essential component of neonatal sepsis empiric therapy (with gentamicin)'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'IDSA Guidelines for Bacterial Meningitis',
      'CDC Guidelines for GBS Prophylaxis'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 10. CLOXACILLIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'cloxacillin-010',
    genericName: 'Cloxacillin',
    brandNames: ['Cloxapen', 'Tegopen', 'Orbenin', 'Cloxacap'],
    atcCode: 'J01CF02',
    therapeuticClass: 'Antistaphylococcal Penicillin (Isoxazolyl Penicillin)',
    pharmacology: {
      mechanism: 'Inhibits bacterial cell wall synthesis by binding to PBPs. Penicillinase-resistant due to bulky side chain protecting beta-lactam ring. Bactericidal.',
      pharmacokinetics: {
        absorption: 'Oral bioavailability 37-60%. Food reduces absorption.',
        distribution: 'Wide distribution but poor CSF penetration even with inflammation. Protein binding: 94-96%.',
        metabolism: 'Hepatic metabolism (some active metabolites).',
        elimination: 'Renal (30-45%) and biliary excretion.',
        halfLife: '0.5-1 hour'
      },
      pharmacodynamics: 'Time-dependent killing. Target >40% time above MIC.'
    },
    indications: [
      { condition: 'MSSA skin/soft tissue infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Cellulitis, abscess, wound infections' },
      { condition: 'MSSA bacteremia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Preferred agent for MSSA bloodstream infections' },
      { condition: 'MSSA osteomyelitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Bone and joint infections' },
      { condition: 'MSSA endocarditis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Native and prosthetic valve (with gentamicin initially)' },
      { condition: 'Staphylococcal pneumonia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Post-influenza pneumonia' }
    ],
    dosing: {
      adults: {
        standard: '250-500mg PO q6h or 1-2g IV q4-6h',
        indicationSpecific: [
          { indication: 'Mild skin infections', dose: '250-500mg PO q6h' },
          { indication: 'Moderate infections', dose: '500mg PO q6h or 1g IV q6h' },
          { indication: 'Severe infections/bacteremia', dose: '2g IV q4h' },
          { indication: 'Endocarditis', dose: '2g IV q4h x 4-6 weeks' }
        ],
        maxDose: '12g/day IV',
        renalImpairment: {
          mild: 'No adjustment',
          moderate: 'No adjustment',
          severe: 'Extend interval slightly',
          dialysis: 'No supplemental dose needed'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment; monitor LFTs'
      },
      pediatric: {
        standard: '25-50mg/kg/day PO divided q6h; 100-200mg/kg/day IV divided q4-6h',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates', dose: '25-50mg/kg/day divided q6-12h' },
          { age: 'Children', dose: '50-100mg/kg/day divided q6h' },
          { age: 'Severe infections', dose: '150-200mg/kg/day IV divided q4-6h' }
        ],
        maxDose: '200mg/kg/day',
        neonatal: '25-50mg/kg/day divided q6-12h'
      },
      elderly: 'No specific adjustment; monitor hepatic function',
      routes: ['Oral', 'IV', 'IM'],
      administration: ['Oral: Take on empty stomach (1 hour before or 2 hours after meals)', 'IV: Infuse over 30-60 minutes']
    },
    contraindications: {
      absolute: ['Penicillin allergy', 'History of cloxacillin-associated hepatitis'],
      relative: ['Cephalosporin allergy', 'Severe hepatic impairment'],
      precautions: ['Monitor LFTs with prolonged therapy', 'History of cholestatic jaundice']
    },
    adverseEffects: {
      common: [
        { effect: 'GI upset', frequency: '5-10%', management: 'Usually mild; can take with small amount of food' },
        { effect: 'Diarrhea', frequency: '5-10%', management: 'Usually self-limiting' },
        { effect: 'Rash', frequency: '3-5%', management: 'Evaluate for true allergy vs non-allergic rash' }
      ],
      serious: [
        { effect: 'Hepatotoxicity (cholestatic)', frequency: '1-2%', management: 'Discontinue; usually reversible', warning: 'More common than with other penicillins' },
        { effect: 'Anaphylaxis', frequency: '<0.05%', management: 'Epinephrine, supportive care', warning: 'Ask about penicillin allergy' },
        { effect: 'C. difficile colitis', frequency: '1%', management: 'Discontinue, treat C. diff' }
      ],
      rare: ['Interstitial nephritis', 'Neutropenia with prolonged use', 'Hemolytic anemia']
    },
    interactions: {
      major: [
        { drug: 'Methotrexate', effect: 'Reduced methotrexate clearance', mechanism: 'Competition for renal tubular secretion', management: 'Monitor methotrexate levels' }
      ],
      moderate: [
        { drug: 'Warfarin', effect: 'May REDUCE warfarin effect (unlike other penicillins)', management: 'Monitor INR closely' },
        { drug: 'Probenecid', effect: 'Increased cloxacillin levels', management: 'May be used therapeutically' }
      ],
      food: ['Food decreases absorption - take on empty stomach']
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'No known teratogenic risk',
        second: 'Generally safe',
        third: 'Safe for use'
      },
      recommendation: 'Safe to use in pregnancy when indicated',
      lactation: { safe: true, notes: 'Small amounts excreted; compatible with breastfeeding' }
    },
    monitoring: {
      baseline: [
        { test: 'LFTs', reason: 'Baseline hepatic function (hepatotoxicity risk)' },
        { test: 'Renal function', reason: 'For dose adjustment if needed' }
      ],
      ongoing: [
        { test: 'LFTs', frequency: 'Weekly for first 2 weeks, then every 2-4 weeks' },
        { test: 'Signs of allergic reaction', frequency: 'Throughout therapy' },
        { test: 'CBC', frequency: 'Monthly if prolonged therapy' }
      ],
      therapeutic: { range: 'Not routinely monitored', timing: 'N/A' }
    },
    storage: {
      temperature: 'Store below 25°C',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Reconstituted IV solution stable for 24 hours refrigerated',
      shelfLife: '2-3 years from manufacture'
    },
    africanContext: {
      availability: 'AVAILABLE - May have variable supply in some regions',
      affordability: 'MODERATE COST - Generic available but may be more expensive than ampicillin',
      supplyChainNotes: 'Less consistently available than ampicillin in some rural areas',
      localManufacturing: 'Limited local manufacturing',
      alternatives: ['Flucloxacillin', 'Nafcillin', 'Oxacillin'],
      resistancePatterns: 'By definition, NOT effective against MRSA. MRSA prevalence varies across Africa (5-50% depending on region).',
      endemicConsiderations: 'CRITICAL: Only for MSSA infections. In regions with high MRSA prevalence, confirm susceptibility before use or consider empiric vancomycin.'
    },
    patientCounseling: {
      keyPoints: ['Take on empty stomach for best absorption', 'Complete full course', 'Report any jaundice or dark urine'],
      commonMisconceptions: ['Works against all staph infections (NOT effective against MRSA)'],
      adherenceTips: ['Set q6h reminders', 'Keep supply consistent'],
      warningSignsToReport: ['Yellowing of skin or eyes', 'Dark urine', 'Severe abdominal pain', 'Allergic reactions']
    },
    clinicalPearls: [
      'ONLY effective against MSSA - completely INEFFECTIVE against MRSA',
      'Higher rate of hepatotoxicity than other penicillins - monitor LFTs',
      'High protein binding (94-96%) limits tissue penetration',
      'Short half-life requires frequent dosing (q4-6h for serious infections)',
      'Take on empty stomach - food significantly reduces absorption'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'IDSA Guidelines for MRSA Infections',
      'Sanford Guide to Antimicrobial Therapy 2024'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'vancomycin-011',
    genericName: 'Vancomycin',
    brandNames: ['Vancocin', 'Vancoled', 'Firvanq', 'Vancor'],
    atcCode: 'J01XA01',
    therapeuticClass: 'Glycopeptide Antibiotic',
    pharmacology: {
      mechanism: 'Inhibits bacterial cell wall synthesis by binding to D-alanyl-D-alanine terminus of cell wall precursor units, preventing transglycosylation and transpeptidation steps. This disrupts peptidoglycan synthesis, leading to cell lysis. Additionally, may alter bacterial cell membrane permeability and inhibit RNA synthesis.',
      pharmacokinetics: {
        absorption: 'Poorly absorbed orally (<10% bioavailability); oral administration only effective for local GI infections (C. difficile). IV administration required for systemic infections with 100% bioavailability.',
        distribution: 'Vd: 0.4-1.0 L/kg; protein binding 30-55%; widely distributed to most tissues including bone, bile, pleural fluid, pericardial fluid, synovial fluid, and ascitic fluid. Poor CNS penetration (7-30%) unless meninges are inflamed.',
        metabolism: 'Minimal hepatic metabolism; excreted largely unchanged.',
        elimination: 'Primarily renal elimination (80-90% unchanged in urine via glomerular filtration). Small amount eliminated in feces.',
        halfLife: '4-6 hours in normal renal function; significantly prolonged in renal impairment (up to 7-12 days in anuria). Half-life in neonates: 6-10 hours.'
      },
      pharmacodynamics: 'Time-dependent killing with AUC/MIC ratio being the best predictor of efficacy. Target AUC/MIC ratio of 400-600 for serious MRSA infections. Exhibits concentration-independent killing and post-antibiotic effect (PAE) of 1.5-3 hours against staphylococci.'
    },
    indications: [
      { condition: 'Methicillin-resistant Staphylococcus aureus (MRSA) infections', evidenceLevel: 'A' as const, recommendationStrength: 'Strong' as const, firstLine: true, notes: 'First-line for serious MRSA infections including bacteremia, endocarditis, osteomyelitis, and pneumonia' },
      { condition: 'Clostridioides difficile infection (CDI)', evidenceLevel: 'A' as const, recommendationStrength: 'Strong' as const, firstLine: true, notes: 'Oral vancomycin first-line for initial non-severe and severe CDI; fidaxomicin preferred where available' },
      { condition: 'Bacterial meningitis (empiric therapy)', evidenceLevel: 'A' as const, recommendationStrength: 'Strong' as const, firstLine: true, notes: 'Combined with ceftriaxone for empiric coverage of penicillin-resistant S. pneumoniae' },
      { condition: 'Infective endocarditis (native and prosthetic valve)', evidenceLevel: 'A' as const, recommendationStrength: 'Strong' as const, firstLine: true, notes: 'First-line for MRSA endocarditis; often combined with gentamicin or rifampin for prosthetic valve infections' },
      { condition: 'Bone and joint infections', evidenceLevel: 'A' as const, recommendationStrength: 'Strong' as const, firstLine: true, notes: 'First-line for MRSA osteomyelitis and septic arthritis' },
      { condition: 'Healthcare-associated pneumonia (MRSA suspected)', evidenceLevel: 'B' as const, recommendationStrength: 'Moderate' as const, firstLine: false, notes: 'Alternative to linezolid for MRSA pneumonia; linezolid may have better lung penetration' },
      { condition: 'Surgical prophylaxis (beta-lactam allergy)', evidenceLevel: 'B' as const, recommendationStrength: 'Moderate' as const, firstLine: false, notes: 'Alternative for patients with serious beta-lactam allergy undergoing cardiac, orthopedic, or neurosurgery' },
      { condition: 'Febrile neutropenia (empiric therapy)', evidenceLevel: 'B' as const, recommendationStrength: 'Moderate' as const, firstLine: false, notes: 'Added to empiric regimen when catheter-related infection, skin/soft tissue infection, or MRSA colonization suspected' }
    ],
    dosing: {
      adults: {
        standard: '15-20 mg/kg IV every 8-12 hours (based on actual body weight); loading dose of 25-30 mg/kg for seriously ill patients',
        indicationSpecific: [
          { indication: 'MRSA bacteremia/endocarditis', dose: '15-20 mg/kg IV every 8-12 hours; target trough 15-20 mcg/mL or AUC 400-600' },
          { indication: 'MRSA meningitis', dose: '15-20 mg/kg IV every 8-12 hours; target trough 15-20 mcg/mL; consider intrathecal 5-20 mg/day if poor response' },
          { indication: 'MRSA osteomyelitis', dose: '15-20 mg/kg IV every 8-12 hours for 4-6 weeks minimum' },
          { indication: 'C. difficile infection (initial episode, non-severe)', dose: '125 mg PO four times daily for 10-14 days' },
          { indication: 'C. difficile infection (severe/fulminant)', dose: '500 mg PO four times daily ± metronidazole 500 mg IV every 8 hours; consider rectal vancomycin 500 mg in 100 mL saline enema every 6 hours for ileus' },
          { indication: 'Surgical prophylaxis', dose: '15 mg/kg IV over 1-2 hours, completed within 120 minutes before incision' }
        ],
        maxDose: '4 g/day IV; higher doses may be used with therapeutic drug monitoring in select cases',
        renalImpairment: {
          mild: 'CrCl 50-80 mL/min: 15-20 mg/kg every 12 hours; monitor trough levels',
          moderate: 'CrCl 30-50 mL/min: 15-20 mg/kg every 24 hours; monitor trough levels closely',
          severe: 'CrCl 10-30 mL/min: 15-20 mg/kg every 24-48 hours; monitor trough levels; consider loading dose',
          dialysis: 'HD: 15-25 mg/kg loading, then redose based on pre-HD levels (target 15-20 mcg/mL); give after HD. PD: 15-25 mg/kg loading, then 7.5-10 mg/kg every 3-5 days. CRRT: 15-25 mg/kg loading, then 7.5-10 mg/kg every 12-24 hours based on effluent rate'
        },
        hepaticImpairment: 'No dosage adjustment required; vancomycin is not hepatically metabolized'
      },
      pediatric: {
        standard: '15 mg/kg IV every 6 hours (60 mg/kg/day divided every 6 hours)',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates <1 week, <1200g', dose: '15 mg/kg IV every 24 hours' },
          { age: 'Neonates <1 week, 1200-2000g', dose: '15 mg/kg IV every 12-18 hours' },
          { age: 'Neonates <1 week, >2000g', dose: '15 mg/kg IV every 12 hours' },
          { age: 'Neonates 1-4 weeks', dose: '15 mg/kg IV every 8-12 hours' },
          { age: 'Infants and children', dose: '15 mg/kg IV every 6 hours (max 60 mg/kg/day)' },
          { age: 'Children with CNS infection', dose: '15 mg/kg IV every 6 hours; target trough 15-20 mcg/mL' },
          { age: 'C. difficile (oral)', dose: '10 mg/kg PO four times daily (max 125 mg/dose) for 10 days' }
        ],
        maxDose: '60 mg/kg/day or 4 g/day IV (whichever is less); 500 mg/day oral for C. difficile',
        neonatal: 'Extended intervals required due to immature renal function; gestational and postnatal age affect dosing. Therapeutic drug monitoring essential.'
      },
      elderly: 'Increased risk of nephrotoxicity and ototoxicity; start with standard weight-based dosing but anticipate need for extended intervals due to age-related renal decline. Close monitoring of renal function and trough levels essential.',
      routes: ['Intravenous', 'Oral (for C. difficile only)', 'Intrathecal (rarely)'],
      administration: [
        'IV: Infuse over at least 60 minutes (rate ≤10 mg/min) to minimize red man syndrome; extend to 90-120 minutes for doses >1g',
        'Concentration should not exceed 5 mg/mL for peripheral administration; up to 10 mg/mL for central line',
        'Oral: Capsules may be opened and mixed with water; powder may be reconstituted for oral solution',
        'Oral vancomycin is NOT effective for systemic infections; only for C. difficile colitis',
        'Do not administer IM (causes tissue necrosis)'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to vancomycin or any component',
        'Previous vancomycin-induced immune thrombocytopenia'
      ],
      relative: [
        'History of red man syndrome (not a true allergy; can pretreat and slow infusion)',
        'Pre-existing hearing loss',
        'Pre-existing renal impairment',
        'Concurrent use of other nephrotoxic or ototoxic agents'
      ],
      precautions: [
        'Monitor renal function closely, especially with concurrent nephrotoxins',
        'Assess hearing at baseline and during prolonged therapy',
        'Use caution in inflammatory bowel disease when giving oral vancomycin (increased systemic absorption)',
        'Risk of superinfection including fungal overgrowth with prolonged use',
        'Cross-reactivity with teicoplanin possible (10-15%)'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Nephrotoxicity', frequency: '5-25%', management: 'Monitor SCr every 2-3 days; adjust dose based on trough levels; avoid concurrent nephrotoxins; maintain adequate hydration' },
        { effect: 'Red man syndrome (flushing, pruritus, hypotension)', frequency: '5-50%', management: 'Slow infusion rate (≥60-120 min); pretreat with antihistamines (diphenhydramine 25-50 mg); not a true allergy' },
        { effect: 'Phlebitis at infusion site', frequency: '10-15%', management: 'Use central venous access for prolonged therapy; dilute appropriately; rotate peripheral sites' },
        { effect: 'Nausea', frequency: '5-10%', management: 'Symptomatic treatment; usually mild and transient' },
        { effect: 'Fever', frequency: '2-5%', management: 'Evaluate for drug fever vs infection; may resolve with slower infusion' }
      ],
      serious: [
        { effect: 'Acute kidney injury', frequency: '5-15%', management: 'Risk increases with trough >20 mcg/mL, concurrent nephrotoxins, prolonged therapy. Monitor closely; consider alternative if progressive.', warning: 'Risk significantly increased when combined with piperacillin-tazobactam' },
        { effect: 'Ototoxicity (hearing loss, tinnitus)', frequency: '1-5%', management: 'Often irreversible; risk increased with high trough levels, prolonged therapy, concurrent ototoxins. Baseline and periodic audiometry for prolonged treatment.' },
        { effect: 'Drug Reaction with Eosinophilia and Systemic Symptoms (DRESS)', frequency: '<1%', management: 'Discontinue immediately; systemic corticosteroids may be required; avoid future exposure' },
        { effect: 'Stevens-Johnson syndrome/Toxic epidermal necrolysis', frequency: '<0.1%', management: 'Discontinue immediately; supportive care; dermatology and burn unit consultation' },
        { effect: 'Vancomycin-induced immune thrombocytopenia', frequency: '<1%', management: 'Discontinue vancomycin; platelet transfusion if severe bleeding; IVIg may be considered' },
        { effect: 'Neutropenia', frequency: '2%', management: 'Usually reversible upon discontinuation; monitor CBC weekly during prolonged therapy' }
      ],
      rare: [
        'Linear IgA bullous dermatosis',
        'Interstitial nephritis',
        'Vasculitis',
        'Cardiac arrest with rapid infusion',
        'Pseudomembranous colitis (paradoxical)'
      ]
    },
    interactions: {
      major: [
        { drug: 'Piperacillin-tazobactam', effect: 'Significantly increased risk of acute kidney injury', mechanism: 'Synergistic nephrotoxicity through unclear mechanism; possibly enhanced tubular toxicity', management: 'Consider alternative beta-lactam (cefepime, meropenem) when possible; if combination necessary, monitor renal function daily' },
        { drug: 'Aminoglycosides (gentamicin, amikacin, tobramycin)', effect: 'Additive nephrotoxicity and ototoxicity', mechanism: 'Both agents cause renal tubular damage and cochlear toxicity', management: 'Avoid combination if possible; if required, monitor renal function and hearing closely; use extended-interval aminoglycoside dosing' },
        { drug: 'Amphotericin B', effect: 'Increased nephrotoxicity', mechanism: 'Additive renal tubular toxicity', management: 'Monitor renal function closely; consider liposomal amphotericin B; ensure adequate hydration' },
        { drug: 'Cisplatin', effect: 'Enhanced nephrotoxicity and ototoxicity', mechanism: 'Additive toxicity to renal tubules and cochlea', management: 'Avoid concurrent use if possible; monitor renal function and hearing' },
        { drug: 'Colistin (colistimethate)', effect: 'Increased nephrotoxicity', mechanism: 'Additive renal tubular damage', management: 'Monitor renal function very closely; may require dose adjustment of both agents' }
      ],
      moderate: [
        { drug: 'NSAIDs', effect: 'Potential increased nephrotoxicity', management: 'Use with caution; monitor renal function; ensure adequate hydration' },
        { drug: 'Loop diuretics (furosemide)', effect: 'Increased ototoxicity risk; potential nephrotoxicity', management: 'Monitor hearing and renal function; use lowest effective diuretic dose' },
        { drug: 'Neuromuscular blocking agents', effect: 'Enhanced neuromuscular blockade', management: 'Monitor for prolonged paralysis; adjust doses accordingly' },
        { drug: 'Cyclosporine, tacrolimus', effect: 'Increased nephrotoxicity', management: 'Monitor renal function and calcineurin inhibitor levels closely' },
        { drug: 'IV contrast dye', effect: 'Additive nephrotoxicity risk', management: 'Ensure adequate hydration; space contrast administration from vancomycin if possible' }
      ],
      food: [
        'No significant food interactions for oral vancomycin',
        'IV vancomycin has no food interactions'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Limited data; use only if clearly needed. No consistent pattern of malformations in limited human data.',
        second: 'Consider potential fetal ototoxicity with prolonged use or high doses; limited systemic data reassuring.',
        third: 'Monitor for fetal ototoxicity; can be used when indicated. Often used for GBS prophylaxis in penicillin-allergic patients.'
      },
      recommendation: 'Use when clearly indicated and benefits outweigh risks. Alternative of choice for MRSA in pregnancy when beta-lactams ineffective. For GBS prophylaxis in high-risk penicillin allergy, use vancomycin if clindamycin resistance suspected.',
      lactation: {
        safe: true,
        notes: 'Minimal excretion in breast milk due to poor oral absorption. Oral vancomycin in infant would not be systemically absorbed. May cause GI flora disruption in breastfed infant; monitor for diarrhea. Generally considered compatible with breastfeeding.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Serum creatinine and BUN', reason: 'Establish baseline renal function for dosing and monitoring nephrotoxicity' },
        { test: 'Complete blood count', reason: 'Baseline for monitoring neutropenia with prolonged therapy' },
        { test: 'Audiometry', reason: 'Baseline hearing assessment for prolonged therapy (>2 weeks) or concurrent ototoxin use' },
        { test: 'Body weight', reason: 'Required for accurate weight-based dosing' }
      ],
      ongoing: [
        { test: 'Vancomycin trough levels', frequency: 'Before 4th or 5th dose (steady state), then every 3-5 days for stable patients; daily if renal function changing', target: 'Trough 10-15 mcg/mL for most infections; 15-20 mcg/mL for serious infections (bacteremia, endocarditis, osteomyelitis, meningitis, pneumonia)' },
        { test: 'Vancomycin AUC monitoring', frequency: 'Preferred method where available; calculate AUC24 using Bayesian software or two-level approach', target: 'AUC24/MIC 400-600 for serious MRSA infections' },
        { test: 'Serum creatinine', frequency: 'Every 2-3 days; daily if unstable or on concurrent nephrotoxins', target: 'Stable or improving renal function' },
        { test: 'Complete blood count', frequency: 'Weekly for therapy >1 week', target: 'Monitor for neutropenia, thrombocytopenia' },
        { test: 'Audiometry', frequency: 'Weekly for therapy >2 weeks or with concurrent ototoxins' }
      ],
      therapeutic: {
        range: 'Trough: 10-20 mcg/mL depending on indication; AUC24: 400-600 mg·h/L for MRSA. Peak levels no longer routinely monitored.',
        timing: 'Trough: within 30 minutes before next dose. For AUC monitoring: obtain two levels (typically 1-2 hours post-infusion and trough) and calculate using validated software.'
      }
    },
    storage: {
      temperature: 'Powder: Store at 20-25°C (68-77°F). Reconstituted solution: Refrigerate at 2-8°C, stable for 14 days. Diluted IV solution: Stable for 24 hours at room temperature or 72 hours refrigerated.',
      light: 'Protect from light during storage',
      humidity: 'Store in dry conditions; powder is hygroscopic',
      specialInstructions: 'Reconstitute 500 mg vial with 10 mL sterile water; 1 g vial with 20 mL. Further dilute for IV infusion to concentration ≤5 mg/mL. Use within 24 hours of preparation.',
      shelfLife: 'Unopened vials: 24-36 months depending on manufacturer. Check expiration date.'
    },
    africanContext: {
      availability: 'LIMITED TO MODERATE - Available primarily in tertiary hospitals and major urban centers. Often unavailable in rural health facilities. Supply interruptions common.',
      affordability: 'EXPENSIVE - Cost $50-150 USD per day for IV treatment. Oral formulation may be more affordable for C. difficile ($30-80 per course). Cost-prohibitive for many patients without health insurance or hospital support.',
      supplyChainNotes: 'Requires cold chain for reconstituted solutions. Powdered form more stable for African supply chains. Stock-outs common at national medical stores. Consider establishing hospital-level strategic reserves for critically ill patients.',
      localManufacturing: 'Limited local manufacturing in Africa. Some production in South Africa, Morocco, and Egypt. Most supply imported from India, China, and Europe.',
      alternatives: [
        'Teicoplanin (similar spectrum, once-daily dosing, less nephrotoxic)',
        'Linezolid (oral option for MRSA, no renal dosing needed)',
        'Clindamycin (if susceptible, for less severe MRSA skin/soft tissue)',
        'Cotrimoxazole (for susceptible MRSA, especially skin infections)',
        'Daptomycin (where available, for bacteremia and endocarditis)'
      ],
      resistancePatterns: 'Vancomycin-resistant enterococci (VRE) increasingly reported in African tertiary hospitals. True vancomycin-resistant S. aureus (VRSA) remains rare globally. Vancomycin-intermediate S. aureus (VISA) emerging concern. MIC creep in MRSA isolates reported in South Africa and North Africa.',
      endemicConsiderations: 'MRSA prevalence varies widely across Africa (5-50% in hospital settings). Community-acquired MRSA increasing in urban areas. Consider empiric MRSA coverage in healthcare-associated infections, post-surgical infections, and patients with healthcare exposure. Many African hospitals lack capacity for routine therapeutic drug monitoring.'
    },
    patientCounseling: {
      keyPoints: [
        'This medication is given through a vein and cannot be taken by mouth for most infections',
        'Infusion typically takes 1-2 hours to minimize flushing reactions',
        'Report any ringing in ears, hearing changes, or dizziness immediately',
        'Report decreased urination or swelling which may indicate kidney problems',
        'Complete the full course of treatment even if feeling better to prevent resistance'
      ],
      commonMisconceptions: [
        'Red man syndrome (flushing during infusion) is NOT an allergic reaction and does not require stopping treatment',
        'Oral vancomycin treats gut infections only and will not work for infections elsewhere in the body',
        'Having MRSA does not mean you are unclean; it is a common hospital-associated infection'
      ],
      adherenceTips: [
        'Keep all scheduled infusion appointments; missing doses reduces effectiveness',
        'Stay well hydrated during treatment to protect kidney function',
        'Inform healthcare providers about all other medications including traditional remedies',
        'Blood tests are essential to ensure safe and effective dosing; attend all monitoring appointments'
      ],
      warningSignsToReport: [
        'Flushing, itching, or rapid heartbeat during infusion (can be managed, inform nurse)',
        'Ringing in ears, hearing loss, or dizziness',
        'Decreased urine output, dark urine, or leg swelling',
        'Skin rash, blisters, or peeling skin',
        'Persistent diarrhea or abdominal pain',
        'Fever or chills after starting treatment'
      ]
    },
    clinicalPearls: [
      'AUC-guided dosing (AUC 400-600) is now preferred over trough-only monitoring for serious MRSA infections; reduces nephrotoxicity while maintaining efficacy',
      'Red man syndrome is a rate-related histamine release, not IgE-mediated allergy; extend infusion time and premedicate rather than avoiding the drug',
      'The combination of vancomycin + piperacillin-tazobactam has significantly higher AKI risk than vancomycin + cefepime or meropenem; avoid this combination when alternatives exist',
      'Oral vancomycin is NOT absorbed systemically and is only effective for C. difficile colitis; do not prescribe oral vancomycin for MRSA bacteremia or other systemic infections',
      'For C. difficile, vancomycin 125 mg QID is as effective as 500 mg QID for non-severe disease and is more cost-effective',
      'In African settings where TDM is unavailable, use conservative dosing (15 mg/kg q12h) with close clinical monitoring of renal function',
      'Loading doses (25-30 mg/kg) are important for critically ill patients to achieve therapeutic levels rapidly; do not skip loading dose in septic patients'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'Rybak MJ et al. Therapeutic monitoring of vancomycin for serious MRSA infections: A revised consensus guideline. Am J Health-Syst Pharm 2020;77:835-864',
      'IDSA Clinical Practice Guidelines for MRSA Infections 2011 (update anticipated)',
      'McDonald LC et al. Clinical Practice Guidelines for C. difficile Infection in Adults and Children: 2017 Update by IDSA and SHEA',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'African Journal of Clinical Microbiology and Antimicrobial Resistance Surveillance Reports'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'meropenem-012',
    genericName: 'Meropenem',
    brandNames: ['Merrem', 'Meronem', 'Meropan', 'Ronem'],
    atcCode: 'J01DH02',
    therapeuticClass: 'Carbapenem Antibiotic',
    pharmacology: {
      mechanism: 'Binds to penicillin-binding proteins (PBPs) in the bacterial cell wall, inhibiting peptidoglycan synthesis and causing cell lysis. Has high affinity for PBP2, PBP3, and PBP4. Stable against most beta-lactamases including ESBLs and AmpC, but hydrolyzed by carbapenemases (KPC, NDM, OXA-48).',
      pharmacokinetics: {
        absorption: 'Not orally absorbed; IV administration only with 100% bioavailability.',
        distribution: 'Vd: 0.25-0.35 L/kg; protein binding approximately 2%; excellent tissue penetration including lung, peritoneal fluid, bile, and CSF (with inflamed meninges 10-50%). Does not penetrate intracellularly.',
        metabolism: 'Minimal hepatic metabolism; hydrolysis by renal dehydropeptidase-1 (DHP-1) but stable without cilastatin (unlike imipenem).',
        elimination: 'Primarily renal elimination (70% unchanged in urine). Approximately 25% metabolized to inactive metabolite.',
        halfLife: '1 hour in normal renal function; prolonged to 3-5 hours in severe renal impairment (CrCl <10 mL/min).'
      },
      pharmacodynamics: 'Time-dependent killing; efficacy correlates with time above MIC (%T>MIC). Target ≥40% T>MIC for bactericidal activity, ≥100% T>MIC optimal for serious infections. Extended or continuous infusions may improve outcomes for organisms with higher MICs. No significant post-antibiotic effect against gram-negatives.'
    },
    indications: [
      { condition: 'Hospital-acquired/Ventilator-associated pneumonia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Empiric therapy for HAP/VAP when MDR gram-negatives suspected; often combined with anti-MRSA agent' },
      { condition: 'Complicated intra-abdominal infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Monotherapy covers gram-negatives and anaerobes; consider adding vancomycin if enterococcal coverage needed' },
      { condition: 'Bacterial meningitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Excellent CNS penetration; covers gram-negatives including P. aeruginosa; does not lower seizure threshold like imipenem' },
      { condition: 'Febrile neutropenia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Appropriate monotherapy for high-risk febrile neutropenia' },
      { condition: 'Complicated urinary tract infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Reserve for ESBL-producing organisms or treatment failure with other agents' },
      { condition: 'Sepsis/Septic shock', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Empiric broad-spectrum coverage; de-escalate when culture results available' },
      { condition: 'Skin and soft tissue infections (severe/complicated)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'For polymicrobial infections or ESBL-producing organisms; add MRSA coverage if suspected' },
      { condition: 'ESBL-producing Enterobacteriaceae infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Carbapenem of choice for serious ESBL infections; avoid for carbapenemase producers' }
    ],
    dosing: {
      adults: {
        standard: '1 g IV every 8 hours; may use 2 g every 8 hours for serious infections or organisms with higher MICs',
        indicationSpecific: [
          { indication: 'HAP/VAP', dose: '1-2 g IV every 8 hours; consider extended infusion (3-hour infusion) for P. aeruginosa' },
          { indication: 'Bacterial meningitis', dose: '2 g IV every 8 hours' },
          { indication: 'Complicated intra-abdominal infection', dose: '1 g IV every 8 hours' },
          { indication: 'Febrile neutropenia', dose: '1 g IV every 8 hours; may increase to 2 g q8h for severe cases' },
          { indication: 'Complicated UTI', dose: '500 mg - 1 g IV every 8 hours' },
          { indication: 'Sepsis/septic shock', dose: '2 g IV loading dose, then 1-2 g IV every 8 hours (consider extended infusion)' }
        ],
        maxDose: '6 g/day (2 g every 8 hours)',
        renalImpairment: {
          mild: 'CrCl 50-80 mL/min: No adjustment; 1 g every 8 hours',
          moderate: 'CrCl 25-50 mL/min: 1 g every 12 hours (or 500 mg every 8 hours)',
          severe: 'CrCl 10-25 mL/min: 500 mg every 12 hours',
          dialysis: 'HD: 500 mg every 24 hours; give after dialysis on dialysis days. CRRT: 1 g every 8-12 hours depending on effluent rate (adjust based on residual renal function)'
        },
        hepaticImpairment: 'No dosage adjustment required; minimal hepatic metabolism'
      },
      pediatric: {
        standard: '20 mg/kg IV every 8 hours (max 1 g per dose for most indications)',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates <32 weeks GA, <14 days', dose: '20 mg/kg IV every 12 hours' },
          { age: 'Neonates <32 weeks GA, ≥14 days', dose: '20 mg/kg IV every 8 hours' },
          { age: 'Neonates ≥32 weeks GA, <14 days', dose: '20 mg/kg IV every 8 hours' },
          { age: 'Neonates ≥32 weeks GA, ≥14 days', dose: '30 mg/kg IV every 8 hours' },
          { age: 'Infants and children (non-CNS)', dose: '20 mg/kg IV every 8 hours (max 1 g/dose)' },
          { age: 'Infants and children (meningitis)', dose: '40 mg/kg IV every 8 hours (max 2 g/dose)' },
          { age: 'Cystic fibrosis', dose: '40 mg/kg IV every 8 hours (max 2 g/dose)' }
        ],
        maxDose: '6 g/day or 2 g per dose (whichever is less)',
        neonatal: 'Dosing varies by gestational age and postnatal age; extended intervals for premature neonates'
      },
      elderly: 'Adjust based on renal function rather than age alone. Monitor renal function closely as elderly more likely to have reduced CrCl.',
      routes: ['Intravenous'],
      administration: [
        'IV bolus: Reconstitute and give over 3-5 minutes',
        'IV infusion (standard): Infuse over 15-30 minutes',
        'Extended infusion: Infuse over 3 hours (may improve outcomes for higher MIC organisms)',
        'Continuous infusion: 3-6 g over 24 hours after loading dose (specialized centers)',
        'Reconstitute 500 mg with 10 mL sterile water; 1 g with 20 mL',
        'Compatible with NS and D5W; stable for 4 hours at room temperature, 24 hours refrigerated'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to meropenem or other carbapenems',
        'History of anaphylaxis to any beta-lactam (relative - see precautions)'
      ],
      relative: [
        'History of non-anaphylactic penicillin allergy (cross-reactivity ~1%)',
        'History of cephalosporin allergy',
        'Seizure disorder (lower risk than imipenem but still present)',
        'Severe renal impairment without dose adjustment'
      ],
      precautions: [
        'Cross-reactivity with penicillins is low (~1%) but monitor for reactions',
        'May cause seizures especially with CNS disorders, renal impairment, or overdose',
        'Risk of C. difficile-associated diarrhea',
        'May reduce valproic acid levels significantly - monitor and supplement',
        'Prolonged use may result in superinfection including fungal infections'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Diarrhea', frequency: '4-5%', management: 'Usually self-limiting; rule out C. difficile if persistent or severe' },
        { effect: 'Nausea/vomiting', frequency: '3-4%', management: 'Symptomatic treatment; slow infusion rate may help' },
        { effect: 'Headache', frequency: '2-8%', management: 'Symptomatic treatment with analgesics' },
        { effect: 'Injection site reactions', frequency: '2-3%', management: 'Rotate sites; use central access for prolonged therapy' },
        { effect: 'Rash', frequency: '2-3%', management: 'Assess for hypersensitivity; mild rash may not require discontinuation' }
      ],
      serious: [
        { effect: 'Seizures', frequency: '<1%', management: 'More common with renal impairment, CNS pathology, or high doses. Discontinue or reduce dose; benzodiazepines for acute seizure.', warning: 'Lower seizure risk than imipenem but still possible' },
        { effect: 'Clostridioides difficile colitis', frequency: '1-2%', management: 'Discontinue meropenem if possible; treat with oral vancomycin or fidaxomicin' },
        { effect: 'Anaphylaxis/severe hypersensitivity', frequency: '<0.5%', management: 'Discontinue immediately; epinephrine, antihistamines, corticosteroids as needed' },
        { effect: 'Stevens-Johnson syndrome/TEN', frequency: '<0.1%', management: 'Discontinue immediately; supportive care; dermatology consultation' },
        { effect: 'Hepatotoxicity (elevated transaminases)', frequency: '1-2%', management: 'Usually mild and reversible; monitor LFTs; discontinue if significant elevation' },
        { effect: 'Thrombocytopenia', frequency: '<1%', management: 'Monitor CBC; usually reversible on discontinuation' }
      ],
      rare: [
        'Hemolytic anemia',
        'Interstitial nephritis',
        'Neutropenia with prolonged use',
        'Positive direct Coombs test',
        'Encephalopathy'
      ]
    },
    interactions: {
      major: [
        { drug: 'Valproic acid/Divalproex', effect: 'Dramatically reduces valproate levels by 60-90%', mechanism: 'Inhibits intestinal absorption and increases renal clearance of valproate; also inhibits glucuronidation', management: 'AVOID combination if possible. If unavoidable, monitor valproate levels closely and increase dose significantly; consider alternative anticonvulsant or alternative antibiotic' },
        { drug: 'Probenecid', effect: 'Increases meropenem levels by 50%', mechanism: 'Inhibits renal tubular secretion', management: 'Not recommended to combine; if used, monitor for meropenem toxicity' }
      ],
      moderate: [
        { drug: 'Live vaccines (typhoid)', effect: 'May reduce vaccine efficacy', management: 'Avoid live vaccines during treatment; wait 3 days after completion' },
        { drug: 'Warfarin', effect: 'May increase INR', management: 'Monitor INR closely; adjust warfarin dose as needed' },
        { drug: 'Other nephrotoxic agents', effect: 'Potential additive nephrotoxicity', management: 'Monitor renal function; hydrate adequately' }
      ],
      food: [
        'No significant food interactions (IV administration only)'
      ]
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'No evidence of teratogenicity in animal studies; limited human data. Use if clearly needed.',
        second: 'Can be used when indicated; generally considered safe based on available data.',
        third: 'Can be used when indicated; no specific concerns for late pregnancy.'
      },
      recommendation: 'Generally considered safe in pregnancy when indicated. One of the preferred beta-lactams for serious infections in pregnancy. Use when benefits outweigh potential risks.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in low concentrations. Generally considered compatible with breastfeeding. Monitor infant for diarrhea or thrush.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Serum creatinine and BUN', reason: 'Renal function for dose adjustment' },
        { test: 'Complete blood count', reason: 'Baseline for monitoring hematologic effects' },
        { test: 'Liver function tests', reason: 'Baseline for hepatotoxicity monitoring' },
        { test: 'Valproic acid level', reason: 'If on concurrent valproate therapy - critical interaction' }
      ],
      ongoing: [
        { test: 'Serum creatinine', frequency: 'Every 2-3 days initially, then weekly', target: 'Stable renal function' },
        { test: 'CBC with differential', frequency: 'Weekly for therapy >7 days', target: 'Stable counts' },
        { test: 'Liver function tests', frequency: 'Weekly for prolonged therapy', target: 'ALT/AST <3x ULN' },
        { test: 'Valproic acid level', frequency: 'Daily if on concurrent valproate', target: 'Therapeutic range 50-100 mcg/mL' },
        { test: 'Clinical response', frequency: 'Daily', target: 'Resolution of infection signs/symptoms' }
      ],
      therapeutic: {
        range: 'Therapeutic drug monitoring not routine; may be useful in critically ill, obese, or patients with augmented renal clearance',
        timing: 'If TDM used: trough level before dose; target trough >2-4x MIC for serious infections'
      }
    },
    storage: {
      temperature: 'Powder: Store at 20-25°C (68-77°F). Reconstituted solution: Stable 4 hours at room temperature, 24 hours refrigerated.',
      light: 'Protect from light',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Reconstitute 500 mg vial with 10 mL sterile water or NS; 1 g vial with 20 mL. Shake to dissolve. Further dilute in 50-250 mL NS or D5W for infusion.',
      shelfLife: 'Unopened vials: 24-36 months. Use reconstituted solution promptly.'
    },
    africanContext: {
      availability: 'LIMITED - Available primarily in tertiary hospitals and major private facilities. Frequently out of stock at national medical stores. Often requires special ordering.',
      affordability: 'EXPENSIVE - Cost $15-50 USD per 1g vial. Full treatment course can cost $300-800. Often unaffordable without health insurance or hospital subsidy.',
      supplyChainNotes: 'Powder form stable at room temperature but should avoid excessive heat. Stock-outs common; hospitals should maintain reserves for critically ill patients. Consider imipenem-cilastatin as alternative if available.',
      localManufacturing: 'Limited local manufacturing. Some production in India and China supplying African markets. Quality-assured generics available but supply inconsistent.',
      alternatives: [
        'Imipenem-cilastatin (similar spectrum)',
        'Ertapenem (once daily, but no Pseudomonas coverage)',
        'Piperacillin-tazobactam (for less severe infections)',
        'Cefepime + metronidazole (for mixed infections)',
        'Ceftazidime-avibactam (where available, for KPC producers)'
      ],
      resistancePatterns: 'Carbapenem-resistant Enterobacteriaceae (CRE) increasingly reported in African tertiary hospitals, particularly K. pneumoniae. NDM-type and OXA-48 carbapenemases identified in East and North Africa. Carbapenem-resistant P. aeruginosa and A. baumannii emerging in ICUs. Always send cultures before empiric carbapenem use.',
      endemicConsiderations: 'High rates of ESBL-producing Enterobacteriaceae in many African settings drive carbapenem use, but this increases selection pressure for CRE. Antimicrobial stewardship essential. Reserve for confirmed ESBL or clinical failure with other agents when possible.'
    },
    patientCounseling: {
      keyPoints: [
        'This is a powerful antibiotic given through a vein for serious infections',
        'Each infusion typically takes 15-30 minutes (or longer if extended infusion)',
        'Report any signs of allergic reaction immediately (rash, difficulty breathing, swelling)',
        'Complete the full course even if feeling better',
        'Report any severe diarrhea which could indicate a secondary infection'
      ],
      commonMisconceptions: [
        'Meropenem is not available as an oral medication - IV only',
        'Having a mild penicillin allergy does not always mean you cannot receive meropenem (discuss with your doctor)',
        'This antibiotic is reserved for serious infections and should not be used for common infections'
      ],
      adherenceTips: [
        'Keep all scheduled infusion appointments - timing is important',
        'Stay well hydrated during treatment',
        'Inform all healthcare providers that you are on meropenem',
        'If you are on seizure medications, inform your doctor immediately'
      ],
      warningSignsToReport: [
        'Skin rash, itching, or hives',
        'Difficulty breathing or swallowing',
        'Severe or persistent diarrhea (could be C. difficile)',
        'Seizures or unusual muscle movements',
        'Yellowing of skin or eyes',
        'Fever or chills that develop during treatment'
      ]
    },
    clinicalPearls: [
      'Extended infusion (3 hours) improves T>MIC and may improve outcomes for organisms with MIC 2-8 mcg/mL, especially P. aeruginosa',
      'Unlike imipenem, meropenem is stable to renal DHP-1 and does not require cilastatin; also has lower seizure potential',
      'The meropenem-valproate interaction is severe and unavoidable - switch anticonvulsant or antibiotic',
      'For meningitis, meropenem is preferred over imipenem due to lower seizure risk and better CNS penetration',
      'Meropenem has no activity against MRSA, Enterococcus faecium, or Stenotrophomonas maltophilia',
      'In African settings with high ESBL rates, carbapenem stewardship is crucial - use only when clearly indicated and de-escalate early',
      'For carbapenemase-producing organisms, meropenem is ineffective - check local resistance patterns and consider combination therapy or novel agents'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'IDSA/ATS Guidelines for HAP/VAP 2016',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Surviving Sepsis Campaign Guidelines 2021',
      'Tamma PD et al. IDSA Guidance on Treatment of AMR Gram-Negative Infections 2023',
      'African Journal of Clinical Microbiology - Carbapenem Resistance Surveillance Reports'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'fluconazole-013',
    genericName: 'Fluconazole',
    brandNames: ['Diflucan', 'Trican', 'Flucoral', 'Flucostat'],
    atcCode: 'J02AC01',
    therapeuticClass: 'Triazole Antifungal',
    pharmacology: {
      mechanism: 'Inhibits fungal cytochrome P450 enzyme 14-alpha-demethylase (CYP51), blocking conversion of lanosterol to ergosterol. Ergosterol depletion disrupts fungal cell membrane integrity, increasing permeability and inhibiting fungal growth. Fungistatic against most Candida species; fungicidal against Cryptococcus at higher concentrations.',
      pharmacokinetics: {
        absorption: 'Excellent oral bioavailability (>90%) independent of food or gastric pH. Peak plasma concentration in 1-2 hours. IV and oral forms bioequivalent.',
        distribution: 'Vd: 0.7-1.0 L/kg; protein binding low (11-12%); excellent tissue penetration including CSF (60-80% of plasma levels), eye, saliva, skin, nails, and vaginal secretions.',
        metabolism: 'Minimal hepatic metabolism (<5%); does not undergo significant CYP450 metabolism but is a potent inhibitor of CYP2C9, CYP2C19, and CYP3A4.',
        elimination: 'Primarily renal excretion (80% unchanged in urine). Small amount in feces.',
        halfLife: '30 hours (range 20-50 hours) in normal renal function; prolonged to 98 hours in severe renal impairment. Long half-life allows once-daily dosing.'
      },
      pharmacodynamics: 'AUC/MIC ratio is the best predictor of efficacy. Target AUC/MIC >25 for mucosal candidiasis, >50-100 for invasive infections. Time-dependent activity with prolonged post-antifungal effect.'
    },
    indications: [
      { condition: 'Oropharyngeal candidiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line treatment; highly effective for HIV-associated oral thrush' },
      { condition: 'Esophageal candidiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line treatment; 14-21 days therapy' },
      { condition: 'Vulvovaginal candidiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single 150 mg dose for uncomplicated; longer courses for recurrent' },
      { condition: 'Candidemia (non-neutropenic)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Step-down therapy after initial echinocandin if C. albicans susceptible; not first-line empiric' },
      { condition: 'Cryptococcal meningitis (consolidation/maintenance)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'After induction with amphotericin B + flucytosine; 800 mg/day consolidation, then 200 mg/day maintenance' },
      { condition: 'Coccidioidomycosis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for non-meningeal disease; lifelong for meningeal involvement' },
      { condition: 'Prophylaxis in immunocompromised', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Prevents invasive candidiasis in high-risk patients (HSCT, ICU, AIDS)' },
      { condition: 'Tinea infections (dermatophytosis)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Second-line; terbinafine preferred for dermatophytes' }
    ],
    dosing: {
      adults: {
        standard: '200-400 mg once daily depending on indication; loading dose of 2x maintenance on day 1',
        indicationSpecific: [
          { indication: 'Oropharyngeal candidiasis', dose: '200 mg on day 1, then 100-200 mg daily for 7-14 days' },
          { indication: 'Esophageal candidiasis', dose: '400 mg on day 1, then 200-400 mg daily for 14-21 days' },
          { indication: 'Vulvovaginal candidiasis (uncomplicated)', dose: '150 mg single oral dose' },
          { indication: 'Vulvovaginal candidiasis (recurrent)', dose: '150 mg every 72 hours x 3 doses, then 150 mg weekly for 6 months' },
          { indication: 'Candidemia', dose: '800 mg on day 1, then 400 mg daily; step-down from echinocandin' },
          { indication: 'Cryptococcal meningitis (consolidation)', dose: '800 mg daily for 8 weeks after induction' },
          { indication: 'Cryptococcal meningitis (maintenance)', dose: '200 mg daily for minimum 1 year (lifelong if persistent immunosuppression)' },
          { indication: 'Prophylaxis (HSCT/neutropenia)', dose: '400 mg daily' }
        ],
        maxDose: '800-1200 mg/day (higher doses used in cryptococcal meningitis)',
        renalImpairment: {
          mild: 'CrCl 50-80 mL/min: No adjustment required',
          moderate: 'CrCl 20-50 mL/min: Reduce dose by 50% after loading dose',
          severe: 'CrCl <20 mL/min: Reduce dose by 50-75% after loading dose',
          dialysis: 'HD: Give full dose after each dialysis session (highly dialyzable). PD: 50% dose reduction. CRRT: No adjustment usually needed; monitor'
        },
        hepaticImpairment: 'Use with caution; monitor LFTs closely. No specific dose reduction but consider reduced dose in severe hepatic impairment.'
      },
      pediatric: {
        standard: '6-12 mg/kg once daily (max based on indication)',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates 0-14 days', dose: '6-12 mg/kg every 72 hours (prolonged half-life)' },
          { age: 'Neonates 15-28 days', dose: '6-12 mg/kg every 48 hours' },
          { age: 'Infants and children (oropharyngeal)', dose: '6 mg/kg on day 1, then 3 mg/kg daily for 7-14 days' },
          { age: 'Infants and children (esophageal)', dose: '6 mg/kg on day 1, then 3-6 mg/kg daily (max 400 mg)' },
          { age: 'Infants and children (invasive candidiasis)', dose: '12 mg/kg on day 1, then 6-12 mg/kg daily (max 600 mg)' },
          { age: 'Cryptococcal meningitis (consolidation)', dose: '12 mg/kg daily (max 800 mg)' }
        ],
        maxDose: '600-800 mg/day depending on indication',
        neonatal: 'Extended dosing intervals due to immature renal function; 72-hour intervals in first 2 weeks of life'
      },
      elderly: 'Adjust based on renal function. Elderly more susceptible to drug interactions due to polypharmacy.',
      routes: ['Oral', 'Intravenous'],
      administration: [
        'Oral: Can be taken with or without food; absorption not affected by gastric pH',
        'IV: Infuse at rate not exceeding 200 mg/hour (typically over 1-2 hours)',
        'IV and oral doses are equivalent - switch to oral when feasible',
        'Loading dose (2x maintenance) recommended for serious infections to rapidly achieve steady state'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to fluconazole or other azole antifungals',
        'Concurrent use with terfenadine, cisapride, pimozide, or quinidine (QT prolongation risk)',
        'Concurrent use with erythromycin (risk of cardiotoxicity)'
      ],
      relative: [
        'Pre-existing hepatic impairment',
        'Proarrhythmic conditions or QT prolongation',
        'Concurrent medications metabolized by CYP2C9, CYP2C19, or CYP3A4',
        'Pregnancy (especially first trimester at high doses)'
      ],
      precautions: [
        'Monitor liver function; discontinue if hepatotoxicity develops',
        'Multiple significant drug interactions due to CYP450 inhibition',
        'May cause QT prolongation at high doses; use caution with other QT-prolonging drugs',
        'Chronic use may select for azole-resistant Candida species',
        'Adrenal insufficiency reported rarely with prolonged use'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Headache', frequency: '10-13%', management: 'Symptomatic treatment; usually transient' },
        { effect: 'Nausea', frequency: '5-7%', management: 'Take with food if bothersome; antiemetics rarely needed' },
        { effect: 'Abdominal pain', frequency: '3-6%', management: 'Usually mild; symptomatic treatment' },
        { effect: 'Diarrhea', frequency: '2-3%', management: 'Usually self-limiting; rule out C. difficile if severe' },
        { effect: 'Rash', frequency: '2-5%', management: 'Assess severity; discontinue if severe or progressive' }
      ],
      serious: [
        { effect: 'Hepatotoxicity', frequency: '1-5%', management: 'Monitor LFTs; discontinue if ALT >3-5x ULN with symptoms or >8x ULN asymptomatic', warning: 'Rare cases of fatal hepatic necrosis reported; monitor closely' },
        { effect: 'QT prolongation/Torsades de pointes', frequency: '<1%', management: 'Avoid in patients with QT prolongation; monitor ECG with high doses or concurrent QT-prolonging drugs', warning: 'Risk increases with dose and drug interactions' },
        { effect: 'Stevens-Johnson syndrome/TEN', frequency: '<0.1%', management: 'Discontinue immediately; supportive care; dermatology consultation' },
        { effect: 'Anaphylaxis', frequency: '<0.1%', management: 'Discontinue; epinephrine, antihistamines, corticosteroids' },
        { effect: 'Adrenal insufficiency', frequency: 'Rare', management: 'Consider in patients with fatigue, weakness, hypotension; test cortisol levels' }
      ],
      rare: [
        'Alopecia (reversible, usually with prolonged high-dose therapy)',
        'Hypokalemia',
        'Thrombocytopenia',
        'Leukopenia',
        'Exfoliative dermatitis'
      ]
    },
    interactions: {
      major: [
        { drug: 'Warfarin', effect: 'Significantly increases warfarin levels and INR', mechanism: 'Inhibits CYP2C9 metabolism of warfarin', management: 'Reduce warfarin dose by 25-50%; monitor INR closely and frequently' },
        { drug: 'Phenytoin', effect: 'Increases phenytoin levels; phenytoin decreases fluconazole levels', mechanism: 'Mutual CYP450 interaction', management: 'Monitor phenytoin levels; may need to increase fluconazole dose' },
        { drug: 'Rifampin/Rifabutin', effect: 'Dramatically reduces fluconazole levels by 50%', mechanism: 'CYP450 induction', management: 'Increase fluconazole dose; consider alternative antifungal' },
        { drug: 'Tacrolimus/Cyclosporine', effect: 'Increases calcineurin inhibitor levels significantly', mechanism: 'Inhibits CYP3A4 metabolism', management: 'Reduce tacrolimus dose by 50-70%; monitor levels closely' },
        { drug: 'QT-prolonging drugs (quinidine, erythromycin, pimozide)', effect: 'Additive QT prolongation risk', mechanism: 'Pharmacodynamic interaction plus CYP inhibition', management: 'Avoid combination; contraindicated with some agents' },
        { drug: 'Statins (simvastatin, atorvastatin)', effect: 'Significantly increases statin levels', mechanism: 'Inhibits CYP3A4', management: 'Use lowest statin dose; consider pravastatin or rosuvastatin (less interaction)' }
      ],
      moderate: [
        { drug: 'Oral hypoglycemics (glipizide, glyburide)', effect: 'Increased hypoglycemic effect', management: 'Monitor blood glucose; may need to reduce sulfonylurea dose' },
        { drug: 'Benzodiazepines (midazolam, triazolam)', effect: 'Prolonged sedation', management: 'Reduce benzodiazepine dose; monitor for excessive sedation' },
        { drug: 'Calcium channel blockers', effect: 'Increased CCB levels', management: 'Monitor blood pressure; may need dose reduction' },
        { drug: 'Opioids (fentanyl, oxycodone)', effect: 'Increased opioid effect', management: 'Monitor for respiratory depression; reduce opioid dose if needed' }
      ],
      food: [
        'No significant food interactions',
        'Avoid excessive alcohol (additive hepatotoxicity risk)'
      ]
    },
    pregnancy: {
      category: 'D (high dose/prolonged) / C (single dose)',
      trimpiesterRisks: {
        first: 'HIGH RISK at doses ≥400 mg/day: Associated with craniofacial, cardiac, and limb abnormalities. Single low-dose (150 mg) appears safe for vaginal candidiasis.',
        second: 'Continue to avoid high doses; single dose for vaginal candidiasis acceptable',
        third: 'Continue to avoid high doses; topical antifungals preferred'
      },
      recommendation: 'AVOID high-dose or prolonged fluconazole in pregnancy, especially first trimester. Single 150 mg dose for vulvovaginal candidiasis is acceptable. For serious fungal infections, use amphotericin B as alternative.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk at concentrations similar to plasma. Generally considered compatible with breastfeeding at standard doses. Monitor infant for GI effects.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Liver function tests (ALT, AST, ALP, bilirubin)', reason: 'Baseline for hepatotoxicity monitoring' },
        { test: 'Serum creatinine', reason: 'Renal function for dose adjustment' },
        { test: 'Potassium', reason: 'Baseline electrolytes; fluconazole may cause hypokalemia' },
        { test: 'ECG', reason: 'Baseline QTc if high-dose therapy planned or concurrent QT-prolonging drugs' }
      ],
      ongoing: [
        { test: 'Liver function tests', frequency: 'Weekly for first month, then monthly for prolonged therapy', target: 'ALT/AST <3x ULN' },
        { test: 'Serum creatinine', frequency: 'Weekly initially, then as clinically indicated', target: 'Stable renal function' },
        { test: 'Drug levels of interacting medications', frequency: 'As appropriate (warfarin INR, tacrolimus level, phenytoin level)', target: 'Therapeutic ranges' },
        { test: 'Clinical response', frequency: 'As appropriate for indication', target: 'Resolution of fungal infection' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed; may be useful in critically ill or suspected non-response',
        timing: 'Trough level if monitoring needed; target depends on MIC of organism'
      }
    },
    storage: {
      temperature: 'Tablets/capsules: Store at 20-25°C (68-77°F). Oral suspension: Store at 5-30°C; discard unused portion after 14 days. IV: Store at 5-25°C.',
      light: 'Protect from excessive light',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Oral suspension should be shaken well before use. IV solution is ready to use; do not add other medications.',
      shelfLife: 'Tablets: 24-36 months. Reconstituted oral suspension: 14 days.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; available in most hospitals and many pharmacies across Africa. Generic formulations common.',
      affordability: 'AFFORDABLE - Generic fluconazole costs $0.10-0.50 per 150-200 mg capsule. 800 mg daily for cryptococcal meningitis more expensive but often subsidized for HIV patients.',
      supplyChainNotes: 'Stable at room temperature; suitable for African supply chains. Oral formulation preferred over IV for cost and logistics. PEPFAR and Global Fund programs support availability for HIV-associated fungal infections.',
      localManufacturing: 'Manufactured in multiple African countries including South Africa, Kenya, Nigeria, and Egypt. Quality-assured generics widely available.',
      alternatives: [
        'Itraconazole (broader spectrum but more interactions)',
        'Voriconazole (for resistant Candida or Aspergillus)',
        'Amphotericin B deoxycholate (for severe infections)',
        'Nystatin (topical/oral for superficial candidiasis)',
        'Clotrimazole troches (for oropharyngeal candidiasis)'
      ],
      resistancePatterns: 'Fluconazole-resistant Candida increasingly reported, especially C. glabrata and C. krusei (intrinsically resistant). C. auris emerging in South Africa with high azole resistance rates. HIV patients with repeated fluconazole exposure at risk for resistant oral/esophageal candidiasis.',
      endemicConsiderations: 'Cryptococcal meningitis is a leading cause of death in HIV/AIDS patients in Africa. Fluconazole maintenance therapy critical for secondary prophylaxis. Access to 800 mg/day for consolidation improved through donation programs but still challenging in some areas.'
    },
    patientCounseling: {
      keyPoints: [
        'Can be taken with or without food',
        'Complete the full course even if feeling better',
        'Report any yellowing of skin/eyes, dark urine, or severe fatigue (liver warning signs)',
        'Single-dose treatment for vaginal yeast may take 2-3 days to fully resolve symptoms',
        'Inform doctor of all other medications due to many drug interactions'
      ],
      commonMisconceptions: [
        'Single dose cures vaginal yeast immediately - may take 2-3 days for full symptom relief',
        'Fluconazole treats all fungal infections - it does not work against Aspergillus or some Candida species',
        'Safe to take with any medication - fluconazole has many significant drug interactions'
      ],
      adherenceTips: [
        'For once-daily dosing, take at the same time each day',
        'For maintenance therapy, set reminders to maintain consistent dosing',
        'If you miss a dose, take it as soon as remembered unless close to next dose',
        'Keep follow-up appointments for blood tests to monitor liver function'
      ],
      warningSignsToReport: [
        'Yellowing of skin or eyes (jaundice)',
        'Dark urine or pale stools',
        'Severe fatigue or weakness',
        'Severe skin rash or blistering',
        'Irregular heartbeat or palpitations',
        'Signs of infection not improving'
      ]
    },
    clinicalPearls: [
      'Fluconazole achieves excellent CSF penetration (60-80% of plasma) - ideal for cryptococcal meningitis consolidation',
      'Loading dose (2x maintenance) on day 1 rapidly achieves steady state due to long half-life',
      'C. krusei is intrinsically resistant; C. glabrata often has reduced susceptibility - check susceptibilities for invasive infections',
      'Fluconazole is dialyzable - give dose AFTER hemodialysis',
      'In African HIV patients with cryptococcal meningitis, follow WHO guidelines: amphotericin B induction → fluconazole 800 mg consolidation → 200 mg maintenance',
      'Single-dose fluconazole 150 mg is safe in early pregnancy for vaginal candidiasis; avoid higher doses and prolonged courses',
      'Drug interactions are extensive - always review medication list; warfarin interaction is particularly dangerous'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'IDSA Guidelines for Candidiasis 2016',
      'WHO Guidelines on Cryptococcal Disease in HIV 2022',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Pappas PG et al. Clinical Practice Guideline for Candidiasis. Clin Infect Dis 2016',
      'Southern African HIV Clinicians Society Guidelines'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'acyclovir-014',
    genericName: 'Acyclovir',
    brandNames: ['Zovirax', 'Acivir', 'Herpex', 'Cyclovir'],
    atcCode: 'J05AB01',
    therapeuticClass: 'Nucleoside Analogue Antiviral',
    pharmacology: {
      mechanism: 'Selectively activated by viral thymidine kinase (TK) in infected cells, converting to acyclovir monophosphate. Host cellular kinases then convert to active acyclovir triphosphate, which competitively inhibits viral DNA polymerase and incorporates into viral DNA, causing chain termination. Minimal effect on uninfected cells due to selectivity for viral TK.',
      pharmacokinetics: {
        absorption: 'Oral bioavailability 15-30% (dose-dependent, decreases at higher doses). Peak plasma levels in 1.5-2 hours. Food does not significantly affect absorption.',
        distribution: 'Vd: 0.6-0.8 L/kg; protein binding 9-33%; widely distributed including CSF (50% of plasma levels), aqueous humor, and vesicle fluid. Crosses placenta.',
        metabolism: 'Minimal hepatic metabolism; partially converted to inactive 9-carboxymethoxymethylguanine (CMMG).',
        elimination: 'Primarily renal excretion (62-91% unchanged via glomerular filtration and tubular secretion). Small amount in feces.',
        halfLife: '2.5-3.5 hours in normal renal function; prolonged to 20 hours in anuria. Neonatal half-life: 4 hours.'
      },
      pharmacodynamics: 'Concentration-independent (time-dependent) activity. Efficacy correlates with maintaining drug concentrations above minimum inhibitory concentration. Most effective when started early in infection (within 48-72 hours of symptom onset).'
    },
    indications: [
      { condition: 'Herpes simplex encephalitis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'IV acyclovir is the treatment of choice; start empirically if suspected' },
      { condition: 'Neonatal herpes simplex infection', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'High-dose IV acyclovir (60 mg/kg/day) for 14-21 days' },
      { condition: 'Herpes zoster (shingles)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Most effective if started within 72 hours of rash onset; reduces duration and PHN risk' },
      { condition: 'Varicella (chickenpox) in high-risk patients', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Immunocompromised, adults, adolescents, and those with chronic skin/lung disease' },
      { condition: 'Genital herpes (first episode)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Reduces duration and severity; 7-10 day course' },
      { condition: 'Genital herpes (recurrent)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Episodic (5 days) or suppressive therapy (daily for ≥6 recurrences/year)' },
      { condition: 'Herpes labialis (cold sores)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Topical or oral; modest benefit if started at prodrome' },
      { condition: 'HSV prophylaxis in immunocompromised', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Prevents reactivation in HSCT, solid organ transplant, and chemotherapy patients' }
    ],
    dosing: {
      adults: {
        standard: 'Varies widely by indication; oral 200-800 mg 2-5 times daily; IV 5-10 mg/kg every 8 hours',
        indicationSpecific: [
          { indication: 'HSV encephalitis', dose: '10 mg/kg IV every 8 hours for 14-21 days' },
          { indication: 'Herpes zoster (immunocompetent)', dose: '800 mg PO 5 times daily for 7-10 days' },
          { indication: 'Herpes zoster (immunocompromised)', dose: '10 mg/kg IV every 8 hours for 7-10 days' },
          { indication: 'Varicella (adults)', dose: '800 mg PO 5 times daily for 5-7 days' },
          { indication: 'Genital herpes (first episode)', dose: '400 mg PO three times daily OR 200 mg PO 5 times daily for 7-10 days' },
          { indication: 'Genital herpes (recurrent, episodic)', dose: '800 mg PO three times daily for 2 days OR 400 mg PO three times daily for 5 days' },
          { indication: 'Genital herpes (suppressive)', dose: '400 mg PO twice daily (indefinitely)' },
          { indication: 'HSV prophylaxis (transplant)', dose: '400-800 mg PO twice daily or 250 mg/m² IV every 12 hours' },
          { indication: 'Herpes labialis', dose: '400 mg PO 5 times daily for 5 days' }
        ],
        maxDose: '4000 mg/day oral; 30 mg/kg/day IV (based on ideal body weight for obese patients)',
        renalImpairment: {
          mild: 'CrCl 25-50 mL/min: Normal dose for genital herpes; reduce herpes zoster dose to 800 mg q8h',
          moderate: 'CrCl 10-25 mL/min: 800 mg PO q12h for herpes zoster; IV 5-10 mg/kg q12h',
          severe: 'CrCl <10 mL/min: 800 mg PO q24h for herpes zoster; IV 5-10 mg/kg q24h',
          dialysis: 'HD: Give dose after dialysis. For HSV encephalitis: 5 mg/kg q24h, supplemental dose after HD. PD: 50% dose reduction. CRRT: 5-10 mg/kg q12-24h'
        },
        hepaticImpairment: 'No dosage adjustment required; minimal hepatic metabolism'
      },
      pediatric: {
        standard: 'Varies by indication and age; typically 20 mg/kg per dose for IV, 20 mg/kg 4 times daily for oral',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonatal HSV (all disease)', dose: '20 mg/kg IV every 8 hours (60 mg/kg/day) for 14-21 days' },
          { age: 'HSV encephalitis (children)', dose: '10-15 mg/kg IV every 8 hours for 14-21 days' },
          { age: 'Varicella (2-18 years, immunocompetent)', dose: '20 mg/kg (max 800 mg) PO 4 times daily for 5 days' },
          { age: 'Varicella (immunocompromised)', dose: '10 mg/kg IV every 8 hours for 7-10 days' },
          { age: 'Herpes zoster (children)', dose: '20 mg/kg (max 800 mg) PO 4 times daily for 5-7 days' },
          { age: 'HSV gingivostomatitis', dose: '15 mg/kg (max 200 mg) PO 5 times daily for 7 days' },
          { age: 'HSV prophylaxis', dose: '10-20 mg/kg PO 3-4 times daily (max 800 mg/dose)' }
        ],
        maxDose: '80 mg/kg/day oral (max 3200 mg); 60 mg/kg/day IV for neonates; 45 mg/kg/day IV for older children',
        neonatal: 'High-dose therapy (60 mg/kg/day) recommended for all neonatal HSV due to high morbidity; duration 14-21 days depending on disease extent'
      },
      elderly: 'Adjust based on renal function. Elderly at higher risk for neurological toxicity and nephrotoxicity.',
      routes: ['Oral', 'Intravenous', 'Topical'],
      administration: [
        'IV: Infuse over at least 1 hour to prevent crystalluria and nephrotoxicity; max concentration 7 mg/mL',
        'Ensure adequate hydration during IV therapy (maintain urine output >1 mL/kg/hour)',
        'Oral: Can be taken with or without food; space doses evenly throughout waking hours',
        'Topical: Apply to lesions 5-6 times daily; limited efficacy compared to systemic therapy',
        'Do not administer IV as bolus - risk of renal failure'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to acyclovir, valacyclovir, or any component'
      ],
      relative: [
        'Dehydration or pre-existing renal impairment',
        'Concurrent nephrotoxic medications',
        'Neurological abnormalities or prior neurological reactions to acyclovir'
      ],
      precautions: [
        'Maintain adequate hydration to prevent crystalline nephropathy',
        'Reduce dose in renal impairment to prevent accumulation and neurotoxicity',
        'Use ideal body weight for dosing in obese patients to prevent overdose',
        'Neurotoxicity more common in elderly, renal impairment, and high doses',
        'Thrombotic thrombocytopenic purpura/hemolytic uremic syndrome reported rarely in immunocompromised'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Nausea', frequency: '5-10%', management: 'Take with food; usually mild' },
        { effect: 'Headache', frequency: '5-10%', management: 'Symptomatic treatment with analgesics' },
        { effect: 'Diarrhea', frequency: '3-5%', management: 'Usually self-limiting; supportive care' },
        { effect: 'Malaise/fatigue', frequency: '5-10%', management: 'Supportive care; usually transient' },
        { effect: 'Injection site reactions (IV)', frequency: '10-15%', management: 'Dilute adequately; use large vein; rotate sites' }
      ],
      serious: [
        { effect: 'Nephrotoxicity (crystalline nephropathy)', frequency: '1-5% (IV)', management: 'Ensure adequate hydration; infuse slowly over 1 hour; monitor renal function; usually reversible', warning: 'Risk increased with rapid infusion, dehydration, high doses, and concurrent nephrotoxins' },
        { effect: 'Neurotoxicity (confusion, hallucinations, seizures, tremor)', frequency: '1-4%', management: 'More common with renal impairment and high doses; reduce dose or discontinue; usually reversible', warning: 'Elderly and patients with renal impairment at highest risk' },
        { effect: 'Thrombotic thrombocytopenic purpura/HUS', frequency: 'Rare', management: 'Discontinue immediately; plasma exchange may be required', warning: 'Primarily in immunocompromised patients' },
        { effect: 'Severe hypersensitivity/anaphylaxis', frequency: '<0.1%', management: 'Discontinue; epinephrine, antihistamines, corticosteroids' },
        { effect: 'Elevated liver enzymes', frequency: '1-2%', management: 'Monitor LFTs; usually mild and reversible' }
      ],
      rare: [
        'Leukopenia/neutropenia',
        'Thrombocytopenia',
        'Disseminated intravascular coagulation',
        'Encephalopathy',
        'Coma (with overdose or severe renal impairment)'
      ]
    },
    interactions: {
      major: [
        { drug: 'Nephrotoxic agents (aminoglycosides, amphotericin B, NSAIDs)', effect: 'Increased risk of nephrotoxicity', mechanism: 'Additive renal toxicity', management: 'Avoid combination if possible; ensure hydration; monitor renal function closely' },
        { drug: 'Probenecid', effect: 'Increases acyclovir levels by 40%', mechanism: 'Inhibits renal tubular secretion', management: 'Monitor for acyclovir toxicity; may need dose reduction' },
        { drug: 'Mycophenolate mofetil', effect: 'Mutual increase in levels', mechanism: 'Competition for renal tubular secretion', management: 'Monitor for toxicity of both drugs; adjust doses if needed' }
      ],
      moderate: [
        { drug: 'Zidovudine', effect: 'Increased risk of neurotoxicity (drowsiness, lethargy)', management: 'Monitor for CNS effects' },
        { drug: 'Cimetidine', effect: 'May increase acyclovir levels', management: 'Monitor for toxicity' },
        { drug: 'Theophylline', effect: 'May increase theophylline levels', management: 'Monitor theophylline levels' }
      ],
      food: [
        'No significant food interactions',
        'Food does not significantly affect absorption'
      ]
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'No increased risk of birth defects in pregnancy registry data. Can use for primary genital herpes or severe infection.',
        second: 'Safe to use when indicated. Consider suppressive therapy from 36 weeks to prevent neonatal transmission.',
        third: 'Suppressive therapy (400 mg TID from 36 weeks) recommended for women with history of genital herpes to reduce recurrence and cesarean delivery.'
      },
      recommendation: 'Generally considered safe throughout pregnancy based on extensive registry data. Use for primary genital herpes and consider suppressive therapy starting at 36 weeks for women with genital herpes history.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk; infant receives <1% of weight-adjusted maternal dose. Considered compatible with breastfeeding. Monitor infant for rare GI effects.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Serum creatinine and BUN', reason: 'Renal function for dose adjustment and baseline' },
        { test: 'Complete blood count', reason: 'Baseline for monitoring hematologic effects' },
        { test: 'Liver function tests', reason: 'Baseline if prolonged therapy planned' }
      ],
      ongoing: [
        { test: 'Serum creatinine', frequency: 'Every 2-3 days during IV therapy; weekly for prolonged oral', target: 'Stable renal function' },
        { test: 'Neurological assessment', frequency: 'Daily during IV therapy', target: 'No signs of encephalopathy (confusion, tremor, hallucinations)' },
        { test: 'Urine output', frequency: 'Daily during IV therapy', target: '>1 mL/kg/hour' },
        { test: 'CBC', frequency: 'Weekly for prolonged therapy', target: 'Stable counts' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed; may consider in renal impairment or suspected toxicity',
        timing: 'If needed, trough levels before next dose'
      }
    },
    storage: {
      temperature: 'Tablets/capsules: Store at 15-25°C (59-77°F). Oral suspension: Store at 15-25°C. IV powder: Store at 15-25°C. Reconstituted IV: Stable 12 hours at room temperature.',
      light: 'Protect from light; store in original container',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Reconstitute 500 mg vial with 10 mL sterile water (50 mg/mL). Further dilute to ≤7 mg/mL for infusion in NS or D5W. Do not refrigerate reconstituted solution (may precipitate).',
      shelfLife: 'Tablets/capsules: 36 months. Reconstituted IV: use within 12 hours at room temperature.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; oral formulations available in most hospitals and pharmacies. IV formulation may be limited to larger facilities.',
      affordability: 'AFFORDABLE - Generic oral acyclovir costs $0.05-0.20 per 200 mg tablet. IV formulation more expensive ($5-15 per 500 mg vial) but still accessible.',
      supplyChainNotes: 'Oral formulations stable at room temperature; suitable for African supply chains. IV formulation requires reconstitution; hospitals should stock for herpes encephalitis emergencies. Valacyclovir (prodrug with better bioavailability) increasingly available but more expensive.',
      localManufacturing: 'Widely manufactured in Africa including South Africa, Kenya, Nigeria, Egypt, and Morocco. Quality-assured generics available.',
      alternatives: [
        'Valacyclovir (better bioavailability, less frequent dosing)',
        'Famciclovir (alternative oral agent)',
        'Ganciclovir (for CMV, not HSV/VZV)',
        'Foscarnet (for acyclovir-resistant HSV)',
        'Topical acyclovir (limited efficacy)'
      ],
      resistancePatterns: 'Acyclovir-resistant HSV rare in immunocompetent (<1%) but reported in 5-10% of immunocompromised patients, especially with prolonged exposure. Resistance mediated by thymidine kinase mutations. Foscarnet or cidofovir alternatives for resistant strains.',
      endemicConsiderations: 'High HSV-2 seroprevalence in sub-Saharan Africa (30-80% in women). HSV-2 increases HIV acquisition risk 2-3 fold. Genital herpes management important for HIV prevention. Neonatal herpes underdiagnosed; consider empiric treatment in neonates with sepsis or vesicular rash.'
    },
    patientCounseling: {
      keyPoints: [
        'Start treatment as early as possible when symptoms begin (ideally within 72 hours)',
        'Complete the full course even if symptoms improve',
        'Drink plenty of fluids during treatment to protect kidneys',
        'Acyclovir does not cure herpes - the virus remains dormant and can reactivate',
        'For genital herpes: reduces but does not eliminate transmission risk; use condoms'
      ],
      commonMisconceptions: [
        'Acyclovir cures herpes - it suppresses but does not eliminate the virus',
        'No need to take if no symptoms - suppressive therapy reduces recurrences and transmission',
        'Topical cream is as effective as oral - systemic therapy is more effective for most indications'
      ],
      adherenceTips: [
        'For 5-times-daily dosing, take doses evenly during waking hours (e.g., 7am, 11am, 3pm, 7pm, 11pm)',
        'Set alarms or reminders for doses',
        'If you miss a dose, take it as soon as remembered; skip if close to next dose',
        'Keep a supply for episodic treatment if you have recurrent herpes'
      ],
      warningSignsToReport: [
        'Decreased urination or blood in urine',
        'Confusion, hallucinations, or unusual behavior',
        'Severe headache or stiff neck',
        'Tremors or seizures',
        'Skin rash or severe fatigue',
        'Symptoms not improving after 7 days of treatment'
      ]
    },
    clinicalPearls: [
      'For HSV encephalitis, start IV acyclovir EMPIRICALLY if suspected - do not wait for PCR results; mortality is high without treatment',
      'Oral bioavailability is only 15-30%; valacyclovir (prodrug) achieves 3-5x higher levels and allows less frequent dosing',
      'Infuse IV acyclovir slowly (≥1 hour) with adequate hydration to prevent crystalline nephropathy; bolus dosing is dangerous',
      'Neurotoxicity (confusion, tremor, hallucinations) often misdiagnosed as disease progression; consider dose reduction in renal impairment',
      'In neonatal HSV, use HIGH dose (60 mg/kg/day) even for skin/eye/mouth disease - CNS involvement may be occult',
      'For genital herpes in pregnancy, suppressive therapy from 36 weeks reduces recurrence at delivery and cesarean rates',
      'Acyclovir has minimal activity against CMV and EBV (lack viral thymidine kinase); do not use for these infections'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'CDC STI Treatment Guidelines 2021',
      'IDSA Guidelines for HSV Encephalitis',
      'AAP Red Book 2024 - Herpes Simplex',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Kimberlin DW et al. Neonatal Herpes Simplex Guidelines'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'artemether-lumefantrine-015',
    genericName: 'Artemether-Lumefantrine',
    brandNames: ['Coartem', 'Riamet', 'Lumartem', 'Lonart', 'Artefan'],
    atcCode: 'P01BF01',
    therapeuticClass: 'Artemisinin-based Combination Therapy (ACT)',
    pharmacology: {
      mechanism: 'Artemether: Artemisinin derivative that generates free radicals within the parasite food vacuole, damaging parasite membranes and proteins. Rapidly kills asexual blood stages and young gametocytes. Lumefantrine: Aryl amino alcohol that inhibits heme polymerization, leading to toxic heme accumulation. Acts as long-acting partner drug to prevent recrudescence and protect artemether from resistance development.',
      pharmacokinetics: {
        absorption: 'Artemether: Well absorbed orally; bioavailability ~30-40% (improved 2-fold with fatty food). Lumefantrine: Highly variable absorption (food-dependent); bioavailability increased 16-fold with high-fat meal.',
        distribution: 'Artemether: Vd 4.5 L/kg; protein binding 95%. Converted to active metabolite dihydroartemisinin (DHA). Lumefantrine: Vd 50 L/kg; protein binding 99.7%. Both distribute widely including CNS.',
        metabolism: 'Artemether: Rapidly metabolized by CYP3A4/5 to dihydroartemisinin (DHA) which is active. Lumefantrine: Metabolized by CYP3A4 to desbutyl-lumefantrine (active).',
        elimination: 'Artemether/DHA: Rapid elimination primarily via bile. Lumefantrine: Slow elimination via feces; minimal renal excretion.',
        halfLife: 'Artemether: 1-2 hours; DHA: 1-2 hours. Lumefantrine: 3-6 days (terminal half-life). The long half-life of lumefantrine provides post-treatment prophylaxis.'
      },
      pharmacodynamics: 'Artemether/DHA provides rapid parasite clearance (kills 10,000-fold parasites per cycle). Lumefantrine eliminates remaining parasites and prevents recrudescence. Peak parasite reduction within 24-48 hours; parasites typically cleared by 72 hours.'
    },
    indications: [
      { condition: 'Uncomplicated Plasmodium falciparum malaria', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'WHO first-line ACT for uncomplicated falciparum malaria in endemic areas' },
      { condition: 'Uncomplicated P. vivax malaria', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Effective against blood stages; follow with primaquine for radical cure (hypnozoites)' },
      { condition: 'Mixed Plasmodium infections', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Effective against all human malaria species' },
      { condition: 'Uncomplicated malaria in pregnancy (2nd/3rd trimester)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'ACT of choice in 2nd/3rd trimester; quinine in 1st trimester' },
      { condition: 'Malaria prophylaxis', evidenceLevel: 'B', recommendationStrength: 'Weak', firstLine: false, notes: 'Not typically used for prophylaxis; atovaquone-proguanil or doxycycline preferred' }
    ],
    dosing: {
      adults: {
        standard: '6-dose regimen over 3 days: 4 tablets (80 mg artemether/480 mg lumefantrine) per dose at 0, 8, 24, 36, 48, and 60 hours',
        indicationSpecific: [
          { indication: 'Uncomplicated malaria (≥35 kg)', dose: '4 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 24 tablets)' },
          { indication: 'Uncomplicated malaria (25-35 kg)', dose: '3 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 18 tablets)' },
          { indication: 'Uncomplicated malaria (15-25 kg)', dose: '2 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 12 tablets)' }
        ],
        maxDose: '4 tablets (80 mg artemether/480 mg lumefantrine) per dose',
        renalImpairment: {
          mild: 'No dose adjustment required',
          moderate: 'No dose adjustment required; use with caution',
          severe: 'Limited data; use with caution; consider alternative if available',
          dialysis: 'No specific recommendations; lumefantrine highly protein-bound (unlikely removed by dialysis)'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment; no specific dose adjustment. Monitor for toxicity.'
      },
      pediatric: {
        standard: 'Weight-based dosing; 6-dose regimen over 3 days',
        weightBased: true,
        ageSpecific: [
          { age: '5-15 kg body weight', dose: '1 tablet per dose at 0, 8, 24, 36, 48, and 60 hours (total 6 tablets)' },
          { age: '15-25 kg body weight', dose: '2 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 12 tablets)' },
          { age: '25-35 kg body weight', dose: '3 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 18 tablets)' },
          { age: '≥35 kg body weight', dose: '4 tablets per dose at 0, 8, 24, 36, 48, and 60 hours (total 24 tablets)' },
          { age: 'Infants <5 kg', dose: 'Limited data; WHO recommends use if no alternative; dispersible formulation preferred' }
        ],
        maxDose: '4 tablets per dose',
        neonatal: 'Not recommended for neonates <5 kg due to limited safety data. Use quinine if treatment required.'
      },
      elderly: 'No specific dose adjustment. Elderly may be more susceptible to QT prolongation; monitor ECG if cardiac risk factors.',
      routes: ['Oral'],
      administration: [
        'MUST be taken with food or milk (preferably fatty food) to ensure adequate absorption - critical for efficacy',
        'If vomiting occurs within 1 hour of dose, repeat full dose',
        'If vomiting occurs 1-2 hours after dose, give half dose',
        'Dispersible tablet available for children; can be crushed and mixed with water',
        'Complete all 6 doses even if feeling better',
        'Second dose at 8 hours is critical for efficacy; do not extend beyond 12 hours'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to artemether, lumefantrine, or artemisinin derivatives',
        'Severe malaria (requires parenteral treatment)',
        'First trimester of pregnancy (use quinine instead)',
        'Concurrent treatment with drugs that prolong QT interval and are CYP3A4 substrates'
      ],
      relative: [
        'Known QT prolongation or family history of long QT syndrome',
        'Concurrent use of other QT-prolonging drugs',
        'Electrolyte abnormalities (hypokalemia, hypomagnesemia)',
        'Severe hepatic impairment',
        'Severe renal impairment'
      ],
      precautions: [
        'Ensure adequate food intake with each dose for proper absorption',
        'Monitor for QT prolongation in patients with cardiac risk factors',
        'Not for severe/complicated malaria - requires IV artesunate',
        'Re-treatment within 28 days may have reduced efficacy',
        'Caution with drugs metabolized by CYP3A4 or CYP2D6'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Headache', frequency: '25-55%', management: 'Often difficult to distinguish from malaria symptoms; symptomatic treatment' },
        { effect: 'Anorexia/decreased appetite', frequency: '15-40%', management: 'Encourage small frequent meals; ensure dose taken with food' },
        { effect: 'Dizziness', frequency: '15-40%', management: 'Rest; usually resolves as malaria clears' },
        { effect: 'Abdominal pain', frequency: '10-20%', management: 'Symptomatic treatment; rule out other causes if severe' },
        { effect: 'Nausea/vomiting', frequency: '10-30%', management: 'Antiemetics if needed; re-dose if vomiting within 1 hour' },
        { effect: 'Asthenia/fatigue', frequency: '10-20%', management: 'Rest; supportive care' },
        { effect: 'Arthralgia/myalgia', frequency: '10-20%', management: 'Symptomatic with analgesics; may be malaria-related' }
      ],
      serious: [
        { effect: 'QT prolongation', frequency: '1-5%', management: 'Baseline and follow-up ECG if risk factors; avoid concurrent QT-prolonging drugs', warning: 'Risk increases with cardiac disease, electrolyte imbalance, and drug interactions' },
        { effect: 'Hypersensitivity/anaphylaxis', frequency: '<1%', management: 'Discontinue; epinephrine, antihistamines, corticosteroids' },
        { effect: 'Hepatotoxicity', frequency: '<1%', management: 'Monitor LFTs if symptoms; usually transient elevation' },
        { effect: 'Severe skin reactions', frequency: 'Rare', management: 'Discontinue immediately; supportive care' },
        { effect: 'Hemolytic anemia', frequency: 'Rare', management: 'More common with primaquine; monitor in G6PD deficiency' }
      ],
      rare: [
        'Palpitations',
        'Torsades de pointes (with QT prolongation)',
        'Angioedema',
        'Cough',
        'Pruritus'
      ]
    },
    interactions: {
      major: [
        { drug: 'QT-prolonging drugs (haloperidol, ondansetron, fluoroquinolones)', effect: 'Additive QT prolongation risk', mechanism: 'Pharmacodynamic interaction', management: 'Avoid combination if possible; if unavoidable, monitor ECG and electrolytes' },
        { drug: 'Strong CYP3A4 inhibitors (ketoconazole, ritonavir, clarithromycin)', effect: 'Increased lumefantrine levels', mechanism: 'CYP3A4 inhibition', management: 'Use with caution; monitor for lumefantrine toxicity' },
        { drug: 'Strong CYP3A4 inducers (rifampicin, phenytoin, carbamazepine)', effect: 'Decreased artemether and lumefantrine levels; risk of treatment failure', mechanism: 'CYP3A4 induction', management: 'Avoid combination; consider alternative antimalarial' },
        { drug: 'Grapefruit juice', effect: 'Increased lumefantrine levels', mechanism: 'CYP3A4 inhibition', management: 'Avoid grapefruit products during treatment' }
      ],
      moderate: [
        { drug: 'CYP2D6 substrates (metoprolol, codeine, TCAs)', effect: 'Increased substrate levels (lumefantrine inhibits CYP2D6)', management: 'Monitor for toxicity; may need dose adjustment' },
        { drug: 'Antiretrovirals (efavirenz, lopinavir/ritonavir)', effect: 'Complex interactions; variable effect on levels', management: 'WHO recommends artemether-lumefantrine can be used; monitor efficacy' },
        { drug: 'Hormonal contraceptives', effect: 'May reduce contraceptive efficacy', management: 'Use additional contraception during treatment and for one cycle after' }
      ],
      food: [
        'MUST be taken with food (especially fatty food) - absorption increased up to 16-fold',
        'Avoid grapefruit and grapefruit juice (increases lumefantrine levels)'
      ]
    },
    pregnancy: {
      category: 'C (1st trimester) / Recommended (2nd/3rd trimester)',
      trimpiesterRisks: {
        first: 'AVOID - Limited safety data in 1st trimester; animal studies show embryotoxicity with artemisinin derivatives. Use oral quinine + clindamycin instead.',
        second: 'Recommended by WHO as first-line ACT. Extensive safety data supports use; benefits outweigh risks.',
        third: 'Recommended by WHO as first-line ACT. Safe and effective in late pregnancy.'
      },
      recommendation: 'AVOID in first trimester; use quinine + clindamycin. RECOMMENDED in second and third trimesters as first-line treatment for uncomplicated falciparum malaria. Treat malaria promptly - untreated malaria poses greater risk to mother and fetus.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in small amounts. WHO recommends artemether-lumefantrine is compatible with breastfeeding. Infant exposure is minimal and not a contraindication.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Malaria rapid diagnostic test (RDT) or microscopy', reason: 'Confirm diagnosis before treatment' },
        { test: 'Hemoglobin/hematocrit', reason: 'Assess for anemia from malaria' },
        { test: 'ECG', reason: 'If cardiac risk factors present; baseline QTc' },
        { test: 'Blood glucose', reason: 'Hypoglycemia common in severe malaria' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: 'Daily until afebrile; day 3 parasite check', target: 'Fever clearance by 48 hours; parasites cleared by day 3' },
        { test: 'Parasite count (if available)', frequency: 'Day 3, day 7 (and day 28 for efficacy studies)', target: 'Negative by day 3' },
        { test: 'Hemoglobin', frequency: 'Day 7-14 if symptomatic', target: 'Improving or stable' },
        { test: 'ECG', frequency: 'If on concurrent QT-prolonging medications', target: 'QTc <500 ms' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed for artemether-lumefantrine',
        timing: 'Day 7 lumefantrine level >200 ng/mL associated with cure (research only)'
      }
    },
    storage: {
      temperature: 'Store below 30°C (86°F). Do not refrigerate.',
      light: 'Store in original blister pack to protect from light',
      humidity: 'Store in dry conditions; protect from moisture',
      specialInstructions: 'Keep in original packaging until use. Dispersible tablets should be reconstituted immediately before administration.',
      shelfLife: '24-36 months depending on formulation. Check expiry date.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO prequalified; first-line ACT in national malaria programs across Africa. Available in public health facilities, pharmacies, and community health worker programs.',
      affordability: 'AFFORDABLE/SUBSIDIZED - Global Fund and PMI subsidize ACTs in malaria-endemic countries. Public sector: often free or nominal cost. Private sector: $1-5 per treatment course.',
      supplyChainNotes: 'Stable at room temperature up to 30°C; suitable for African climate. Available in child-friendly dispersible formulation. Pre-packaged blister packs facilitate correct dosing. Stock-outs remain a challenge in some areas.',
      localManufacturing: 'WHO-prequalified manufacturers in several African countries including South Africa and Morocco. Cipla, Ipca, and other generic manufacturers supply African markets.',
      alternatives: [
        'Artesunate-amodiaquine (alternative first-line ACT)',
        'Dihydroartemisinin-piperaquine (alternative first-line ACT)',
        'Artesunate-mefloquine (alternative ACT)',
        'Quinine + clindamycin (for 1st trimester pregnancy)',
        'IV artesunate (for severe malaria - NOT artemether-lumefantrine)'
      ],
      resistancePatterns: 'Artemisinin partial resistance (delayed parasite clearance) confirmed in Rwanda and suspected in Uganda, Tanzania, and other East African countries. Lumefantrine resistance rare but monitoring ongoing. ACTs remain effective for treatment, but WHO recommends close surveillance.',
      endemicConsiderations: 'Malaria remains leading cause of morbidity/mortality in sub-Saharan Africa. Presumptive treatment without testing discouraged but still occurs. Community case management programs improve access. Test, treat, track strategy essential. Seasonal malaria chemoprevention uses different drugs (SP + amodiaquine).'
    },
    patientCounseling: {
      keyPoints: [
        'Take EVERY dose with food or milk - this is essential for the medicine to work',
        'Complete ALL 6 doses over 3 days even if you feel better',
        'Take the second dose 8 hours after the first dose (very important)',
        'If you vomit within 1 hour of taking a dose, take another full dose',
        'Return immediately if fever returns, symptoms worsen, or vomiting prevents taking medicine'
      ],
      commonMisconceptions: [
        'One dose cures malaria - NO, you must complete all 6 doses',
        'Can skip doses if feeling better - NO, incomplete treatment causes resistance',
        'Medicine works without food - NO, must take with food for proper absorption',
        'This medicine treats severe malaria - NO, severe malaria requires hospital admission and IV treatment'
      ],
      adherenceTips: [
        'Set alarms for the 8-hour second dose',
        'Eat something (even small amount of fatty food like milk, avocado, or groundnuts) with each dose',
        'Keep blister pack to track doses taken',
        'Have someone help remind you if you are unwell',
        'Pre-position next dose where you will remember'
      ],
      warningSignsToReport: [
        'Persistent vomiting preventing medication intake',
        'High fever not improving after 48 hours',
        'Confusion, extreme weakness, or unable to eat/drink',
        'Convulsions/seizures',
        'Yellow eyes (jaundice)',
        'Dark or reduced urine',
        'Severe abdominal pain',
        'Symptoms returning after completing treatment'
      ]
    },
    clinicalPearls: [
      'FOOD IS CRITICAL - Lumefantrine absorption increases 16-fold with fatty food; treatment failure often due to poor absorption without food',
      'The 8-hour second dose is crucial - this maintains drug levels during rapid parasite multiplication',
      'NOT for severe malaria - patients with any danger signs need IV artesunate and hospital admission',
      'Day 7 lumefantrine level >200 ng/mL is the best predictor of cure; levels are food-dependent',
      'Artemisinin partial resistance (K13 mutations) emerging in East Africa - monitor day 3 parasites; >10% day 3 positivity suggests resistance',
      'For vivax malaria, add primaquine for radical cure (after G6PD testing where possible)',
      'In pregnancy, treat malaria promptly - untreated malaria causes more harm than ACTs in 2nd/3rd trimester'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines for Malaria 2023',
      'WHO Technical Note: Artemisinin Resistance in Africa 2024',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Medicines for Malaria Venture (MMV) Product Information',
      'National Malaria Control Program Guidelines (various African countries)'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'artesunate-016',
    genericName: 'Artesunate',
    brandNames: ['Artesun', 'Falcimon', 'Artesunat', 'Gsunate'],
    atcCode: 'P01BE03',
    therapeuticClass: 'Artemisinin Derivative Antimalarial',
    pharmacology: {
      mechanism: 'Water-soluble artemisinin derivative rapidly hydrolyzed to active metabolite dihydroartemisinin (DHA). Generates free radicals via interaction with heme iron in parasite food vacuole, causing oxidative damage to parasite membranes and proteins. Rapidly kills all asexual blood stages including young ring forms, and reduces gametocyte carriage. Fastest parasite clearance of all antimalarials.',
      pharmacokinetics: {
        absorption: 'IV/IM: 100% bioavailability. Rectal: ~50% bioavailability. Oral: Rapidly absorbed but lower bioavailability (~60%). Rapidly converted to DHA.',
        distribution: 'Artesunate Vd: 0.1-0.3 L/kg; DHA Vd: 0.5-1.0 L/kg. Protein binding: artesunate 75-80%, DHA 93%. Widely distributed including CSF.',
        metabolism: 'Rapidly hydrolyzed by plasma esterases to DHA (active). DHA undergoes glucuronidation (UGT1A9, UGT2B7). CYP2A6 involved in minor pathway.',
        elimination: 'DHA-glucuronide excreted in urine and bile. Minimal unchanged drug in urine.',
        halfLife: 'Artesunate: 2-15 minutes (rapidly converted to DHA). DHA: 0.5-1.5 hours. Short half-life necessitates combination therapy or follow-on treatment.'
      },
      pharmacodynamics: 'Most rapid parasite clearance of any antimalarial. Parasite reduction ratio ~10,000-fold per 48-hour cycle. Kills ring-stage parasites (prevents sequestration). Time to 50% parasite clearance: 4-8 hours. Must be followed by longer-acting antimalarial to prevent recrudescence.'
    },
    indications: [
      { condition: 'Severe malaria (all species)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'WHO first-line treatment for severe malaria; superior to quinine with 35% mortality reduction' },
      { condition: 'Severe P. falciparum malaria', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'IV artesunate is treatment of choice; start immediately on suspicion' },
      { condition: 'Uncomplicated malaria (when oral therapy not possible)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'IM/rectal when vomiting prevents oral ACT; follow with oral ACT when able' },
      { condition: 'Pre-referral treatment of severe malaria', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose IM/rectal before transfer to higher facility' },
      { condition: 'Hyperparasitemia (>10% parasitized RBCs)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Rapidly reduces parasite burden; continue until parasitemia <1% and patient can take oral' }
    ],
    dosing: {
      adults: {
        standard: '2.4 mg/kg IV or IM at 0, 12, 24 hours, then once daily until oral therapy possible (minimum 24 hours parenteral)',
        indicationSpecific: [
          { indication: 'Severe malaria', dose: '2.4 mg/kg IV at 0, 12, 24 hours, then daily. Minimum 3 doses (24 hours). Continue until able to take oral, then complete with full course of oral ACT.' },
          { indication: 'Severe malaria with hyperparasitemia', dose: '2.4 mg/kg IV at 0, 12, 24 hours, then daily. May require extended IV therapy until parasitemia <1%.' },
          { indication: 'Pre-referral (IM)', dose: '2.4 mg/kg IM single dose before transfer' },
          { indication: 'Pre-referral (rectal)', dose: '10 mg/kg rectal single dose if injection not available' }
        ],
        maxDose: '2.4 mg/kg per dose; no maximum total dose established for severe malaria',
        renalImpairment: {
          mild: 'No dose adjustment required',
          moderate: 'No dose adjustment required',
          severe: 'No dose adjustment required; artesunate is hepatically cleared',
          dialysis: 'No specific adjustment; unlikely to be significantly dialyzed'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment; no specific dose adjustment but monitor closely'
      },
      pediatric: {
        standard: '3 mg/kg IV or IM at 0, 12, 24 hours, then once daily (higher dose in children <20 kg)',
        weightBased: true,
        ageSpecific: [
          { age: 'Children <20 kg', dose: '3 mg/kg IV/IM at 0, 12, 24 hours, then daily (higher dose due to faster clearance)' },
          { age: 'Children ≥20 kg', dose: '2.4 mg/kg IV/IM at 0, 12, 24 hours, then daily' },
          { age: 'Pre-referral rectal (children)', dose: '10 mg/kg rectal single dose; use rectal artesunate suppositories' },
          { age: 'Infants <5 kg', dose: 'Limited data; use 3 mg/kg; WHO recommends use as no alternative for severe malaria' }
        ],
        maxDose: '3 mg/kg per dose for <20 kg; 2.4 mg/kg for ≥20 kg',
        neonatal: 'Very limited data in neonates; use if severe malaria and no alternative. Dose: 3 mg/kg IV.'
      },
      elderly: 'No specific dose adjustment. Elderly may have higher malaria mortality; treat promptly.',
      routes: ['Intravenous (preferred)', 'Intramuscular', 'Rectal (suppositories)'],
      administration: [
        'IV: Reconstitute 60 mg vial with 1 mL 5% sodium bicarbonate, shake 2-3 minutes until clear, then dilute with 5 mL NS or D5W to give 10 mg/mL. Give slow IV bolus over 1-2 minutes or infuse in 100 mL over 30 min.',
        'IV is preferred route for severe malaria - most reliable absorption',
        'IM: Reconstitute as above; inject into anterior thigh. Use if IV access impossible.',
        'Rectal: 10 mg/kg suppository; retain for minimum 10 minutes. If expelled within 10 min, repeat dose.',
        'After minimum 24 hours parenteral and ability to tolerate oral, switch to full course of oral ACT (e.g., artemether-lumefantrine)'
      ]
    },
    contraindications: {
      absolute: [
        'Known severe hypersensitivity to artesunate or artemisinin derivatives',
        'First trimester pregnancy (relative - use if life-threatening and no alternative)'
      ],
      relative: [
        'First trimester pregnancy (but use for severe malaria as benefits outweigh risks)',
        'History of blackwater fever (use with close monitoring)',
        'Severe cardiac disease with QT prolongation'
      ],
      precautions: [
        'Post-artesunate delayed hemolysis (PADH) - monitor hemoglobin for 4 weeks after treatment',
        'Hypoglycemia common in severe malaria - monitor glucose frequently',
        'Do not use as monotherapy - always follow with full oral ACT course',
        'Monitor for neurological complications of severe malaria',
        'Report suspected artemisinin resistance if day 3 parasitemia >10% of day 0'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Nausea/vomiting', frequency: '10-20%', management: 'Often difficult to distinguish from malaria symptoms; antiemetics if needed' },
        { effect: 'Dizziness', frequency: '5-15%', management: 'Usually resolves; ensure bed rest in severe malaria' },
        { effect: 'Injection site pain (IM)', frequency: '10-20%', management: 'Local care; prefer IV route when possible' },
        { effect: 'Anorexia', frequency: '5-15%', management: 'Encourage oral intake when able; nutritional support' },
        { effect: 'Transient reticulocytopenia', frequency: '10-30%', management: 'Usually asymptomatic; resolves spontaneously' }
      ],
      serious: [
        { effect: 'Post-artesunate delayed hemolysis (PADH)', frequency: '10-15% (in non-immune patients)', management: 'Monitor Hb weekly for 4 weeks post-treatment; transfuse if severe anemia (Hb <7 g/dL)', warning: 'Occurs 1-4 weeks after treatment; more common with high initial parasitemia and hyperparasitemia. Mechanism involves delayed clearance of pitted RBCs.' },
        { effect: 'Severe anemia (acute)', frequency: '5-10%', management: 'Blood transfusion if Hb <5 g/dL or symptomatic; investigate for hemolysis' },
        { effect: 'Hypoglycemia', frequency: '5-20%', management: 'Monitor glucose q4-6h; dextrose infusion if hypoglycemic. Less common than with quinine.', warning: 'Children and pregnant women at highest risk' },
        { effect: 'QT prolongation', frequency: 'Rare', management: 'ECG monitoring if cardiac risk factors; avoid concurrent QT-prolonging drugs' },
        { effect: 'Hypersensitivity/anaphylaxis', frequency: '<1%', management: 'Discontinue; epinephrine, antihistamines, corticosteroids' }
      ],
      rare: [
        'Neutropenia (usually transient)',
        'Elevated liver transaminases',
        'Bradycardia',
        'First-degree heart block',
        'Cough'
      ]
    },
    interactions: {
      major: [
        { drug: 'Drugs causing hemolysis in G6PD deficiency', effect: 'Additive hemolytic risk', mechanism: 'Both may cause oxidative stress to RBCs', management: 'Use artesunate regardless (severe malaria is life-threatening); monitor closely for hemolysis' },
        { drug: 'Strong CYP2A6 inhibitors', effect: 'Potentially reduced DHA formation', mechanism: 'CYP2A6 minor pathway for metabolism', management: 'Clinical significance uncertain; no dose adjustment recommended' }
      ],
      moderate: [
        { drug: 'QT-prolonging drugs', effect: 'Potential additive QT prolongation', management: 'Use with caution; ECG monitoring if combination necessary' },
        { drug: 'Mefloquine', effect: 'Increased risk of neuropsychiatric effects', management: 'Generally avoid concurrent use; mefloquine can be used as follow-on therapy after artesunate' },
        { drug: 'Rifampicin', effect: 'May reduce artesunate/DHA levels', management: 'Monitor efficacy; clinical significance in severe malaria uncertain' }
      ],
      food: [
        'IV/IM administration not affected by food',
        'No significant food interactions'
      ]
    },
    pregnancy: {
      category: 'C (1st trimester) / B (2nd/3rd trimester)',
      trimpiesterRisks: {
        first: 'Animal studies show embryotoxicity. Limited human data but no clear teratogenicity signal. WHO: Use for severe malaria as malaria mortality exceeds drug risk.',
        second: 'Extensive safety data; recommended by WHO as first-line for severe malaria.',
        third: 'Safe and effective; recommended by WHO. No increased adverse pregnancy outcomes.'
      },
      recommendation: 'USE FOR SEVERE MALARIA IN ALL TRIMESTERS - untreated severe malaria has high maternal/fetal mortality (50%). Benefits clearly outweigh risks. WHO strongly recommends IV artesunate for severe malaria in pregnancy regardless of trimester.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in small amounts. WHO recommends continued breastfeeding during treatment. Infant exposure is minimal.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Malaria microscopy with parasite count', reason: 'Confirm diagnosis; quantify parasitemia for prognosis and response monitoring' },
        { test: 'Hemoglobin/hematocrit', reason: 'Baseline for anemia; severe malaria often causes hemolysis' },
        { test: 'Blood glucose', reason: 'Hypoglycemia common in severe malaria, especially children and pregnant women' },
        { test: 'Renal function (creatinine)', reason: 'Assess for acute kidney injury in severe malaria' },
        { test: 'Lactate (if available)', reason: 'Elevated lactate indicates tissue hypoperfusion; prognostic marker' }
      ],
      ongoing: [
        { test: 'Parasite count', frequency: 'Every 12 hours until negative, then day 3, day 7', target: 'Negative by day 3; if >10% of baseline at day 3, suspect resistance' },
        { test: 'Blood glucose', frequency: 'Every 4-6 hours during IV therapy', target: '>4 mmol/L (70 mg/dL)' },
        { test: 'Hemoglobin', frequency: 'Daily during treatment, then weekly for 4 weeks', target: 'Monitor for PADH; transfuse if Hb <5-7 g/dL' },
        { test: 'Clinical status', frequency: 'Continuous in severe malaria', target: 'Improving consciousness, respiratory status, urine output' },
        { test: 'Temperature', frequency: 'Every 4-6 hours', target: 'Fever clearance usually within 24-48 hours' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed or widely available',
        timing: 'Parasite clearance is the key efficacy marker'
      }
    },
    storage: {
      temperature: 'Powder: Store below 30°C. Protect from heat. Reconstituted solution: Use immediately; stable for 1 hour at room temperature.',
      light: 'Protect from light',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Reconstitute ONLY with provided 5% sodium bicarbonate diluent (1 mL for 60 mg vial). Shake for 2-3 minutes until clear. Then dilute with 5 mL NS or D5W. Do not use if solution is cloudy or has particles.',
      shelfLife: 'Powder: 24-36 months. Reconstituted: Use within 1 hour.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO prequalified injectable artesunate available in most hospitals. May be limited in peripheral health facilities. Rectal artesunate increasingly available for pre-referral.',
      affordability: 'AFFORDABLE/SUBSIDIZED - Global Fund, PMI, and WHO procurement. Public sector: often free. Cost: $1-3 per vial (60 mg). Full treatment course $3-10.',
      supplyChainNotes: 'Store below 30°C; avoid excessive heat. 5% sodium bicarbonate diluent must be supplied with each vial. Pre-referral rectal suppositories critical for community case management. Ensure cold chain for suppositories in hot climates.',
      localManufacturing: 'WHO-prequalified manufacturers include Guilin (China), Ipca (India). Some African manufacturers producing artesunate but WHO prequalification essential for quality assurance.',
      alternatives: [
        'Quinine IV/IM (second-line if artesunate unavailable)',
        'Artemether IM (less preferred - erratic absorption)',
        'Artesunate rectal (pre-referral only)',
        'Artemether-lumefantrine (after parenteral phase, for oral follow-on)'
      ],
      resistancePatterns: 'Artemisinin partial resistance (K13 mutations) confirmed in Rwanda and emerging in Uganda, Tanzania. Manifests as delayed parasite clearance (>10% parasitemia at day 3). IV artesunate still effective but requires close monitoring. No complete artemisinin resistance reported in Africa yet.',
      endemicConsiderations: 'Severe malaria is a medical emergency. Mortality without treatment approaches 100%. IV artesunate reduced mortality by 35% compared to quinine (SEAQUAMAT/AQUAMAT trials). Pre-referral rectal artesunate saves lives when IV not immediately available. Post-artesunate hemolysis more common in travelers/non-immune; monitor all patients.'
    },
    patientCounseling: {
      keyPoints: [
        'This is emergency treatment for severe malaria - it can save your life',
        'You will need to stay in hospital until you can take medicine by mouth',
        'After IV treatment, you must complete a full course of oral antimalarial tablets',
        'Report any symptoms of anemia (weakness, dizziness, pale skin) for 4 weeks after treatment',
        'Complete the full treatment course - incomplete treatment can cause the malaria to return'
      ],
      commonMisconceptions: [
        'One injection cures malaria - NO, you need multiple doses plus oral follow-up',
        'Feeling better means treatment is done - NO, you must complete the full course to prevent recurrence',
        'Artesunate prevents future malaria - NO, you can get malaria again; use preventive measures'
      ],
      adherenceTips: [
        'Stay in hospital until doctors confirm you can take oral medication',
        'Take ALL the oral antimalarial tablets given after IV treatment',
        'Return immediately if fever comes back or you feel worse',
        'Attend follow-up appointments to check for delayed anemia'
      ],
      warningSignsToReport: [
        'Fever returning after treatment',
        'Worsening weakness, dizziness, or fatigue (could be delayed hemolysis)',
        'Dark or reduced urine',
        'Yellow eyes or skin',
        'Confusion or drowsiness',
        'Difficulty breathing'
      ]
    },
    clinicalPearls: [
      'IV artesunate is THE treatment of choice for severe malaria - 35% mortality reduction vs quinine (SEAQUAMAT/AQUAMAT trials)',
      'Start artesunate IMMEDIATELY on clinical suspicion of severe malaria - do not wait for lab confirmation',
      'Children <20 kg need 3 mg/kg (not 2.4 mg/kg) due to faster drug clearance',
      'ALWAYS follow parenteral artesunate with full oral ACT course - artesunate alone is not curative',
      'Post-artesunate delayed hemolysis (PADH) occurs 1-4 weeks post-treatment - monitor Hb weekly for 4 weeks, especially in non-immune patients',
      'Rectal artesunate is life-saving pre-referral treatment - give before transfer if IV not available',
      'Day 3 parasitemia >10% of day 0 suggests artemisinin resistance - report to surveillance system'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines for Malaria 2023',
      'Dondorp AM et al. SEAQUAMAT Trial. Lancet 2005;366:717-725',
      'Dondorp AM et al. AQUAMAT Trial. Lancet 2010;376:1647-1657',
      'WHO. Management of Severe Malaria: A Practical Handbook. 3rd ed. 2012',
      'Rolling T et al. Post-artesunate delayed hemolysis. Lancet Infect Dis 2014'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'quinine-017',
    genericName: 'Quinine',
    brandNames: ['Qualaquin', 'Quinmax', 'Quinimax', 'Quinine Sulphate'],
    atcCode: 'P01BC01',
    therapeuticClass: 'Cinchona Alkaloid Antimalarial',
    pharmacology: {
      mechanism: 'Accumulates in parasite food vacuole and inhibits heme polymerization, leading to toxic heme accumulation that damages parasite membranes. Also intercalates into DNA and interferes with protein synthesis. Active against asexual blood stages of all Plasmodium species. Does not eliminate hypnozoites (P. vivax/ovale) or mature gametocytes.',
      pharmacokinetics: {
        absorption: 'Oral: Well absorbed (76-88% bioavailability); peak levels 1-3 hours. IM: Rapidly absorbed but painful. IV: 100% bioavailability.',
        distribution: 'Vd: 1.5-3.0 L/kg (reduced in malaria due to acute phase proteins). Protein binding: 70-90% (higher in malaria). Distributes to all tissues including CSF (2-7% of plasma). Crosses placenta.',
        metabolism: 'Hepatic metabolism (80%) via CYP3A4 to inactive metabolites. 3-hydroxyquinine is primary metabolite.',
        elimination: 'Renal excretion of metabolites (20% unchanged in urine). Renal clearance increased in acidic urine.',
        halfLife: '11-18 hours in healthy adults; 8-12 hours in acute malaria (increased clearance); 18-26 hours in severe malaria; prolonged in elderly.'
      },
      pharmacodynamics: 'Slower parasite clearance than artemisinins. Parasite reduction ratio ~100-fold per 48 hours (vs 10,000-fold for artesunate). Narrow therapeutic index with significant toxicity at concentrations only 2-3 times therapeutic levels.'
    },
    indications: [
      { condition: 'Severe malaria (when artesunate unavailable)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Second-line to IV artesunate; use only when artesunate not available' },
      { condition: 'Uncomplicated malaria (when ACTs unavailable)', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Reserve for areas without ACT access or ACT failure; combine with doxycycline or clindamycin' },
      { condition: 'Malaria in first trimester pregnancy', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Quinine + clindamycin is first-line for uncomplicated malaria in 1st trimester' },
      { condition: 'Chloroquine-resistant P. falciparum', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Historical use; ACTs now preferred where available' },
      { condition: 'Nocturnal leg cramps', evidenceLevel: 'B', recommendationStrength: 'Weak', firstLine: false, notes: 'FDA warning against this use due to serious adverse effects; use only when other treatments fail' }
    ],
    dosing: {
      adults: {
        standard: 'IV: 20 mg/kg loading (max 1400 mg) over 4 hours, then 10 mg/kg every 8 hours until oral tolerated. Oral: 600 mg (salt) every 8 hours for 7 days.',
        indicationSpecific: [
          { indication: 'Severe malaria (IV)', dose: 'Loading: 20 mg salt/kg (max 1400 mg) in NS over 4 hours. Maintenance: 10 mg salt/kg q8h infused over 4 hours. Switch to oral when able. Total 7 days.' },
          { indication: 'Severe malaria (no loading dose)', dose: 'If quinine given within 24h or concern for toxicity: 10 mg salt/kg q8h from start (no loading dose)' },
          { indication: 'Uncomplicated malaria (oral)', dose: '600 mg salt (=500 mg base) PO q8h for 7 days + doxycycline 100 mg BID x7 days OR clindamycin 450 mg TID x7 days' },
          { indication: 'First trimester pregnancy', dose: 'Quinine 600 mg PO q8h for 7 days + clindamycin 300-450 mg PO q8h for 7 days' }
        ],
        maxDose: '1800 mg salt/day (maintenance); loading dose max 1400 mg',
        renalImpairment: {
          mild: 'No dose adjustment for initial doses; may need reduction after day 2 if accumulation',
          moderate: 'Loading dose unchanged. Maintenance: 10 mg/kg q12h after day 2',
          severe: 'Loading dose unchanged. Maintenance: 10 mg/kg q24h after initial doses. Monitor levels if available.',
          dialysis: 'Give loading dose. HD removes ~25% of dose; give supplemental dose after HD. CRRT: 10 mg/kg q8-12h based on effluent rate.'
        },
        hepaticImpairment: 'Loading dose unchanged. Reduce maintenance dose in severe hepatic impairment; monitor for toxicity. Half-life significantly prolonged.'
      },
      pediatric: {
        standard: 'IV: 20 mg/kg loading over 4 hours, then 10 mg/kg q8h. Oral: 10 mg/kg (max 600 mg) q8h for 7 days.',
        weightBased: true,
        ageSpecific: [
          { age: 'All children (IV severe malaria)', dose: 'Loading: 20 mg salt/kg over 4 hours. Maintenance: 10 mg salt/kg q8h over 2-4 hours until oral tolerated.' },
          { age: 'All children (oral)', dose: '10 mg salt/kg (max 600 mg) PO q8h for 7 days + age-appropriate partner drug' },
          { age: 'Infants (with clindamycin)', dose: 'Quinine 10 mg/kg q8h + clindamycin 5-7 mg/kg q8h for 7 days' }
        ],
        maxDose: '600 mg per dose; 1800 mg/day',
        neonatal: 'Limited data. If severe malaria and artesunate unavailable: loading 20 mg/kg, maintenance 10 mg/kg q12h (longer interval due to immature metabolism). Close monitoring essential.'
      },
      elderly: 'No specific dose adjustment but increased risk of toxicity. Monitor closely for cinchonism and cardiac effects. Longer half-life expected.',
      routes: ['Intravenous', 'Oral', 'Intramuscular (diluted, use only if IV impossible)'],
      administration: [
        'IV: Dilute in NS to concentration of 1-2 mg/mL. Infuse loading dose over 4 hours, maintenance over 2-4 hours. NEVER give IV push (fatal arrhythmias).',
        'IV infusion must use controlled rate - use infusion pump if available',
        'IM: Dilute to 60 mg/mL; give anterior thigh. Very painful; risk of abscess. Use only if IV impossible.',
        'Oral: Take with food to reduce GI upset. Complete 7-day course.',
        'Monitor ECG during IV infusion for QT prolongation, especially with loading dose',
        'Monitor blood glucose frequently - quinine causes hyperinsulinemic hypoglycemia'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to quinine or quinidine',
        'History of blackwater fever (quinine-associated hemolysis)',
        'History of quinine-induced thrombocytopenia',
        'Myasthenia gravis (may worsen weakness)',
        'Optic neuritis'
      ],
      relative: [
        'G6PD deficiency (increased hemolysis risk)',
        'Cardiac arrhythmias or QT prolongation',
        'Atrial fibrillation (quinidine-like effect may cause conversion)',
        'Concurrent use of other QT-prolonging drugs',
        'Severe hepatic impairment'
      ],
      precautions: [
        'Monitor blood glucose frequently - causes hyperinsulinemic hypoglycemia',
        'ECG monitoring during IV therapy, especially loading dose',
        'Cinchonism (tinnitus, headache, nausea) common and dose-related',
        'Narrow therapeutic index - therapeutic levels close to toxic levels',
        'Monitor for hemolysis in G6PD deficiency and malaria-related hemolysis'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Cinchonism (tinnitus, headache, nausea, visual disturbance)', frequency: '30-75%', management: 'Dose-related and usually reversible; reduce dose if severe. Some degree expected at therapeutic doses.' },
        { effect: 'Hypoglycemia', frequency: '10-30%', management: 'Monitor glucose q4-6h; give dextrose infusion if hypoglycemic. More common in pregnancy, children, and with IV therapy.', },
        { effect: 'Nausea/vomiting', frequency: '20-40%', management: 'Give with food; antiemetics if needed' },
        { effect: 'Diarrhea', frequency: '10-20%', management: 'Usually self-limiting; supportive care' },
        { effect: 'Abdominal pain', frequency: '10-20%', management: 'Symptomatic treatment' }
      ],
      serious: [
        { effect: 'Cardiac arrhythmias (QT prolongation, torsades de pointes)', frequency: '1-5%', management: 'ECG monitoring during IV therapy; stop if QTc >500ms or increases >60ms. Correct electrolytes. IV magnesium for torsades.', warning: 'Risk increased with rapid infusion, electrolyte abnormalities, and concurrent QT-prolonging drugs' },
        { effect: 'Severe hypoglycemia', frequency: '5-15%', management: 'Monitor glucose; IV dextrose. More common with high parasitemia, pregnancy, and prolonged treatment.', warning: 'Can be fatal; maintain IV dextrose during infusion' },
        { effect: 'Quinine-induced thrombocytopenia (immune-mediated)', frequency: '<1%', management: 'Discontinue quinine permanently; platelet transfusion if severe bleeding; drug-dependent antibodies', warning: 'Can occur even after minimal exposure; lifelong avoidance of quinine required' },
        { effect: 'Blackwater fever (massive intravascular hemolysis)', frequency: 'Rare', management: 'Stop quinine immediately; supportive care; blood transfusion; dialysis if renal failure', warning: 'High mortality; never re-expose to quinine' },
        { effect: 'Blindness/visual impairment', frequency: 'Rare (overdose)', management: 'Usually with overdose; may be permanent. Supportive care.' }
      ],
      rare: [
        'Hemolytic uremic syndrome',
        'Acute interstitial nephritis',
        'Hepatotoxicity',
        'Photosensitivity',
        'Agranulocytosis'
      ]
    },
    interactions: {
      major: [
        { drug: 'QT-prolonging drugs (haloperidol, amiodarone, fluoroquinolones, macrolides)', effect: 'Additive QT prolongation risk; risk of torsades de pointes', mechanism: 'Pharmacodynamic interaction', management: 'Avoid combination if possible; if unavoidable, ECG monitoring and correct electrolytes' },
        { drug: 'Mefloquine', effect: 'Increased risk of seizures and cardiac arrhythmias', mechanism: 'Additive cardiotoxicity and neurotoxicity', management: 'Avoid concurrent use; wait 12 hours after quinine before giving mefloquine' },
        { drug: 'Digoxin', effect: 'Increased digoxin levels', mechanism: 'Quinine reduces digoxin renal clearance', management: 'Reduce digoxin dose by 50%; monitor digoxin levels' },
        { drug: 'Warfarin', effect: 'Enhanced anticoagulation', mechanism: 'CYP450 inhibition and displacement from protein binding', management: 'Monitor INR closely; reduce warfarin dose as needed' },
        { drug: 'CYP3A4 inhibitors (ketoconazole, ritonavir)', effect: 'Increased quinine levels', mechanism: 'Reduced quinine metabolism', management: 'Monitor for quinine toxicity; may need dose reduction' }
      ],
      moderate: [
        { drug: 'Antacids', effect: 'Reduced quinine absorption', management: 'Separate administration by 2-4 hours' },
        { drug: 'Rifampicin', effect: 'Markedly reduced quinine levels', management: 'May require increased quinine dose; monitor efficacy' },
        { drug: 'Neuromuscular blocking agents', effect: 'Enhanced blockade', management: 'Monitor for prolonged paralysis; reduce NMB dose' },
        { drug: 'Insulin/oral hypoglycemics', effect: 'Additive hypoglycemia', management: 'Close glucose monitoring; adjust diabetes medications' }
      ],
      food: [
        'Grapefruit juice increases quinine levels - avoid',
        'Take with food to reduce GI upset',
        'Tonic water contains quinine - avoid additional quinine exposure'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Associated with ototoxicity, optic nerve hypoplasia, and limb abnormalities at high doses. However, untreated malaria has higher risk. Quinine + clindamycin is WHO-recommended for 1st trimester uncomplicated malaria.',
        second: 'Can be used; ACTs now preferred if available. Risk of hypoglycemia increased.',
        third: 'Can be used; may cause uterine contractions at high doses. ACTs preferred. Risk of severe hypoglycemia during delivery.'
      },
      recommendation: 'FIRST TRIMESTER: Quinine + clindamycin is first-line for uncomplicated malaria (ACTs not recommended). SECOND/THIRD TRIMESTER: ACTs preferred; use quinine only if ACTs unavailable. SEVERE MALARIA: IV artesunate is first-line in ALL trimesters; IV quinine only if artesunate unavailable.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in small amounts (<3% of maternal dose). Generally compatible with breastfeeding. Monitor infant for hemolysis if G6PD deficient.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Malaria microscopy/RDT', reason: 'Confirm diagnosis and quantify parasitemia' },
        { test: 'Blood glucose', reason: 'Baseline before quinine-induced hypoglycemia' },
        { test: 'ECG', reason: 'Baseline QTc before QT-prolonging therapy' },
        { test: 'Complete blood count', reason: 'Baseline for hemolysis and thrombocytopenia monitoring' },
        { test: 'Renal function', reason: 'Dose adjustment in renal impairment' },
        { test: 'Electrolytes (K+, Mg2+)', reason: 'Electrolyte abnormalities increase arrhythmia risk' }
      ],
      ongoing: [
        { test: 'Blood glucose', frequency: 'Every 4 hours during IV therapy; TID during oral', target: '>4 mmol/L (70 mg/dL)' },
        { test: 'ECG', frequency: 'During loading dose; then daily during IV therapy', target: 'QTc <500 ms; no increase >60 ms from baseline' },
        { test: 'Parasite count', frequency: 'Every 12-24 hours until negative', target: 'Negative by day 7' },
        { test: 'Hemoglobin', frequency: 'Daily during treatment', target: 'Monitor for hemolysis' },
        { test: 'Platelet count', frequency: 'Baseline and if bleeding or petechiae', target: 'Monitor for immune thrombocytopenia' }
      ],
      therapeutic: {
        range: '8-15 mg/L (therapeutic); >20 mg/L (toxic). TDM rarely available in endemic settings.',
        timing: 'Trough level before next dose; peak 1-2 hours after oral dose'
      }
    },
    storage: {
      temperature: 'Store at 15-25°C (59-77°F). Protect from excessive heat.',
      light: 'Protect from light; store in original container',
      humidity: 'Store in dry conditions',
      specialInstructions: 'IV solution: Dilute in NS to 1-2 mg/mL before infusion. Diluted solution stable for 24 hours at room temperature.',
      shelfLife: 'Tablets: 36-60 months. Injection: 24-36 months.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - Injectable and oral quinine available in most health facilities. Often more available than IV artesunate in peripheral facilities.',
      affordability: 'AFFORDABLE - Oral quinine very cheap ($1-2 per course). IV quinine $5-15 per treatment course. No patent protection.',
      supplyChainNotes: 'Stable at room temperature; suitable for African supply chains. Oral formulation preferred for uncomplicated cases to reduce injection-related complications. Ensure glucose monitoring capacity when using IV quinine.',
      localManufacturing: 'Widely manufactured across Africa. Quality varies; source from reputable manufacturers. WHO prequalified products available.',
      alternatives: [
        'IV artesunate (PREFERRED for severe malaria)',
        'Artemether-lumefantrine (for uncomplicated, 2nd/3rd trimester)',
        'Artesunate-amodiaquine (alternative ACT)',
        'Mefloquine (for uncomplicated malaria)'
      ],
      resistancePatterns: 'Quinine resistance rare but documented in Southeast Asia. In Africa, reduced susceptibility reported in some areas but clinically significant resistance uncommon. Complete treatment course essential to prevent resistance development.',
      endemicConsiderations: 'Quinine remains important where artesunate unavailable. Critical for first trimester pregnancy (uncomplicated malaria). Severe hypoglycemia more common in African children with malaria - mandatory glucose monitoring. Blackwater fever historically more common in Africa; avoid re-exposure after any quinine-associated hemolysis.'
    },
    patientCounseling: {
      keyPoints: [
        'Take the medicine three times daily for the full 7 days, even if you feel better',
        'Take with food to reduce stomach upset',
        'Ringing in the ears (tinnitus) is common and expected - this is not dangerous',
        'Report any unusual bleeding, bruising, or dark urine immediately',
        'You must also take the second medicine (doxycycline or clindamycin) to fully cure malaria'
      ],
      commonMisconceptions: [
        'Tinnitus means allergy - NO, this is a common expected side effect at treatment doses',
        'Quinine in tonic water prevents malaria - NO, the amount is far too small to be effective',
        'Feeling better means malaria is cured - NO, complete full 7-day course to prevent relapse'
      ],
      adherenceTips: [
        'Take doses at regular 8-hour intervals (e.g., 6am, 2pm, 10pm)',
        'Set reminders for each dose',
        'Take with food or after meals to reduce nausea',
        'Keep taking even if you experience mild tinnitus or headache'
      ],
      warningSignsToReport: [
        'Visual changes or difficulty seeing',
        'Irregular or rapid heartbeat',
        'Unusual bleeding or bruising',
        'Dark or red urine (sign of hemolysis)',
        'Severe dizziness or fainting',
        'Symptoms not improving after 48 hours'
      ]
    },
    clinicalPearls: [
      'IV artesunate is now FIRST-LINE for severe malaria - use quinine only when artesunate unavailable',
      'NEVER give IV quinine as bolus - fatal arrhythmias; always infuse loading dose over 4 hours',
      'Quinine causes HYPERINSULINEMIC hypoglycemia - glucose monitoring mandatory, especially in pregnancy',
      'Omit loading dose if quinine given within 24 hours, quinidine used, or mefloquine given within 12 hours',
      'Quinine + clindamycin remains first-line for uncomplicated malaria in FIRST TRIMESTER pregnancy',
      'Cinchonism (tinnitus, headache) is expected at therapeutic doses - not indication to stop therapy',
      'Blackwater fever (massive hemolysis + dark urine) = STOP quinine immediately; never re-expose patient'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines for Malaria 2023',
      'Achan J et al. Quinine, an old antimalarial drug in a modern world. Malar J 2011',
      'White NJ. The treatment of malaria. NEJM 1996',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'BMJ Best Practice: Malaria 2024'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'albendazole-018',
    genericName: 'Albendazole',
    brandNames: ['Albenza', 'Zentel', 'Eskazole', 'Andazol'],
    atcCode: 'P02CA03',
    therapeuticClass: 'Benzimidazole Anthelmintic',
    pharmacology: {
      mechanism: 'Selectively binds to parasite beta-tubulin, inhibiting tubulin polymerization and microtubule formation. This disrupts glucose uptake, depletes glycogen stores, and prevents ATP formation in the parasite. Also inhibits fumarate reductase. Results in immobilization and death of susceptible parasites. Ovicidal, larvicidal, and vermicidal.',
      pharmacokinetics: {
        absorption: 'Poorly absorbed from GI tract (<5% unchanged). Absorption increased 4-5 fold with fatty meal. Peak levels 2-5 hours.',
        distribution: 'Active metabolite (albendazole sulfoxide) Vd: 1.3 L/kg. Protein binding 70%. Penetrates cyst walls (hydatid, neurocysticercosis). Crosses blood-brain barrier. Reaches CSF.',
        metabolism: 'Extensive first-pass hepatic metabolism via CYP3A4 and flavin monooxygenases to active metabolite albendazole sulfoxide, then to inactive albendazole sulfone.',
        elimination: 'Primarily biliary/fecal excretion. <1% excreted in urine. Enterohepatic circulation occurs.',
        halfLife: 'Albendazole sulfoxide: 8-12 hours. Parent drug: very short (rapidly converted).'
      },
      pharmacodynamics: 'Time-dependent activity. Efficacy correlates with prolonged exposure above minimum effective concentration. Single-dose therapy effective for intestinal helminths; prolonged courses needed for tissue parasites (hydatid, neurocysticercosis).'
    },
    indications: [
      { condition: 'Ascariasis (roundworm)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single 400 mg dose; >95% cure rate' },
      { condition: 'Hookworm (Ancylostoma, Necator)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single 400 mg dose; high efficacy' },
      { condition: 'Trichuriasis (whipworm)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose less effective; 3-day course for better efficacy' },
      { condition: 'Enterobiasis (pinworm)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose; repeat at 2 weeks to kill hatched larvae' },
      { condition: 'Strongyloidiasis', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Ivermectin preferred; albendazole 7-day course alternative' },
      { condition: 'Hydatid disease (Echinococcus)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Pre/post-surgical; prolonged courses for inoperable disease' },
      { condition: 'Neurocysticercosis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'With corticosteroids and antiseizure medication; course varies by cyst viability' },
      { condition: 'Lymphatic filariasis (combination)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with ivermectin or DEC for mass drug administration' },
      { condition: 'Cutaneous/visceral larva migrans', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: '3-5 day course' },
      { condition: 'Giardiasis (alternative)', evidenceLevel: 'B', recommendationStrength: 'Weak', firstLine: false, notes: 'Alternative to metronidazole; 5-day course' }
    ],
    dosing: {
      adults: {
        standard: '400 mg single dose for intestinal nematodes; extended courses for tissue parasites',
        indicationSpecific: [
          { indication: 'Ascariasis/hookworm/enterobiasis', dose: '400 mg single dose; repeat enterobiasis at 2 weeks' },
          { indication: 'Trichuriasis', dose: '400 mg daily for 3 days (single dose less effective)' },
          { indication: 'Strongyloidiasis', dose: '400 mg twice daily for 7 days (ivermectin preferred)' },
          { indication: 'Hydatid disease (pre-surgical)', dose: '400 mg twice daily for 1-4 weeks before surgery' },
          { indication: 'Hydatid disease (post-surgical)', dose: '400 mg twice daily for 4-8 weeks after surgery' },
          { indication: 'Hydatid disease (inoperable)', dose: '400 mg twice daily in 28-day cycles with 14-day breaks; 3-6 cycles' },
          { indication: 'Neurocysticercosis', dose: '400 mg (or 7.5 mg/kg) twice daily for 8-30 days depending on cyst burden/viability; with steroids' },
          { indication: 'Cutaneous larva migrans', dose: '400 mg daily for 3 days' },
          { indication: 'Visceral larva migrans', dose: '400 mg twice daily for 5 days' },
          { indication: 'Lymphatic filariasis (MDA)', dose: '400 mg single dose with ivermectin or DEC annually' }
        ],
        maxDose: '800 mg/day (400 mg twice daily)',
        renalImpairment: {
          mild: 'No dose adjustment required',
          moderate: 'No dose adjustment required',
          severe: 'No dose adjustment required - minimal renal elimination',
          dialysis: 'No specific adjustment; unlikely significantly removed by dialysis'
        },
        hepaticImpairment: 'Use with caution in hepatic impairment. Monitor LFTs closely, especially during prolonged therapy. May need dose reduction if LFTs significantly elevated.'
      },
      pediatric: {
        standard: '400 mg for children ≥2 years for intestinal worms; weight-based for tissue parasites',
        weightBased: true,
        ageSpecific: [
          { age: 'Children 1-2 years', dose: '200 mg single dose for intestinal worms (half the adult dose)' },
          { age: 'Children ≥2 years (intestinal worms)', dose: '400 mg single dose (same as adult)' },
          { age: 'Children <2 years (WHO MDA)', dose: '200 mg single dose for deworming programs' },
          { age: 'Hydatid/neurocysticercosis', dose: '7.5 mg/kg twice daily (max 400 mg per dose)' },
          { age: 'Strongyloidiasis', dose: '7.5 mg/kg twice daily for 7 days (max 400 mg per dose)' }
        ],
        maxDose: '800 mg/day',
        neonatal: 'Not recommended in neonates. Safety not established in infants <1 year except for specific indications under specialist guidance.'
      },
      elderly: 'No specific dose adjustment. Monitor LFTs during prolonged courses. May have increased sensitivity to adverse effects.',
      routes: ['Oral'],
      administration: [
        'Take with fatty food to increase absorption 4-5 fold (especially important for tissue parasites)',
        'Tablets can be crushed, chewed, or swallowed whole',
        'Chewable tablets available for children',
        'For neurocysticercosis: start steroids before albendazole to reduce inflammatory response',
        'For hydatid: avoid cyst rupture during therapy (risk of anaphylaxis/dissemination)'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to albendazole or benzimidazole class',
        'First trimester pregnancy (teratogenic)'
      ],
      relative: [
        'Pre-existing hepatic impairment or elevated transaminases',
        'Cirrhosis (limited experience)',
        'Retinal lesions in neurocysticercosis (risk of visual loss)',
        'Bone marrow suppression'
      ],
      precautions: [
        'Obtain negative pregnancy test before starting in women of reproductive age',
        'Use effective contraception during and for 1 month after treatment',
        'Monitor LFTs before, during (every 2 weeks), and after prolonged therapy',
        'CBC monitoring for prolonged courses (risk of neutropenia)',
        'In neurocysticercosis: use with steroids and antiseizure medication',
        'Risk of cyst fluid leak/anaphylaxis in hydatid disease'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Abdominal pain', frequency: '5-10%', management: 'Usually transient; take with food' },
        { effect: 'Nausea/vomiting', frequency: '3-6%', management: 'Take with food; antiemetics if needed' },
        { effect: 'Headache', frequency: '5-10%', management: 'Symptomatic treatment; common in neurocysticercosis (may be inflammatory)' },
        { effect: 'Dizziness', frequency: '2-5%', management: 'Usually mild and transient' },
        { effect: 'Transient LFT elevation', frequency: '5-15% (prolonged courses)', management: 'Monitor LFTs; usually returns to normal; discontinue if >3x ULN' }
      ],
      serious: [
        { effect: 'Hepatotoxicity', frequency: '1-5% (prolonged courses)', management: 'Monitor LFTs every 2 weeks; discontinue if ALT >3x ULN or symptomatic', warning: 'Rare cases of hepatitis and hepatic failure reported' },
        { effect: 'Bone marrow suppression (pancytopenia, agranulocytosis)', frequency: '<1%', management: 'Monitor CBC for prolonged courses; discontinue if significant cytopenias', warning: 'Usually reversible on discontinuation' },
        { effect: 'Stevens-Johnson syndrome/TEN', frequency: 'Rare', management: 'Discontinue immediately; supportive care; dermatology consultation' },
        { effect: 'Increased intracranial pressure (neurocysticercosis)', frequency: '5-15%', management: 'Pre-treat with corticosteroids; monitor for headache, seizures', warning: 'Due to inflammatory response to dying cysts' },
        { effect: 'Anaphylaxis (hydatid cyst rupture)', frequency: 'Rare', management: 'Emergency treatment; epinephrine; usually due to cyst spillage', warning: 'Risk during surgery or if cyst ruptures spontaneously' }
      ],
      rare: [
        'Alopecia (reversible, prolonged courses)',
        'Acute renal failure',
        'Erythema multiforme',
        'Rhabdomyolysis',
        'Reversible hair loss'
      ]
    },
    interactions: {
      major: [
        { drug: 'Carbamazepine, phenytoin, phenobarbital', effect: 'Reduced albendazole levels by 50%', mechanism: 'CYP3A4 induction', management: 'May need higher albendazole doses or longer courses; monitor efficacy' },
        { drug: 'Ritonavir, other strong CYP3A4 inhibitors', effect: 'Increased albendazole sulfoxide levels', mechanism: 'CYP3A4 inhibition', management: 'Monitor for albendazole toxicity; may need dose reduction' }
      ],
      moderate: [
        { drug: 'Cimetidine', effect: 'Increased albendazole levels by 50%', management: 'Monitor for toxicity; may be beneficial in tissue parasites' },
        { drug: 'Dexamethasone', effect: 'Increased albendazole sulfoxide levels by 50%', management: 'Used together in neurocysticercosis; monitor for toxicity' },
        { drug: 'Praziquantel', effect: 'Increases albendazole sulfoxide levels', management: 'Combination used in neurocysticercosis; monitor' },
        { drug: 'Theophylline', effect: 'Decreased theophylline levels', management: 'Monitor theophylline levels' }
      ],
      food: [
        'MUST take with fatty food - increases absorption 4-5 fold',
        'Grapefruit juice may increase levels (CYP3A4 inhibition)'
      ]
    },
    pregnancy: {
      category: 'C (avoid in 1st trimester)',
      trimpiesterRisks: {
        first: 'CONTRAINDICATED - Teratogenic in animals (skeletal malformations, embryotoxicity). WHO recommends avoiding. Rule out pregnancy before treatment.',
        second: 'Can be used if clearly indicated and no alternative. WHO deworming programs include pregnant women after 1st trimester.',
        third: 'Can be used if clearly indicated. WHO recommends deworming in endemic areas after 1st trimester.'
      },
      recommendation: 'AVOID in first trimester. Can be used in 2nd/3rd trimester for deworming programs (WHO) or if treatment clearly indicated. Women of reproductive age should have negative pregnancy test before starting prolonged courses. Use contraception during and for 1 month after treatment.',
      lactation: {
        safe: true,
        notes: 'Excreted in breast milk in small amounts. WHO considers compatible with breastfeeding. Single-dose treatment poses minimal risk to nursing infant.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Liver function tests (ALT, AST)', reason: 'Baseline for hepatotoxicity monitoring' },
        { test: 'Complete blood count', reason: 'Baseline for bone marrow suppression (prolonged courses)' },
        { test: 'Pregnancy test', reason: 'Rule out pregnancy before starting (women of reproductive age)' },
        { test: 'Stool examination', reason: 'Confirm diagnosis and identify specific parasite' }
      ],
      ongoing: [
        { test: 'LFTs (ALT, AST)', frequency: 'Every 2 weeks during prolonged therapy (>14 days)', target: 'ALT <3x ULN' },
        { test: 'CBC', frequency: 'Every 2-4 weeks during prolonged therapy', target: 'Stable counts; ANC >1000' },
        { test: 'Clinical response', frequency: 'As per indication', target: 'Symptom resolution' },
        { test: 'Stool for ova/parasites', frequency: '2-4 weeks after treatment for intestinal worms', target: 'Negative' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed; albendazole sulfoxide levels can be measured but not widely available',
        timing: 'Efficacy assessed clinically and parasitologically'
      }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F). Protect from excessive heat.',
      light: 'Protect from light',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Store in original container. Chewable tablets may be crushed and mixed with food.',
      shelfLife: '36-60 months depending on formulation.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO essential medicine; available in public health facilities and pharmacies across Africa. Distributed through mass drug administration (MDA) programs.',
      affordability: 'VERY AFFORDABLE - Among the cheapest essential medicines. Single tablet costs $0.01-0.10 in generic form. WHO/NGO programs provide free through MDA.',
      supplyChainNotes: 'Stable at room temperature; excellent for African supply chains. Chewable formulations available for children. Often donated through NTD (neglected tropical disease) programs. Pre-packaged single doses facilitate community distribution.',
      localManufacturing: 'Widely manufactured in Africa and India. WHO prequalified generics available. Quality-assured products important for MDA programs.',
      alternatives: [
        'Mebendazole (alternative benzimidazole; 3-day course for roundworms)',
        'Ivermectin (preferred for strongyloidiasis; used in combination for LF)',
        'Pyrantel pamoate (alternative for hookworm/roundworm)',
        'Praziquantel (for flukes and tapeworms - different spectrum)'
      ],
      resistancePatterns: 'Benzimidazole resistance emerging in veterinary settings; human resistance rare but monitoring ongoing. Reduced efficacy of single-dose therapy for hookworm and Trichuris reported in some high-transmission areas. Three-day courses more effective than single dose for these infections.',
      endemicConsiderations: 'Soil-transmitted helminths (STH) affect over 1 billion people globally, concentrated in Africa. WHO recommends periodic deworming of at-risk populations (school-age children, pregnant women after 1st trimester, high-risk adults). Combined with ivermectin for lymphatic filariasis elimination. Albendazole + DEC + ivermectin (IDA) triple therapy in some LF programs.'
    },
    patientCounseling: {
      keyPoints: [
        'Take with food, preferably a fatty meal, for best absorption',
        'For pinworm, the whole household should be treated at the same time',
        'A single dose treats most intestinal worms; follow provider instructions for longer courses',
        'Repeat stool test after treatment to confirm the worms are cleared',
        'Practice good hygiene (handwashing, wearing shoes) to prevent reinfection'
      ],
      commonMisconceptions: [
        'Deworming once means you are protected forever - NO, reinfection is common in endemic areas',
        'More doses are better - NO, follow the prescribed dose; overdosing can cause liver problems',
        'Only children get worms - NO, adults can also be infected and should be treated'
      ],
      adherenceTips: [
        'Tablets can be crushed and mixed with food if difficult to swallow',
        'For prolonged courses, take both doses daily as prescribed',
        'Set reminders for multi-day courses',
        'Complete the full course even if feeling better'
      ],
      warningSignsToReport: [
        'Yellowing of skin or eyes (jaundice)',
        'Severe abdominal pain',
        'Dark urine',
        'Unusual fatigue or weakness',
        'Rash or skin blistering',
        'Fever developing during treatment'
      ]
    },
    clinicalPearls: [
      'ALWAYS take with fatty food - absorption increases 4-5 fold; critical for tissue parasites',
      'Single-dose 400 mg is highly effective for Ascaris and hookworm, but less effective for Trichuris (use 3-day course)',
      'For pinworm (Enterobius), repeat dose at 2 weeks to kill larvae hatched after first dose; treat household contacts',
      'In neurocysticercosis, START STEROIDS BEFORE albendazole to prevent dangerous inflammatory response',
      'Monitor LFTs every 2 weeks during prolonged courses - hepatotoxicity is dose-dependent',
      'Avoid in first trimester pregnancy; can use in 2nd/3rd trimester for deworming programs per WHO',
      'Triple drug therapy (IDA: ivermectin + DEC + albendazole) accelerates lymphatic filariasis elimination'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines on Soil-Transmitted Helminth Infections 2017',
      'WHO PCT Databank - Preventive Chemotherapy',
      'Garcia HH et al. Neurocysticercosis Guidelines. Lancet Neurol 2014',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Horton J. Albendazole: A review of anthelmintic efficacy and safety. Parasitology 2000'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'ivermectin-019',
    genericName: 'Ivermectin',
    brandNames: ['Stromectol', 'Mectizan', 'Ivexterm', 'Soolantra'],
    atcCode: 'P02CF01',
    therapeuticClass: 'Avermectin Antiparasitic',
    pharmacology: {
      mechanism: 'Binds selectively to glutamate-gated chloride channels in invertebrate nerve and muscle cells, causing hyperpolarization and paralysis. Also binds to GABA-gated chloride channels. This increases cell membrane permeability to chloride ions, leading to paralysis and death of the parasite. Active against many nematodes (worms) and arthropods (scabies, lice). Does not cross intact blood-brain barrier in humans (safe in mammals).',
      pharmacokinetics: {
        absorption: 'Well absorbed orally; bioavailability approximately 60%. Peak plasma levels in 4-5 hours. High-fat meal increases absorption by ~2.5 fold.',
        distribution: 'Vd: ~3.5 L/kg (highly lipophilic). Protein binding >93%. Widely distributed to tissues with high fat content. Does not significantly cross blood-brain barrier (P-glycoprotein efflux). Concentrates in skin, fat, and liver.',
        metabolism: 'Hepatic metabolism primarily via CYP3A4 to at least 10 metabolites. Metabolites less active than parent compound.',
        elimination: 'Almost exclusively fecal excretion (<1% urine). Undergoes enterohepatic circulation.',
        halfLife: '18 hours (range 12-56 hours); longer in elderly.'
      },
      pharmacodynamics: 'Rapid onset against microfilariae (within hours). Kills microfilariae but not adult worms of Onchocerca and Wuchereria - requires repeated dosing. For scabies and lice, kills mites/lice but not eggs - repeat dose at 7-14 days. For strongyloidiasis, kills larvae but cure requires adequate immune function.'
    },
    indications: [
      { condition: 'Onchocerciasis (river blindness)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Mainstay of elimination programs; annual/biannual mass drug administration' },
      { condition: 'Strongyloidiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Drug of choice; 2-day course or single dose depending on intensity' },
      { condition: 'Lymphatic filariasis (combination)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Combined with albendazole ± DEC for MDA programs' },
      { condition: 'Scabies', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for crusted scabies; alternative to permethrin for classic scabies' },
      { condition: 'Head lice', evidenceLevel: 'A', recommendationStrength: 'Moderate', firstLine: false, notes: 'Alternative when topical treatments fail or impractical' },
      { condition: 'Ascariasis', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Less effective than albendazole; ~80% efficacy' },
      { condition: 'Cutaneous larva migrans', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose usually effective; alternative to albendazole' },
      { condition: 'Gnathostomiasis', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'Better efficacy than albendazole' },
      { condition: 'Loiasis (with low microfilariae)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Only if Loa loa microfilariae <8000/mL; risk of encephalopathy if high' }
    ],
    dosing: {
      adults: {
        standard: '200 mcg/kg (0.2 mg/kg) single dose for most indications; 150-200 mcg/kg for onchocerciasis',
        indicationSpecific: [
          { indication: 'Onchocerciasis', dose: '150 mcg/kg single dose every 6-12 months until adult worms die (10-15 years)' },
          { indication: 'Strongyloidiasis', dose: '200 mcg/kg once daily for 2 days; single dose may be sufficient in uncomplicated cases' },
          { indication: 'Hyperinfection/disseminated strongyloidiasis', dose: '200 mcg/kg daily until stool/sputum negative for 2 weeks (may need weeks of therapy)' },
          { indication: 'Scabies (classic)', dose: '200 mcg/kg single dose; repeat at day 8-14' },
          { indication: 'Scabies (crusted/Norwegian)', dose: '200 mcg/kg on days 1, 2, 8, 9, 15 (± additional doses); combine with topical therapy' },
          { indication: 'Head lice', dose: '200-400 mcg/kg single dose; repeat at day 10' },
          { indication: 'Lymphatic filariasis (MDA)', dose: '150-200 mcg/kg single dose annually with albendazole (± DEC)' },
          { indication: 'Cutaneous larva migrans', dose: '200 mcg/kg single dose' },
          { indication: 'Ascariasis', dose: '150-200 mcg/kg single dose' }
        ],
        maxDose: 'No defined maximum; doses up to 800 mcg/kg have been used for crusted scabies',
        renalImpairment: {
          mild: 'No dose adjustment required',
          moderate: 'No dose adjustment required',
          severe: 'No dose adjustment required - minimal renal elimination',
          dialysis: 'No dose adjustment; not significantly dialyzed (highly protein-bound, large Vd)'
        },
        hepaticImpairment: 'Use with caution in severe hepatic impairment. May have prolonged half-life; monitor for toxicity.'
      },
      pediatric: {
        standard: '150-200 mcg/kg single dose; same weight-based dosing as adults',
        weightBased: true,
        ageSpecific: [
          { age: 'Children ≥15 kg (approx ≥3-5 years)', dose: '150-200 mcg/kg single dose (same as adults)' },
          { age: 'Children <15 kg', dose: 'NOT recommended - safety not established' },
          { age: 'Onchocerciasis (children ≥15 kg)', dose: '150 mcg/kg single dose every 6-12 months' },
          { age: 'Strongyloidiasis (children ≥15 kg)', dose: '200 mcg/kg daily for 2 days' },
          { age: 'Scabies (children ≥15 kg)', dose: '200 mcg/kg single dose; repeat at day 8-14' }
        ],
        maxDose: 'Weight-based; no defined maximum in children',
        neonatal: 'NOT recommended in neonates. Safety not established. Blood-brain barrier may be immature.'
      },
      elderly: 'No specific dose adjustment. May have prolonged half-life. Start with standard dose and monitor response.',
      routes: ['Oral'],
      administration: [
        'Take on empty stomach with water (traditional recommendation) OR with food (increases absorption 2.5-fold)',
        'WHO programs often give with food for better tolerability and absorption',
        'Tablets may be crushed and mixed with water',
        'For scabies: repeat dose at 7-14 days to kill mites hatching from eggs',
        'For strongyloidiasis in immunocompromised: may need prolonged or repeated courses'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to ivermectin',
        'Children weighing <15 kg (safety not established)',
        'Co-infection with Loa loa with high microfilaremia (>8000-30,000 microfilariae/mL)'
      ],
      relative: [
        'Pregnancy (limited data; avoid unless clearly needed)',
        'Breastfeeding within 1 week of treatment (excreted in milk)',
        'CNS disorders where blood-brain barrier may be compromised',
        'Hepatic impairment',
        'Concurrent use of drugs increasing ivermectin levels (CYP3A4 inhibitors, P-gp inhibitors)'
      ],
      precautions: [
        'SCREEN for Loa loa before treatment in endemic areas (Central/West Africa) - risk of fatal encephalopathy',
        'Mazzotti reaction in onchocerciasis (due to dying microfilariae) - manage with antihistamines/corticosteroids',
        'Not effective against adult worms of Onchocerca or Wuchereria - kills microfilariae only',
        'Immunocompromised patients may need prolonged treatment for strongyloidiasis',
        'For scabies: household contacts should be treated simultaneously'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Mazzotti reaction (onchocerciasis)', frequency: '20-50%', management: 'Due to dying microfilariae; fever, pruritus, rash, lymphadenopathy, hypotension. Manage with antihistamines, NSAIDs, corticosteroids if severe.' },
        { effect: 'Pruritus', frequency: '10-30%', management: 'Common in onchocerciasis (microfilariae death); antihistamines' },
        { effect: 'Dizziness', frequency: '2-5%', management: 'Usually transient; caution with driving/operating machinery' },
        { effect: 'Nausea', frequency: '2-5%', management: 'Take with food; usually mild' },
        { effect: 'Diarrhea', frequency: '2-5%', management: 'Usually self-limiting' }
      ],
      serious: [
        { effect: 'Severe Mazzotti reaction', frequency: '1-5% (onchocerciasis)', management: 'Hypotension, respiratory distress, shock. Hospitalize; IV fluids, corticosteroids, supportive care.', warning: 'Risk related to microfilarial load; more severe with high parasite burden' },
        { effect: 'Loa loa encephalopathy', frequency: 'Rare but potentially fatal', management: 'Occurs with high Loa loa microfilaremia (>8000-30,000/mL). Screen before treatment in endemic areas. ICU care; exchange transfusion may help.', warning: 'SCREEN for Loa loa before ivermectin in Central/West African endemic areas' },
        { effect: 'Stevens-Johnson syndrome/TEN', frequency: 'Rare', management: 'Discontinue immediately; supportive care; dermatology consultation' },
        { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Monitor LFTs if symptoms; usually mild and reversible' },
        { effect: 'Seizures (with CNS penetration)', frequency: 'Very rare', management: 'Usually only if blood-brain barrier compromised; supportive care' }
      ],
      rare: [
        'Hypotension',
        'Tachycardia',
        'Facial/peripheral edema',
        'Tremor',
        'Visual disturbances'
      ]
    },
    interactions: {
      major: [
        { drug: 'Warfarin', effect: 'Increased INR and bleeding risk', mechanism: 'Unknown; possibly CYP interaction', management: 'Monitor INR closely; may need warfarin dose reduction' },
        { drug: 'P-glycoprotein inhibitors (verapamil, quinidine, cyclosporine)', effect: 'Increased ivermectin CNS penetration', mechanism: 'P-gp efflux inhibition at blood-brain barrier', management: 'Use with caution; monitor for CNS effects' }
      ],
      moderate: [
        { drug: 'CYP3A4 inhibitors (ketoconazole, itraconazole, ritonavir)', effect: 'Increased ivermectin levels', management: 'Monitor for toxicity; usually not clinically significant with single doses' },
        { drug: 'CYP3A4 inducers (rifampicin, carbamazepine, phenytoin)', effect: 'Decreased ivermectin levels', management: 'May reduce efficacy; consider higher dose or repeat dosing' },
        { drug: 'Alcohol', effect: 'May increase CNS depression', management: 'Avoid alcohol during treatment' }
      ],
      food: [
        'High-fat meal increases absorption 2.5-fold',
        'Can be taken with or without food; WHO MDA programs often give with food',
        'Orange juice may increase absorption (P-gp inhibition)'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Limited human data. Animal studies at high doses show cleft palate. Avoid unless no alternative and benefits clearly outweigh risks.',
        second: 'Limited data; use with caution if clearly needed. No clear teratogenic signal in limited human experience.',
        third: 'Can be used if clearly needed. Often used inadvertently in MDA programs without adverse outcomes reported.'
      },
      recommendation: 'Generally AVOID in pregnancy due to limited safety data. However, inadvertent exposure in MDA programs has not shown increased adverse outcomes. Use only if benefits clearly outweigh risks and no alternative available.',
      lactation: {
        safe: false,
        notes: 'Excreted in breast milk. WHO recommends waiting at least 1 week after treatment before breastfeeding. For single-dose treatment, withhold breastfeeding for 1 week or pump and discard milk.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Loa loa screening (in endemic areas)', reason: 'CRITICAL - high microfilaremia increases encephalopathy risk. Use thick blood smear at midday.' },
        { test: 'Pregnancy test', reason: 'Avoid use in pregnancy' },
        { test: 'Microfilariae count (onchocerciasis)', reason: 'Assess parasite burden; higher loads = more Mazzotti reaction risk' },
        { test: 'Stool examination (strongyloidiasis)', reason: 'Confirm diagnosis; may need serial samples' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: 'Varies by indication', target: 'Resolution of symptoms/parasites' },
        { test: 'Stool for strongyloides (if immunocompromised)', frequency: 'Weekly until negative x2', target: 'Negative larvae' },
        { test: 'Skin snip microfilariae (onchocerciasis)', frequency: 'Annually in endemic areas', target: 'Decreasing or negative' },
        { test: 'CBC, LFTs', frequency: 'Only if symptoms suggest toxicity', target: 'Normal values' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed or widely available',
        timing: 'Efficacy assessed clinically and parasitologically'
      }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F). Stable up to 30°C.',
      light: 'Protect from light; store in original container',
      humidity: 'Store in dry conditions; moisture sensitive',
      specialInstructions: 'Keep in original blister pack until use. Tablets can be crushed if needed.',
      shelfLife: '36-48 months.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - Donated through Mectizan Donation Program (Merck) for onchocerciasis and lymphatic filariasis since 1987. Available in most endemic areas through MDA programs.',
      affordability: 'FREE for onchocerciasis and LF programs through Mectizan donation. Commercial supply $0.50-2.00 per tablet. MDA programs have treated billions of doses.',
      supplyChainNotes: 'Stable at room temperature; suitable for African supply chains. Donated through Mectizan program with extensive distribution networks (community-directed treatment). Pre-packaged dose poles facilitate community distribution by height/weight.',
      localManufacturing: 'Mectizan (Merck) donated product is standard for MDA. Generic manufacturers in India supply commercial market. WHO prequalification of generics ongoing.',
      alternatives: [
        'Albendazole (for STH; combined with ivermectin for LF)',
        'DEC (diethylcarbamazine) - for LF where onchocerciasis not co-endemic',
        'Moxidectin (newer agent for onchocerciasis; single dose may have longer effect)',
        'Permethrin (topical for scabies; first-line in some settings)'
      ],
      resistancePatterns: 'Resistance concerns emerging in veterinary setting. Suboptimal response reported in some onchocerciasis foci (Ghana, Cameroon) - possibly reduced susceptibility. Monitoring ongoing through community microfilarial loads.',
      endemicConsiderations: 'CRITICAL: Screen for Loa loa in Central/West Africa before ivermectin - risk of fatal encephalopathy with high microfilaremia. Mectizan Donation Program has provided >4 billion treatments. Onchocerciasis elimination achieved in several African foci. Triple-drug IDA (ivermectin + DEC + albendazole) accelerating LF elimination but contraindicated where onchocerciasis co-endemic.'
    },
    patientCounseling: {
      keyPoints: [
        'Take the medication as directed by your healthcare provider or community distributor',
        'Tablets can be swallowed whole or crushed and mixed with water',
        'You may experience itching, mild fever, or rash after treatment - this is usually a sign the medicine is working',
        'For scabies: all household members and close contacts should be treated at the same time',
        'Treatment may need to be repeated for full effectiveness (scabies, onchocerciasis)'
      ],
      commonMisconceptions: [
        'One dose cures onchocerciasis - NO, treatment must continue for 10-15 years to outlive adult worms',
        'Ivermectin prevents future infection - NO, you can be reinfected; continue preventive measures',
        'Itching after treatment means allergy - NO, itching (Mazzotti reaction) is usually from dying parasites'
      ],
      adherenceTips: [
        'Take during community distribution campaigns when offered',
        'Remember to return for follow-up doses as scheduled',
        'For scabies, treat all household members at the same time',
        'Wash bedding and clothing after scabies treatment'
      ],
      warningSignsToReport: [
        'Severe itching, swelling, or rash',
        'Difficulty breathing',
        'Fainting or severe dizziness',
        'Confusion or unusual behavior (seek immediate care)',
        'High fever not improving',
        'Visual changes'
      ]
    },
    clinicalPearls: [
      'SCREEN for Loa loa before ivermectin in Central/West Africa - fatal encephalopathy can occur with high microfilaremia (>8000/mL)',
      'For strongyloidiasis in immunocompromised patients, continue treatment until stool/sputum negative for 2 consecutive weeks - may need prolonged courses',
      'Ivermectin kills microfilariae but NOT adult Onchocerca worms - must continue annual treatment for 10-15 years (adult worm lifespan)',
      'For crusted (Norwegian) scabies, use multiple doses PLUS topical therapy - single dose is insufficient',
      'Mazzotti reaction in onchocerciasis is not allergy - it is inflammatory response to dying microfilariae; manage with antihistamines/steroids',
      'Mectizan Donation Program = largest and longest disease-specific drug donation program in history (since 1987)',
      'Do NOT use ivermectin + DEC in areas co-endemic for onchocerciasis - risk of severe Mazzotti reaction'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines on Onchocerciasis 2022',
      'WHO Guidelines on Lymphatic Filariasis 2021',
      'CDC Yellow Book 2024 - Parasitic Infections',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Mectizan Donation Program Annual Report',
      'Omura S, Crump A. Ivermectin: panacea for resource-poor communities? Trends Parasitol 2014'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
    id: 'praziquantel-020',
    genericName: 'Praziquantel',
    brandNames: ['Biltricide', 'Distocide', 'Cesol', 'Cysticide'],
    atcCode: 'P02BA01',
    therapeuticClass: 'Anthelmintic (Antischistosomal/Anticestodal)',
    pharmacology: {
      mechanism: 'Increases cell membrane permeability to calcium ions, causing rapid muscle contraction (spastic paralysis) and tegumental damage in susceptible worms. Exposes parasite antigens, making worms susceptible to host immune response. Also causes tegumental vacuolization and disintegration. Active against all Schistosoma species, most trematodes (flukes), and adult cestodes (tapeworms).',
      pharmacokinetics: {
        absorption: 'Rapidly and nearly completely absorbed orally (80%). Bioavailability 60-80% due to first-pass metabolism. Peak levels 1-3 hours. Food increases absorption.',
        distribution: 'Vd: 0.4 L/kg. Protein binding 80%. Enters CSF (15-20% of plasma levels). Concentrates in bile.',
        metabolism: 'Extensive first-pass hepatic metabolism via CYP450 (CYP3A4, CYP2B6) to inactive hydroxylated metabolites.',
        elimination: 'Primarily renal excretion of metabolites (>80%). <1% excreted unchanged. Small amount in feces.',
        halfLife: '0.8-1.5 hours for parent drug; 4-5 hours for metabolites.'
      },
      pharmacodynamics: 'Rapid onset of action (within hours). Causes paralysis and tegumental damage making worms susceptible to immune attack. Dead/dying worms detach from vessel walls and are destroyed by host immune system. Egg production ceases within hours of treatment.'
    },
    indications: [
      { condition: 'Schistosomiasis (all species)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Drug of choice for S. mansoni, S. haematobium, S. japonicum, S. intercalatum, S. mekongi' },
      { condition: 'Clonorchiasis (liver fluke)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '75 mg/kg/day divided TID for 1-2 days' },
      { condition: 'Opisthorchiasis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Same as clonorchiasis' },
      { condition: 'Paragonimiasis (lung fluke)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: '75 mg/kg/day divided TID for 2-3 days' },
      { condition: 'Taeniasis (intestinal tapeworms)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose for T. saginata, T. solium (intestinal); does NOT treat cysticercosis' },
      { condition: 'Diphyllobothriasis (fish tapeworm)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose effective' },
      { condition: 'Hymenolepiasis (dwarf tapeworm)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose; may need to repeat at 7-10 days' },
      { condition: 'Fasciolopsiasis', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose or short course' },
      { condition: 'Heterophyiasis/Metagonimiasis', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'Single dose effective' }
    ],
    dosing: {
      adults: {
        standard: '40-60 mg/kg/day divided into 2-3 doses for schistosomiasis; varies by parasite',
        indicationSpecific: [
          { indication: 'Schistosomiasis (S. mansoni, S. haematobium, S. intercalatum)', dose: '40 mg/kg single dose OR 20 mg/kg x 2 doses 4-6 hours apart' },
          { indication: 'Schistosomiasis (S. japonicum, S. mekongi)', dose: '60 mg/kg/day in 2-3 divided doses for 1 day' },
          { indication: 'Clonorchiasis/Opisthorchiasis', dose: '25 mg/kg TID for 1-2 days (75 mg/kg/day)' },
          { indication: 'Paragonimiasis', dose: '25 mg/kg TID for 2-3 days (75 mg/kg/day)' },
          { indication: 'Taeniasis (T. saginata, T. solium)', dose: '5-10 mg/kg single dose' },
          { indication: 'Diphyllobothriasis', dose: '5-10 mg/kg single dose' },
          { indication: 'Hymenolepiasis', dose: '25 mg/kg single dose; repeat at 7-10 days if needed' },
          { indication: 'Fascioliasis', dose: 'NOT recommended - use triclabendazole; praziquantel not effective' },
          { indication: 'MDA for schistosomiasis (WHO)', dose: '40 mg/kg single dose; annual or biannual in endemic areas' }
        ],
        maxDose: '75 mg/kg/day in divided doses',
        renalImpairment: {
          mild: 'No dose adjustment required',
          moderate: 'No dose adjustment required',
          severe: 'Use with caution; metabolites may accumulate. No specific guidance available.',
          dialysis: 'Limited data; give dose after dialysis if possible'
        },
        hepaticImpairment: 'Use with caution in moderate-severe hepatic impairment. In hepatosplenic schistosomiasis, higher plasma levels may occur but still effective and safe. Monitor for side effects.'
      },
      pediatric: {
        standard: 'Same weight-based dosing as adults; commonly 40 mg/kg single dose for schistosomiasis',
        weightBased: true,
        ageSpecific: [
          { age: 'Children ≥4 years (schistosomiasis)', dose: '40 mg/kg single dose OR 20 mg/kg x 2 doses' },
          { age: 'Children ≥4 years (liver flukes)', dose: '25 mg/kg TID for 1-2 days' },
          { age: 'Children ≥4 years (tapeworms)', dose: '5-10 mg/kg single dose' },
          { age: 'Children <4 years', dose: 'WHO recommends treatment in children ≥2 years in endemic areas; safety established' },
          { age: 'Preschool children (WHO MDA)', dose: '40 mg/kg single dose; crushed tablet in food' }
        ],
        maxDose: 'Weight-based; same as adults',
        neonatal: 'Not studied in neonates. Use only if clearly indicated and no alternative.'
      },
      elderly: 'No specific dose adjustment. Monitor for side effects as elderly may be more sensitive.',
      routes: ['Oral'],
      administration: [
        'Take with food and plenty of water to reduce GI side effects and improve absorption',
        'Swallow tablets whole during meals - do not chew (very bitter taste causes nausea/vomiting)',
        'If doses divided, space 4-6 hours apart',
        'For young children: tablets can be crushed and mixed with food to mask bitter taste',
        'WHO dose poles available for MDA - dose based on height as proxy for weight'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to praziquantel',
        'Ocular cysticercosis (destruction of parasites may cause irreversible eye damage)'
      ],
      relative: [
        'Cerebral cysticercosis (risk of increased intracranial pressure from dying cysts - requires hospitalization)',
        'Severe hepatic impairment (reduced first-pass metabolism, higher levels)',
        'Pregnancy (limited data; generally avoided unless clearly needed)',
        'Breastfeeding (interrupt breastfeeding for 72 hours after treatment)'
      ],
      precautions: [
        'For neurocysticercosis, praziquantel is NOT first-line (use albendazole); if used, hospitalize and give corticosteroids',
        'May cause dizziness/drowsiness - avoid driving/operating machinery on day of treatment',
        'In hepatosplenic schistosomiasis, portal hypertension may worsen transiently',
        'Does NOT treat Fasciola hepatica (liver fluke) - use triclabendazole',
        'Mass drug administration: standard WHO protocols; treat SAC (school-age children) and adults in endemic communities'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Abdominal pain/discomfort', frequency: '10-40%', management: 'Take with food; usually transient (hours). May reflect dying worms.' },
        { effect: 'Nausea', frequency: '10-30%', management: 'Take with food; swallow tablets whole without chewing (bitter)' },
        { effect: 'Headache', frequency: '10-30%', management: 'Symptomatic treatment; usually resolves within 24 hours' },
        { effect: 'Dizziness/drowsiness', frequency: '10-20%', management: 'Avoid driving/operating machinery on treatment day' },
        { effect: 'Malaise/fatigue', frequency: '5-15%', management: 'Rest; resolves within 24 hours' }
      ],
      serious: [
        { effect: 'Increased intracranial pressure (neurocysticercosis)', frequency: 'Variable', management: 'Hospitalize; corticosteroids before/during treatment; anti-seizure prophylaxis', warning: 'Due to inflammatory response to dying cysts; can be life-threatening' },
        { effect: 'Seizures (neurocysticercosis)', frequency: 'Variable', management: 'Anticonvulsant prophylaxis required; manage acutely with benzodiazepines' },
        { effect: 'Hypersensitivity reactions', frequency: '<1%', management: 'Discontinue; antihistamines, corticosteroids, epinephrine if severe' },
        { effect: 'Cardiac arrhythmias', frequency: 'Rare', management: 'Usually in patients with underlying cardiac disease; ECG monitoring if concerned' },
        { effect: 'Bloody diarrhea (schistosomiasis)', frequency: '1-5%', management: 'Usually from mucosal damage as worms detach; supportive care; rule out other causes' }
      ],
      rare: [
        'Urticaria',
        'Fever',
        'Eosinophilia',
        'Polyserositis',
        'Liver enzyme elevation'
      ]
    },
    interactions: {
      major: [
        { drug: 'Rifampicin', effect: 'Reduces praziquantel levels by up to 85%', mechanism: 'Strong CYP3A4 induction', management: 'AVOID combination. Wait 4 weeks after rifampicin before praziquantel if possible.' },
        { drug: 'Carbamazepine, phenytoin, phenobarbital', effect: 'Significantly reduced praziquantel levels', mechanism: 'CYP3A4/CYP2B6 induction', management: 'May need increased praziquantel dose; consider alternative antiparasitics or anticonvulsants' },
        { drug: 'Dexamethasone (prolonged use)', effect: 'Reduced praziquantel levels by 50%', mechanism: 'CYP3A4 induction', management: 'Short-term steroids for inflammation OK; prolonged use may reduce efficacy' }
      ],
      moderate: [
        { drug: 'Cimetidine', effect: 'Increased praziquantel levels', management: 'May increase side effects; monitor' },
        { drug: 'Ketoconazole, itraconazole', effect: 'Increased praziquantel levels', management: 'Monitor for side effects' },
        { drug: 'Chloroquine', effect: 'Reduced praziquantel levels', management: 'Space administration; clinical significance uncertain' },
        { drug: 'Grapefruit juice', effect: 'Increased praziquantel levels', management: 'Avoid large amounts; may increase side effects' }
      ],
      food: [
        'Take with food to increase absorption and reduce GI side effects',
        'Avoid grapefruit juice (increases drug levels)'
      ]
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'Limited human data. No clear teratogenic signal but generally avoided in 1st trimester unless urgent treatment needed.',
        second: 'WHO recommends treatment can be given in 2nd/3rd trimester if indicated. Benefits outweigh theoretical risks.',
        third: 'Safe to use. WHO supports treatment in endemic area programs for pregnant women.'
      },
      recommendation: 'Previously avoided in pregnancy, but WHO now recommends treatment in 2nd and 3rd trimester if indicated (benefits of treating schistosomiasis outweigh theoretical risks). Can include pregnant women in MDA programs after 1st trimester.',
      lactation: {
        safe: false,
        notes: 'Excreted in breast milk. WHO recommends avoiding breastfeeding for 72 hours after treatment and discarding milk during this period.'
      }
    },
    monitoring: {
      baseline: [
        { test: 'Stool/urine microscopy for ova', reason: 'Confirm diagnosis and species; quantify intensity (eggs per gram)' },
        { test: 'CBC with eosinophil count', reason: 'Baseline for monitoring response (eosinophilia common with helminth infections)' },
        { test: 'Liver function tests', reason: 'Baseline in hepatosplenic schistosomiasis' }
      ],
      ongoing: [
        { test: 'Stool/urine for ova', frequency: '4-6 weeks after treatment', target: 'Negative or markedly reduced egg count' },
        { test: 'Stool/urine for ova', frequency: '3-6 months if initial post-treatment positive', target: 'Negative (may need retreatment)' },
        { test: 'Clinical symptoms', frequency: 'As indicated', target: 'Resolution of hematuria, hepatomegaly, etc.' },
        { test: 'Ultrasound (hepatosplenic schistosomiasis)', frequency: 'Annually if liver/spleen involvement', target: 'Regression of fibrosis over years' }
      ],
      therapeutic: {
        range: 'TDM not routinely performed; efficacy assessed parasitologically',
        timing: 'Cure assessed 4-6 weeks post-treatment to allow eggs to clear'
      }
    },
    storage: {
      temperature: 'Store below 30°C (86°F). Protect from excessive heat.',
      light: 'Protect from light; store in original container',
      humidity: 'Store in dry conditions',
      specialInstructions: 'Keep in original packaging. Very bitter taste - do not crush unless mixing immediately with food for children.',
      shelfLife: '36-60 months depending on formulation.'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - WHO Essential Medicine; widely distributed through NTD (neglected tropical disease) programs. Available in most health facilities in endemic areas.',
      affordability: 'FREE through WHO/NGO mass drug administration programs. Commercial cost $0.20-0.50 per 600 mg tablet. Very affordable even when purchased.',
      supplyChainNotes: 'Stable at room temperature up to 30°C. Pre-packaged for MDA programs. WHO dose poles facilitate community distribution. Donated through multiple programs (Merck Praziquantel Donation Program for schistosomiasis control).',
      localManufacturing: 'WHO-prequalified generics produced in several countries. Merck donates through WHO programs. Local production in some African countries for public sector.',
      alternatives: [
        'Oxamniquine (for S. mansoni only; limited availability)',
        'Metrifonate (for S. haematobium only; largely unavailable)',
        'Triclabendazole (for Fasciola only - praziquantel NOT effective)',
        'Artemether (some effect on juvenile schistosomes; investigational)'
      ],
      resistancePatterns: 'Resistance concerns have been raised but confirmed praziquantel resistance remains rare in Africa. Reduced cure rates in some high-intensity infection areas may reflect reinfection or need for repeated doses rather than true resistance. WHO recommends continued MDA.',
      endemicConsiderations: 'Schistosomiasis affects >200 million people globally, majority in Africa. S. haematobium (urogenital) and S. mansoni (intestinal/hepatic) most common in Africa. WHO strategy: preventive chemotherapy (MDA) to school-age children and at-risk adults annually or biannually. Treatment reduces morbidity even without preventing reinfection. Snail control and WASH improvements complement drug treatment.'
    },
    patientCounseling: {
      keyPoints: [
        'Take the tablets with food and plenty of water',
        'Swallow tablets whole - do not chew (very bitter taste)',
        'You may experience dizziness - do not drive or operate machinery today',
        'Mild stomach discomfort and headache are common and usually resolve within 24 hours',
        'Treatment may need to be repeated if you live in an area with ongoing transmission'
      ],
      commonMisconceptions: [
        'One treatment provides lifelong protection - NO, you can be reinfected; repeat treatment as recommended',
        'Only sick people need treatment - NO, even people without symptoms can benefit from treatment',
        'Side effects mean the medicine is harmful - NO, side effects usually mean the parasites are dying'
      ],
      adherenceTips: [
        'Take during school or community distribution programs when offered',
        'If given multiple tablets, take them all on the same day as directed',
        'Eat before and after taking medication to reduce stomach upset',
        'Rest for the remainder of the day after treatment'
      ],
      warningSignsToReport: [
        'Severe headache or confusion (especially if history of cysticercosis)',
        'Seizures',
        'Severe abdominal pain not improving after 24 hours',
        'Bloody vomit or black/bloody stool',
        'Difficulty breathing or swallowing',
        'Severe rash or swelling'
      ]
    },
    clinicalPearls: [
      'Praziquantel is safe, effective, and the ONLY drug available for all Schistosoma species - backbone of elimination programs',
      'Does NOT treat Fasciola hepatica (common liver fluke in Africa) - must use triclabendazole instead',
      'For neurocysticercosis, ALBENDAZOLE is preferred over praziquantel (better CNS penetration); hospitalize and give steroids first',
      'Side effects (headache, abdominal pain) are often due to dying parasites and should not prevent completion of treatment',
      'Cure rates may be lower in high-intensity infections - consider repeat treatment at 4-6 weeks if eggs persist',
      'Avoid rifampicin co-administration - reduces praziquantel levels by 85%',
      'WHO now recommends treating pregnant women (after 1st trimester) and preschool children in endemic areas'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guidelines on Schistosomiasis 2022',
      'WHO PCT Databank - Preventive Chemotherapy',
      'Colley DG et al. Human schistosomiasis. Lancet 2014',
      'Sanford Guide to Antimicrobial Therapy 2024',
      'Cioli D et al. Praziquantel. Parasitol Res 2014'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }
,
  {
  id: 'amlodipine-021',
  genericName: 'Amlodipine',
  brandNames: ['Norvasc', 'Istin', 'Amlovas', 'Amlod', 'Amlostin', 'Amloc'],
  atcCode: 'C08CA01',
  therapeuticClass: 'Dihydropyridine Calcium Channel Blocker',
  pharmacology: {
    mechanism: 'Inhibits calcium ion influx through L-type voltage-gated calcium channels in vascular smooth muscle and cardiac muscle. Causes peripheral arterial vasodilation, reducing peripheral vascular resistance and blood pressure. Has greater selectivity for vascular smooth muscle than cardiac muscle, resulting in minimal negative inotropic effects at therapeutic doses.',
    pharmacokinetics: {
      absorption: 'Well absorbed orally with bioavailability of 64-90%. Peak plasma concentrations reached in 6-12 hours. Absorption not affected by food.',
      distribution: 'Volume of distribution approximately 21 L/kg. Highly protein bound (93-98%) to plasma proteins. Crosses placenta; minimal CNS penetration.',
      metabolism: 'Extensively metabolized in liver by CYP3A4 to inactive metabolites. No significant first-pass metabolism despite hepatic metabolism due to slow absorption.',
      elimination: 'Elimination half-life 30-50 hours allows once-daily dosing. 60% excreted in urine as metabolites, 10% as unchanged drug. 20-25% eliminated in feces.',
      halfLife: '30-50 hours (prolonged in elderly and hepatic impairment)'
    },
    pharmacodynamics: 'Gradual onset of action over 24-48 hours with peak effect at 6-9 days. Sustained antihypertensive effect with minimal reflex tachycardia due to gradual onset. Reduces both systolic and diastolic blood pressure. Has antianginal effects through coronary vasodilation and reduced afterload.'
  },
  indications: [
    {
      condition: 'Essential Hypertension',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Particularly effective in elderly, Black patients, and those with low-renin hypertension. First-line therapy per JNC-8 and WHO guidelines.'
    },
    {
      condition: 'Chronic Stable Angina',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for both effort-induced and vasospastic angina. Can be combined with beta-blockers.'
    },
    {
      condition: 'Vasospastic (Prinzmetal) Angina',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Drug of choice for coronary artery spasm.'
    },
    {
      condition: 'Coronary Artery Disease',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: false,
      notes: 'CAMELOT trial demonstrated reduction in cardiovascular events in CAD patients.'
    },
    {
      condition: 'Raynaud Phenomenon',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Reduces frequency and severity of attacks through peripheral vasodilation.'
    }
  ],
  dosing: {
    adults: {
      standard: '5 mg once daily initially, may increase to 10 mg once daily after 1-2 weeks based on response',
      indicationSpecific: [
        { indication: 'Hypertension', dose: '2.5-10 mg once daily; start 5 mg, titrate at 7-14 day intervals' },
        { indication: 'Angina', dose: '5-10 mg once daily' },
        { indication: 'Coronary artery disease', dose: '5-10 mg once daily' }
      ],
      maxDose: '10 mg/day',
      renalImpairment: {
        mild: 'No adjustment required',
        moderate: 'No adjustment required',
        severe: 'No adjustment required; use with caution, start low',
        dialysis: 'Not dialyzable; no supplemental dose needed'
      },
      hepaticImpairment: 'Start 2.5 mg daily in severe impairment; titrate slowly. Half-life prolonged to 60 hours.'
    },
    pediatric: {
      standard: '2.5-5 mg once daily for children 6-17 years',
      weightBased: false,
      ageSpecific: [
        { age: '6-17 years', dose: '2.5-5 mg once daily' },
        { age: '<6 years', dose: 'Limited data; 0.05-0.2 mg/kg/day has been used' }
      ],
      maxDose: '5 mg/day in children 6-17 years',
      neonatal: 'Not established; limited neonatal data available'
    },
    elderly: 'Start 2.5 mg daily due to decreased clearance. Titrate slowly based on response.',
    routes: ['Oral'],
    administration: [
      'May be taken with or without food',
      'Administer at same time each day for consistent effect',
      'Tablets may be crushed if needed',
      'Do not discontinue abruptly in angina patients'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to amlodipine or other dihydropyridines',
      'Severe hypotension (systolic <90 mmHg)',
      'Cardiogenic shock',
      'Severe aortic stenosis'
    ],
    relative: [
      'Moderate to severe heart failure (use with caution)',
      'Severe hepatic impairment',
      'Hypertrophic cardiomyopathy with outflow obstruction',
      'Unstable angina (not established as sole therapy)'
    ],
    precautions: [
      'Monitor for peripheral edema, especially at higher doses',
      'Use caution in patients with heart failure (NYHA III-IV)',
      'Worsening angina or acute MI may occur at initiation or dose increase',
      'May cause reflex tachycardia in some patients',
      'Gingival hyperplasia with long-term use - maintain dental hygiene'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Peripheral edema', frequency: '10-15%', management: 'Dose-dependent; reduce dose, add ACE-I/ARB which reduces edema, or switch to alternative' },
      { effect: 'Headache', frequency: '7-8%', management: 'Usually transient; symptomatic treatment with paracetamol' },
      { effect: 'Flushing', frequency: '2-5%', management: 'Often diminishes with continued therapy' },
      { effect: 'Dizziness', frequency: '3-4%', management: 'Advise caution with driving; usually transient' },
      { effect: 'Fatigue', frequency: '4-5%', management: 'Often improves with continued use; take at bedtime if persistent' },
      { effect: 'Palpitations', frequency: '1-4%', management: 'Usually benign; evaluate if persistent' }
    ],
    serious: [
      { effect: 'Severe hypotension', frequency: 'Rare', management: 'Discontinue; supportive care; IV fluids if needed', warning: 'More common in volume-depleted patients' },
      { effect: 'Worsening heart failure', frequency: 'Rare', management: 'Monitor closely in HF patients; may need to discontinue', warning: 'Risk higher in severe HF (NYHA III-IV)' },
      { effect: 'Acute MI/angina exacerbation', frequency: 'Rare', management: 'Discontinue; standard ACS management', warning: 'Can occur at initiation or dose increase' },
      { effect: 'Hepatitis/jaundice', frequency: 'Very rare', management: 'Discontinue immediately; monitor LFTs', warning: 'Usually reversible upon discontinuation' }
    ],
    rare: [
      'Gingival hyperplasia (long-term use)',
      'Erythema multiforme',
      'Leukopenia/thrombocytopenia',
      'Gynecomastia',
      'Hyperglycemia',
      'Mood changes/depression',
      'Myalgia/muscle cramps',
      'Visual disturbances',
      'Tinnitus'
    ]
  },
  interactions: {
    major: [
      { drug: 'Strong CYP3A4 inhibitors (ketoconazole, itraconazole, ritonavir, clarithromycin)', effect: 'Increased amlodipine levels and hypotensive effect', mechanism: 'CYP3A4 inhibition reduces amlodipine metabolism', management: 'Monitor BP closely; may need dose reduction to 2.5 mg' },
      { drug: 'Simvastatin', effect: 'Increased simvastatin exposure and myopathy risk', mechanism: 'Amlodipine inhibits CYP3A4-mediated simvastatin metabolism', management: 'Limit simvastatin to 20 mg/day when combined with amlodipine' },
      { drug: 'Cyclosporine', effect: 'Increased cyclosporine levels', mechanism: 'Competition for CYP3A4 metabolism', management: 'Monitor cyclosporine levels; may need dose adjustment' }
    ],
    moderate: [
      { drug: 'Beta-blockers', effect: 'Additive hypotensive and negative chronotropic effects', management: 'Generally beneficial combination; monitor HR and BP' },
      { drug: 'Digoxin', effect: 'Possible increased digoxin levels', management: 'Monitor digoxin levels when initiating amlodipine' },
      { drug: 'Sildenafil/PDE5 inhibitors', effect: 'Enhanced hypotensive effect', management: 'Use with caution; advise patient about orthostatic symptoms' },
      { drug: 'CYP3A4 inducers (rifampicin, phenytoin, carbamazepine)', effect: 'Reduced amlodipine levels', management: 'May need higher amlodipine doses; monitor BP' }
    ],
    food: [
      'Grapefruit juice may increase amlodipine levels (clinical significance variable)',
      'Food does not significantly affect absorption'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'Limited human data; animal studies show fetal harm at high doses. Avoid if possible.',
      second: 'May affect fetal growth through placental blood flow reduction. Use only if clearly needed.',
      third: 'Risk of maternal hypotension and fetal compromise. Other agents preferred (methyldopa, labetalol).'
    },
    recommendation: 'Not recommended in pregnancy. Use methyldopa, labetalol, or nifedipine (extended-release) as alternatives. If inadvertent exposure, monitor fetal growth.',
    lactation: {
      safe: false,
      notes: 'Excreted in breast milk. Consider alternative antihypertensives in breastfeeding (labetalol, nifedipine). If used, monitor infant for hypotension and poor feeding.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Blood pressure', reason: 'Establish baseline before treatment' },
      { test: 'Heart rate', reason: 'Assess for relative tachycardia' },
      { test: 'Hepatic function (LFTs)', reason: 'Metabolism is hepatic; dose adjustment needed in impairment' },
      { test: 'Assessment for peripheral edema', reason: 'Document baseline to monitor for drug-induced edema' }
    ],
    ongoing: [
      { test: 'Blood pressure', frequency: 'Weekly during titration, then every 3-6 months', target: '<130/80 mmHg (or <140/90 in elderly)' },
      { test: 'Heart rate', frequency: 'Each visit', target: '60-80 bpm' },
      { test: 'Assessment for edema', frequency: 'Each visit', target: 'Minimal or no peripheral edema' },
      { test: 'LFTs', frequency: 'If symptoms of hepatotoxicity develop' }
    ],
    therapeutic: {
      range: 'Blood pressure target individualized; generally <130/80 mmHg',
      timing: 'Full antihypertensive effect seen in 6-9 days of consistent dosing'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light; store in original container',
    humidity: 'Protect from moisture',
    specialInstructions: 'Keep container tightly closed',
    shelfLife: '3 years from manufacture; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa as generic formulation. WHO Essential Medicine. Available in most public health facilities.',
    affordability: 'Very affordable as generic; approximately $0.50-2 USD per month. Among most cost-effective antihypertensives.',
    supplyChainNotes: 'Generally stable supply due to multiple generic manufacturers. Heat stability good but protect from extreme temperatures during transport.',
    localManufacturing: 'Manufactured locally in Nigeria (Emzor, Fidson), Kenya (Cosmos), South Africa (Adcock Ingram), Egypt (EIPICO), and Morocco.',
    alternatives: [
      'Nifedipine extended-release (alternative CCB)',
      'Enalapril or lisinopril (ACE inhibitors)',
      'Hydrochlorothiazide (thiazide diuretic)',
      'Atenolol (beta-blocker)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Hypertension highly prevalent across Africa (estimated 30-40% adult prevalence). Amlodipine particularly effective in Black African populations due to low-renin hypertension phenotype. Heat and humidity may affect tablet stability - educate patients on proper storage.'
  },
  patientCounseling: {
    keyPoints: [
      'Take at the same time every day, with or without food',
      'Do not stop taking suddenly without consulting your doctor',
      'Blood pressure lowering is gradual - full effect takes about 1 week',
      'Report swelling of ankles or feet, which may indicate need for dose adjustment',
      'Attend all scheduled appointments for blood pressure monitoring'
    ],
    commonMisconceptions: [
      'Misconception: Can stop taking when feeling better - Truth: Continue even if asymptomatic; hypertension is lifelong',
      'Misconception: Only take when BP is high - Truth: Must take daily for prevention',
      'Misconception: Causes kidney damage - Truth: Actually protects kidneys from hypertensive damage',
      'Misconception: Herbal alternatives are safer - Truth: Unproven efficacy; may interact with amlodipine'
    ],
    adherenceTips: [
      'Use pill box or phone alarm as daily reminder',
      'Link to existing daily routine (e.g., with breakfast or at bedtime)',
      'Keep medication visible but out of reach of children',
      'Do not double dose if missed - take next dose as scheduled',
      'Refill prescription before running out'
    ],
    warningSignsToReport: [
      'Severe dizziness or fainting',
      'Rapid or irregular heartbeat',
      'Significant swelling of feet, ankles, or hands',
      'Chest pain or worsening angina',
      'Yellowing of skin or eyes (jaundice)',
      'Severe or persistent headache'
    ]
  },
  clinicalPearls: [
    'Amlodipine is particularly effective in Black African patients with low-renin hypertension - often provides better BP control than ACE-I or ARB monotherapy in this population',
    'Peripheral edema is dose-dependent and due to arteriolar vasodilation, not fluid retention - diuretics do not help; adding ACE-I/ARB redistributes fluid and reduces edema',
    'Long half-life (30-50 hours) provides forgiveness for missed doses and smooth 24-hour BP control',
    'No renal dose adjustment needed - advantage in settings where renal function monitoring is limited',
    'Combination with ACE-I or ARB is particularly effective and reduces peripheral edema incidence',
    'Unlike some other CCBs, amlodipine has no significant negative inotropic effect - can be used cautiously in stable heart failure',
    'Generic amlodipine is highly affordable and among the most cost-effective antihypertensives globally',
    'In African settings with limited access to multiple medications, amlodipine/ACE-I or amlodipine/ARB fixed-dose combinations simplify regimens and improve adherence'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'WHO HEARTS Technical Package for Cardiovascular Disease Management, 2016',
    'ALLHAT Collaborative Research Group. JAMA 2002 - antihypertensive treatment outcomes',
    'Pitt B et al. CAMELOT trial. JAMA 2004 - amlodipine in coronary disease',
    'NICE Guideline NG136: Hypertension in adults, 2019 (updated 2022)',
    'Pan African Society of Cardiology Hypertension Guidelines 2020',
    'Nigerian Hypertension Society Guidelines 2020',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'enalapril-022',
  genericName: 'Enalapril',
  brandNames: ['Vasotec', 'Renitec', 'Enaladex', 'Envas', 'Enapril', 'Invoril'],
  atcCode: 'C09AA02',
  therapeuticClass: 'Angiotensin-Converting Enzyme (ACE) Inhibitor',
  pharmacology: {
    mechanism: 'Prodrug converted to active metabolite enalaprilat in the liver. Competitively inhibits angiotensin-converting enzyme (ACE), blocking conversion of angiotensin I to angiotensin II. This reduces vasoconstriction, aldosterone secretion, and sodium/water retention. Also inhibits bradykinin degradation, contributing to vasodilation and potentially causing cough.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability approximately 60%. Not affected by food. Peak plasma concentration of enalapril in 1 hour; enalaprilat (active) peaks at 3-4 hours.',
      distribution: 'Volume of distribution 1.7 L/kg. Protein binding approximately 50-60%. Does not significantly cross blood-brain barrier. Crosses placenta.',
      metabolism: 'Rapidly hydrolyzed in liver to active metabolite enalaprilat. Enalaprilat not further metabolized significantly.',
      elimination: 'Primarily renal excretion (60% as enalaprilat, 20% as enalapril). Accumulates in renal impairment.',
      halfLife: 'Enalapril: 2 hours; Enalaprilat: 11 hours (prolonged to 40+ hours in severe renal impairment)'
    },
    pharmacodynamics: 'Onset of action 1-2 hours, peak effect 4-6 hours, duration 12-24 hours. Reduces systemic vascular resistance, arterial and venous blood pressure. Decreases left ventricular preload and afterload. Improves cardiac output in heart failure without increasing heart rate. Reduces urinary protein excretion through renal efferent arteriolar dilation.'
  },
  indications: [
    {
      condition: 'Essential Hypertension',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective across populations. Particularly beneficial in diabetes, CKD, and heart failure. May be less effective as monotherapy in Black patients - often combined with CCB or diuretic.'
    },
    {
      condition: 'Heart Failure with Reduced Ejection Fraction (HFrEF)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'CONSENSUS and SOLVD trials demonstrated mortality benefit. Foundation of guideline-directed medical therapy for HFrEF.'
    },
    {
      condition: 'Asymptomatic Left Ventricular Dysfunction',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Prevents progression to symptomatic heart failure. SOLVD Prevention trial evidence.'
    },
    {
      condition: 'Diabetic Nephropathy',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces proteinuria and slows progression of diabetic kidney disease independent of BP lowering.'
    },
    {
      condition: 'Chronic Kidney Disease with Proteinuria',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Renoprotective effects; reduces proteinuria and slows CKD progression.'
    },
    {
      condition: 'Post-Myocardial Infarction',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Start early (within 24 hours) in hemodynamically stable patients with LV dysfunction, anterior MI, or diabetes.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Hypertension: Start 5 mg once daily, usual maintenance 10-40 mg/day in 1-2 doses. Heart failure: Start 2.5 mg once or twice daily, titrate to target 10-20 mg twice daily.',
      indicationSpecific: [
        { indication: 'Hypertension', dose: 'Initial 5 mg once daily; maintenance 10-40 mg/day in 1-2 divided doses' },
        { indication: 'Heart failure', dose: 'Initial 2.5 mg once or twice daily; target 10-20 mg twice daily as tolerated' },
        { indication: 'Asymptomatic LV dysfunction', dose: 'Start 2.5 mg twice daily; target 10 mg twice daily' },
        { indication: 'Diabetic nephropathy', dose: '5-20 mg daily; titrate to maximum tolerated dose' },
        { indication: 'Post-MI', dose: 'Start 2.5-5 mg; target 10-20 mg twice daily' }
      ],
      maxDose: '40 mg/day',
      renalImpairment: {
        mild: 'CrCl 30-60 mL/min: Initial 5 mg daily; titrate cautiously',
        moderate: 'CrCl 10-30 mL/min: Initial 2.5 mg daily; maximum 40 mg/day',
        severe: 'CrCl <10 mL/min: Initial 2.5 mg daily; titrate slowly based on response',
        dialysis: 'Initial 2.5 mg on dialysis days; give post-dialysis (enalaprilat is dialyzable)'
      },
      hepaticImpairment: 'Conversion to enalaprilat may be reduced in severe hepatic failure. Monitor closely; may need higher doses.'
    },
    pediatric: {
      standard: 'Initial 0.08 mg/kg/day (up to 5 mg) once daily; adjust at 2-week intervals',
      weightBased: true,
      ageSpecific: [
        { age: '1 month to <16 years', dose: '0.08-0.6 mg/kg/day once daily or in 2 divided doses' },
        { age: 'Neonates', dose: '0.04-0.1 mg/kg/dose every 24-48 hours; very sensitive to ACE inhibitors' }
      ],
      maxDose: '0.6 mg/kg/day or 40 mg/day, whichever is less',
      neonatal: '0.04-0.1 mg/kg/dose every 24-48 hours. Neonates extremely sensitive - start very low and titrate slowly. Risk of profound hypotension and renal impairment.'
    },
    elderly: 'Start with 2.5 mg daily due to age-related renal decline. Titrate slowly based on BP response and renal function.',
    routes: ['Oral'],
    administration: [
      'May be taken with or without food',
      'Tablets can be crushed and mixed with food if needed',
      'Oral suspension can be compounded for pediatric use',
      'Maintain consistent timing for once-daily dosing',
      'For heart failure: start low and titrate slowly over weeks to months'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to enalapril or any ACE inhibitor',
      'History of angioedema with ACE inhibitors',
      'Hereditary or idiopathic angioedema',
      'Pregnancy (2nd and 3rd trimesters) - teratogenic',
      'Concurrent use with aliskiren in diabetics or patients with GFR <60',
      'Concomitant use with sacubitril (requires 36-hour washout)'
    ],
    relative: [
      'Bilateral renal artery stenosis or stenosis in solitary kidney',
      'Severe aortic stenosis or hypertrophic cardiomyopathy',
      'Volume/salt depletion (correct before starting)',
      'Severe renal impairment (use with caution, reduce dose)',
      'Hyperkalemia (K+ >5.5 mEq/L)',
      'First trimester pregnancy (discontinue immediately if pregnancy confirmed)'
    ],
    precautions: [
      'Monitor potassium, especially with concurrent K+-sparing diuretics or supplements',
      'Check renal function before starting and 1-2 weeks after initiation or dose increase',
      'Risk of first-dose hypotension, especially in heart failure, volume depletion, or high-dose diuretics',
      'Higher risk of angioedema in Black patients',
      'Cough occurs in 5-20% - may need to switch to ARB',
      'Monitor for hyperkalemia in diabetics and those with renal impairment'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Dry cough', frequency: '5-20%', management: 'Often resolves after discontinuation (1-4 weeks). Switch to ARB if intolerable.' },
      { effect: 'Hypotension', frequency: '5-10%', management: 'More common in volume-depleted patients. Reduce diuretic dose before starting. Start low, go slow.' },
      { effect: 'Dizziness', frequency: '4-8%', management: 'Usually transient. Advise caution with driving initially.' },
      { effect: 'Headache', frequency: '5%', management: 'Symptomatic treatment; often resolves with continued use.' },
      { effect: 'Fatigue', frequency: '3%', management: 'Often improves over time; consider dose adjustment if persistent.' },
      { effect: 'Hyperkalemia', frequency: '2-6%', management: 'Monitor K+; avoid K+ supplements and K+-sparing diuretics unless indicated.' }
    ],
    serious: [
      { effect: 'Angioedema', frequency: '0.1-0.7%', management: 'Discontinue immediately. May require airway management, epinephrine, antihistamines, corticosteroids. Do not rechallenge.', warning: 'Higher incidence in Black patients. Can occur anytime but most common in first week.' },
      { effect: 'Acute renal failure', frequency: 'Rare', management: 'Discontinue or reduce dose. Usually reversible. Check for renal artery stenosis.', warning: 'Risk factors: bilateral RAS, volume depletion, concurrent NSAIDs' },
      { effect: 'Severe hypotension', frequency: 'Rare', management: 'Supine position, IV fluids. May occur with first dose in heart failure or after aggressive diuresis.', warning: 'Start low dose in heart failure; consider holding diuretic dose before first ACE-I dose' },
      { effect: 'Agranulocytosis/neutropenia', frequency: 'Very rare', management: 'Discontinue; monitor CBC. More common with higher doses and renal impairment.', warning: 'Risk increased with concurrent immunosuppressants or in collagen vascular disease' },
      { effect: 'Hepatotoxicity', frequency: 'Very rare', management: 'Discontinue; usually reversible. Monitor LFTs if symptoms.', warning: 'Can present as cholestatic jaundice' }
    ],
    rare: [
      'Taste disturbance (dysgeusia)',
      'Proteinuria',
      'Pemphigus/pemphigoid skin reactions',
      'Pancreatitis',
      'Syndrome of inappropriate ADH secretion (SIADH)',
      'Photosensitivity',
      'Alopecia',
      'Impotence'
    ]
  },
  interactions: {
    major: [
      { drug: 'Potassium supplements/K+-sparing diuretics (spironolactone, amiloride)', effect: 'Significant hyperkalemia risk', mechanism: 'Reduced aldosterone from ACE inhibition plus additional K+ retention', management: 'Monitor K+ closely. In heart failure, low-dose spironolactone used carefully with frequent monitoring.' },
      { drug: 'NSAIDs (ibuprofen, diclofenac, etc.)', effect: 'Reduced antihypertensive effect; increased risk of acute renal failure', mechanism: 'NSAIDs inhibit prostaglandin-mediated renal blood flow and sodium excretion', management: 'Avoid if possible; if used, monitor BP and renal function closely' },
      { drug: 'Lithium', effect: 'Increased lithium levels and toxicity risk', mechanism: 'Reduced renal lithium clearance', management: 'Monitor lithium levels frequently; may need dose reduction' },
      { drug: 'Aliskiren', effect: 'Increased risk of hypotension, hyperkalemia, and renal impairment', mechanism: 'Dual RAAS blockade', management: 'Contraindicated in diabetics and CKD (GFR <60); avoid combination generally' },
      { drug: 'Sacubitril/valsartan (Entresto)', effect: 'Increased risk of angioedema', mechanism: 'Sacubitril inhibits neprilysin which degrades bradykinin', management: 'Requires 36-hour washout period when switching between agents' }
    ],
    moderate: [
      { drug: 'Thiazide/loop diuretics', effect: 'Enhanced hypotensive effect; increased risk of first-dose hypotension', management: 'Consider reducing or holding diuretic before first ACE-I dose; start ACE-I low' },
      { drug: 'Antidiabetic agents (insulin, sulfonylureas)', effect: 'Enhanced hypoglycemic effect', management: 'Monitor blood glucose; may need to reduce antidiabetic dose' },
      { drug: 'Trimethoprim', effect: 'Increased hyperkalemia risk', management: 'Monitor potassium, especially in renal impairment' },
      { drug: 'Other antihypertensives', effect: 'Additive hypotension', management: 'Monitor BP closely; usually a beneficial combination' }
    ],
    food: [
      'Food does not significantly affect absorption',
      'Avoid high-potassium foods in excess (bananas, oranges, potatoes) if prone to hyperkalemia',
      'Salt substitutes containing potassium should be avoided'
    ]
  },
  pregnancy: {
    category: 'D (2nd/3rd trimester); C (1st trimester)',
    trimpiesterRisks: {
      first: 'Limited data on teratogenicity in 1st trimester, but cardiovascular and CNS malformations reported. Discontinue when pregnancy confirmed.',
      second: 'Fetal renal dysfunction, oligohydramnios, fetal limb contractures, pulmonary hypoplasia. CONTRAINDICATED.',
      third: 'Fetal/neonatal renal failure, hypotension, skull hypoplasia, death. CONTRAINDICATED.'
    },
    recommendation: 'Contraindicated in pregnancy. Discontinue immediately when pregnancy confirmed. Switch to methyldopa, labetalol, or nifedipine. Women of childbearing potential should use effective contraception.',
    lactation: {
      safe: true,
      notes: 'Enalapril and enalaprilat excreted in breast milk in low concentrations. Generally considered compatible with breastfeeding by AAP. Monitor infant for hypotension if concerned.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Serum creatinine/eGFR', reason: 'Dose adjustment needed in renal impairment; detect baseline kidney disease' },
      { test: 'Serum potassium', reason: 'Risk of hyperkalemia; identify baseline abnormalities' },
      { test: 'Blood pressure', reason: 'Establish baseline for monitoring response' },
      { test: 'Urinalysis (for protein)', reason: 'Baseline proteinuria assessment in diabetic or CKD patients' }
    ],
    ongoing: [
      { test: 'Serum creatinine/eGFR', frequency: '1-2 weeks after initiation or dose increase, then every 3-6 months', target: 'Creatinine rise <30% from baseline is acceptable' },
      { test: 'Serum potassium', frequency: '1-2 weeks after initiation or dose increase, then every 3-6 months', target: '<5.5 mEq/L' },
      { test: 'Blood pressure', frequency: 'Weekly during titration, then every visit', target: '<130/80 mmHg (individualized)' },
      { test: 'Urine protein (if diabetic/CKD)', frequency: 'Every 6-12 months', target: 'Reduction from baseline' }
    ],
    therapeutic: {
      range: 'No specific drug level monitoring; titrate based on BP and clinical response',
      timing: 'Full antihypertensive effect in 1-2 weeks; heart failure benefits accrue over months'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture; keep container tightly closed',
    specialInstructions: 'Do not remove from blister pack until use if applicable',
    shelfLife: '3 years from manufacture; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa as generic. WHO Essential Medicine for hypertension and heart failure. Available in most public health facilities.',
    affordability: 'Very affordable as generic; approximately $0.50-3 USD per month. Highly cost-effective for cardiovascular and renal protection.',
    supplyChainNotes: 'Stable supply from multiple generic manufacturers. Heat stable at tropical temperatures for storage.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, May & Baker), South Africa (Aspen), Kenya (Cosmos, Laboratory & Allied), Egypt (multiple), and Morocco.',
    alternatives: [
      'Lisinopril (longer-acting ACE inhibitor, once daily)',
      'Captopril (shorter-acting, three times daily)',
      'Losartan or telmisartan (ARBs - for ACE-I intolerance)',
      'Amlodipine or nifedipine (if ACE-I contraindicated)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'ACE inhibitors may be less effective as monotherapy in Black African patients due to low-renin hypertension phenotype - often combined with CCB or thiazide for optimal BP control. Higher angioedema risk in Black populations (0.5-0.7% vs 0.1-0.2%). Essential for diabetic nephropathy, which is increasingly common across Africa. In resource-limited settings, simplified monitoring protocols may be needed - prioritize baseline and 2-week creatinine and potassium checks.'
  },
  patientCounseling: {
    keyPoints: [
      'May cause dry cough in some people - report if troublesome',
      'Take at the same time each day, with or without food',
      'Report any swelling of face, lips, tongue, or throat immediately (angioedema)',
      'Avoid becoming pregnant while taking this medicine - use contraception',
      'May cause dizziness when starting - rise slowly from sitting or lying position',
      'Avoid salt substitutes containing potassium'
    ],
    commonMisconceptions: [
      'Misconception: Can stop when BP normal - Truth: Continue for ongoing protection; hypertension is lifelong',
      'Misconception: Cough means allergy - Truth: Cough is common side effect, not allergic reaction; report but do not stop without consulting doctor',
      'Misconception: Only for blood pressure - Truth: Protects heart and kidneys too, important even if BP not the main problem',
      'Misconception: More effective at higher doses - Truth: Often works well at moderate doses; higher doses increase side effects'
    ],
    adherenceTips: [
      'Take at same time daily - link to existing routine',
      'Use pill organizer or phone reminder',
      'Keep medication where you will see it (not in bathroom - humidity)',
      'Do not double up if missed dose - take next dose as scheduled',
      'Get refills before running out'
    ],
    warningSignsToReport: [
      'Swelling of face, lips, tongue, or difficulty breathing (seek emergency care)',
      'Persistent dry cough that is bothersome',
      'Lightheadedness or fainting',
      'Signs of infection (fever, sore throat, mouth ulcers)',
      'Reduced urine output',
      'Muscle weakness or irregular heartbeat (hyperkalemia)'
    ]
  },
  clinicalPearls: [
    'In Black African patients, combine ACE-I with CCB or thiazide for optimal BP control - ACE-I monotherapy often insufficient due to low-renin hypertension',
    'First-dose hypotension risk highest in heart failure patients on high-dose diuretics - consider holding diuretic or starting with 2.5 mg dose',
    'A rise in creatinine up to 30% after starting ACE-I is acceptable and expected, especially in heart failure - reflects decreased glomerular pressure, not nephrotoxicity',
    'ACE-I cough is due to bradykinin accumulation - switching to ARB eliminates cough in most patients while preserving RAAS blockade benefits',
    'In diabetic nephropathy, ACE-I reduces proteinuria independent of BP lowering - titrate to maximum tolerated dose for nephroprotection',
    'Angioedema risk is higher in Black patients (up to 4x) - counsel all patients but be especially vigilant in this population',
    'For heart failure, target doses from clinical trials (e.g., enalapril 10-20 mg twice daily) provide proven mortality benefit - titrate toward these targets',
    'In African settings with limited monitoring, prioritize baseline and 2-week potassium/creatinine checks as minimum; stable patients may extend monitoring intervals'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'CONSENSUS Trial Study Group. N Engl J Med 1987 - enalapril in severe heart failure',
    'SOLVD Investigators. N Engl J Med 1991 - enalapril in heart failure',
    'SOLVD Investigators. N Engl J Med 1992 - enalapril in asymptomatic LV dysfunction',
    'Lewis EJ et al. N Engl J Med 1993 - ACE-I in diabetic nephropathy',
    'ACC/AHA Heart Failure Guidelines 2022',
    'KDIGO Clinical Practice Guideline for CKD 2024',
    'Pan African Society of Cardiology Hypertension Guidelines 2020',
    'British National Formulary (BNF) 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'atenolol-023',
  genericName: 'Atenolol',
  brandNames: ['Tenormin', 'Atenol', 'Blokium', 'Atehexal', 'Noten', 'Betacard'],
  atcCode: 'C07AB03',
  therapeuticClass: 'Beta-1 Selective Adrenergic Receptor Blocker',
  pharmacology: {
    mechanism: 'Selectively blocks beta-1 adrenergic receptors in the heart and kidneys with minimal effect on beta-2 receptors in bronchi and blood vessels at therapeutic doses. Reduces heart rate, myocardial contractility, and cardiac output. Decreases renin release from the kidneys. At higher doses, beta-1 selectivity may be lost.',
    pharmacokinetics: {
      absorption: 'Approximately 50% absorbed from GI tract. Food does not significantly affect absorption. Peak plasma concentration in 2-4 hours.',
      distribution: 'Volume of distribution 0.7 L/kg. Low protein binding (6-16%). Minimal CNS penetration due to hydrophilicity (less lipophilic than propranolol). Crosses placenta.',
      metabolism: 'Minimal hepatic metabolism (<10%). Excreted largely unchanged.',
      elimination: 'Primarily renal excretion (85-100% unchanged). Renal clearance approximately equals creatinine clearance.',
      halfLife: '6-9 hours (prolonged to 16-27 hours in severe renal impairment)'
    },
    pharmacodynamics: 'Onset within 1 hour of oral dose. Peak effect at 2-4 hours. Duration of effect 24 hours with once-daily dosing. Reduces resting and exercise heart rate. Decreases blood pressure through reduced cardiac output and renin suppression. Anti-ischemic effect through reduced myocardial oxygen demand.'
  },
  indications: [
    {
      condition: 'Essential Hypertension',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'No longer first-line for uncomplicated hypertension per recent guidelines (NICE, ESC). Preferred when compelling indication exists (post-MI, HF, angina). May be less effective in elderly and Black patients.'
    },
    {
      condition: 'Chronic Stable Angina Pectoris',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line anti-anginal therapy. Reduces heart rate and myocardial oxygen demand. Can be combined with CCB or nitrates.'
    },
    {
      condition: 'Post-Myocardial Infarction',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces mortality and reinfarction risk. Start early if hemodynamically stable. Continue long-term.'
    },
    {
      condition: 'Heart Failure with Reduced Ejection Fraction',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Other beta-blockers (carvedilol, bisoprolol, metoprolol succinate) have stronger evidence. Use if alternatives unavailable.'
    },
    {
      condition: 'Supraventricular Tachycardia / Rate Control in Atrial Fibrillation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for ventricular rate control. First-line for AF rate control with CCB alternative.'
    },
    {
      condition: 'Migraine Prophylaxis',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Less established than propranolol but may be effective. Consider if propranolol unavailable.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Hypertension/Angina: 25-50 mg once daily initially, may increase to 100 mg once daily. Post-MI: 50-100 mg once daily.',
      indicationSpecific: [
        { indication: 'Hypertension', dose: 'Start 25-50 mg once daily; maintenance 50-100 mg once daily' },
        { indication: 'Angina', dose: 'Start 50 mg once daily; maintenance 50-100 mg once daily or 50 mg twice daily' },
        { indication: 'Post-MI', dose: '50-100 mg once daily; start as soon as stable' },
        { indication: 'AF rate control', dose: '25-100 mg once daily; titrate to target HR' },
        { indication: 'Migraine prophylaxis', dose: '50-100 mg once daily' }
      ],
      maxDose: '100 mg/day (200 mg/day in some refractory cases)',
      renalImpairment: {
        mild: 'CrCl 35-60 mL/min: Maximum 50 mg once daily',
        moderate: 'CrCl 15-35 mL/min: Maximum 50 mg every other day',
        severe: 'CrCl <15 mL/min: Maximum 25 mg daily or 50 mg every other day',
        dialysis: '25-50 mg after each dialysis session (significantly removed by hemodialysis)'
      },
      hepaticImpairment: 'No dose adjustment typically needed as minimal hepatic metabolism.'
    },
    pediatric: {
      standard: 'Hypertension: 0.5-1 mg/kg/day once daily, maximum 2 mg/kg/day',
      weightBased: true,
      ageSpecific: [
        { age: '1-17 years (hypertension)', dose: '0.5-1 mg/kg/day once daily; max 2 mg/kg/day or 100 mg' },
        { age: 'SVT/arrhythmias', dose: '0.5-2 mg/kg/day in 1-2 divided doses' }
      ],
      maxDose: '2 mg/kg/day or 100 mg/day, whichever is less',
      neonatal: 'Limited data. 1-2 mg/kg/day has been used. Extreme caution; risk of hypoglycemia and bradycardia.'
    },
    elderly: 'Start with 25 mg once daily. Elderly may have exaggerated response. Titrate slowly based on HR and BP.',
    routes: ['Oral', 'Intravenous (not common in African settings)'],
    administration: [
      'May be taken with or without food',
      'Take at same time each day, preferably in morning',
      'Do not discontinue abruptly - taper over 1-2 weeks to avoid rebound tachycardia/angina',
      'Tablets can be halved if needed',
      'IV formulation: 2.5-10 mg slow push, rarely used outside hospital settings'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to atenolol or any beta-blocker',
      'Severe bradycardia (HR <50 bpm symptomatic)',
      'Sick sinus syndrome without pacemaker',
      'Second or third-degree AV block without pacemaker',
      'Cardiogenic shock',
      'Decompensated heart failure requiring inotropic support',
      'Severe hypotension (SBP <90 mmHg)',
      'Pheochromocytoma (without prior alpha-blockade)'
    ],
    relative: [
      'Asthma or severe COPD (beta-1 selectivity lost at higher doses)',
      'Peripheral arterial disease (may worsen claudication)',
      'Diabetes mellitus (may mask hypoglycemia symptoms)',
      'Prinzmetal (vasospastic) angina - may worsen',
      'Severe renal impairment (dose adjustment required)',
      'First-degree AV block',
      'History of depression',
      'Myasthenia gravis (may exacerbate weakness)'
    ],
    precautions: [
      'Monitor heart rate - hold if HR <50-55 bpm',
      'May mask tachycardia of hypoglycemia in diabetics - educate patients',
      'May mask thyrotoxicosis symptoms',
      'Taper gradually to avoid rebound symptoms (angina, MI, hypertensive crisis)',
      'Use cautiously with other AV node blocking agents (CCBs, digoxin)',
      'Perioperative management: continue if possible; if stopped, restart as soon as hemodynamically stable'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Fatigue/tiredness', frequency: '10-25%', management: 'Often improves with time. Consider dose reduction or switching to more lipophilic agent (less CNS effects paradoxically).' },
      { effect: 'Cold extremities', frequency: '10-15%', management: 'Due to reduced cardiac output; more common in cold weather. Warn patients; consider dose reduction.' },
      { effect: 'Bradycardia', frequency: '5-10%', management: 'Monitor HR. Reduce dose if symptomatic. May need to discontinue if severe.' },
      { effect: 'Dizziness', frequency: '5-10%', management: 'Usually transient. Rise slowly from sitting/lying position.' },
      { effect: 'Nausea/GI upset', frequency: '2-5%', management: 'Take with food if needed. Usually mild and transient.' },
      { effect: 'Sleep disturbances', frequency: '2-5%', management: 'Take in morning. Less common with atenolol than lipophilic beta-blockers.' }
    ],
    serious: [
      { effect: 'Severe bradycardia/heart block', frequency: 'Rare', management: 'Stop drug. Atropine for bradycardia; temporary pacing if needed. IV isoprenaline in severe cases.', warning: 'Risk increased with concomitant CCB, digoxin, or pre-existing conduction disease' },
      { effect: 'Worsening heart failure', frequency: 'Rare (1-2% in HF)', management: 'Reduce dose or discontinue. Optimize diuretics. May need to switch to carvedilol with slower uptitration.', warning: 'Start low and go slow in HF; do not initiate during decompensation' },
      { effect: 'Bronchospasm', frequency: 'Rare', management: 'Discontinue. Beta-2 agonist for acute relief. Contraindicated in asthma.', warning: 'Can occur even with beta-1 selective agents, especially at higher doses' },
      { effect: 'Hypoglycemia masking', frequency: 'Variable in diabetics', management: 'Educate diabetic patients about subtle hypoglycemia signs (sweating preserved). Monitor blood glucose closely.', warning: 'Tachycardia masked; sweating usually preserved as warning sign' },
      { effect: 'Rebound hypertension/angina on withdrawal', frequency: 'Variable', management: 'Taper over 1-2 weeks minimum. Never stop abruptly in angina or post-MI patients.', warning: 'Risk of MI or unstable angina with sudden discontinuation' }
    ],
    rare: [
      'Depression/mood changes',
      'Erectile dysfunction',
      'Vivid dreams/nightmares (less than with lipophilic beta-blockers)',
      'Psoriasis exacerbation',
      'Raynaud phenomenon',
      'Lupus-like syndrome',
      'Alopecia',
      'Visual disturbances',
      'Thrombocytopenia'
    ]
  },
  interactions: {
    major: [
      { drug: 'Non-dihydropyridine CCBs (verapamil, diltiazem)', effect: 'Additive negative chronotropic and dromotropic effects; risk of severe bradycardia, heart block, hypotension', mechanism: 'Both depress SA and AV node conduction', management: 'Avoid combination or use with extreme caution and monitoring. Never give IV verapamil with beta-blocker.' },
      { drug: 'Clonidine', effect: 'Rebound hypertension on clonidine withdrawal may be exacerbated', mechanism: 'Beta-blockade prevents compensatory vasodilation during clonidine withdrawal', management: 'If both used, taper beta-blocker first before clonidine withdrawal' },
      { drug: 'Digoxin', effect: 'Additive bradycardia and AV block', mechanism: 'Both slow AV conduction', management: 'Monitor HR and ECG. Beneficial for rate control in AF when used together cautiously.' },
      { drug: 'Antiarrhythmics (amiodarone, flecainide, propafenone)', effect: 'Enhanced cardiac depression, risk of bradycardia and hypotension', mechanism: 'Additive negative inotropic and chronotropic effects', management: 'Use combination with caution; monitor closely' }
    ],
    moderate: [
      { drug: 'Insulin/sulfonylureas', effect: 'Hypoglycemia masking; possible prolongation of hypoglycemia', management: 'Educate patients on subtle hypoglycemia signs. Monitor blood glucose more frequently.' },
      { drug: 'NSAIDs', effect: 'May reduce antihypertensive effect', management: 'Monitor BP; may need to increase atenolol dose or avoid chronic NSAID use' },
      { drug: 'Dihydropyridine CCBs (amlodipine, nifedipine)', effect: 'Additive hypotension; usually beneficial in angina', management: 'Monitor BP; often a useful combination for angina' },
      { drug: 'Alpha-blockers', effect: 'Enhanced first-dose hypotension', management: 'Start alpha-blocker at low dose; monitor BP' },
      { drug: 'Ergot derivatives', effect: 'Enhanced peripheral vasoconstriction', management: 'Monitor for cold extremities; avoid if possible' }
    ],
    food: [
      'Food does not significantly affect absorption',
      'Consistent timing with meals recommended for stable blood levels',
      'Avoid excessive alcohol (additive hypotension and CNS depression)'
    ]
  },
  pregnancy: {
    category: 'D',
    trimpiesterRisks: {
      first: 'May cause intrauterine growth restriction (IUGR). Use only if clearly needed and safer alternatives inappropriate.',
      second: 'Risk of IUGR, neonatal bradycardia, hypoglycemia. Monitor fetal growth closely.',
      third: 'Continued risk of IUGR, fetal bradycardia, hypoglycemia. May affect uterine contractions. Monitor neonate after delivery.'
    },
    recommendation: 'Avoid if possible. Labetalol is preferred beta-blocker in pregnancy for hypertension. If atenolol must be used, monitor fetal growth, use lowest effective dose, and observe neonate for 48-72 hours post-delivery.',
    lactation: {
      safe: false,
      notes: 'Concentrated in breast milk (ratio 1.5-6.8 vs plasma). May cause bradycardia and hypoglycemia in nursing infant. Metoprolol or propranolol preferred if beta-blocker needed during breastfeeding.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Heart rate', reason: 'Establish baseline; ensure HR >50-55 before starting' },
      { test: 'Blood pressure', reason: 'Establish baseline for monitoring response' },
      { test: 'ECG', reason: 'Rule out AV block, bradycardia, or other contraindications' },
      { test: 'Renal function', reason: 'Dose adjustment needed in renal impairment' }
    ],
    ongoing: [
      { test: 'Heart rate', frequency: 'Each visit; weekly during titration', target: '55-70 bpm at rest; <110 bpm with moderate exercise' },
      { test: 'Blood pressure', frequency: 'Each visit', target: '<130/80 mmHg (individualized)' },
      { test: 'Blood glucose (diabetics)', frequency: 'More frequently during titration', target: 'Avoid hypoglycemia' },
      { test: 'Symptoms of heart failure', frequency: 'Each visit', target: 'No worsening dyspnea, edema, or exercise intolerance' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring; titrate by HR and BP response',
      timing: 'Maximum effect on HR/BP seen within 1-2 weeks of dose change'
    }
  },
  storage: {
    temperature: '15-25°C (59-77°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Store in original container',
    shelfLife: '3-5 years depending on formulation; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa as generic. WHO Essential Medicine. Present in most public health facilities.',
    affordability: 'Very affordable; approximately $0.50-2 USD per month. One of the most cost-effective antihypertensives.',
    supplyChainNotes: 'Stable supply from multiple generic manufacturers. Good heat stability for tropical storage.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, Fidson, May & Baker), South Africa (Aspen), Kenya (Cosmos), Egypt (multiple manufacturers), and North Africa.',
    alternatives: [
      'Propranolol (non-selective beta-blocker)',
      'Metoprolol (alternative beta-1 selective)',
      'Bisoprolol (longer-acting beta-1 selective)',
      'Carvedilol (alpha/beta blocker for heart failure)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Beta-blockers may be less effective for hypertension in Black African populations compared to CCBs or thiazides - often used as add-on therapy rather than first-line. However, valuable for rate control in AF (common in rheumatic heart disease), post-MI, and angina. In settings with limited ECG access, careful clinical assessment (pulse rate and regularity, signs of heart failure) is essential before initiation. Warn patients not to stop abruptly, especially in regions where medication supply may be interrupted.'
  },
  patientCounseling: {
    keyPoints: [
      'Take at the same time each day, usually in the morning',
      'Do not stop taking suddenly - can cause serious heart problems',
      'Check your pulse regularly - report if consistently below 50 beats per minute',
      'May cause tiredness or cold hands/feet - usually improves with time',
      'Continue taking even if you feel well - prevents heart problems',
      'Diabetics: be aware that this medicine may mask low blood sugar symptoms'
    ],
    commonMisconceptions: [
      'Misconception: Can stop when feeling better - Truth: Must continue to protect heart; stopping suddenly is dangerous',
      'Misconception: Cold hands mean medicine is not working - Truth: This is a common side effect; medicine is still working',
      'Misconception: Only for high blood pressure - Truth: Also protects heart after heart attack and controls heart rhythm',
      'Misconception: Causes impotence - Truth: Uncommon side effect; discuss with doctor rather than stopping'
    ],
    adherenceTips: [
      'Link to daily routine - take with breakfast',
      'Use pill box or alarm reminder',
      'Keep 2-week supply to avoid running out',
      'If you miss a dose, take it as soon as remembered unless next dose is within 8 hours',
      'Get refills early - never run out abruptly'
    ],
    warningSignsToReport: [
      'Heart rate consistently below 50 beats/minute',
      'Severe dizziness or fainting',
      'Worsening shortness of breath or ankle swelling',
      'New or worsening chest pain',
      'Wheezing or difficulty breathing',
      'Severe cold hands or feet with color changes'
    ]
  },
  clinicalPearls: [
    'No longer first-line for uncomplicated hypertension (NICE, ESC guidelines) - CCBs, ACE-I/ARBs, or thiazides preferred. Use atenolol when compelling indication exists (angina, post-MI, AF, certain arrhythmias).',
    'In Black African patients, beta-blockers are generally less effective for BP control than CCBs or thiazides - use as add-on or for specific indications.',
    'Beta-1 selectivity is dose-dependent - at doses >100 mg, selectivity decreases and bronchospasm risk increases.',
    'For heart failure, carvedilol, bisoprolol, or metoprolol succinate have stronger mortality evidence. Use atenolol only if these alternatives unavailable.',
    'In diabetics, atenolol masks tachycardia of hypoglycemia but preserves sweating as warning sign - educate patients about this.',
    'Significant renal excretion means dose must be reduced in renal impairment; unlike metoprolol or propranolol which are hepatically cleared.',
    'Low lipophilicity means less CNS penetration - may cause fewer nightmares/depression than propranolol, but fatigue still common.',
    'Never stop abruptly - can precipitate rebound angina, MI, or hypertensive crisis. Taper over 1-2 weeks minimum.',
    'In African settings with rheumatic heart disease and AF, atenolol valuable for rate control - low cost and once-daily dosing advantages.'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'NICE Guideline NG136: Hypertension in adults, 2019 (updated 2022)',
    'ESC Guidelines on Arterial Hypertension 2023',
    'ESC Guidelines on Chronic Coronary Syndromes 2019',
    'ACC/AHA Guidelines for Management of Patients with NSTE-ACS 2021',
    'Pan African Society of Cardiology Guidelines 2020',
    'Carlberg B et al. Lancet 2004 - Atenolol in hypertension: is it a wise choice?',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'furosemide-024',
  genericName: 'Furosemide',
  brandNames: ['Lasix', 'Frusol', 'Frusemide', 'Diurin', 'Frumil', 'Salurin'],
  atcCode: 'C03CA01',
  therapeuticClass: 'Loop Diuretic (High-Ceiling Diuretic)',
  pharmacology: {
    mechanism: 'Inhibits sodium-potassium-chloride cotransporter (NKCC2) in the thick ascending limb of the loop of Henle. This blocks reabsorption of approximately 25% of filtered sodium, producing potent diuresis. Also increases renal prostaglandin synthesis, contributing to renal vasodilation. The resulting natriuresis and diuresis reduce plasma volume and extracellular fluid volume.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability 40-70% (variable, reduced in edematous states and heart failure). IM absorption rapid and complete. Peak oral effect 1-2 hours.',
      distribution: 'Volume of distribution 0.1-0.2 L/kg. Highly protein bound (91-99%), mainly to albumin. Crosses placenta; minimal CNS penetration.',
      metabolism: 'Minimal hepatic metabolism. Glucuronide conjugation accounts for about 10% of elimination.',
      elimination: 'Primarily renal excretion (60-90% unchanged in urine via tubular secretion). Some biliary/fecal excretion (10-20%), increased in renal impairment.',
      halfLife: '1-2 hours (prolonged to 4-8 hours in renal impairment, 9-11 hours in end-stage renal disease)'
    },
    pharmacodynamics: 'Oral: onset 30-60 minutes, peak 1-2 hours, duration 6-8 hours. IV: onset 5 minutes, peak 30 minutes, duration 2-3 hours. Diuretic effect proportional to amount reaching tubular fluid. Dose-response relationship maintained even in renal impairment but higher doses needed. Also causes venodilation (preload reduction) within minutes of IV administration, before diuresis occurs.'
  },
  indications: [
    {
      condition: 'Acute Decompensated Heart Failure (Pulmonary Edema)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Mainstay of acute treatment. IV administration preferred for rapid effect. Reduces preload and congestion.'
    },
    {
      condition: 'Chronic Heart Failure with Fluid Overload',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Used for symptom relief and congestion. Does not improve mortality - use lowest dose needed for euvolemia.'
    },
    {
      condition: 'Peripheral Edema (Various Causes)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for edema from hepatic cirrhosis, nephrotic syndrome, and other causes. Address underlying cause.'
    },
    {
      condition: 'Acute Kidney Injury (Oliguria)',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'May increase urine output but does not improve outcomes or prevent need for dialysis. Use to manage fluid balance, not to "kick-start" kidneys.'
    },
    {
      condition: 'Hypertension',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'Generally not first-line for hypertension. Consider in resistant hypertension or with significant CKD. Thiazides preferred for uncomplicated HTN.'
    },
    {
      condition: 'Hypercalcemia',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'After adequate IV hydration, can enhance calcium excretion. Monitor electrolytes closely.'
    },
    {
      condition: 'Acute Pulmonary Edema (Non-Cardiogenic)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'May help reduce pulmonary fluid. Address underlying cause (ARDS, aspiration, etc.).'
    }
  ],
  dosing: {
    adults: {
      standard: 'Edema: Start 20-40 mg oral once or twice daily, titrate based on response. Heart failure acute: 40-80 mg IV, may repeat or increase. Chronic: 20-160 mg/day divided.',
      indicationSpecific: [
        { indication: 'Acute pulmonary edema', dose: 'IV 40-80 mg initial; if no response in 1 hour, double dose (max ~200 mg)' },
        { indication: 'Chronic heart failure', dose: 'Start 20-40 mg once or twice daily; titrate to euvolemia; typical 40-160 mg/day' },
        { indication: 'Hepatic cirrhosis with ascites', dose: 'Start 40 mg with spironolactone (ratio ~40:100); max often limited by encephalopathy' },
        { indication: 'Acute kidney injury', dose: 'IV 40-80 mg; may use continuous infusion (5-40 mg/hour) if needed' },
        { indication: 'Resistant hypertension', dose: '20-80 mg twice daily as adjunct' },
        { indication: 'Hypercalcemia', dose: '80-100 mg IV every 1-2 hours (with saline hydration)' }
      ],
      maxDose: '600 mg/day oral; higher doses used in resistant cases with close monitoring',
      renalImpairment: {
        mild: 'No adjustment; normal doses usually effective',
        moderate: 'May need higher doses for effect (up to 200 mg IV or oral)',
        severe: 'High doses often required (200-400 mg); response diminishes but still present',
        dialysis: 'Dialysis patients often need 100-200 mg; give after dialysis on dialysis days'
      },
      hepaticImpairment: 'Start with lower doses; monitor for hepatic encephalopathy. Combine with spironolactone in cirrhosis.'
    },
    pediatric: {
      standard: '1-2 mg/kg/dose oral, up to 6 mg/kg/day maximum',
      weightBased: true,
      ageSpecific: [
        { age: 'Neonates (premature)', dose: '0.5-1 mg/kg IV/IM/oral every 12-24 hours' },
        { age: 'Neonates (term)', dose: '1-2 mg/kg/dose every 12-24 hours' },
        { age: 'Infants and children', dose: '1-2 mg/kg/dose every 6-12 hours; max 6 mg/kg/day' },
        { age: 'Adolescents', dose: 'Approach adult dosing' }
      ],
      maxDose: '6 mg/kg/day',
      neonatal: '0.5-2 mg/kg/dose every 12-24 hours. Neonates have prolonged half-life; use cautiously. Monitor for hyponatremia and hypocalcemia.'
    },
    elderly: 'Start with lower doses (10-20 mg). Increased sensitivity to volume depletion and electrolyte disturbances. Monitor closely.',
    routes: ['Oral', 'Intravenous', 'Intramuscular'],
    administration: [
      'Oral: May take with or without food; take in morning to avoid nocturia',
      'IV push: Give slowly over 1-2 minutes (≤4 mg/min); rapid injection risks ototoxicity',
      'IV infusion: 0.1-1 mg/mL in NS or D5W; 5-40 mg/hour continuous',
      'IM: Deep IM injection; use when IV access unavailable',
      'If second dose needed, wait at least 2 hours after IV, 4-6 hours after oral'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to furosemide or sulfonamides (cross-reactivity rare but possible)',
      'Anuria unresponsive to fluid challenge',
      'Severe hypovolemia or dehydration',
      'Hepatic coma or precoma (without concurrent potassium-sparing diuretic)'
    ],
    relative: [
      'Severe electrolyte depletion (correct before use)',
      'Hypokalemia or hyponatremia (correct or monitor closely)',
      'Gout (may precipitate acute attack)',
      'Diabetes mellitus (may worsen glycemic control)',
      'SLE (may exacerbate)',
      'Severe renal impairment (may need high doses; response reduced)',
      'Hepatic cirrhosis (use with K+-sparing diuretic to prevent encephalopathy)'
    ],
    precautions: [
      'Monitor electrolytes, especially potassium, sodium, magnesium',
      'Risk of prerenal azotemia if overdiuresed',
      'Ototoxicity risk with rapid IV administration or concurrent aminoglycosides',
      'May worsen urinary retention in prostatic hypertrophy',
      'Dehydration risk in elderly or those with limited access to fluids',
      'May exacerbate diabetes and hyperuricemia'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Hypokalemia', frequency: '10-50%', management: 'Monitor K+; supplement potassium or add K+-sparing diuretic. Encourage dietary potassium (bananas, oranges).' },
      { effect: 'Hyponatremia', frequency: '5-20%', management: 'Monitor sodium. May need to fluid restrict or reduce dose. Severe cases require hypertonic saline.' },
      { effect: 'Dehydration/hypovolemia', frequency: 'Variable', management: 'Monitor weight, BP, and clinical signs. Reduce dose or hold if overdiuresed.' },
      { effect: 'Orthostatic hypotension', frequency: '5-10%', management: 'Rise slowly; ensure adequate but not excessive hydration. May need dose reduction.' },
      { effect: 'Hyperuricemia', frequency: '10-30%', management: 'Monitor uric acid in at-risk patients. May precipitate gout. Treat gout if occurs.' },
      { effect: 'Hypochloremic alkalosis', frequency: '5-10%', management: 'Monitor chloride and bicarbonate. Usually mild; severe cases may need chloride replacement.' }
    ],
    serious: [
      { effect: 'Severe electrolyte imbalance', frequency: 'Variable', management: 'Monitor electrolytes regularly. Replace deficits. Life-threatening arrhythmias can occur with severe hypokalemia.', warning: 'Risk highest with high doses, poor oral intake, concurrent illness' },
      { effect: 'Ototoxicity (hearing loss, tinnitus)', frequency: 'Rare (<1%)', management: 'Stop or slow infusion. Usually reversible if caught early. Avoid rapid IV push.', warning: 'Risk increased with rapid IV (>4 mg/min), high doses, concurrent aminoglycosides, renal impairment' },
      { effect: 'Acute kidney injury', frequency: 'Variable', management: 'Reduce dose or hold. Ensure adequate volume status. Avoid NSAIDs.', warning: 'Usually prerenal from over-diuresis; can occur with excessive volume depletion' },
      { effect: 'Severe dehydration/shock', frequency: 'Rare', management: 'Stop diuretic. IV fluid resuscitation. Monitor hemodynamics.', warning: 'Especially in elderly or those unable to maintain oral intake' },
      { effect: 'Pancreatitis', frequency: 'Very rare', management: 'Discontinue; supportive care', warning: 'Mechanism unclear; reported with all loop diuretics' }
    ],
    rare: [
      'Agranulocytosis/thrombocytopenia',
      'Stevens-Johnson syndrome/TEN',
      'Interstitial nephritis',
      'Photosensitivity',
      'Myalgia/muscle cramps',
      'Tinnitus',
      'Paresthesias',
      'Hyperglycemia/worsened diabetes'
    ]
  },
  interactions: {
    major: [
      { drug: 'Aminoglycosides (gentamicin, amikacin)', effect: 'Additive ototoxicity and nephrotoxicity', mechanism: 'Both drugs cause cochlear and renal tubular damage', management: 'Avoid combination if possible; if necessary, monitor hearing and renal function closely' },
      { drug: 'Digoxin', effect: 'Furosemide-induced hypokalemia increases digoxin toxicity risk', mechanism: 'Hypokalemia potentiates digoxin effects on cardiac cells', management: 'Monitor K+ closely; maintain K+ >4.0 mEq/L. Monitor for digoxin toxicity signs.' },
      { drug: 'Lithium', effect: 'Increased lithium levels and toxicity', mechanism: 'Sodium depletion causes increased lithium reabsorption', management: 'Monitor lithium levels frequently; may need dose reduction' },
      { drug: 'NSAIDs', effect: 'Reduced diuretic effect; increased risk of AKI', mechanism: 'NSAIDs inhibit prostaglandin-mediated renal blood flow and sodium excretion', management: 'Avoid chronic NSAID use; if needed, monitor response and renal function' }
    ],
    moderate: [
      { drug: 'ACE inhibitors/ARBs', effect: 'Enhanced hypotension, especially first-dose; possible acute renal impairment', management: 'Start ACE-I/ARB at low dose; monitor BP and renal function' },
      { drug: 'Corticosteroids', effect: 'Enhanced potassium loss', management: 'Monitor K+ closely; may need increased supplementation' },
      { drug: 'Antihypertensives', effect: 'Additive hypotension', management: 'Monitor BP; usually beneficial combination' },
      { drug: 'Sucralfate', effect: 'Reduced furosemide absorption', management: 'Separate administration by at least 2 hours' },
      { drug: 'Phenytoin', effect: 'Reduced furosemide effect', management: 'May need higher furosemide doses' }
    ],
    food: [
      'Food may reduce rate but not extent of absorption',
      'High-sodium diet reduces diuretic effect',
      'Encourage potassium-rich foods unless hyperkalemia risk (bananas, oranges, potatoes)',
      'Licorice increases potassium loss (avoid excessive consumption)'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'Limited human data. Animal studies show fetal harm at high doses. Avoid unless essential.',
      second: 'May cause fetal electrolyte imbalances. Monitor fetal growth. Use only if benefits outweigh risks.',
      third: 'Risk of fetal electrolyte disturbance and reduced amniotic fluid. Avoid near term if possible.'
    },
    recommendation: 'Use only when clearly needed and no safer alternative. Monitor fetal wellbeing. Not recommended for routine pregnancy edema or gestational hypertension. May be used cautiously for severe heart failure or pulmonary edema.',
    lactation: {
      safe: false,
      notes: 'Excreted in breast milk. May suppress lactation by decreasing prolactin. Use only if essential; monitor infant for dehydration if breastfeeding.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Serum electrolytes (Na, K, Cl, Mg)', reason: 'Establish baseline; correct abnormalities before starting' },
      { test: 'Renal function (BUN, creatinine)', reason: 'Assess kidney function; dose may need adjustment' },
      { test: 'Blood pressure', reason: 'Risk of hypotension with diuresis' },
      { test: 'Weight', reason: 'Establish baseline for fluid status monitoring' },
      { test: 'Uric acid', reason: 'Baseline for hyperuricemia monitoring in at-risk patients' },
      { test: 'Blood glucose (in diabetics)', reason: 'May worsen glycemic control' }
    ],
    ongoing: [
      { test: 'Serum potassium', frequency: 'Daily in acute setting; weekly initially then monthly in chronic use', target: '4.0-5.0 mEq/L' },
      { test: 'Serum sodium', frequency: 'Daily in acute setting; weekly to monthly in chronic use', target: '135-145 mEq/L' },
      { test: 'Renal function', frequency: 'Daily if acute; weekly then monthly if chronic', target: 'Stable creatinine' },
      { test: 'Weight', frequency: 'Daily in acute HF; weekly in chronic management', target: 'Achieving and maintaining dry weight' },
      { test: 'Clinical volume status', frequency: 'Each visit', target: 'Euvolemia without overdiuresis' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring; titrate to clinical response (weight, edema, congestion)',
      timing: 'Assess response 1-2 hours after oral dose; 30-60 minutes after IV dose'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F) for tablets; 15-25°C for injection',
    light: 'Protect from light (especially injection); store in original container',
    humidity: 'Protect from moisture',
    specialInstructions: 'IV solution may yellow with light exposure - slight discoloration does not affect potency but discard if heavily discolored',
    shelfLife: '3-4 years for tablets; check expiry for injection'
  },
  africanContext: {
    availability: 'Widely available across Africa in both oral and injectable forms. WHO Essential Medicine. Present in most health facilities including primary care.',
    affordability: 'Very affordable; approximately $0.50-2 USD per month for oral. Injectable also low cost.',
    supplyChainNotes: 'Stable supply chain. Tablets stable in tropical conditions. Injectable requires protection from light.',
    localManufacturing: 'Manufactured locally in Nigeria (Emzor, Fidson, Swiss Pharma), Kenya (Cosmos, Dawa), South Africa (Aspen, Adcock Ingram), Egypt (multiple), and across North Africa.',
    alternatives: [
      'Bumetanide (more potent loop diuretic; less available)',
      'Torsemide (longer acting; limited availability)',
      'Hydrochlorothiazide (less potent; better for hypertension)',
      'Spironolactone (potassium-sparing; used in combination for cirrhosis and HF)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Essential for managing heart failure, which is common from hypertensive heart disease, rheumatic heart disease, and cardiomyopathies across Africa. Injectable formulation critical for acute pulmonary edema in resource-limited hospitals. Monitor electrolytes if possible - in settings with limited labs, clinical assessment (weakness, cramps, palpitations for hypokalemia) becomes essential. Encourage dietary potassium (widely available: bananas, oranges, coconut water, beans). Caution in tropical climates - patients more susceptible to dehydration; advise adequate fluid intake while on diuretic.'
  },
  patientCounseling: {
    keyPoints: [
      'Take in the morning to avoid getting up at night to urinate',
      'Expect to urinate more frequently - this is how the medicine works',
      'Weigh yourself daily if possible; report sudden weight gain (2-3 kg in a week)',
      'Eat potassium-rich foods like bananas, oranges, and beans',
      'Report muscle cramps, weakness, or irregular heartbeat',
      'Avoid excess salt in food - reduces medicine effectiveness'
    ],
    commonMisconceptions: [
      'Misconception: More urination means kidneys are damaged - Truth: Increased urination is the intended effect',
      'Misconception: Can take extra doses to lose weight - Truth: Dangerous; causes dehydration and electrolyte problems',
      'Misconception: Should limit all fluids - Truth: Maintain adequate hydration unless doctor advises otherwise',
      'Misconception: Once swelling gone, can stop medicine - Truth: Continue as prescribed to prevent fluid buildup'
    ],
    adherenceTips: [
      'Take at consistent time each morning',
      'Plan activities around increased urination in first few hours',
      'If going out, take dose after returning or take earlier in day',
      'Keep daily weight log to track fluid status',
      'Do not double dose if missed - take next dose as scheduled'
    ],
    warningSignsToReport: [
      'Severe muscle cramps or weakness',
      'Irregular or fast heartbeat',
      'Excessive thirst or dry mouth',
      'Dizziness or fainting',
      'Hearing changes or ringing in ears',
      'Little or no urine output',
      'Rapid weight gain or worsening swelling'
    ]
  },
  clinicalPearls: [
    'In acute decompensated heart failure, IV furosemide provides venodilation within minutes (before diuresis), reducing preload and relieving pulmonary congestion',
    'Double the oral dose to get equivalent IV effect (e.g., 40 mg oral ≈ 20 mg IV) due to variable oral bioavailability',
    'In diuretic resistance, strategies include: higher doses, continuous infusion, combination with thiazide (sequential nephron blockade), or addressing poor gut absorption with IV route',
    'Always combine with spironolactone in cirrhotic ascites (ratio 40:100 furosemide:spironolactone) to prevent severe hypokalemia and reduce encephalopathy risk',
    'Ototoxicity risk increases with rapid IV administration (>4 mg/min) - always give slowly. Risk additive with aminoglycosides.',
    'In severe renal impairment, higher doses are needed because less drug reaches tubular fluid, but the drug can still work',
    'Furosemide does not improve mortality in heart failure - use the lowest dose needed to maintain euvolemia. The mortality benefit comes from ACE-I/ARBs, beta-blockers, and aldosterone antagonists.',
    'Daily weights are the best guide to diuretic therapy - target 0.5-1 kg/day weight loss in fluid-overloaded patients',
    'In African settings with limited potassium monitoring, encourage dietary potassium (bananas, oranges, coconut water) and watch for clinical signs of hypokalemia (weakness, cramps, palpitations)'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'ACC/AHA Guidelines for Heart Failure 2022',
    'ESC Guidelines for Heart Failure 2023',
    'KDIGO Clinical Practice Guideline for Acute Kidney Injury 2012',
    'AASLD Practice Guidance on Ascites and Hepatorenal Syndrome 2021',
    'Pan African Society of Cardiology Heart Failure Guidelines',
    'Ellison DH, Felker GM. Diuretic Treatment in Heart Failure. N Engl J Med 2017',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'hydrochlorothiazide-025',
  genericName: 'Hydrochlorothiazide',
  brandNames: ['Microzide', 'HydroDIURIL', 'Esidrix', 'Oretic', 'Dichlotride', 'Esidrex'],
  atcCode: 'C03AA03',
  therapeuticClass: 'Thiazide Diuretic',
  pharmacology: {
    mechanism: 'Inhibits sodium-chloride cotransporter (NCC) in the distal convoluted tubule of the nephron, blocking reabsorption of approximately 5% of filtered sodium. Initial antihypertensive effect due to reduced plasma volume and cardiac output. Long-term effect involves decreased peripheral vascular resistance through unclear mechanisms, possibly involving reduced vascular smooth muscle calcium content.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability approximately 65-75%. Absorbed from duodenum. Peak plasma concentration in 1-2.5 hours. Food slightly increases absorption.',
      distribution: 'Volume of distribution 3-6 L/kg. Protein binding approximately 40-70%. Crosses placenta; excreted in breast milk.',
      metabolism: 'Not significantly metabolized. Excreted unchanged.',
      elimination: 'Primarily renal excretion (>95% unchanged). Secreted by proximal tubule. Cleared by both glomerular filtration and tubular secretion.',
      halfLife: '6-15 hours (prolonged in renal impairment)'
    },
    pharmacodynamics: 'Diuretic onset 2 hours, peak 4-6 hours, duration 6-12 hours. Antihypertensive effect develops over 2-4 weeks. At low doses (12.5-25 mg), antihypertensive effect near-maximal with minimal metabolic effects. Higher doses increase diuresis but add little to BP lowering while increasing adverse effects.'
  },
  indications: [
    {
      condition: 'Essential Hypertension',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line antihypertensive per JNC-8, NICE, and WHO guidelines. Particularly effective in elderly, Black patients, and salt-sensitive hypertension. ALLHAT trial demonstrated equal or superior outcomes to ACE-I and CCB.'
    },
    {
      condition: 'Edema (Heart Failure, Cirrhosis, Renal)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Less potent than loop diuretics; used for mild edema or as adjunct. Can enhance loop diuretic effect in resistant edema (sequential nephron blockade).'
    },
    {
      condition: 'Nephrolithiasis (Calcium Stones)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces urinary calcium excretion by 40-50%. Effective in preventing recurrent calcium oxalate and calcium phosphate stones.'
    },
    {
      condition: 'Osteoporosis',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Reduces urinary calcium loss and may preserve bone density. Considered in hypertensive patients with osteoporosis.'
    },
    {
      condition: 'Diabetes Insipidus (Nephrogenic)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: true,
      notes: 'Paradoxically reduces urine volume in nephrogenic DI by 25-50%. Mechanism involves sodium depletion and enhanced proximal reabsorption.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Hypertension: 12.5-25 mg once daily. Edema: 25-100 mg daily in single or divided doses.',
      indicationSpecific: [
        { indication: 'Hypertension', dose: 'Start 12.5-25 mg once daily; max antihypertensive effect at 25 mg; rarely need >50 mg' },
        { indication: 'Edema', dose: '25-100 mg daily in 1-2 doses; use intermittently if possible' },
        { indication: 'Calcium nephrolithiasis', dose: '25-50 mg once or twice daily' },
        { indication: 'Nephrogenic DI', dose: '50-100 mg daily in divided doses' }
      ],
      maxDose: '100 mg/day (50 mg for hypertension; higher doses add toxicity without efficacy)',
      renalImpairment: {
        mild: 'No adjustment usually needed',
        moderate: 'May have reduced efficacy; consider loop diuretic instead',
        severe: 'Generally ineffective when GFR <30 mL/min; loop diuretics preferred',
        dialysis: 'Ineffective; do not use'
      },
      hepaticImpairment: 'Use with caution in cirrhosis. Risk of hypokalemia-induced hepatic encephalopathy. Combine with spironolactone.'
    },
    pediatric: {
      standard: '1-2 mg/kg/day in 1-2 divided doses',
      weightBased: true,
      ageSpecific: [
        { age: '<6 months', dose: '1-3 mg/kg/day in 2 divided doses' },
        { age: '6 months - 2 years', dose: '1-2 mg/kg/day in 1-2 divided doses' },
        { age: '2-12 years', dose: '1-2 mg/kg/day once daily; max 37.5 mg/day' },
        { age: '>12 years', dose: 'Approach adult dosing; 12.5-50 mg once daily' }
      ],
      maxDose: '3 mg/kg/day or 50 mg/day, whichever is less',
      neonatal: '1-2 mg/kg/day in 1-2 divided doses. Monitor electrolytes closely. Used in bronchopulmonary dysplasia.'
    },
    elderly: 'Start with 12.5 mg once daily. Increased sensitivity to volume depletion and electrolyte disturbances. Particularly effective for hypertension in elderly.',
    routes: ['Oral'],
    administration: [
      'Take in the morning to avoid nocturia',
      'May take with food to reduce GI upset',
      'Consistent timing daily for stable effect',
      'If twice daily dosing, take second dose by early afternoon',
      'Tablets can be split or crushed if needed'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to hydrochlorothiazide or sulfonamide derivatives',
      'Anuria or severe renal failure (GFR <30 mL/min for hypertension)',
      'Refractory hypokalemia, hyponatremia, or hypercalcemia',
      'Symptomatic hyperuricemia/gout (relative - use with caution)'
    ],
    relative: [
      'Diabetes mellitus (may worsen glycemic control)',
      'Gout or hyperuricemia',
      'Severe hepatic impairment',
      'History of hyponatremia',
      'Systemic lupus erythematosus (may exacerbate)',
      'Hyperlipidemia (may worsen lipid profile at high doses)',
      'Sulfonamide allergy (cross-reactivity rare but reported)'
    ],
    precautions: [
      'Monitor electrolytes, especially potassium',
      'May precipitate acute gout attack',
      'Can worsen glucose tolerance - monitor in diabetics',
      'May increase lithium levels',
      'Risk of severe hyponatremia, especially in elderly women',
      'Possible increased risk of non-melanoma skin cancer with long-term use',
      'May worsen hypercalcemia'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Hypokalemia', frequency: '10-40%', management: 'Monitor K+; encourage dietary potassium; supplement if needed; consider adding ACE-I/ARB or K+-sparing diuretic' },
      { effect: 'Hyperuricemia', frequency: '10-30%', management: 'Monitor uric acid; usually asymptomatic; treat gout if occurs; consider allopurinol in recurrent gout' },
      { effect: 'Hyponatremia', frequency: '5-15%', management: 'Monitor sodium; more common in elderly women; may need to discontinue; severe cases require hospital management' },
      { effect: 'Hyperglycemia', frequency: '5-15%', management: 'Monitor glucose in diabetics; usually mild at low doses; may need diabetes medication adjustment' },
      { effect: 'Hyperlipidemia', frequency: '5-10%', management: 'Usually transient and mild at low doses; monitor lipids' },
      { effect: 'Dizziness/orthostatic hypotension', frequency: '5-10%', management: 'Rise slowly; ensure adequate hydration; may need dose reduction' }
    ],
    serious: [
      { effect: 'Severe hyponatremia', frequency: '1-2%', management: 'Discontinue; hospitalization for severe cases; careful sodium correction to avoid osmotic demyelination', warning: 'Highest risk in elderly women within first 2 weeks of therapy' },
      { effect: 'Severe hypokalemia with arrhythmias', frequency: 'Rare', management: 'IV potassium for severe cases; cardiac monitoring; discontinue thiazide', warning: 'Risk increased with concurrent digoxin use' },
      { effect: 'Acute gout attack', frequency: '2-5%', management: 'Treat gout; may need to discontinue or reduce dose; consider alternative antihypertensive', warning: 'Can occur even without prior gout history' },
      { effect: 'Acute interstitial nephritis', frequency: 'Rare', management: 'Discontinue; usually reversible; may require steroids', warning: 'Hypersensitivity reaction' },
      { effect: 'Photosensitivity reaction', frequency: 'Rare', management: 'Sun protection; may need to discontinue if severe' }
    ],
    rare: [
      'Aplastic anemia/agranulocytosis',
      'Hemolytic anemia',
      'Pancreatitis',
      'Systemic lupus exacerbation',
      'Stevens-Johnson syndrome',
      'Erectile dysfunction',
      'Non-melanoma skin cancer (with prolonged use - cumulative dose related)',
      'Jaundice/intrahepatic cholestasis',
      'Xanthopsia (yellow vision)'
    ]
  },
  interactions: {
    major: [
      { drug: 'Lithium', effect: 'Increased lithium levels and toxicity risk', mechanism: 'Reduced renal lithium clearance due to sodium depletion', management: 'Monitor lithium levels closely; often need 25-40% dose reduction' },
      { drug: 'Digoxin', effect: 'Thiazide-induced hypokalemia increases digoxin toxicity', mechanism: 'Hypokalemia potentiates digoxin cardiac effects', management: 'Monitor K+ closely; maintain K+ >4.0 mEq/L; monitor for digoxin toxicity' },
      { drug: 'NSAIDs', effect: 'Reduced diuretic and antihypertensive effect; increased AKI risk', mechanism: 'NSAIDs reduce prostaglandin-mediated renal effects', management: 'Avoid chronic NSAID use; if needed, monitor BP and renal function' },
      { drug: 'QT-prolonging drugs (amiodarone, sotalol, antipsychotics)', effect: 'Hypokalemia from thiazide increases QT prolongation and torsades risk', mechanism: 'Hypokalemia enhances QT prolongation', management: 'Monitor K+ closely; keep K+ >4.0 mEq/L; ECG monitoring' }
    ],
    moderate: [
      { drug: 'ACE inhibitors/ARBs', effect: 'Enhanced hypotension, especially first dose; reduced hypokalemia', management: 'Start ACE-I low; beneficial combination that reduces K+ loss from thiazide' },
      { drug: 'Corticosteroids', effect: 'Enhanced potassium loss', management: 'Monitor K+ more frequently; may need increased supplementation' },
      { drug: 'Antidiabetic agents', effect: 'Reduced hypoglycemic effect due to thiazide-induced hyperglycemia', management: 'Monitor blood glucose; may need antidiabetic dose increase' },
      { drug: 'Allopurinol', effect: 'Increased hypersensitivity reaction risk', management: 'Monitor for rash, fever; both drugs associated with hypersensitivity' },
      { drug: 'Calcium/Vitamin D supplements', effect: 'Increased risk of hypercalcemia', management: 'Monitor calcium levels periodically' }
    ],
    food: [
      'Food may slightly increase absorption',
      'Licorice (containing glycyrrhizin) worsens hypokalemia - avoid',
      'Encourage potassium-rich foods (bananas, oranges, potatoes) unless contraindicated',
      'High-sodium diet reduces antihypertensive effect'
    ]
  },
  pregnancy: {
    category: 'B (but D if used for pregnancy-induced hypertension)',
    trimpiesterRisks: {
      first: 'No evidence of teratogenicity. May reduce plasma volume and placental perfusion. Avoid for gestational hypertension.',
      second: 'May cause electrolyte disturbances. Risk of reduced uteroplacental perfusion. Not recommended for routine use.',
      third: 'Risk of neonatal thrombocytopenia, hyponatremia, hypokalemia. May reduce amniotic fluid. Avoid if possible.'
    },
    recommendation: 'Not recommended for hypertension in pregnancy. May be used short-term for pathological edema. Methyldopa, labetalol, or nifedipine preferred for gestational hypertension.',
    lactation: {
      safe: false,
      notes: 'Excreted in breast milk. May suppress lactation through volume depletion. Avoid if possible; if essential, monitor infant for jaundice, thrombocytopenia, electrolyte abnormalities.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Serum electrolytes (Na, K, Cl)', reason: 'Establish baseline; correct abnormalities before starting' },
      { test: 'Renal function (BUN, creatinine)', reason: 'Assess kidney function; thiazides ineffective in severe CKD' },
      { test: 'Fasting glucose', reason: 'Baseline for monitoring glucose effects' },
      { test: 'Uric acid', reason: 'Baseline for hyperuricemia monitoring' },
      { test: 'Lipid panel', reason: 'Baseline for monitoring metabolic effects' },
      { test: 'Blood pressure', reason: 'Establish baseline for response monitoring' }
    ],
    ongoing: [
      { test: 'Serum potassium', frequency: '2-4 weeks after initiation, then every 3-6 months', target: '3.5-5.0 mEq/L' },
      { test: 'Serum sodium', frequency: '2-4 weeks after initiation, then every 3-6 months', target: '135-145 mEq/L' },
      { test: 'Renal function', frequency: 'Every 6-12 months if stable', target: 'Stable creatinine' },
      { test: 'Blood pressure', frequency: 'Regularly until stable, then every visit', target: '<130/80 mmHg (individualized)' },
      { test: 'Glucose (if diabetic)', frequency: 'Per diabetes protocol', target: 'Stable glycemic control' }
    ],
    therapeutic: {
      range: 'No drug level monitoring; titrate to BP response and tolerability',
      timing: 'Antihypertensive effect maximal at 4 weeks; check BP after this interval before increasing dose'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture; keep container tightly closed',
    specialInstructions: 'Store in original container',
    shelfLife: '3-5 years depending on formulation; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa as generic. WHO Essential Medicine. One of the most accessible antihypertensives at all levels of healthcare.',
    affordability: 'Extremely affordable; often <$0.50 USD per month. Among the cheapest antihypertensive options.',
    supplyChainNotes: 'Very stable supply due to multiple manufacturers and low cost. Tablets heat-stable for tropical storage.',
    localManufacturing: 'Widely manufactured across Africa: Nigeria (Emzor, May & Baker, Fidson), South Africa (Aspen, Adcock Ingram), Kenya (Cosmos, Dawa), Egypt (multiple), Morocco, and others.',
    alternatives: [
      'Chlorthalidone (longer acting thiazide-like; limited availability)',
      'Indapamide (thiazide-like; available in some markets)',
      'Furosemide (loop diuretic for more potent diuresis)',
      'Spironolactone (potassium-sparing; for resistant hypertension)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Particularly effective in Black African populations with salt-sensitive, low-renin hypertension. First-line choice per WHO HEARTS protocol for cardiovascular disease management in primary care. Low cost makes it ideal for resource-limited settings. However, electrolyte monitoring may be limited - educate patients about dietary potassium and symptoms of hypokalemia (weakness, cramps). In tropical climates with high sweating and diarrheal illness, patients on thiazides are at increased risk of volume depletion - advise on fluid intake during illness. Fix-dose combinations with ACE-I or ARB increasingly available and improve adherence.'
  },
  patientCounseling: {
    keyPoints: [
      'Take in the morning to avoid getting up at night to urinate',
      'This medicine lowers blood pressure even when you feel well - do not stop taking it',
      'Reduce salt in your diet for better blood pressure control',
      'Eat foods high in potassium (bananas, oranges, beans, potatoes)',
      'Protect your skin from strong sun - increased sensitivity to sunburn',
      'Tell your doctor if you have gout or develop joint pain'
    ],
    commonMisconceptions: [
      'Misconception: Once blood pressure normal, can stop medicine - Truth: Continue lifelong; stopping causes BP to rise again',
      'Misconception: This is a "water pill" so must drink less water - Truth: Drink adequate fluids unless doctor advises otherwise',
      'Misconception: Medicine makes you weak so should not exercise - Truth: Exercise is beneficial; medicine may cause initial tiredness',
      'Misconception: High blood pressure always has symptoms - Truth: Often no symptoms; take medicine even when feeling fine'
    ],
    adherenceTips: [
      'Take at same time each morning',
      'Link to daily routine like breakfast',
      'Use pill box if taking multiple medicines',
      'Do not double dose if missed; take next dose as scheduled',
      'Keep supply at home - do not run out'
    ],
    warningSignsToReport: [
      'Severe muscle cramps or weakness',
      'Irregular heartbeat or palpitations',
      'Extreme thirst with confusion (may be low sodium)',
      'Sudden joint pain, especially big toe (may be gout)',
      'Severe skin rash or sun reaction',
      'Dizziness or fainting'
    ]
  },
  clinicalPearls: [
    'Low-dose HCTZ (12.5-25 mg) provides near-maximal antihypertensive effect with minimal metabolic side effects - higher doses add little BP benefit but increase hypokalemia, hyperuricemia, and hyperglycemia',
    'Particularly effective in Black African patients with salt-sensitive, low-renin hypertension - consider as first-line in this population per evidence and guidelines',
    'Combining HCTZ with ACE-I or ARB reduces hypokalemia risk while providing additive BP lowering - a rational and widely-used combination',
    'Watch for severe hyponatremia in elderly women, especially in first 2 weeks - can present as confusion, falls, or seizures',
    'May be added to loop diuretic for resistant edema ("sequential nephron blockade") - very effective but requires close electrolyte monitoring',
    'Does not work well when GFR <30 mL/min - switch to loop diuretic in advanced CKD',
    'Reduces urinary calcium excretion by 40-50% - beneficial in osteoporosis and calcium stone formers, but can worsen hypercalcemia',
    'Fixed-dose combination pills (HCTZ + ACE-I/ARB or CCB) are increasingly available in Africa and greatly improve adherence - consider using when available',
    'ALLHAT trial showed thiazide (chlorthalidone) equal or superior to ACE-I (lisinopril) and CCB (amlodipine) for cardiovascular outcomes - supports first-line use',
    'In African primary care settings with limited lab access, clinical monitoring for hypokalemia symptoms (weakness, cramps) and encouraging dietary potassium become especially important'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'WHO HEARTS Technical Package for Cardiovascular Disease Management 2016',
    'ALLHAT Officers and Coordinators. JAMA 2002 - Major outcomes in high-risk hypertensive patients',
    'JNC 8 Hypertension Guideline. JAMA 2014',
    'NICE Guideline NG136: Hypertension in adults 2019 (updated 2022)',
    'ESC Guidelines on Arterial Hypertension 2023',
    'Pan African Society of Cardiology Hypertension Guidelines 2020',
    'Sica DA. Diuretic-related side effects. J Clin Hypertens 2004',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'aspirin-026',
  genericName: 'Aspirin (Acetylsalicylic Acid)',
  brandNames: ['Disprin', 'Ecotrin', 'Bayer Aspirin', 'Aspro', 'Cartia', 'Micropirin', 'Nu-Seals'],
  atcCode: 'B01AC06',
  therapeuticClass: 'Antiplatelet Agent / Non-Steroidal Anti-Inflammatory Drug (NSAID)',
  pharmacology: {
    mechanism: 'Irreversibly inhibits cyclooxygenase-1 (COX-1) and COX-2 enzymes by acetylation of serine residues. In platelets, this prevents thromboxane A2 (TXA2) synthesis, inhibiting platelet aggregation for the platelet lifespan (7-10 days). At higher doses, inhibits prostaglandin synthesis in tissues, producing analgesic, antipyretic, and anti-inflammatory effects.',
    pharmacokinetics: {
      absorption: 'Rapidly absorbed from stomach and upper small intestine. Bioavailability 40-50% due to first-pass hydrolysis. Enteric-coated forms absorbed in small intestine with delayed but complete absorption. Peak levels in 1-2 hours (4-6 hours for enteric-coated).',
      distribution: 'Volume of distribution 0.1-0.2 L/kg. Protein binding concentration-dependent: 90% at low doses, decreasing at higher concentrations. Crosses placenta and blood-brain barrier.',
      metabolism: 'Rapidly hydrolyzed to salicylic acid (active metabolite) by esterases in GI mucosa, plasma, and liver. Salicylic acid further metabolized by conjugation (saturable at high doses).',
      elimination: 'Renal excretion of salicylic acid and metabolites. Elimination half-life dose-dependent due to saturable metabolism.',
      halfLife: 'Aspirin: 15-20 minutes; Salicylic acid: 2-3 hours (low dose) to 15-30 hours (high dose/toxic)'
    },
    pharmacodynamics: 'Antiplatelet effect occurs within 1 hour of ingestion and persists for platelet lifespan (7-10 days) due to irreversible COX-1 inhibition. Analgesic/antipyretic effect onset 30-60 minutes, duration 4-6 hours. Anti-inflammatory effect requires higher doses and several days for full effect.'
  },
  indications: [
    {
      condition: 'Secondary Prevention of Cardiovascular Events (Post-MI, Post-Stroke, Established CVD)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces risk of recurrent MI, stroke, and cardiovascular death by 20-25%. Cornerstone of secondary prevention. Continue indefinitely unless contraindicated.'
    },
    {
      condition: 'Acute Coronary Syndrome (STEMI, NSTEMI, Unstable Angina)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Give immediately on presentation (chew 162-325 mg non-enteric). Reduces mortality by 23%. Continue with P2Y12 inhibitor as dual antiplatelet therapy.'
    },
    {
      condition: 'Acute Ischemic Stroke',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Start within 24-48 hours of onset (after excluding hemorrhage). IST and CAST trials showed reduced death and recurrence.'
    },
    {
      condition: 'Primary Prevention of CVD (High-Risk Patients)',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'Benefits must outweigh bleeding risk. Consider in high 10-year CVD risk (>10%) without increased bleeding risk. Not routinely recommended in low-moderate risk.'
    },
    {
      condition: 'Post-Percutaneous Coronary Intervention (PCI) / Coronary Stenting',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Essential component of dual antiplatelet therapy (DAPT) with P2Y12 inhibitor. Duration depends on stent type and bleeding risk.'
    },
    {
      condition: 'Atrial Fibrillation (When Anticoagulation Not Suitable)',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'Inferior to anticoagulation for stroke prevention in AF. Consider only when anticoagulants contraindicated or refused.'
    },
    {
      condition: 'Mild to Moderate Pain, Fever',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Effective analgesic/antipyretic at 325-650 mg doses. Paracetamol often preferred due to better GI safety profile.'
    },
    {
      condition: 'Kawasaki Disease',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'High-dose aspirin (80-100 mg/kg/day) during acute phase, then low-dose (3-5 mg/kg/day) for antiplatelet effect. Given with IVIG.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Antiplatelet (CVD prevention): 75-100 mg once daily. Acute MI/stroke: Loading 162-325 mg chewed, then 75-100 mg daily. Analgesia: 325-650 mg every 4-6 hours.',
      indicationSpecific: [
        { indication: 'Secondary CVD prevention', dose: '75-100 mg once daily indefinitely' },
        { indication: 'Acute coronary syndrome', dose: 'Loading 162-325 mg chewed immediately, then 75-100 mg daily' },
        { indication: 'Acute ischemic stroke', dose: '160-325 mg within 48 hours, then 75-100 mg daily' },
        { indication: 'Post-PCI (with P2Y12 inhibitor)', dose: '75-100 mg daily as part of DAPT' },
        { indication: 'Primary prevention (if indicated)', dose: '75-100 mg once daily' },
        { indication: 'Analgesia/antipyretic', dose: '325-650 mg every 4-6 hours as needed' },
        { indication: 'Anti-inflammatory (rarely used)', dose: '3-6 g/day in divided doses' }
      ],
      maxDose: 'Antiplatelet: 100 mg/day; Analgesia: 4 g/day; Anti-inflammatory: 6 g/day (rarely used)',
      renalImpairment: {
        mild: 'No adjustment for low-dose antiplatelet use',
        moderate: 'Use with caution; avoid high analgesic doses',
        severe: 'Avoid if possible; increased bleeding and toxicity risk',
        dialysis: 'Avoid; not effectively removed by dialysis'
      },
      hepaticImpairment: 'Avoid in severe hepatic impairment. Increased bleeding risk due to coagulopathy. Use with caution in mild-moderate impairment.'
    },
    pediatric: {
      standard: 'Antiplatelet: 1-5 mg/kg/day. Kawasaki (acute): 80-100 mg/kg/day divided every 6 hours. Kawasaki (maintenance): 3-5 mg/kg/day once daily.',
      weightBased: true,
      ageSpecific: [
        { age: 'Kawasaki disease (acute phase)', dose: '80-100 mg/kg/day divided every 6 hours until afebrile 48-72 hours' },
        { age: 'Kawasaki disease (maintenance)', dose: '3-5 mg/kg/day once daily for 6-8 weeks or longer if coronary abnormalities' },
        { age: 'Antiplatelet (cardiac indications)', dose: '1-5 mg/kg/day once daily' },
        { age: 'Juvenile rheumatoid arthritis', dose: '60-100 mg/kg/day in divided doses (rarely used now)' }
      ],
      maxDose: '100 mg/kg/day for Kawasaki acute phase; 5 mg/kg/day for antiplatelet',
      neonatal: 'Generally avoided in neonates. If essential for cardiac indications, 1-5 mg/kg/day with close monitoring.'
    },
    elderly: 'Standard antiplatelet dosing (75-100 mg). Higher bleeding risk - assess carefully. Avoid high analgesic doses.',
    routes: ['Oral', 'Rectal (suppository)'],
    administration: [
      'Low-dose antiplatelet: Take with food to reduce GI irritation',
      'Acute MI/ACS: Chew non-enteric coated tablet for rapid absorption',
      'Enteric-coated tablets should be swallowed whole, not crushed or chewed',
      'For analgesia, may take on empty stomach for faster effect but with increased GI risk',
      'Consistent daily timing for antiplatelet use'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to aspirin or NSAIDs',
      'Active peptic ulcer disease or GI bleeding',
      'Aspirin-exacerbated respiratory disease (aspirin-induced asthma)',
      'Severe bleeding disorders (hemophilia, thrombocytopenia)',
      'Third trimester of pregnancy',
      'Children/adolescents with viral illness (Reye syndrome risk)'
    ],
    relative: [
      'History of GI ulceration or bleeding',
      'Concurrent anticoagulant therapy (increased bleeding)',
      'Uncontrolled hypertension',
      'Gout (may increase uric acid at low doses)',
      'G6PD deficiency (hemolysis risk at high doses)',
      'Asthma (may trigger bronchospasm in sensitive patients)',
      'Pre-operative period (discontinue 7-10 days before major surgery if safe)',
      'First and second trimester pregnancy (use only if clearly needed)'
    ],
    precautions: [
      'Monitor for GI bleeding signs (black stools, abdominal pain)',
      'Consider PPI co-prescription in high GI risk patients',
      'Assess bleeding risk before starting',
      'Avoid in children with fever of unknown etiology',
      'Increased bleeding with alcohol use',
      'May mask signs of infection (antipyretic effect)'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Dyspepsia/GI upset', frequency: '10-30%', management: 'Take with food; consider enteric-coated formulation; add PPI if needed' },
      { effect: 'Increased bleeding time', frequency: 'Universal (therapeutic)', management: 'Expected effect; inform patients about bruising and bleeding precautions' },
      { effect: 'Bruising', frequency: '5-10%', management: 'Usually minor; advise patients this is expected' },
      { effect: 'Nausea', frequency: '5-10%', management: 'Take with food; usually mild' },
      { effect: 'Tinnitus (at high doses)', frequency: 'Variable', management: 'Indicates salicylate toxicity; reduce dose' }
    ],
    serious: [
      { effect: 'GI bleeding (peptic ulcer, hemorrhage)', frequency: '1-4% annually', management: 'Stop aspirin if severe; PPI, endoscopy, blood transfusion as needed. Add PPI prophylaxis in high-risk patients.', warning: 'Risk increased with age >65, H. pylori, concurrent NSAIDs/steroids/anticoagulants, prior ulcer' },
      { effect: 'Intracranial hemorrhage', frequency: '0.1-0.4% annually', management: 'Stop aspirin; neurosurgical evaluation; reversal with platelet transfusion if severe', warning: 'Risk increased with concurrent anticoagulation, uncontrolled hypertension' },
      { effect: 'Aspirin-exacerbated respiratory disease', frequency: 'Rare (10-20% of asthmatics with nasal polyps)', management: 'Avoid aspirin permanently. Emergency treatment for bronchospasm if occurs.', warning: 'Classic triad: asthma, nasal polyps, aspirin sensitivity' },
      { effect: 'Reye syndrome', frequency: 'Very rare', management: 'Supportive ICU care; often fatal. Prevention: avoid aspirin in children with viral illness.', warning: 'Associated with aspirin use in children during viral infections (varicella, influenza)' },
      { effect: 'Severe allergic reaction/anaphylaxis', frequency: 'Rare', management: 'Stop aspirin; epinephrine, antihistamines, steroids as needed', warning: 'Cross-reactivity with other NSAIDs possible' }
    ],
    rare: [
      'Hemolytic anemia (G6PD deficiency)',
      'Thrombocytopenia',
      'Hepatotoxicity (high doses)',
      'Nephrotoxicity (high doses, chronic use)',
      'Salicylism (chronic high-dose: tinnitus, hearing loss, vertigo, confusion)',
      'Angioedema',
      'Stevens-Johnson syndrome'
    ]
  },
  interactions: {
    major: [
      { drug: 'Anticoagulants (warfarin, heparin, DOACs)', effect: 'Significantly increased bleeding risk', mechanism: 'Additive effects on hemostasis', management: 'Use combination only when clearly indicated (e.g., mechanical valve + CAD). Monitor closely; lower aspirin dose (75-100 mg).' },
      { drug: 'Other antiplatelet agents (clopidogrel, ticagrelor)', effect: 'Increased bleeding risk but often therapeutic goal', mechanism: 'Synergistic antiplatelet effects', management: 'Used intentionally as DAPT post-ACS/PCI. Monitor for bleeding; use appropriate duration.' },
      { drug: 'Methotrexate', effect: 'Increased methotrexate toxicity', mechanism: 'Aspirin reduces renal methotrexate clearance and displaces from protein binding', management: 'Avoid combination with high-dose methotrexate; monitor methotrexate levels if unavoidable' },
      { drug: 'NSAIDs (ibuprofen, naproxen)', effect: 'Ibuprofen may reduce antiplatelet effect; increased GI bleeding', mechanism: 'Ibuprofen competitively inhibits aspirin binding to COX-1 if taken before aspirin', management: 'Take aspirin 30-60 minutes before ibuprofen, or use alternative analgesic. Avoid chronic NSAID use.' }
    ],
    moderate: [
      { drug: 'ACE inhibitors/ARBs', effect: 'May reduce antihypertensive effect; increased renal risk at high aspirin doses', management: 'Low-dose aspirin (≤100 mg) has minimal effect; monitor BP' },
      { drug: 'Diuretics', effect: 'Reduced diuretic effect at high aspirin doses', management: 'Low-dose aspirin unlikely to interfere; monitor if using analgesic doses' },
      { drug: 'SSRIs', effect: 'Increased GI bleeding risk', management: 'Consider PPI co-prescription' },
      { drug: 'Corticosteroids', effect: 'Increased GI ulcer and bleeding risk', management: 'Add PPI prophylaxis; monitor for GI symptoms' },
      { drug: 'Valproic acid', effect: 'Increased valproic acid levels and toxicity', management: 'Monitor valproic acid levels' }
    ],
    food: [
      'Food reduces GI irritation but may delay absorption',
      'Alcohol increases GI bleeding risk significantly - limit intake',
      'Vitamin E supplements may enhance antiplatelet effect',
      'Ginkgo biloba may increase bleeding risk'
    ]
  },
  pregnancy: {
    category: 'C (1st/2nd trimester); D (3rd trimester)',
    trimpiesterRisks: {
      first: 'Low-dose aspirin (75-150 mg) used for preeclampsia prevention in high-risk women. Conflicting data on teratogenicity with analgesic doses.',
      second: 'Low-dose generally considered safe. Analgesic doses may affect fetal renal function and amniotic fluid.',
      third: 'CONTRAINDICATED. Risk of premature ductus arteriosus closure, oligohydramnios, prolonged labor, increased maternal/fetal bleeding.'
    },
    recommendation: 'Low-dose aspirin (75-150 mg) recommended starting at 12-16 weeks for preeclampsia prevention in high-risk women. Avoid analgesic doses throughout pregnancy. Discontinue all aspirin by 36 weeks (or earlier if cesarean planned).',
    lactation: {
      safe: true,
      notes: 'Low-dose aspirin (≤100 mg/day) compatible with breastfeeding. Occasional analgesic doses likely safe. Avoid high or chronic doses - risk of Reye syndrome in infant with viral illness.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Assessment of bleeding risk', reason: 'HAS-BLED score or similar to weigh benefits vs risks' },
      { test: 'Hemoglobin/hematocrit', reason: 'Baseline for monitoring occult GI blood loss' },
      { test: 'Renal function', reason: 'Caution in renal impairment, especially at higher doses' },
      { test: 'GI symptom assessment', reason: 'Identify pre-existing peptic disease' }
    ],
    ongoing: [
      { test: 'Signs of bleeding (clinical)', frequency: 'Each visit', target: 'No major bleeding' },
      { test: 'Hemoglobin (if symptomatic)', frequency: 'As needed; annually if asymptomatic', target: 'Stable hemoglobin' },
      { test: 'Stool occult blood', frequency: 'Annually or if GI symptoms', target: 'Negative' },
      { test: 'GI symptoms assessment', frequency: 'Each visit', target: 'No dyspepsia, melena, or hematemesis' }
    ],
    therapeutic: {
      range: 'No routine monitoring of aspirin levels for antiplatelet use. Salicylate levels monitored only in suspected toxicity.',
      timing: 'Antiplatelet effect present within 1 hour; full platelet recovery 7-10 days after stopping'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light and moisture',
    humidity: 'Store in dry place; moisture causes hydrolysis and vinegar odor',
    specialInstructions: 'Discard if tablets have strong vinegar smell (indicates degradation to acetic acid and salicylic acid)',
    shelfLife: '2-3 years; check expiry date. Degraded aspirin less effective.'
  },
  africanContext: {
    availability: 'Universally available across Africa. WHO Essential Medicine. Present at all levels of healthcare, including community pharmacies and primary care.',
    affordability: 'Extremely affordable; often <$0.50 USD per month for low-dose formulation. One of the most cost-effective cardiovascular interventions.',
    supplyChainNotes: 'Very stable supply. However, tablets sensitive to moisture and heat - proper storage important in tropical climates. Vinegar smell indicates degradation.',
    localManufacturing: 'Manufactured in virtually all African countries with pharmaceutical capacity: Nigeria (Emzor, May & Baker, Evans), South Africa (Aspen, Adcock Ingram), Kenya, Egypt, Morocco, and many others.',
    alternatives: [
      'Clopidogrel (if aspirin allergic/intolerant)',
      'Dipyridamole (rarely used alone)',
      'Paracetamol (for analgesia/antipyresis, but no antiplatelet effect)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial). Note: "Aspirin resistance" refers to variable antiplatelet response in some patients.',
    endemicConsiderations: 'Essential for secondary CVD prevention, which is increasingly important as cardiovascular disease rises across Africa. Extremely cost-effective - among the best value interventions in medicine. In settings with limited diagnostic capacity, empiric aspirin for suspected ACS/stroke can be life-saving while arranging transfer. Caution with widespread paracetamol + aspirin combinations marketed for pain - patients may unknowingly take aspirin. Avoid in children with fever from malaria, dengue, or other viral illness due to Reye syndrome and bleeding risks. H. pylori prevalence high in Africa - consider testing/treating before long-term aspirin to reduce GI bleeding risk.'
  },
  patientCounseling: {
    keyPoints: [
      'Take low-dose aspirin every day at the same time to protect your heart',
      'Take with food to reduce stomach upset',
      'Do not stop taking without consulting your doctor - increases heart attack/stroke risk',
      'Report any signs of bleeding: black stools, blood in vomit, unusual bruising',
      'Tell doctors and dentists you take aspirin before any procedure',
      'Avoid giving aspirin to children with fever or viral illness'
    ],
    commonMisconceptions: [
      'Misconception: Aspirin is just for pain, can stop anytime - Truth: Low-dose aspirin protects heart; stopping suddenly is dangerous',
      'Misconception: Enteric coating prevents all stomach problems - Truth: Reduces local irritation but systemic bleeding risk remains',
      'Misconception: Can take aspirin and ibuprofen together freely - Truth: Ibuprofen may block aspirin effect; avoid or take aspirin first',
      'Misconception: More aspirin is better for heart - Truth: Low dose (75-100 mg) is optimal; higher doses add bleeding without benefit'
    ],
    adherenceTips: [
      'Take at same time daily - link to a routine (e.g., with breakfast)',
      'Keep in original container away from moisture',
      'Do not double up if missed; take next dose as scheduled',
      'Carry card or wear bracelet indicating aspirin use',
      'Get refills before running out - do not stop abruptly'
    ],
    warningSignsToReport: [
      'Black, tarry, or bloody stools',
      'Vomiting blood or material that looks like coffee grounds',
      'Unusual or prolonged bleeding from cuts',
      'Severe stomach pain',
      'Ringing in ears or hearing changes',
      'Severe headache or confusion (may indicate intracranial bleeding)',
      'Difficulty breathing or wheezing (may indicate aspirin sensitivity)'
    ]
  },
  clinicalPearls: [
    'For acute MI/ACS, have patient CHEW non-enteric aspirin for fastest absorption - enteric coating delays effect by hours',
    'Low-dose aspirin (75-100 mg) provides near-maximal antiplatelet effect - higher doses increase bleeding without additional CV benefit',
    'Aspirin "resistance" affects 5-40% of patients but routine testing not recommended; clinical outcomes matter more than lab tests',
    'Ibuprofen given before aspirin can block aspirin binding to COX-1 - if both needed, take aspirin 30-60 min before ibuprofen or use different NSAID',
    'PPI co-prescription reduces GI bleeding by 60-80% in high-risk patients - consider for all patients >65 years or with GI risk factors',
    'In Africa, test and treat H. pylori before initiating long-term aspirin when feasible - reduces ulcer and bleeding risk',
    'Stopping aspirin abruptly in established CVD increases MI/stroke risk within 7-10 days - only stop for life-threatening bleeding or essential surgery',
    'Enteric coating does NOT reduce risk of GI hemorrhage - it only reduces local irritation. Systemic COX inhibition still causes bleeding.',
    'Aspirin for primary prevention is controversial - 2018-2019 trials (ARRIVE, ASPREE, ASCEND) showed smaller benefits than expected. Reserve for truly high-risk patients.',
    'Cost-effectiveness of aspirin for secondary CVD prevention is exceptional - among the best value interventions in global health'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Antithrombotic Trialists Collaboration. Lancet 2009 - Aspirin in primary and secondary prevention',
    'ISIS-2 Collaborative Group. Lancet 1988 - Aspirin in acute MI',
    'CAST and IST trials - Aspirin in acute ischemic stroke',
    'ACC/AHA Guidelines on Primary Prevention of CVD 2019',
    'ESC Guidelines on Chronic Coronary Syndromes 2019',
    'USPSTF Recommendation on Aspirin for CVD Prevention 2022',
    'Bhatt DL et al. NEJM 2006 - Clopidogrel and aspirin (CHARISMA)',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'simvastatin-027',
  genericName: 'Simvastatin',
  brandNames: ['Zocor', 'Simvacor', 'Simvor', 'Lipostat', 'Simcard', 'Simvatin'],
  atcCode: 'C10AA01',
  therapeuticClass: 'HMG-CoA Reductase Inhibitor (Statin)',
  pharmacology: {
    mechanism: 'Prodrug converted to active beta-hydroxyacid form in liver. Competitively inhibits 3-hydroxy-3-methylglutaryl coenzyme A (HMG-CoA) reductase, the rate-limiting enzyme in cholesterol biosynthesis. Reduces hepatic cholesterol synthesis, upregulating LDL receptors and increasing LDL clearance from circulation. Also has pleiotropic effects: improves endothelial function, reduces inflammation, and stabilizes atherosclerotic plaques.',
    pharmacokinetics: {
      absorption: 'Well absorbed (60-85%) but extensive first-pass extraction limits bioavailability to <5% of active inhibitors. Food does not significantly affect absorption. Peak plasma concentration in 1-2 hours.',
      distribution: 'Volume of distribution not well characterized due to extensive hepatic uptake. Highly protein bound (>95%). Does not significantly cross blood-brain barrier.',
      metabolism: 'Extensively metabolized by CYP3A4 in liver and intestinal wall. Active metabolite (beta-hydroxyacid) and several other metabolites formed. CYP3A4 interactions clinically significant.',
      elimination: 'Primarily biliary/fecal excretion (60%); 13% renal. Active metabolites have half-lives of 1.9-3 hours.',
      halfLife: 'Parent drug: 1-3 hours; Active inhibitors: 1.9-3 hours. However, due to hepatic accumulation, lipid-lowering effect persists with once-daily dosing.'
    },
    pharmacodynamics: 'LDL reduction begins within 1-2 weeks, maximal effect at 4-6 weeks. LDL reduction dose-dependent but not linear (doubling dose adds ~6% more reduction). At 40 mg: LDL reduction 35-40%, triglycerides 15-20%, HDL increase 5-10%.'
  },
  indications: [
    {
      condition: 'Hypercholesterolemia (Primary/Heterozygous Familial)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for LDL reduction. For high-intensity needs, atorvastatin or rosuvastatin may be preferred.'
    },
    {
      condition: 'Secondary Prevention of Cardiovascular Disease',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: '4S trial demonstrated 30% reduction in total mortality, 42% reduction in coronary deaths. Foundation of post-MI, post-stroke, and PAD management.'
    },
    {
      condition: 'Primary Prevention in High-Risk Patients',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Indicated in diabetes, high ASCVD risk score, or elevated LDL despite lifestyle. HPS trial showed benefit even with "normal" cholesterol.'
    },
    {
      condition: 'Mixed Dyslipidemia',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces LDL and triglycerides; modestly increases HDL.'
    },
    {
      condition: 'Chronic Kidney Disease with Dyslipidemia',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'SHARP trial showed benefit. Reduces cardiovascular events in CKD. Dose adjustment not required but monitor for myopathy.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Start 10-20 mg once daily in evening. Usual range 10-40 mg daily. Maximum 40 mg daily (80 mg only in patients already tolerating it long-term).',
      indicationSpecific: [
        { indication: 'Moderate-intensity therapy', dose: '10-20 mg once daily in evening' },
        { indication: 'High-intensity therapy (if used)', dose: '40 mg once daily in evening; do not initiate at 80 mg' },
        { indication: 'With amlodipine', dose: 'Maximum 20 mg simvastatin daily' },
        { indication: 'With diltiazem/verapamil', dose: 'Maximum 10 mg simvastatin daily' },
        { indication: 'With amiodarone', dose: 'Maximum 20 mg simvastatin daily' },
        { indication: 'With ranolazine', dose: 'Maximum 20 mg simvastatin daily' }
      ],
      maxDose: '40 mg/day for new patients; 80 mg only in those already on it long-term without myopathy',
      renalImpairment: {
        mild: 'No adjustment required',
        moderate: 'No adjustment required; monitor for myopathy',
        severe: 'Start 5-10 mg; maximum 10 mg if CrCl <30 mL/min; use with caution',
        dialysis: 'Start 5-10 mg; use with caution; monitor for myopathy'
      },
      hepaticImpairment: 'Contraindicated in active liver disease or unexplained persistent transaminase elevations. Use with caution in mild impairment.'
    },
    pediatric: {
      standard: 'Heterozygous familial hypercholesterolemia: 10-40 mg once daily in evening',
      weightBased: false,
      ageSpecific: [
        { age: '10-17 years (HeFH)', dose: 'Start 10 mg once daily in evening; max 40 mg daily' },
        { age: '<10 years', dose: 'Not established; specialist referral recommended' }
      ],
      maxDose: '40 mg/day in adolescents with HeFH',
      neonatal: 'Not indicated in neonates'
    },
    elderly: 'No specific dose adjustment, but increased myopathy risk. Start at lower doses and monitor closely.',
    routes: ['Oral'],
    administration: [
      'Take in the evening (hepatic cholesterol synthesis peaks overnight)',
      'May be taken with or without food',
      'Swallow tablets whole',
      'Avoid grapefruit juice (CYP3A4 inhibition increases simvastatin levels)',
      'If dose missed, take next dose as scheduled - do not double'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to simvastatin or any component',
      'Active liver disease or unexplained persistent elevations of serum transaminases (>3x ULN)',
      'Pregnancy and breastfeeding',
      'Concomitant use of strong CYP3A4 inhibitors (itraconazole, ketoconazole, posaconazole, HIV protease inhibitors, erythromycin, clarithromycin, telithromycin, nefazodone, cobicistat)',
      'Concomitant use of gemfibrozil, cyclosporine, or danazol'
    ],
    relative: [
      'History of statin-induced myopathy',
      'Predisposition to myopathy (hypothyroidism, renal impairment, elderly, Asian ancestry)',
      'Heavy alcohol use (hepatotoxicity risk)',
      'Concurrent medications requiring dose limitation (amlodipine, diltiazem, verapamil, amiodarone)',
      'Chinese patients taking ≥1 g/day niacin (increased myopathy risk)'
    ],
    precautions: [
      'Monitor for muscle symptoms at each visit',
      'Check baseline LFTs; repeat if symptoms of hepatotoxicity',
      'Women of childbearing potential must use effective contraception',
      'Discontinue if CK >10x ULN or myopathy diagnosed',
      'Temporarily hold if acute serious condition predisposing to myopathy (sepsis, hypotension, major surgery, trauma, severe metabolic/endocrine disorders)',
      'Higher myopathy risk with 80 mg dose - avoid initiating at this dose'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Headache', frequency: '3-7%', management: 'Usually mild and transient; symptomatic treatment' },
      { effect: 'GI disturbances (constipation, dyspepsia, nausea)', frequency: '2-5%', management: 'Usually mild; take with food; fiber for constipation' },
      { effect: 'Myalgia (without CK elevation)', frequency: '2-10%', management: 'Check CK if symptomatic. If normal CK and tolerable, may continue. Consider dose reduction or switch to different statin.' },
      { effect: 'Upper respiratory infection', frequency: '2-5%', management: 'Symptomatic treatment; likely coincidental' },
      { effect: 'Elevated transaminases', frequency: '1-2%', management: 'Usually transient; if >3x ULN persistent, discontinue and investigate' }
    ],
    serious: [
      { effect: 'Rhabdomyolysis', frequency: 'Rare (0.01-0.1%)', management: 'Stop statin immediately. Aggressive IV hydration. Monitor CK, renal function, electrolytes. May need dialysis for severe acute kidney injury.', warning: 'Risk highest with 80 mg dose, drug interactions (CYP3A4 inhibitors), and predisposing conditions' },
      { effect: 'Myopathy (CK >10x ULN with symptoms)', frequency: '0.1-0.5%', management: 'Discontinue simvastatin. Monitor CK until normalized. May cautiously try different statin at low dose once recovered.', warning: 'More common at high doses and with interacting drugs' },
      { effect: 'Hepatotoxicity', frequency: 'Rare (<0.1%)', management: 'Discontinue if ALT >3x ULN persistent or with symptoms. Usually reversible.', warning: 'Serious hepatic failure very rare but reported' },
      { effect: 'New-onset diabetes mellitus', frequency: '0.1-0.5% additional risk', management: 'CV benefits outweigh diabetes risk. Monitor glucose. Manage diabetes if develops.', warning: 'Higher risk with intensive statin therapy and predisposing factors' },
      { effect: 'Immune-mediated necrotizing myopathy (IMNM)', frequency: 'Very rare', management: 'Discontinue statin; immunosuppressive therapy may be needed; specialist referral', warning: 'Can occur or worsen after statin discontinuation; associated with anti-HMGCR antibodies' }
    ],
    rare: [
      'Cognitive impairment (usually reversible)',
      'Peripheral neuropathy',
      'Interstitial lung disease',
      'Lupus-like syndrome',
      'Dermatomyositis',
      'Tendon rupture',
      'Depression',
      'Sleep disturbances',
      'Erectile dysfunction'
    ]
  },
  interactions: {
    major: [
      { drug: 'Strong CYP3A4 inhibitors (itraconazole, ketoconazole, HIV protease inhibitors, clarithromycin, erythromycin)', effect: 'Dramatically increased simvastatin levels and myopathy/rhabdomyolysis risk', mechanism: 'CYP3A4 inhibition prevents simvastatin metabolism', management: 'CONTRAINDICATED. Use alternative statin (pravastatin, rosuvastatin) or alternative antifungal/antibiotic.' },
      { drug: 'Gemfibrozil', effect: 'Increased myopathy risk by 10-15 fold', mechanism: 'Inhibition of glucuronidation and OATP1B1 transport', management: 'CONTRAINDICATED. If fibrate needed, use fenofibrate with caution at low simvastatin dose.' },
      { drug: 'Cyclosporine', effect: 'Markedly increased simvastatin levels', mechanism: 'CYP3A4 and OATP1B1 inhibition', management: 'CONTRAINDICATED. Use pravastatin or low-dose fluvastatin if statin needed.' },
      { drug: 'Amlodipine', effect: 'Increased simvastatin exposure', mechanism: 'Weak CYP3A4 inhibition', management: 'Limit simvastatin to 20 mg/day maximum when combined' },
      { drug: 'Diltiazem/Verapamil', effect: 'Increased simvastatin levels and myopathy risk', mechanism: 'CYP3A4 inhibition', management: 'Limit simvastatin to 10 mg/day maximum' },
      { drug: 'Amiodarone', effect: 'Increased myopathy risk', mechanism: 'CYP3A4 inhibition', management: 'Limit simvastatin to 20 mg/day maximum' }
    ],
    moderate: [
      { drug: 'Grapefruit juice', effect: 'Increased simvastatin levels', management: 'Avoid large quantities (>1 quart/day); small amounts unlikely to be clinically significant' },
      { drug: 'Warfarin', effect: 'Possible increased INR', management: 'Monitor INR when initiating or changing simvastatin dose' },
      { drug: 'Digoxin', effect: 'Slight increase in digoxin levels', management: 'Monitor digoxin levels when initiating simvastatin' },
      { drug: 'Colchicine', effect: 'Increased myopathy risk', management: 'Use combination with caution; monitor for muscle symptoms' },
      { drug: 'Niacin (≥1 g/day)', effect: 'Increased myopathy risk', management: 'Limit simvastatin to 20 mg daily; higher risk in Chinese patients' }
    ],
    food: [
      'Grapefruit and grapefruit juice - avoid large amounts (inhibits CYP3A4)',
      'Food does not significantly affect absorption',
      'Red yeast rice contains natural statins - avoid concurrent use'
    ]
  },
  pregnancy: {
    category: 'X',
    trimpiesterRisks: {
      first: 'CONTRAINDICATED. Cholesterol essential for fetal development. Theoretical risk of congenital anomalies based on mechanism.',
      second: 'CONTRAINDICATED. Continue to avoid due to essential role of cholesterol in fetal development.',
      third: 'CONTRAINDICATED. No indication for use; fetal risks outweigh any benefit.'
    },
    recommendation: 'Absolutely contraindicated in pregnancy. Discontinue immediately if pregnancy occurs or is planned. Women of childbearing potential must use effective contraception. If lipid-lowering essential in pregnancy, bile acid sequestrants are preferred.',
    lactation: {
      safe: false,
      notes: 'Contraindicated during breastfeeding. Unknown if excreted in human milk. Potential for serious adverse effects in nursing infant. Discontinue drug or discontinue breastfeeding.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Fasting lipid panel', reason: 'Establish baseline LDL, HDL, triglycerides for treatment goals' },
      { test: 'Liver function tests (ALT, AST)', reason: 'Rule out liver disease before starting; establish baseline' },
      { test: 'CK', reason: 'Baseline for comparison if muscle symptoms develop (optional if asymptomatic)' },
      { test: 'Fasting glucose or HbA1c', reason: 'Baseline for monitoring diabetes risk' },
      { test: 'TSH (if not recently checked)', reason: 'Hypothyroidism increases myopathy risk and causes dyslipidemia' }
    ],
    ongoing: [
      { test: 'Fasting lipid panel', frequency: '4-12 weeks after initiation or dose change, then every 6-12 months', target: 'LDL reduction ≥30-50% or absolute LDL goal based on risk category' },
      { test: 'ALT/AST', frequency: 'If symptoms of hepatotoxicity develop; routine monitoring no longer required', target: '<3x ULN' },
      { test: 'CK', frequency: 'Only if muscle symptoms develop', target: '<10x ULN with symptoms necessitates discontinuation' },
      { test: 'Muscle symptoms (clinical)', frequency: 'Every visit', target: 'No unexplained muscle pain, tenderness, or weakness' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring. Target based on LDL reduction or absolute LDL goal.',
      timing: 'Recheck lipids 4-12 weeks after initiation or dose change to assess response'
    }
  },
  storage: {
    temperature: '5-30°C (41-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Keep in original container with desiccant if present',
    shelfLife: '2-3 years from manufacture; check expiry date'
  },
  africanContext: {
    availability: 'Widely available as generic across Africa. WHO Essential Medicine. Present in most public health facilities and pharmacies.',
    affordability: 'Affordable as generic; approximately $1-5 USD per month. Patent-expired, enabling low-cost production.',
    supplyChainNotes: 'Stable supply due to multiple generic manufacturers. Relatively heat-stable for tropical storage.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, Fidson), South Africa (Aspen, Adcock Ingram), Kenya, Egypt, Morocco, and imported from India.',
    alternatives: [
      'Atorvastatin (higher potency; similar availability)',
      'Rosuvastatin (higher potency; less CYP3A4 interaction; variable availability)',
      'Pravastatin (fewer drug interactions; limited availability)',
      'Fluvastatin (limited availability)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Cardiovascular disease rising rapidly across Africa due to urbanization, dietary changes, and demographic transition. Statins underutilized despite evidence of benefit. Simvastatin cost-effective for secondary prevention - should be prioritized in post-MI, stroke, and diabetic patients. Note that Chinese and other Asian patients may have higher myopathy risk - start at lower doses. Limited access to lipid testing in some settings - consider empiric treatment in high-risk patients (diabetes, established CVD) without baseline lipid panel if unavailable. Drug interactions common - be aware of concurrent use of calcium channel blockers and other interacting medications prevalent in African practice.'
  },
  patientCounseling: {
    keyPoints: [
      'Take in the evening for best effect (cholesterol production highest at night)',
      'Continue taking even if you feel well - the medicine prevents heart disease',
      'Report any unexplained muscle pain, tenderness, or weakness immediately',
      'Avoid grapefruit and grapefruit juice in large amounts',
      'Women must not become pregnant while taking this medicine - use contraception',
      'Limit alcohol intake to reduce liver side effects'
    ],
    commonMisconceptions: [
      'Misconception: Can stop once cholesterol normal - Truth: Cholesterol will rise again; continue indefinitely',
      'Misconception: All muscle aches mean dangerous side effect - Truth: Many aches unrelated; report them but doctor will assess',
      'Misconception: Statins damage liver - Truth: Serious liver damage very rare; benefits far outweigh risks',
      'Misconception: Diet alone enough if on statin - Truth: Healthy diet essential alongside medication'
    ],
    adherenceTips: [
      'Take at same time every evening',
      'Link to bedtime routine',
      'Use pill organizer if taking multiple medicines',
      'Set phone reminder',
      'Do not stop because of minor side effects - discuss with doctor first'
    ],
    warningSignsToReport: [
      'Unexplained muscle pain, tenderness, or weakness (especially with fever or malaise)',
      'Dark or brown urine (may indicate muscle breakdown)',
      'Yellowing of skin or eyes',
      'Severe fatigue or weakness',
      'Upper abdominal pain with nausea',
      'Pregnancy or plan to become pregnant'
    ]
  },
  clinicalPearls: [
    'Take in evening - hepatic cholesterol synthesis peaks at night; LDL reduction 30-40% at 40 mg',
    'Do NOT initiate at 80 mg dose - FDA warning due to high myopathy risk at this dose. 80 mg only for patients already tolerating it long-term.',
    'Critical drug interactions: max 20 mg with amlodipine, max 10 mg with diltiazem/verapamil, max 20 mg with amiodarone - these are common in cardiology practice!',
    'Simvastatin extensively metabolized by CYP3A4 - many drug interactions. Consider atorvastatin, rosuvastatin, or pravastatin if patient on interacting drugs.',
    'For high-intensity statin therapy (>50% LDL reduction), atorvastatin 40-80 mg or rosuvastatin 20-40 mg preferred over simvastatin',
    'Myopathy risk increases exponentially above 40 mg and with interacting drugs - the "rule of 6" (avoid exposures >6-fold baseline)',
    'CV benefit is class effect of statins - switch to alternative if intolerant rather than abandoning therapy',
    'Asian patients may have higher myopathy risk with simvastatin - consider lower starting dose or alternative statin',
    'New-onset diabetes with statins is real but modest (1 case per 255 patients treated for 4 years) - CV benefits clearly outweigh this risk',
    'In African settings with limited drug monitoring, clinical vigilance for muscle symptoms is essential - educate all patients about warning signs'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Scandinavian Simvastatin Survival Study (4S). Lancet 1994',
    'Heart Protection Study (HPS). Lancet 2002',
    'SHARP Collaborative Group. Lancet 2011 - Statins in CKD',
    'ACC/AHA Guideline on the Management of Blood Cholesterol 2018',
    'ESC/EAS Guidelines for the Management of Dyslipidaemias 2019',
    'FDA Drug Safety Communication: Simvastatin dose limitation 2011',
    'Collins R et al. Lancet 2016 - Interpretation of statin evidence',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'digoxin-028',
  genericName: 'Digoxin',
  brandNames: ['Lanoxin', 'Digitek', 'Digox', 'Toloxin', 'Cardioxin', 'Eudigox'],
  atcCode: 'C01AA05',
  therapeuticClass: 'Cardiac Glycoside',
  pharmacology: {
    mechanism: 'Inhibits sodium-potassium ATPase pump in cardiac myocytes, increasing intracellular sodium and subsequently intracellular calcium via sodium-calcium exchanger. Results in positive inotropic effect (increased contractility). Also increases vagal tone and decreases conduction through AV node, slowing ventricular rate in atrial fibrillation. Additional neurohormonal effects include reduced sympathetic activity and renin secretion.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability 60-80% (tablets), 70-85% (elixir), nearly 100% (capsules). Absorption reduced by high-fiber meals, antacids, kaolin-pectin. Peak levels 1-3 hours oral.',
      distribution: 'Large volume of distribution (5-7 L/kg) due to extensive tissue binding, especially to skeletal muscle. Crosses placenta. Low protein binding (20-30%). Distributes slowly; tissue equilibration takes 6-8 hours.',
      metabolism: 'Minimal hepatic metabolism (16%). Some patients have gut flora (Eubacterium lentum) that inactivates digoxin - antibiotic treatment can increase levels.',
      elimination: 'Primarily renal excretion (60-80% unchanged). Elimination correlates with creatinine clearance. Non-renal clearance by biliary excretion and metabolism.',
      halfLife: '36-48 hours (normal renal function); prolonged to 4-6 days in anuria'
    },
    pharmacodynamics: 'Onset: oral 1-2 hours, IV 5-30 minutes. Peak effect: oral 4-6 hours, IV 1-4 hours. Duration depends on elimination (half-life). Therapeutic effects include: reduced heart rate in AF, improved cardiac output in HF (modest). Narrow therapeutic index - toxic effects occur close to therapeutic range.'
  },
  indications: [
    {
      condition: 'Atrial Fibrillation - Ventricular Rate Control',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Effective for rate control at rest but not during exercise. Now second-line after beta-blockers or CCBs. Still useful in HF with AF (beta-blockers may not be tolerated), sedentary patients.'
    },
    {
      condition: 'Heart Failure with Reduced Ejection Fraction (HFrEF)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'DIG trial showed no mortality benefit but reduced hospitalizations. Consider for persistent symptoms despite optimal GDMT. Does NOT improve mortality - use for symptom control.'
    },
    {
      condition: 'Heart Failure with Atrial Fibrillation',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Dual benefit of rate control and symptom improvement. Useful when beta-blockers not tolerated.'
    },
    {
      condition: 'Supraventricular Tachycardia (SVT)',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'Less commonly used now. May be considered for rate control. Not effective for conversion to sinus rhythm.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Loading (if needed): 0.75-1.5 mg over 24 hours in divided doses. Maintenance: 0.125-0.25 mg once daily. Target serum level 0.5-1.0 ng/mL.',
      indicationSpecific: [
        { indication: 'AF rate control (rapid loading)', dose: 'IV: 0.25-0.5 mg, then 0.25 mg every 6 hours until rate controlled or 1-1.5 mg total. Oral: 0.5 mg, then 0.25 mg every 6 hours' },
        { indication: 'AF rate control (gradual)', dose: '0.125-0.25 mg daily; steady state in 7-10 days' },
        { indication: 'Heart failure (maintenance)', dose: '0.125-0.25 mg once daily; lower doses (0.0625-0.125 mg) often sufficient' },
        { indication: 'Combination with beta-blocker for AF', dose: 'Lower dose often adequate (0.125 mg daily)' }
      ],
      maxDose: '0.25 mg/day in most patients; 0.5 mg/day rarely indicated. Target trough level 0.5-1.0 ng/mL.',
      renalImpairment: {
        mild: 'CrCl 50-80 mL/min: 0.125-0.25 mg daily',
        moderate: 'CrCl 25-50 mL/min: 0.0625-0.125 mg daily (or 0.125 mg every other day)',
        severe: 'CrCl 10-25 mL/min: 0.0625 mg daily or every other day; monitor levels closely',
        dialysis: '0.0625-0.125 mg after dialysis on dialysis days (not significantly removed)'
      },
      hepaticImpairment: 'No specific dose adjustment for liver disease (minimal hepatic metabolism). However, hepatic congestion in heart failure may increase digoxin levels.'
    },
    pediatric: {
      standard: 'Highly variable by age. Total digitalizing dose (TDD) given as 50% initially, then 25% x 2 at 8-12 hour intervals.',
      weightBased: true,
      ageSpecific: [
        { age: 'Premature neonates', dose: 'TDD: 15-25 mcg/kg IV; Maintenance: 4-6 mcg/kg/day divided every 12h' },
        { age: 'Full-term neonates', dose: 'TDD: 20-30 mcg/kg IV (25-35 mcg/kg PO); Maintenance: 5-8 mcg/kg/day divided every 12h' },
        { age: '1-24 months', dose: 'TDD: 30-50 mcg/kg IV (35-60 mcg/kg PO); Maintenance: 8-10 mcg/kg/day divided every 12h' },
        { age: '2-5 years', dose: 'TDD: 25-35 mcg/kg IV (30-45 mcg/kg PO); Maintenance: 8-10 mcg/kg/day divided every 12h' },
        { age: '5-10 years', dose: 'TDD: 15-30 mcg/kg IV (20-35 mcg/kg PO); Maintenance: 5-8 mcg/kg/day' },
        { age: '>10 years', dose: 'TDD: 8-12 mcg/kg IV (10-15 mcg/kg PO); Maintenance: 2.5-5 mcg/kg/day' }
      ],
      maxDose: 'Varies by age; monitor levels',
      neonatal: 'Premature: TDD 15-25 mcg/kg IV. Term: TDD 20-30 mcg/kg IV. Maintenance 4-8 mcg/kg/day. Neonates very sensitive; monitor closely.'
    },
    elderly: 'Start 0.0625-0.125 mg daily. Elderly have reduced muscle mass (smaller Vd), reduced renal function, and increased sensitivity. Target lower therapeutic levels.',
    routes: ['Oral', 'Intravenous'],
    administration: [
      'Oral: May take with or without food, but avoid high-fiber meals and antacids at same time',
      'IV: Give over at least 5 minutes (risk of vasoconstriction with rapid push)',
      'IM route not recommended (pain, erratic absorption)',
      'Tablets and elixir not bioequivalent - do not interchange without dose adjustment',
      'Wait at least 6 hours after dose before measuring serum level (distribution phase)'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to digoxin or digitalis glycosides',
      'Ventricular fibrillation',
      'Hypertrophic obstructive cardiomyopathy (HOCM) unless concurrent AF',
      'Wolff-Parkinson-White syndrome with atrial fibrillation (digoxin can accelerate conduction through accessory pathway)',
      'Second or third-degree AV block without pacemaker'
    ],
    relative: [
      'Sick sinus syndrome without pacemaker',
      'Carotid sinus syndrome',
      'Severe renal impairment (dose reduction required)',
      'Electrolyte abnormalities (hypokalemia, hypomagnesemia, hypercalcemia - correct before using)',
      'Acute myocarditis',
      'Restrictive cardiomyopathy',
      'Constrictive pericarditis',
      'Amyloid heart disease'
    ],
    precautions: [
      'Maintain potassium 4.0-5.0 mEq/L (hypokalemia increases digoxin toxicity)',
      'Maintain magnesium in normal range',
      'Hypothyroidism decreases digoxin clearance',
      'Hyperthyroidism increases digoxin clearance',
      'Many drug interactions - review medication list',
      'Electrical cardioversion: lower energy levels and caution in digitalized patients',
      'Narrow therapeutic index - toxicity common'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Nausea/vomiting', frequency: '5-20%', management: 'Check digoxin level; may indicate toxicity. If level therapeutic, give with food.' },
      { effect: 'Anorexia', frequency: '5-10%', management: 'May be early sign of toxicity; check level' },
      { effect: 'Dizziness', frequency: '3-5%', management: 'Check level; assess for bradycardia' },
      { effect: 'Fatigue', frequency: '3-5%', management: 'Check level and heart rate' },
      { effect: 'Visual disturbances (blurred vision, yellow-green halos)', frequency: '2-5%', management: 'Classic sign of toxicity; check level immediately and hold drug' }
    ],
    serious: [
      { effect: 'Digoxin toxicity / Cardiac arrhythmias', frequency: '2-5%', management: 'Hold digoxin. Correct electrolytes (K+, Mg2+). For life-threatening arrhythmias: Digoxin-specific antibody fragments (Digibind/DigiFab). Temporary pacing for bradycardia.', warning: 'Hypokalemia greatly increases toxicity risk. Almost any arrhythmia possible: bradycardia, AV block, atrial tachycardia with block, ventricular ectopy, VT, VF.' },
      { effect: 'Bradycardia / AV block', frequency: '2-5%', management: 'Hold digoxin. Atropine for symptomatic bradycardia. Temporary pacing if needed.', warning: 'Risk increased with concurrent beta-blockers, CCBs, or amiodarone' },
      { effect: 'Ventricular arrhythmias (PVCs, VT, VF)', frequency: '1-3%', management: 'Stop digoxin. Correct potassium/magnesium. Lidocaine or phenytoin for VT (avoid cardioversion if possible). Digoxin antibodies for severe toxicity.', warning: 'Bidirectional ventricular tachycardia pathognomonic for digoxin toxicity' },
      { effect: 'Atrial tachycardia with AV block', frequency: '1-2%', management: 'Classic "digitalis toxic" rhythm. Hold digoxin; correct electrolytes; supportive care.', warning: 'Paradoxically may present as increased heart rate despite toxic levels' }
    ],
    rare: [
      'Gynecomastia (with chronic use)',
      'Thrombocytopenia',
      'Intestinal ischemia/necrosis',
      'Altered mental status/confusion',
      'Delirium (especially in elderly)',
      'Skin rash',
      'Eosinophilia'
    ]
  },
  interactions: {
    major: [
      { drug: 'Amiodarone', effect: 'Increases digoxin levels by 70-100%', mechanism: 'Inhibits P-glycoprotein and reduces renal clearance', management: 'Reduce digoxin dose by 50% when starting amiodarone; monitor levels' },
      { drug: 'Quinidine', effect: 'Doubles digoxin levels', mechanism: 'Inhibits P-glycoprotein and renal tubular secretion', management: 'Reduce digoxin dose by 50%; monitor levels closely' },
      { drug: 'Verapamil', effect: 'Increases digoxin levels by 50-75%', mechanism: 'Inhibits P-glycoprotein', management: 'Reduce digoxin dose; avoid combination if possible for rate control' },
      { drug: 'Dronedarone', effect: 'Increases digoxin levels significantly', mechanism: 'P-glycoprotein inhibition', management: 'Reduce digoxin dose by 50%' },
      { drug: 'Potassium-depleting diuretics', effect: 'Hypokalemia increases digoxin toxicity', mechanism: 'Low K+ increases digoxin binding to Na-K-ATPase', management: 'Monitor and maintain K+ 4.0-5.0 mEq/L; supplement as needed' },
      { drug: 'Erythromycin/clarithromycin/tetracycline', effect: 'Can increase digoxin levels in some patients', mechanism: 'Kill gut bacteria (Eubacterium lentum) that inactivate digoxin', management: 'Monitor for toxicity; may need dose reduction' }
    ],
    moderate: [
      { drug: 'Beta-blockers', effect: 'Additive bradycardia and AV nodal suppression', management: 'Monitor heart rate; often beneficial combination for rate control' },
      { drug: 'Diltiazem', effect: 'Additive bradycardia; slight increase in digoxin levels', management: 'Monitor heart rate and digoxin levels' },
      { drug: 'Spironolactone', effect: 'May increase digoxin levels; interferes with some assays', management: 'Monitor levels; use assay that does not cross-react' },
      { drug: 'NSAIDs', effect: 'May increase digoxin levels by reducing renal clearance', management: 'Monitor levels, especially in patients with impaired renal function' },
      { drug: 'Antacids, cholestyramine, kaolin-pectin', effect: 'Reduced digoxin absorption', management: 'Separate administration by at least 2 hours' }
    ],
    food: [
      'High-fiber meals may reduce absorption - take consistently with respect to meals',
      'St. Johns Wort reduces digoxin levels (P-glycoprotein induction) - avoid',
      'Hawthorn may increase digoxin effects - avoid or use with caution'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'Limited data. No clear teratogenic effect. Use if maternal benefit justifies fetal risk.',
      second: 'Crosses placenta. Can be used for maternal or fetal arrhythmias. Monitor maternal levels.',
      third: 'Digoxin used for maternal AF and fetal SVT. Crosses placenta effectively. Maternal levels guide fetal dosing.'
    },
    recommendation: 'Can be used in pregnancy when indicated for maternal arrhythmias or heart failure. Also used to treat fetal SVT. Monitor maternal digoxin levels; pregnancy increases renal clearance, may need dose adjustment.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in low concentrations. Considered compatible with breastfeeding. Infant exposure low. Monitor infant for feeding difficulties if concerned.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Serum creatinine/eGFR', reason: 'Primarily renal elimination; dose adjustment needed' },
      { test: 'Serum potassium and magnesium', reason: 'Hypokalemia/hypomagnesemia increase toxicity risk' },
      { test: 'ECG', reason: 'Assess rhythm, conduction, and baseline for comparison' },
      { test: 'Thyroid function', reason: 'Hypo/hyperthyroidism affects digoxin clearance and sensitivity' }
    ],
    ongoing: [
      { test: 'Serum digoxin level', frequency: 'Steady state (7-10 days after initiation or dose change), then periodically; check if toxicity suspected', target: '0.5-1.0 ng/mL (lower range preferred)' },
      { test: 'Serum potassium', frequency: 'Regularly, especially if on diuretics', target: '4.0-5.0 mEq/L' },
      { test: 'Serum creatinine', frequency: 'Every 3-6 months; more often if unstable', target: 'Stable kidney function' },
      { test: 'Heart rate/pulse', frequency: 'Every visit; patient self-monitoring recommended', target: 'Resting HR 60-80 in AF; no symptomatic bradycardia' },
      { test: 'ECG', frequency: 'As clinically indicated; if symptoms of toxicity', target: 'No toxic arrhythmias' }
    ],
    therapeutic: {
      range: '0.5-1.0 ng/mL (previously 0.8-2.0, but lower levels safer and equally effective). Higher levels increase mortality without benefit.',
      timing: 'Draw level at least 6-8 hours after dose (ideally trough, just before next dose) due to prolonged distribution phase.'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Store in original container. Do not interchange brands without prescriber consultation.',
    shelfLife: '2-3 years from manufacture; check expiry date'
  },
  africanContext: {
    availability: 'Available in most African countries but not always consistently. WHO Essential Medicine. May have availability gaps in some regions.',
    affordability: 'Relatively affordable as generic; approximately $1-5 USD per month.',
    supplyChainNotes: 'Variable supply chain stability. Some countries have periodic shortages. Heat and humidity stable within recommended range.',
    localManufacturing: 'Limited local manufacturing; mainly imported. Available from Indian generics manufacturers. Some production in South Africa and Egypt.',
    alternatives: [
      'Beta-blockers (atenolol, metoprolol) for AF rate control',
      'Verapamil or diltiazem for AF rate control',
      'No direct alternative for inotropic effect in HF'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Rheumatic heart disease with AF common in Africa - digoxin valuable for rate control, especially when beta-blockers not tolerated due to concurrent HF. Limited access to serum digoxin level monitoring in many settings - clinical monitoring (pulse rate, symptoms) becomes essential. Hypokalemia from malnutrition, diarrheal disease, and diuretic use increases toxicity risk - monitor and supplement potassium aggressively. Traditional medicine use common - some herbal remedies contain cardiac glycosides (e.g., Calotropis, oleander) that can cause additive toxicity. Educate patients about narrow therapeutic index and importance of taking exactly as prescribed.'
  },
  patientCounseling: {
    keyPoints: [
      'Take exactly as prescribed - small changes in dose can cause problems',
      'Check your pulse daily; report if consistently <60 or irregular',
      'Take at the same time each day, with or without food',
      'Do not stop taking without consulting your doctor',
      'Report nausea, vomiting, loss of appetite, visual changes, or confusion immediately',
      'Keep all appointments for blood tests to monitor medicine levels'
    ],
    commonMisconceptions: [
      'Misconception: Can adjust dose based on how I feel - Truth: Dose must be exact; small changes dangerous',
      'Misconception: If one pill good, two better - Truth: Never take extra; very dangerous',
      'Misconception: Herbal medicines safe to take with digoxin - Truth: Some herbs very dangerous with digoxin; always ask doctor first',
      'Misconception: Side effects mean medicine working - Truth: Nausea, vision changes may indicate toxicity'
    ],
    adherenceTips: [
      'Take at same time daily',
      'Use pill box to avoid double dosing',
      'If miss dose, take as soon as remembered unless almost time for next dose',
      'Never take two doses together',
      'Keep medicine in original container'
    ],
    warningSignsToReport: [
      'Nausea, vomiting, or loss of appetite',
      'Visual disturbances (blurred vision, yellow-green halos around lights)',
      'Pulse consistently below 60 beats per minute',
      'New or worsening irregular heartbeat',
      'Confusion, weakness, or feeling unwell',
      'Diarrhea (can lower potassium and increase toxicity)'
    ]
  },
  clinicalPearls: [
    'Target digoxin level 0.5-1.0 ng/mL - DIG trial post-hoc analysis showed higher levels (>1.0) increase mortality without benefit',
    'Wait at least 6 hours after oral dose to draw serum level - prolonged distribution phase causes falsely high levels if drawn too soon',
    'Hypokalemia is the most important modifiable factor for digoxin toxicity - always maintain K+ 4.0-5.0 mEq/L',
    'Digoxin provides rate control at REST but not during exercise - beta-blockers or CCBs needed for exertional rate control',
    'In HF, digoxin reduces hospitalizations but NOT mortality - use for symptom control after optimizing GDMT',
    'Classic digoxin toxicity arrhythmia: atrial tachycardia with AV block, or bidirectional VT - pathognomonic',
    'The "reverse checkmark" ST depression (sagging ST) is a digoxin EFFECT, not toxicity - do not confuse with ischemia',
    'When starting amiodarone, reduce digoxin by 50% empirically - levels double predictably',
    'Digibind (digoxin immune Fab) is life-saving for severe toxicity but expensive and not always available in Africa - prevention through careful dosing and monitoring is critical',
    'In African settings without level monitoring, maintain low doses (0.125 mg or less), ensure potassium supplementation, and monitor pulse and symptoms closely'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Digitalis Investigation Group (DIG) Trial. N Engl J Med 1997',
    'Rathore SS et al. JAMA 2003 - Association of serum digoxin concentration and mortality',
    'ACC/AHA Guidelines for Heart Failure 2022',
    'ESC Guidelines for the Diagnosis and Treatment of AF 2020',
    'Bauman JL et al. Clin Pharmacokinet 2006 - Digoxin Clinical Pharmacology',
    'AFFIRM Investigators. NEJM 2002 - Rate control vs rhythm control in AF',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'metformin-029',
  genericName: 'Metformin',
  brandNames: ['Glucophage', 'Glucophage XR', 'Fortamet', 'Glumetza', 'Riomet', 'Diabex', 'Diaformin'],
  atcCode: 'A10BA02',
  therapeuticClass: 'Biguanide Antihyperglycemic Agent',
  pharmacology: {
    mechanism: 'Reduces hepatic gluconeogenesis (primary effect), improves insulin sensitivity in peripheral tissues (muscle, adipose), and decreases intestinal glucose absorption. Does not stimulate insulin secretion from pancreatic beta cells, hence does not cause hypoglycemia when used alone. Also activates AMP-activated protein kinase (AMPK), improves lipid profiles, and may have cardiovascular protective effects beyond glucose lowering.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability 50-60% (immediate-release), absorbed mainly in small intestine. Peak levels 2-3 hours (IR), 4-8 hours (XR). Food decreases rate but not extent of absorption.',
      distribution: 'Volume of distribution 654 ± 358 L. Negligible protein binding (<1%). Distributed widely; concentrates in GI tract, liver, kidneys. Does not cross blood-brain barrier significantly.',
      metabolism: 'Not metabolized. Excreted unchanged.',
      elimination: 'Primarily renal excretion via tubular secretion (90%) and glomerular filtration. Renal clearance approximately 450-540 mL/min (far exceeds GFR, indicating tubular secretion).',
      halfLife: '4-9 hours (plasma), but effective half-life longer due to tissue accumulation; 17-18 hours for elimination from erythrocytes'
    },
    pharmacodynamics: 'Glucose-lowering effect begins within days, maximal at 2-3 weeks. Reduces fasting glucose by 20-30% and HbA1c by 1-1.5%. Weight-neutral or slight weight loss (vs weight gain with sulfonylureas/insulin). Modest improvements in lipid profile (reduces triglycerides 10-20%, LDL 5-10%).'
  },
  indications: [
    {
      condition: 'Type 2 Diabetes Mellitus',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line pharmacologic therapy per ADA, EASD, and WHO guidelines. UKPDS showed CV mortality benefit in overweight patients. Continue unless contraindicated or not tolerated.'
    },
    {
      condition: 'Prediabetes / Diabetes Prevention',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'DPP trial showed 31% diabetes risk reduction vs placebo. Consider in high-risk individuals, especially if lifestyle modifications insufficient.'
    },
    {
      condition: 'Polycystic Ovary Syndrome (PCOS)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Improves ovulation, menstrual regularity, and metabolic parameters. Not first-line for fertility but useful for metabolic features.'
    },
    {
      condition: 'Gestational Diabetes (when insulin not preferred)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Alternative to insulin in some guidelines. Crosses placenta. Consider if insulin not feasible or refused.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Start 500 mg once or twice daily with meals. Increase by 500 mg weekly to minimize GI side effects. Usual effective dose 1500-2000 mg/day. Maximum 2550 mg/day (IR) or 2000 mg/day (XR).',
      indicationSpecific: [
        { indication: 'Type 2 diabetes (immediate release)', dose: 'Start 500 mg twice daily with meals; increase by 500 mg/week; target 2000 mg/day in divided doses' },
        { indication: 'Type 2 diabetes (extended release)', dose: 'Start 500-1000 mg once daily with evening meal; increase by 500 mg weekly; max 2000 mg once daily' },
        { indication: 'Prediabetes/prevention', dose: '850 mg twice daily (as used in DPP trial) or 500-1000 mg twice daily' },
        { indication: 'PCOS', dose: '500 mg three times daily or 850 mg twice daily; titrate to tolerance' }
      ],
      maxDose: '2550 mg/day (immediate release), 2000 mg/day (extended release)',
      renalImpairment: {
        mild: 'eGFR 45-60 mL/min/1.73m²: No dose adjustment; monitor renal function every 3-6 months',
        moderate: 'eGFR 30-45 mL/min/1.73m²: Maximum 1000 mg/day; do not initiate if eGFR <45; monitor renal function every 3 months',
        severe: 'eGFR <30 mL/min/1.73m²: CONTRAINDICATED',
        dialysis: 'CONTRAINDICATED (risk of lactic acidosis)'
      },
      hepaticImpairment: 'Avoid in patients with hepatic impairment due to risk of lactic acidosis. Liver metabolizes lactate; impaired liver function increases risk.'
    },
    pediatric: {
      standard: 'For type 2 diabetes in children ≥10 years: Start 500 mg twice daily, increase by 500 mg weekly; max 2000 mg/day',
      weightBased: false,
      ageSpecific: [
        { age: '10-16 years', dose: 'Start 500 mg twice daily with meals; increase weekly; max 2000 mg/day in divided doses' },
        { age: '≥17 years', dose: 'Adult dosing' },
        { age: '<10 years', dose: 'Not approved; limited data' }
      ],
      maxDose: '2000 mg/day in children',
      neonatal: 'Not indicated in neonates'
    },
    elderly: 'Start at lowest dose (500 mg daily). Titrate cautiously. Monitor renal function regularly as age-related decline affects metformin clearance. Avoid in those with eGFR <30.',
    routes: ['Oral'],
    administration: [
      'Take with meals to reduce GI side effects',
      'Immediate-release: usually twice or three times daily with meals',
      'Extended-release: take once daily with evening meal; swallow whole, do not crush or chew',
      'Titrate slowly (weekly increases) to minimize GI intolerance',
      'If GI intolerance persists, try extended-release formulation'
    ]
  },
  contraindications: {
    absolute: [
      'eGFR <30 mL/min/1.73m² (high lactic acidosis risk)',
      'Acute or chronic metabolic acidosis, including diabetic ketoacidosis',
      'Known hypersensitivity to metformin',
      'Acute conditions with potential for hypoxia (decompensated heart failure, respiratory failure, recent MI, sepsis)',
      'Severe hepatic impairment'
    ],
    relative: [
      'eGFR 30-45 mL/min/1.73m² (use with caution; reduced dose)',
      'History of lactic acidosis',
      'Heavy alcohol use (impairs lactate metabolism)',
      'Planned use of iodinated contrast (hold before procedure)',
      'Acute illness with risk of dehydration or hypoxia',
      'Elderly with reduced renal reserve'
    ],
    precautions: [
      'Hold before iodinated contrast procedures if eGFR <60; restart 48 hours after if renal function stable',
      'Hold during acute illness with dehydration, vomiting, or infection risk',
      'Hold 48 hours before elective surgery requiring general anesthesia',
      'Monitor vitamin B12 levels with long-term use (malabsorption)',
      'Warn patients about symptoms of lactic acidosis',
      'Monitor renal function at least annually, more often if borderline'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Diarrhea', frequency: '10-50%', management: 'Take with food; start low and titrate slowly; try extended-release formulation' },
      { effect: 'Nausea/vomiting', frequency: '10-25%', management: 'Take with meals; reduce dose; slow titration; often improves over weeks' },
      { effect: 'Abdominal discomfort/bloating', frequency: '10-20%', management: 'Take with meals; gradual dose increases; may improve with continued use' },
      { effect: 'Flatulence', frequency: '10-15%', management: 'Usually improves with time; dietary modification may help' },
      { effect: 'Metallic taste', frequency: '3-5%', management: 'Usually transient; try different formulation' },
      { effect: 'Decreased appetite', frequency: '5-10%', management: 'May contribute to weight neutrality; usually mild' }
    ],
    serious: [
      { effect: 'Lactic acidosis', frequency: 'Rare (2-9 per 100,000 patient-years)', management: 'Stop metformin immediately. Supportive care; IV fluids, correct acidosis. Hemodialysis effectively removes metformin and lactate.', warning: 'Risk factors: renal impairment, hepatic disease, sepsis, hypoxia, dehydration, excessive alcohol, iodinated contrast. Mortality high (50%) if occurs.' },
      { effect: 'Vitamin B12 deficiency', frequency: '5-30% with long-term use', management: 'Screen B12 periodically (every 2-3 years). Supplement if deficient. Consider in patients with neuropathy.', warning: 'May worsen or mimic diabetic neuropathy' },
      { effect: 'Severe hypoglycemia (when combined with insulin/sulfonylureas)', frequency: 'Variable', management: 'Glucose tablets/gel; IV dextrose if severe. Reduce sulfonylurea/insulin dose when adding metformin.', warning: 'Metformin alone rarely causes hypoglycemia; risk is with combination therapy' }
    ],
    rare: [
      'Megaloblastic anemia (from B12 deficiency)',
      'Hepatotoxicity (very rare)',
      'Pancreatitis (very rare)',
      'Photosensitivity',
      'Rash/urticaria'
    ]
  },
  interactions: {
    major: [
      { drug: 'Iodinated contrast media', effect: 'Increased risk of contrast-induced nephropathy and subsequent lactic acidosis', mechanism: 'Contrast can cause acute kidney injury, impairing metformin clearance', management: 'Hold metformin before contrast if eGFR <60; restart 48h after if renal function stable. If eGFR ≥60, can continue but reassess after.' },
      { drug: 'Alcohol (chronic/excessive)', effect: 'Increased lactic acidosis risk', mechanism: 'Alcohol inhibits gluconeogenesis and lactate metabolism', management: 'Advise moderate alcohol intake; avoid binge drinking; caution in alcohol use disorder' },
      { drug: 'Cationic drugs (cimetidine, ranolazine, vandetanib, dolutegravir)', effect: 'Increased metformin levels', mechanism: 'Competition for renal tubular secretion', management: 'Monitor for metformin side effects; may need dose adjustment' }
    ],
    moderate: [
      { drug: 'Carbonic anhydrase inhibitors (topiramate, acetazolamide)', effect: 'Increased lactic acidosis risk', management: 'Monitor closely; use combination with caution' },
      { drug: 'ACE inhibitors/ARBs', effect: 'May improve glycemic control; monitor for hypoglycemia with insulin/sulfonylureas', management: 'Generally beneficial combination; monitor blood glucose' },
      { drug: 'Beta-blockers', effect: 'May mask hypoglycemia symptoms (when combined with insulin/SU)', management: 'Educate patient about subtle hypoglycemia signs' },
      { drug: 'Corticosteroids', effect: 'May counteract metformin effect by raising glucose', management: 'Monitor glucose; may need increased diabetes therapy' }
    ],
    food: [
      'Take with food to reduce GI side effects',
      'Food decreases rate but not extent of absorption',
      'Avoid excessive alcohol - increases lactic acidosis risk and hypoglycemia risk'
    ]
  },
  pregnancy: {
    category: 'B',
    trimpiesterRisks: {
      first: 'Crosses placenta. No clear teratogenic effects in human studies. Some guidelines permit use. Insulin traditionally preferred.',
      second: 'May continue if good control achieved. Monitor fetal growth. Some guidelines support use throughout pregnancy.',
      third: 'May continue; monitor closely. Some stop at delivery due to theoretical risk during stress of labor/delivery.'
    },
    recommendation: 'May be used in pregnancy, especially if insulin not feasible or refused. MiG (Metformin in Gestational Diabetes) trial showed similar outcomes to insulin. Crosses placenta but no clear fetal harm demonstrated. Insulin remains preferred by many guidelines, especially in type 2 diabetes. For GDM, either acceptable.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in very low concentrations. Infant exposure minimal (<1% of maternal dose). Considered compatible with breastfeeding. No adverse effects reported in nursing infants.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Fasting glucose and/or HbA1c', reason: 'Establish baseline glycemic control' },
      { test: 'Serum creatinine and eGFR', reason: 'Contraindicated if eGFR <30; dose adjustment if eGFR 30-45' },
      { test: 'Liver function tests', reason: 'Avoid in hepatic impairment' },
      { test: 'Vitamin B12 level', reason: 'Baseline before long-term use' }
    ],
    ongoing: [
      { test: 'HbA1c', frequency: 'Every 3 months until stable, then every 6 months', target: '<7% for most adults; individualized' },
      { test: 'Fasting glucose', frequency: 'As needed for dose adjustment', target: '80-130 mg/dL' },
      { test: 'eGFR', frequency: 'At least annually; every 3-6 months if eGFR 30-60', target: 'eGFR ≥30 (preferably ≥45) to continue' },
      { test: 'Vitamin B12', frequency: 'Every 2-3 years with long-term use; sooner if symptoms', target: 'Normal range; supplement if deficient' }
    ],
    therapeutic: {
      range: 'No routine metformin level monitoring. Therapeutic effect assessed by HbA1c and blood glucose.',
      timing: 'Full glycemic effect in 2-3 weeks. Check HbA1c after 3 months to assess response.'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Store in original container',
    shelfLife: '2-3 years from manufacture; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa as generic. WHO Essential Medicine. Present in most public health facilities and pharmacies.',
    affordability: 'Very affordable; approximately $1-5 USD per month. Generic availability makes it accessible.',
    supplyChainNotes: 'Stable supply from multiple generic manufacturers. Heat stable within recommended range.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, Fidson, GSK Nigeria), South Africa (Aspen, Adcock Ingram), Kenya (Cosmos), Egypt, Morocco, and imported from India.',
    alternatives: [
      'Sulfonylureas (glibenclamide, glimepiride) - less preferred due to hypoglycemia risk',
      'DPP-4 inhibitors (limited availability)',
      'SGLT2 inhibitors (limited availability but increasing)',
      'Insulin (for advanced disease)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Type 2 diabetes epidemic growing rapidly across Africa. Metformin is first-line and most cost-effective oral agent. Limited access to HbA1c testing in some settings - fasting glucose used as alternative. GI side effects major barrier to adherence - emphasize slow titration and taking with food. In settings with limited refrigeration, metformin (unlike insulin) does not require cold chain. Diarrheal illness common and can cause dehydration - educate patients to hold metformin during severe gastroenteritis. Renal function monitoring limited in some areas - clinical assessment and baseline creatinine when available important.'
  },
  patientCounseling: {
    keyPoints: [
      'Take with meals to reduce stomach upset',
      'Start with low dose and increase slowly as directed',
      'Stomach side effects usually improve after a few weeks',
      'Continue healthy diet and exercise - medicine works best with lifestyle changes',
      'Stop taking and seek medical attention if you have severe vomiting, diarrhea, or feel very unwell',
      'Limit alcohol intake - increases risk of serious side effect'
    ],
    commonMisconceptions: [
      'Misconception: Metformin damages kidneys - Truth: Safe for kidneys when used correctly; actually may be protective. Dosing adjusted for kidney function.',
      'Misconception: Can stop when blood sugar normal - Truth: Continue lifelong; stopping causes blood sugar to rise',
      'Misconception: Metformin causes low blood sugar - Truth: Rarely causes hypoglycemia alone; low blood sugar risk mainly with insulin or sulfonylureas',
      'Misconception: Can eat anything since taking medication - Truth: Diet and exercise still essential; metformin most effective with healthy lifestyle'
    ],
    adherenceTips: [
      'Take with breakfast and dinner to remember and reduce GI upset',
      'If stomach upset severe, try extended-release formulation',
      'Use pill box if taking multiple medications',
      'Do not stop because of initial stomach upset - usually improves',
      'Keep appointments for blood tests'
    ],
    warningSignsToReport: [
      'Severe or persistent diarrhea, nausea, or vomiting',
      'Muscle pain with unusual tiredness',
      'Difficulty breathing',
      'Unusual sleepiness or weakness',
      'Stomach pain',
      'Cold or blue skin',
      'Signs of illness especially with dehydration'
    ]
  },
  clinicalPearls: [
    'First-line for type 2 diabetes universally - start metformin at diagnosis along with lifestyle intervention, even if HbA1c near target',
    'GI side effects are dose-related and timing-related - START LOW (500 mg daily), GO SLOW (increase by 500 mg weekly), take with food. Extended-release has better GI tolerability.',
    'Lactic acidosis is rare with appropriate patient selection (avoid if eGFR <30, hepatic impairment, hypoxic states, severe infection). Do not fear metformin in appropriate patients.',
    'UKPDS showed metformin uniquely reduces cardiovascular mortality in overweight type 2 diabetics - not just glucose lowering but CV protection',
    'B12 deficiency develops in 5-30% with long-term use - screen periodically, especially if neuropathy present or worsening',
    'eGFR thresholds: Do not initiate if <45; discontinue if <30. May continue with caution at 30-45 with reduced dose.',
    'Hold before iodinated contrast only if eGFR <60 (AKI risk higher in renal impairment). If eGFR ≥60, can continue per updated guidelines.',
    'Does not cause hypoglycemia as monotherapy - but watch when combined with insulin or sulfonylureas (reduce their doses when adding metformin)',
    'Weight-neutral to slight weight loss - major advantage over sulfonylureas and insulin which cause weight gain',
    'In African settings, emphasize that metformin does not require refrigeration (unlike insulin) - a practical advantage in resource-limited settings'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'UK Prospective Diabetes Study (UKPDS) Group. Lancet 1998 - Metformin effect on complications',
    'Diabetes Prevention Program (DPP) Research Group. NEJM 2002 - Prevention of type 2 diabetes',
    'ADA Standards of Medical Care in Diabetes 2024',
    'EASD/ADA Consensus Report on Management of Hyperglycemia in Type 2 Diabetes 2022',
    'IDF Clinical Practice Recommendations for Managing Type 2 Diabetes in Primary Care 2017',
    'Rowan JA et al. (MiG Trial). NEJM 2008 - Metformin vs insulin in gestational diabetes',
    'de Jager J et al. BMJ 2010 - B12 deficiency with long-term metformin',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'insulin-regular-030',
  genericName: 'Insulin Regular (Soluble Insulin, Human Insulin)',
  brandNames: ['Humulin R', 'Novolin R', 'Actrapid', 'Insuman Rapid', 'Myxredlin', 'Afrezza (inhaled)'],
  atcCode: 'A10AB01',
  therapeuticClass: 'Short-Acting Insulin (Antidiabetic Hormone)',
  pharmacology: {
    mechanism: 'Recombinant human insulin identical to endogenous insulin. Binds to insulin receptors on muscle, adipose tissue, and liver cells, activating glucose transporters (GLUT4) and promoting glucose uptake. Suppresses hepatic gluconeogenesis and glycogenolysis. Promotes protein synthesis, inhibits lipolysis, and facilitates potassium entry into cells.',
    pharmacokinetics: {
      absorption: 'Subcutaneous: onset 30-60 minutes, peak 2-4 hours, duration 5-8 hours. Absorption varies with injection site (fastest: abdomen > arm > thigh > buttock), depth, volume, exercise, and temperature. IV: immediate onset.',
      distribution: 'Does not significantly bind to plasma proteins. Volume of distribution approximately 0.26-0.36 L/kg. Does not cross placenta significantly.',
      metabolism: 'Metabolized primarily in liver (60%), kidneys (40%), and muscle. Degraded by insulinase enzymes.',
      elimination: 'Metabolites excreted renally. Clearance reduced in renal impairment.',
      halfLife: 'IV: 5-10 minutes; Subcutaneous: effective duration 5-8 hours due to absorption rate'
    },
    pharmacodynamics: 'Subcutaneous onset 30-60 min, peak 2-4 hours, duration 5-8 hours. IV onset immediate, very short duration. Lowers blood glucose by promoting cellular uptake, inhibiting hepatic glucose output. Also shifts potassium intracellularly (used in hyperkalemia management). Dose-response relationship; effect proportional to dose until receptors saturated.'
  },
  indications: [
    {
      condition: 'Type 1 Diabetes Mellitus',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Essential for survival in T1DM. Used as part of basal-bolus regimen (with long-acting insulin) or in insulin pumps. DCCT trial demonstrated benefit of intensive therapy.'
    },
    {
      condition: 'Type 2 Diabetes Mellitus (Advanced/Uncontrolled)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: false,
      notes: 'Added when oral agents fail to achieve glycemic targets. May be used as part of basal-bolus regimen or for correction doses.'
    },
    {
      condition: 'Diabetic Ketoacidosis (DKA)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV regular insulin is treatment of choice. Continuous infusion until resolution, then transition to subcutaneous.'
    },
    {
      condition: 'Hyperglycemic Hyperosmolar State (HHS)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV insulin after initial fluid resuscitation. Similar protocol to DKA.'
    },
    {
      condition: 'Hyperkalemia (Emergency Treatment)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV insulin with dextrose drives potassium into cells. Temporizing measure while definitive treatment arranged.'
    },
    {
      condition: 'Gestational Diabetes Mellitus',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line pharmacotherapy when lifestyle insufficient. Does not cross placenta.'
    },
    {
      condition: 'Perioperative Glucose Management',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV insulin infusion for tight glucose control during surgery, especially cardiac surgery.'
    },
    {
      condition: 'Critical Illness Hyperglycemia',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV insulin for glucose control in ICU. Target typically 140-180 mg/dL to avoid hypoglycemia.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Highly individualized. Type 2 starting dose: 0.1-0.2 units/kg/day. Type 1 total daily dose: 0.4-1.0 units/kg/day (includes basal and bolus). Mealtime (bolus): 0.05-0.1 units/kg per meal or per carbohydrate ratio.',
      indicationSpecific: [
        { indication: 'Type 2 diabetes (adding to orals)', dose: 'Start 10 units at largest meal or 0.1 units/kg; adjust based on postprandial glucose' },
        { indication: 'Type 1 diabetes (bolus)', dose: '50% of total daily dose as bolus divided among meals; adjust by carb counting or correction factor' },
        { indication: 'DKA (IV)', dose: 'Bolus 0.1 units/kg IV, then 0.1 units/kg/hour infusion; adjust to lower glucose 50-70 mg/dL/hour' },
        { indication: 'Hyperkalemia', dose: '10 units IV regular insulin with 25-50g dextrose (D50W 50mL); repeat if needed' },
        { indication: 'Sliding scale (hospital)', dose: 'Typically 2-10 units SC based on blood glucose; avoid as sole therapy' },
        { indication: 'Perioperative', dose: 'IV infusion 0.5-1 unit/hour, adjusted to maintain glucose 140-180 mg/dL' }
      ],
      maxDose: 'No absolute maximum; titrate to glycemic targets. Daily doses >200 units suggest significant insulin resistance - investigate.',
      renalImpairment: {
        mild: 'CrCl 50-80 mL/min: Monitor closely; may need dose reduction',
        moderate: 'CrCl 10-50 mL/min: Reduce dose by 25-50%; increased hypoglycemia risk',
        severe: 'CrCl <10 mL/min: Reduce dose by 50%; monitor very closely',
        dialysis: 'Significant dose reduction; administer after dialysis; monitor closely'
      },
      hepaticImpairment: 'Reduce dose and monitor closely. Liver metabolizes insulin and produces glucose; impairment affects both, increasing hypoglycemia risk.'
    },
    pediatric: {
      standard: 'Type 1 diabetes: 0.5-1.0 units/kg/day total (varies with age and puberty). Prepubertal: 0.5-0.7 units/kg/day. Pubertal: 1.0-1.5 units/kg/day during growth spurt.',
      weightBased: true,
      ageSpecific: [
        { age: 'Infants/toddlers', dose: '0.3-0.5 units/kg/day total; very sensitive; use diluted insulin if needed' },
        { age: 'Prepubertal children', dose: '0.5-0.7 units/kg/day total' },
        { age: 'Pubertal adolescents', dose: '0.8-1.5 units/kg/day total (higher due to growth hormone and hormonal changes)' },
        { age: 'DKA (pediatric)', dose: '0.1 units/kg/hour IV infusion (no bolus in children); lower rate 0.05 units/kg/hour if glucose dropping >90 mg/dL/hour' }
      ],
      maxDose: 'Individualized; pubertal children may require up to 2 units/kg/day during growth spurts',
      neonatal: 'Hyperglycemia of prematurity: 0.01-0.1 units/kg/hour continuous IV infusion. Use diluted insulin. Extreme caution - high hypoglycemia risk.'
    },
    elderly: 'Start conservatively (lower doses). Increased hypoglycemia risk and reduced awareness. Target HbA1c may be relaxed (7.5-8.5%). Avoid complex regimens if adherence concerns.',
    routes: ['Subcutaneous', 'Intravenous', 'Intramuscular (rarely)', 'Inhalation (Afrezza)'],
    administration: [
      'Subcutaneous: Inject 30 minutes before meals (allows for absorption). Rotate injection sites.',
      'IV: Only regular (soluble) insulin can be given IV. Use infusion pump for continuous infusion.',
      'IM: Faster absorption than SC; use in emergencies if IV access unavailable.',
      'Do not mix regular insulin with glargine or detemir in same syringe.',
      'Can mix with NPH insulin in same syringe - draw regular first.',
      'Inspect visually - regular insulin should be clear; do not use if cloudy or contains particles.'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to insulin or any component',
      'Hypoglycemia (do not give if blood glucose low)'
    ],
    relative: [
      'Hypokalemia (correct before giving - insulin drives K+ into cells)',
      'Renal or hepatic impairment (increased hypoglycemia risk - reduce dose)',
      'Situations with variable absorption (lipohypertrophy, edema at injection sites)',
      'Conditions causing insulin resistance may require higher doses (infection, stress, steroids)'
    ],
    precautions: [
      'Always check blood glucose before and after administering',
      'Ensure patient can eat before giving mealtime insulin',
      'Educate about hypoglycemia symptoms and treatment',
      'Check potassium when using IV insulin',
      'Avoid lipohypertrophy by rotating injection sites',
      'Adjust doses during illness, exercise, changes in diet',
      'Never share insulin pens between patients (even with needle change)'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Hypoglycemia', frequency: '10-30%', management: 'Educate on symptoms (tremor, sweating, confusion, palpitations). Treat with 15-20g fast-acting carbohydrate (glucose tablets, juice). Recheck in 15 min. Severe: glucagon IM or IV dextrose.' },
      { effect: 'Injection site reactions', frequency: '5-10%', management: 'Rotate injection sites; proper injection technique; may improve with time' },
      { effect: 'Weight gain', frequency: '2-4 kg typical', management: 'Diet and exercise counseling; may use metformin or GLP-1 agonist to mitigate' },
      { effect: 'Lipohypertrophy', frequency: '5-30%', management: 'Rotate injection sites systematically; avoid injecting into hypertrophied areas (variable absorption)' },
      { effect: 'Hypokalemia (with IV insulin)', frequency: 'Common', management: 'Monitor potassium; replace as needed; expect K+ to drop 0.5-1 mEq/L per 10 units insulin' }
    ],
    serious: [
      { effect: 'Severe hypoglycemia', frequency: '1-5% per year', management: 'Glucagon IM/SC/IV or nasal; IV dextrose (25-50 mL D50W). Identify precipitant (missed meal, excessive dose, exercise, alcohol).', warning: 'Can cause seizures, coma, death. Risk higher with tight control, hypoglycemia unawareness, renal impairment, elderly.' },
      { effect: 'Hypoglycemia unawareness', frequency: 'Variable (long-standing T1DM)', management: 'Relax glucose targets temporarily; structured education (DAFNE/DSME); consider CGM.', warning: 'Patients lose warning symptoms; increased risk of severe hypoglycemia' },
      { effect: 'Allergic reactions (local or systemic)', frequency: 'Rare', management: 'Local: antihistamines, may resolve with continued use. Systemic: discontinue; desensitization may be needed.', warning: 'True anaphylaxis very rare with human insulin' },
      { effect: 'Insulin edema', frequency: 'Rare', management: 'Usually self-limiting. Occurs with rapid improvement of glucose control. Support stockings; rarely need diuretics.', warning: 'Occurs when initiating insulin in poorly controlled patients' },
      { effect: 'Factitious hypoglycemia (insulin abuse)', frequency: 'Rare', management: 'Suspect if unexplained hypoglycemia with high insulin and low C-peptide levels', warning: 'Consider in healthcare workers, diabetic family members, psychiatric patients' }
    ],
    rare: [
      'Lipoatrophy (rare with human insulin)',
      'Amyloidosis at injection sites (with very long-term use)',
      'Worsening diabetic retinopathy (with rapid glucose improvement)',
      'Peripheral neuropathy worsening (treatment-induced neuropathy)',
      'Sodium retention and edema'
    ]
  },
  interactions: {
    major: [
      { drug: 'Sulfonylureas and meglitinides', effect: 'Additive hypoglycemia risk', mechanism: 'Both stimulate insulin release/action', management: 'May need to reduce sulfonylurea dose by 50% when adding insulin' },
      { drug: 'Beta-blockers', effect: 'Mask hypoglycemia symptoms (tachycardia); may prolong hypoglycemia', mechanism: 'Block adrenergic warning symptoms and counterregulatory response', management: 'Educate patient about subtle hypoglycemia signs (sweating preserved). Consider cardioselective beta-blockers.' },
      { drug: 'Alcohol', effect: 'Unpredictable glucose effects; can cause delayed hypoglycemia', mechanism: 'Inhibits gluconeogenesis; may mask hypoglycemia symptoms', management: 'Advise moderate intake with food; never drink on empty stomach; monitor glucose' },
      { drug: 'MAO inhibitors', effect: 'Enhanced hypoglycemic effect', mechanism: 'Unclear; possibly increased insulin sensitivity', management: 'Monitor glucose closely; may need insulin dose reduction' }
    ],
    moderate: [
      { drug: 'Corticosteroids', effect: 'Antagonizes insulin effect; hyperglycemia', management: 'Increase insulin dose during steroid therapy; taper when steroids discontinued' },
      { drug: 'Thiazide diuretics', effect: 'May increase glucose and reduce insulin sensitivity', management: 'Monitor glucose; may need insulin adjustment' },
      { drug: 'ACE inhibitors', effect: 'May enhance hypoglycemic effect', management: 'Monitor glucose; may need to reduce insulin' },
      { drug: 'Fluoroquinolones', effect: 'Can cause hypo- or hyperglycemia', management: 'Monitor glucose closely during antibiotic course' },
      { drug: 'Octreotide', effect: 'Decreases insulin secretion; complex effects', management: 'Monitor glucose and adjust insulin' }
    ],
    food: [
      'Carbohydrate intake directly affects insulin requirements',
      'Alcohol can cause delayed hypoglycemia - take with food',
      'High-fat meals delay carbohydrate absorption and glucose spike'
    ]
  },
  pregnancy: {
    category: 'B',
    trimpiesterRisks: {
      first: 'Safe. Insulin is treatment of choice. Tight glucose control reduces malformation risk. Adjust doses for changing requirements.',
      second: 'Safe. Insulin requirements typically increase due to placental hormones.',
      third: 'Safe. Insulin requirements may increase 50-100% by end of pregnancy. Tight control reduces macrosomia risk.'
    },
    recommendation: 'Insulin is the preferred treatment for diabetes in pregnancy (pregestational and gestational). Does not cross placenta. Requirements increase throughout pregnancy, especially in 2nd and 3rd trimesters. Requirements drop abruptly after delivery.',
    lactation: {
      safe: true,
      notes: 'Insulin does not pass into breast milk in significant amounts. Safe during breastfeeding. May need to reduce doses as breastfeeding increases insulin sensitivity. Monitor for hypoglycemia.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Blood glucose', reason: 'Establish baseline; never give insulin if glucose already low' },
      { test: 'HbA1c', reason: 'Assess overall glycemic control; guide therapy intensity' },
      { test: 'Renal function', reason: 'Dose adjustment needed in renal impairment' },
      { test: 'Serum potassium', reason: 'Insulin drives K+ into cells; correct hypokalemia first for IV insulin' }
    ],
    ongoing: [
      { test: 'Blood glucose (self-monitoring)', frequency: 'Varies: T1DM 4-10x/day; T2DM on insulin 1-4x/day depending on regimen', target: 'Fasting 80-130 mg/dL; postprandial <180 mg/dL' },
      { test: 'HbA1c', frequency: 'Every 3 months until stable, then every 6 months', target: '<7% for most; individualized (higher for elderly, comorbidities)' },
      { test: 'Serum potassium (with IV insulin)', frequency: 'Every 2-4 hours during infusion', target: '4.0-5.0 mEq/L' },
      { test: 'Continuous glucose monitoring (CGM)', frequency: 'Continuous if available', target: 'Time in range >70%; time below range <4%' },
      { test: 'Weight', frequency: 'Every visit', target: 'Stable; address if significant gain' },
      { test: 'Injection sites', frequency: 'Every visit', target: 'No lipohypertrophy; proper rotation' }
    ],
    therapeutic: {
      range: 'No insulin level monitoring. Therapeutic effect assessed by blood glucose and HbA1c.',
      timing: 'Peak effect 2-4 hours after subcutaneous injection; check 2-hour postprandial glucose to assess bolus effectiveness'
    }
  },
  storage: {
    temperature: 'Unopened: 2-8°C (36-46°F) in refrigerator. In-use: Room temperature (15-30°C / 59-86°F) for up to 28-31 days (varies by product).',
    light: 'Protect from light; do not freeze',
    humidity: 'Keep away from moisture',
    specialInstructions: 'Never freeze insulin (destroys efficacy). Do not use if frozen, cloudy, or contains particles. Discard 28-31 days after opening even if vial not empty. Do not store in car or direct sunlight. In hot climates, use insulated bags for transport.',
    shelfLife: 'Unopened: until expiry date on package. Opened/in-use: 28-31 days maximum.'
  },
  africanContext: {
    availability: 'Available in most African countries but supply chain challenges exist. WHO Essential Medicine. May not be consistently available at primary care level in some regions. Increasing availability of human insulin generics.',
    affordability: 'Cost remains a barrier despite being off-patent. Approximately $10-50 USD per vial in Africa. Biosimilar insulin increasingly available at lower cost. Still unaffordable for many without insurance or subsidy programs.',
    supplyChainNotes: 'COLD CHAIN CRITICAL - requires refrigeration. This is a major challenge in Africa. Some insulins stable at room temperature for 28-31 days once opened. Insulin pens improving accessibility. Supply interruptions common in some regions.',
    localManufacturing: 'Limited local manufacturing. Most insulin imported from major manufacturers (Novo Nordisk, Eli Lilly, Sanofi) or from India (Biocon). Emerging local production in Egypt and plans in other countries.',
    alternatives: [
      'Human insulin NPH (intermediate-acting)',
      'Premixed insulin (70/30) for simplified regimens',
      'Insulin analogs (glargine, aspart, lispro) - less available, more expensive',
      'Oral agents - not alternatives for T1DM or advanced T2DM'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Access to insulin is a critical issue across Africa. Many Type 1 diabetics die due to lack of insulin access. Cold chain is the biggest challenge - power outages common. Educate patients that insulin can be kept at room temperature for 4 weeks (reduces cold chain need). Insulin pens more convenient but more expensive. Syringes and vials remain primary method. Glucose monitoring strips also a cost barrier - minimum monitoring protocols may be needed. Traditional belief that insulin means "end stage" disease creates resistance - education crucial. Teach families about hypoglycemia recognition and treatment. Sick day rules critical in malaria-endemic areas where infections cause glucose swings.'
  },
  patientCounseling: {
    keyPoints: [
      'Inject 30 minutes before meals (regular insulin) to match glucose rise from food',
      'Rotate injection sites to prevent lumps under skin',
      'Always carry fast-acting sugar (glucose tablets, juice) for low blood sugar',
      'Learn to recognize low blood sugar: shaking, sweating, confusion, fast heartbeat',
      'Never skip meals after taking insulin',
      'Keep insulin cool but never freeze it',
      'Check blood sugar regularly as directed'
    ],
    commonMisconceptions: [
      'Misconception: Insulin means diabetes is very bad or near death - Truth: Insulin is essential treatment that helps live well with diabetes',
      'Misconception: Insulin causes blindness/amputations - Truth: These are complications of uncontrolled diabetes, not insulin; insulin prevents them',
      'Misconception: Can stop insulin when feeling better - Truth: Type 1 diabetics need insulin for life; Type 2 may be able to reduce with lifestyle but should not stop without doctor',
      'Misconception: Insulin is addictive - Truth: Insulin is a natural hormone; body needs it to survive',
      'Misconception: Insulin makes you gain weight uncontrollably - Truth: Some weight gain common but manageable with diet and exercise'
    ],
    adherenceTips: [
      'Set meal and injection times that fit your routine',
      'Keep insulin and supplies visible (not hidden away)',
      'Use insulin pen if available - more convenient than syringes',
      'Write down doses and blood sugars in a log book',
      'Tell family/friends about hypoglycemia signs and treatment',
      'Wear medical identification (bracelet or card)',
      'Never share needles or pens with anyone'
    ],
    warningSignsToReport: [
      'Frequent low blood sugar episodes',
      'Severe low blood sugar requiring help from others',
      'Lumps or skin changes at injection sites',
      'Blood sugar consistently very high despite taking insulin',
      'Signs of infection at injection site',
      'Nausea, vomiting, abdominal pain, rapid breathing (may indicate DKA)',
      'Allergic reactions (rash, swelling)'
    ]
  },
  clinicalPearls: [
    'Regular (soluble) insulin is the ONLY insulin that can be given IV - never give other insulins intravenously',
    'For subcutaneous use, give regular insulin 30 minutes before meals to match peak action with postprandial glucose rise (vs rapid analogs which can be given at meal time)',
    'In DKA, continuous IV insulin infusion is preferred over bolus dosing. Target glucose drop of 50-70 mg/dL/hour. Too fast correction causes cerebral edema (especially in children).',
    'When treating hyperkalemia: 10 units regular insulin + 25g dextrose shifts K+ into cells within 15-30 minutes. Effect lasts 4-6 hours. This is temporizing - does not remove K+ from body.',
    'Lipohypertrophy from repeated injections in same site causes erratic absorption. Systematic site rotation essential. Educate at every visit.',
    'Regular insulin can be mixed with NPH in same syringe - draw regular (clear) first, then NPH (cloudy). Do NOT mix with glargine or detemir.',
    'In African settings with limited cold chain: once opened, regular insulin is stable at room temperature (<30°C) for 28-31 days. This knowledge is life-saving.',
    'Insulin needs increase during illness, infection, stress, steroids - teach "sick day rules": increase monitoring, maintain hydration, adjust insulin upward.',
    'Watch for Somogyi effect (nocturnal hypoglycemia causing rebound morning hyperglycemia) vs Dawn phenomenon (morning hyperglycemia from growth hormone/cortisol). Different treatments!',
    'In resource-limited settings, simplified regimens (premixed insulin twice daily) may be more practical than basal-bolus, though less physiologic.'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'DCCT Research Group. N Engl J Med 1993 - Intensive diabetes therapy and complications',
    'UKPDS Group. Lancet 1998 - Intensive blood-glucose control with conventional vs intensive treatment',
    'ADA Standards of Medical Care in Diabetes 2024',
    'Kitabchi AE et al. Diabetes Care 2009 - Hyperglycemic crises in diabetes',
    'IDF Clinical Practice Recommendations for Managing Type 2 Diabetes in Primary Care 2017',
    'WHO Package of Essential NCD Interventions (PEN)',
    'ISPAD Clinical Practice Consensus Guidelines 2022 - Insulin treatment in children',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'salbutamol-031',
  genericName: 'Salbutamol (Albuterol)',
  brandNames: ['Ventolin', 'ProAir', 'Proventil', 'Asthalin', 'Salamol', 'Airomir'],
  atcCode: 'R03AC02',
  therapeuticClass: 'Short-Acting Beta-2 Adrenergic Agonist (SABA)',
  pharmacology: {
    mechanism: 'Selectively stimulates beta-2 adrenergic receptors in bronchial smooth muscle, activating adenylyl cyclase and increasing intracellular cyclic AMP. This leads to relaxation of bronchial smooth muscle, bronchodilation, and relief of bronchospasm. Also inhibits release of mediators from mast cells, increases mucociliary clearance, and has minor effects on skeletal muscle and cardiac beta-1 receptors at higher doses.',
    pharmacokinetics: {
      absorption: 'Inhaled: only 10-20% reaches lower airways; remainder deposited in oropharynx and swallowed. Onset 5-15 minutes inhaled, 15-30 minutes oral. Oral bioavailability ~50%.',
      distribution: 'Volume of distribution 2.5 L/kg. Protein binding 10%. Crosses placenta; minimal CNS penetration.',
      metabolism: 'Metabolized in liver via sulfate conjugation to inactive metabolite (salbutamol 4-O-sulfate). No significant CYP450 involvement.',
      elimination: 'Primarily renal excretion (70-80% within 24 hours). 60% as unchanged drug, 30% as sulfate conjugate after inhalation.',
      halfLife: '4-6 hours (elimination); clinical effect duration 4-6 hours inhaled'
    },
    pharmacodynamics: 'Inhaled onset 5-15 minutes, peak effect 30-60 minutes, duration 4-6 hours. Nebulized onset similar. Oral onset 15-30 minutes, duration 4-8 hours. Beta-2 selectivity approximately 29:1 over beta-1 at therapeutic doses, but selectivity decreases at higher doses.'
  },
  indications: [
    {
      condition: 'Acute Asthma Exacerbation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line rescue therapy for acute bronchospasm. Repeat doses every 20 minutes for up to 3 doses in severe exacerbation.'
    },
    {
      condition: 'Chronic Asthma - Symptom Relief (Rescue)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'As-needed reliever therapy. Frequent use (>2 days/week) indicates need for controller therapy. GINA now recommends ICS-formoterol as preferred reliever in adults.'
    },
    {
      condition: 'Exercise-Induced Bronchoconstriction',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Administer 15-30 minutes before exercise for prevention. Duration of protection approximately 2-4 hours.'
    },
    {
      condition: 'COPD - Acute Exacerbation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line bronchodilator for acute COPD exacerbations. Often combined with ipratropium.'
    },
    {
      condition: 'COPD - Maintenance (as rescue)',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Used as needed for symptom relief. Long-acting bronchodilators (LABA, LAMA) preferred for maintenance.'
    },
    {
      condition: 'Hyperkalemia (Adjunctive)',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Nebulized salbutamol 10-20 mg shifts potassium intracellularly. Adjunct to insulin/dextrose, not sole therapy.'
    },
    {
      condition: 'Preterm Labor (Tocolysis)',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'IV salbutamol used short-term to delay delivery for steroid administration. Associated with significant maternal side effects.'
    }
  ],
  dosing: {
    adults: {
      standard: 'MDI: 100-200 mcg (1-2 puffs) as needed, max 8 puffs/day for maintenance; up to 10 puffs in acute attack. Nebulizer: 2.5-5 mg every 4-6 hours or as needed.',
      indicationSpecific: [
        { indication: 'Acute asthma (mild-moderate)', dose: 'MDI: 4-8 puffs via spacer, repeat every 20 min for up to 3 doses' },
        { indication: 'Acute severe asthma', dose: 'Nebulizer: 5 mg, repeat every 20 min or continuous 10-15 mg/hour' },
        { indication: 'Maintenance relief', dose: 'MDI: 1-2 puffs (100-200 mcg) as needed, max 8 puffs/day' },
        { indication: 'Exercise-induced bronchospasm', dose: '2 puffs (200 mcg) 15-30 minutes before exercise' },
        { indication: 'Hyperkalemia', dose: 'Nebulized: 10-20 mg in 4 mL saline over 10-15 minutes' },
        { indication: 'COPD exacerbation', dose: 'Nebulizer: 2.5-5 mg every 4-6 hours; often combined with ipratropium' }
      ],
      maxDose: 'MDI: 8-10 puffs/day chronic; more in acute attack under supervision. Nebulizer: 5 mg every 4 hours routine; continuous nebulization in severe attack.',
      renalImpairment: {
        mild: 'No adjustment required',
        moderate: 'No adjustment required; monitor for side effects',
        severe: 'Use with caution; may accumulate; monitor for tachycardia and hypokalemia',
        dialysis: 'No specific adjustment; use with caution'
      },
      hepaticImpairment: 'No dose adjustment typically required. Use with caution in severe impairment.'
    },
    pediatric: {
      standard: 'MDI with spacer preferred. 100-200 mcg (1-2 puffs) as needed. Nebulizer: 2.5-5 mg depending on age.',
      weightBased: false,
      ageSpecific: [
        { age: '<2 years', dose: 'MDI via spacer with mask: 100 mcg (1 puff) as needed; Nebulizer: 2.5 mg' },
        { age: '2-5 years', dose: 'MDI via spacer: 100-200 mcg (1-2 puffs) as needed; Nebulizer: 2.5 mg' },
        { age: '6-11 years', dose: 'MDI via spacer: 200 mcg (2 puffs) as needed; Nebulizer: 2.5-5 mg' },
        { age: '≥12 years', dose: 'Adult dosing' },
        { age: 'Acute severe asthma (any age)', dose: 'MDI: 6 puffs (<6 years) or 12 puffs (≥6 years) via spacer, repeat every 20 min' }
      ],
      maxDose: 'Varies by age and severity; in acute attack, up to 10 puffs via spacer repeated every 20 minutes under supervision',
      neonatal: 'Nebulized: 2.5 mg every 4-6 hours; limited data in neonates. Use with caution.'
    },
    elderly: 'Standard dosing, but increased sensitivity to cardiovascular effects (tachycardia, arrhythmias). Monitor closely.',
    routes: ['Inhalation (MDI)', 'Inhalation (Nebulizer)', 'Oral', 'Intravenous (specialized indications)'],
    administration: [
      'MDI: Shake well; use spacer device for optimal delivery; rinse mouth after use',
      'MDI technique: exhale fully, place in mouth/spacer, press canister while inhaling slowly, hold breath 10 seconds',
      'Nebulizer: dilute with normal saline to 3-4 mL; usual nebulization time 10-15 minutes',
      'Spacer use essential in children and improves delivery in all ages',
      'Prime new MDI with 4 puffs; re-prime if not used for 2 weeks'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to salbutamol or any component'
    ],
    relative: [
      'Cardiovascular disease (ischemic heart disease, arrhythmias, hypertension)',
      'Hyperthyroidism',
      'Diabetes mellitus (may cause hyperglycemia)',
      'Seizure disorders',
      'Hypokalemia (can worsen)',
      'Severe asthma (requires monitoring and additional therapy)'
    ],
    precautions: [
      'Do not use as sole therapy for asthma - combine with inhaled corticosteroids for persistent asthma',
      'Increased use may indicate worsening asthma - step up controller therapy',
      'Monitor potassium in patients on diuretics or with severe asthma',
      'May cause paradoxical bronchospasm - discontinue if occurs',
      'Use with caution in elderly due to cardiovascular sensitivity',
      'Avoid excessive use - can cause tolerance and rebound bronchospasm'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Tremor', frequency: '10-20%', management: 'Usually decreases with continued use; reduce dose if troublesome' },
      { effect: 'Tachycardia/palpitations', frequency: '5-15%', management: 'Usually mild and transient; reduce dose; use spacer to reduce systemic absorption' },
      { effect: 'Headache', frequency: '5-10%', management: 'Usually mild; symptomatic treatment' },
      { effect: 'Nervousness/anxiety', frequency: '5-10%', management: 'Reduce dose; reassure patient' },
      { effect: 'Throat irritation', frequency: '5-10%', management: 'Use spacer; rinse mouth after use' },
      { effect: 'Muscle cramps', frequency: '3-5%', management: 'Often related to hypokalemia; check potassium' }
    ],
    serious: [
      { effect: 'Hypokalemia', frequency: '1-5% (higher with high doses)', management: 'Monitor potassium in severe asthma and with concurrent diuretics/steroids. Supplement as needed.', warning: 'Risk increased with hypoxia, concurrent diuretics, corticosteroids, theophylline' },
      { effect: 'Cardiac arrhythmias', frequency: 'Rare', management: 'Discontinue if significant; ECG monitoring in high-dose therapy', warning: 'Risk higher in patients with pre-existing heart disease' },
      { effect: 'Paradoxical bronchospasm', frequency: 'Rare', management: 'Discontinue immediately; use alternative bronchodilator; treat bronchospasm', warning: 'If occurs, do not re-challenge with salbutamol' },
      { effect: 'Severe hypotension (IV use)', frequency: 'Rare', management: 'Reduce rate or stop infusion; supportive care', warning: 'Mainly with IV tocolysis' },
      { effect: 'Lactic acidosis', frequency: 'Rare', management: 'Usually with IV use or very high doses; supportive care', warning: 'Can occur with continuous high-dose nebulization' }
    ],
    rare: [
      'Hyperglycemia',
      'Urticaria/angioedema',
      'QTc prolongation',
      'Pulmonary edema (IV tocolysis)',
      'Myocardial ischemia',
      'Insomnia',
      'Behavioral changes in children'
    ]
  },
  interactions: {
    major: [
      { drug: 'Non-selective beta-blockers (propranolol)', effect: 'Antagonism of bronchodilator effect; may precipitate severe bronchospasm', mechanism: 'Beta-receptor blockade prevents salbutamol effect', management: 'Avoid non-selective beta-blockers in asthma/COPD. If beta-blocker essential, use cardioselective (bisoprolol, metoprolol) with caution.' },
      { drug: 'MAO inhibitors', effect: 'Enhanced cardiovascular effects of salbutamol', mechanism: 'Reduced catecholamine metabolism', management: 'Use with caution; monitor HR and BP' },
      { drug: 'Tricyclic antidepressants', effect: 'Enhanced cardiovascular effects', mechanism: 'Inhibition of catecholamine reuptake', management: 'Monitor for arrhythmias and tachycardia' }
    ],
    moderate: [
      { drug: 'Diuretics (loop and thiazide)', effect: 'Additive hypokalemia', management: 'Monitor potassium; supplement as needed' },
      { drug: 'Corticosteroids (systemic)', effect: 'Additive hypokalemia', management: 'Monitor potassium, especially in severe asthma receiving both' },
      { drug: 'Theophylline', effect: 'Additive effects and hypokalemia risk', management: 'Monitor theophylline levels and potassium' },
      { drug: 'Digoxin', effect: 'Hypokalemia from salbutamol increases digoxin toxicity risk', management: 'Monitor potassium and digoxin levels' },
      { drug: 'Other sympathomimetics', effect: 'Additive cardiovascular effects', management: 'Avoid concurrent use if possible; monitor HR and BP' }
    ],
    food: [
      'No significant food interactions with inhaled formulation',
      'Caffeine may enhance tremor and cardiovascular effects'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'No clear teratogenic risk in humans. Use if benefits outweigh risks. Uncontrolled asthma more dangerous than medication.',
      second: 'Inhaled salbutamol considered safe. IV use for tocolysis has more risks.',
      third: 'Inhaled safe for asthma. IV tocolysis: risk of maternal pulmonary edema, hyperglycemia, hypokalemia, fetal tachycardia.'
    },
    recommendation: 'Inhaled salbutamol is preferred rescue bronchodilator in pregnancy. Uncontrolled asthma is greater risk to mother and fetus than medication. IV tocolysis use has significant risks and requires close monitoring.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in small amounts. Inhaled salbutamol considered compatible with breastfeeding. Monitor infant for irritability or tremor, though unlikely with inhaled use.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Peak expiratory flow (PEF) or spirometry', reason: 'Establish baseline lung function' },
      { test: 'Heart rate and blood pressure', reason: 'Baseline for monitoring cardiovascular effects' },
      { test: 'Serum potassium', reason: 'Baseline if using high doses or patient at risk' }
    ],
    ongoing: [
      { test: 'Symptom assessment/frequency of use', frequency: 'Every visit', target: 'SABA use ≤2 days/week indicates good asthma control' },
      { test: 'Peak flow or spirometry', frequency: 'As indicated by asthma action plan', target: '>80% personal best' },
      { test: 'Heart rate', frequency: 'During acute treatment', target: 'Avoid persistent tachycardia >120 bpm' },
      { test: 'Serum potassium', frequency: 'With high-dose therapy or concurrent risk factors', target: '>3.5 mEq/L' }
    ],
    therapeutic: {
      range: 'No drug level monitoring. Clinical response (symptom relief, PEF improvement) guides therapy.',
      timing: 'Effect begins within 5-15 minutes of inhalation; peak at 30-60 minutes'
    }
  },
  storage: {
    temperature: 'MDI: 15-25°C (59-77°F); protect from freezing. Nebulizer solution: 15-30°C.',
    light: 'Protect from direct sunlight',
    humidity: 'Keep MDI dry; store with cap on',
    specialInstructions: 'MDI: Do not puncture or expose to heat/fire. Discard when counter shows 0 or 12 months after opening. Nebules: use within 3 months of opening foil pouch.',
    shelfLife: 'MDI: typically 2 years; check expiry. Nebules: check individual product labeling.'
  },
  africanContext: {
    availability: 'Widely available across Africa. WHO Essential Medicine. MDIs and nebulizer solutions present in most health facilities. Spacers often not available.',
    affordability: 'Relatively affordable; MDI approximately $2-10 USD. Nebulizer solutions also affordable. Cost of spacers may be barrier.',
    supplyChainNotes: 'MDIs contain pressurized propellant - sensitive to heat and direct sun. Store properly. Generic versions widely available.',
    localManufacturing: 'Some local manufacturing in South Africa, Egypt, Morocco. Most imported from India and multinational companies.',
    alternatives: [
      'Epinephrine (for anaphylaxis/severe bronchospasm if salbutamol unavailable)',
      'Terbutaline (alternative SABA)',
      'Ipratropium (anticholinergic bronchodilator)',
      'Oral salbutamol (less effective than inhaled but option if inhalers unavailable)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Asthma and COPD increasingly common across Africa. Access to inhalers variable. Spacers critical for effective MDI use but often unavailable - improvised spacers from plastic bottles can be effective. Nebulizers require electricity which may be unreliable. MDI with spacer as effective as nebulizer for most acute asthma - promote this in resource-limited settings. Over-reliance on SABA alone (without controllers) common - educate about need for inhaled corticosteroids. Biomass fuel exposure contributes to respiratory disease - consider environmental interventions.'
  },
  patientCounseling: {
    keyPoints: [
      'This is a "reliever" or "rescue" medicine - use when breathing is difficult',
      'Learn proper inhaler technique - ask your doctor or pharmacist to show you',
      'Use a spacer device if available - helps medicine reach your lungs better',
      'If you need to use this more than twice a week, your asthma may not be well controlled - see your doctor',
      'Carry your inhaler with you at all times',
      'Check expiry date and remaining doses regularly'
    ],
    commonMisconceptions: [
      'Misconception: Blue inhaler is all I need for asthma - Truth: Most people with asthma need a "controller" (usually brown/orange inhaler) daily to prevent attacks',
      'Misconception: Inhaler is addictive - Truth: Not addictive; needing it frequently means asthma is not controlled',
      'Misconception: Should use inhaler before every exercise - Truth: If needed before every exercise, asthma control is poor; see doctor',
      'Misconception: Can stop using when feeling better - Truth: Asthma is chronic; follow your action plan'
    ],
    adherenceTips: [
      'Keep inhaler where you will remember it (beside bed, in bag)',
      'Check inhaler is not empty before you need it urgently',
      'Learn your asthma triggers and avoid them',
      'Have written asthma action plan and follow it',
      'Rinse spacer weekly; let air dry'
    ],
    warningSignsToReport: [
      'Needing reliever more than twice a week',
      'Waking at night due to breathing problems',
      'Reliever not lasting usual 4 hours',
      'Peak flow dropping below personal best',
      'Rapid heartbeat or chest pain after using inhaler',
      'Inhaler making breathing worse (rare but stop and seek help)'
    ]
  },
  clinicalPearls: [
    'MDI + spacer is as effective as nebulizer for acute asthma in most patients - promotes this in resource-limited settings where nebulizers and electricity unreliable',
    'Frequent SABA use (>2 days/week or >1 canister/month) indicates poor asthma control - step up controller therapy, do not just refill SABA',
    'GINA 2019+ recommends ICS-formoterol as preferred reliever in adults with asthma (reduces exacerbations vs SABA alone) - SABA-only treatment no longer preferred',
    'In acute severe asthma, continuous nebulization (10-15 mg/hour) may be more effective than intermittent dosing',
    'Hypokalemia in severe asthma is multifactorial: salbutamol, hypoxia, and steroids all contribute - monitor and replace K+',
    'For hyperkalemia: nebulized salbutamol 10-20 mg shifts K+ 0.5-1.0 mEq/L within 30 minutes; use as adjunct to insulin/dextrose, not alone',
    'Paradoxical bronchospasm can occur - if patient worsens after salbutamol, this may be the cause; switch to alternative',
    'Improvised spacers from 500 mL plastic bottles are effective when commercial spacers unavailable - cut hole in bottom for MDI, inhale from bottle opening',
    'In Africa, wood/charcoal smoke exposure is major contributor to respiratory disease - inhaler alone cannot overcome ongoing environmental exposure'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Global Initiative for Asthma (GINA) 2023 Report',
    'Global Initiative for Chronic Obstructive Lung Disease (GOLD) 2023',
    'British Thoracic Society/SIGN Asthma Guideline 2019',
    'Rodrigo GJ et al. Cochrane Review - Continuous vs intermittent beta-agonists for acute asthma',
    'Cates CJ et al. Cochrane Review - Spacer devices for inhaled therapy',
    'African Thoracic Society Guidelines',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'beclomethasone-032',
  genericName: 'Beclomethasone Dipropionate',
  brandNames: ['Qvar', 'Beclovent', 'Vanceril', 'Becotide', 'Clenil', 'Beconase (nasal)'],
  atcCode: 'R03BA01',
  therapeuticClass: 'Inhaled Corticosteroid (ICS)',
  pharmacology: {
    mechanism: 'Synthetic glucocorticoid with potent anti-inflammatory activity. Binds to intracellular glucocorticoid receptors, forming a complex that modifies gene transcription. Suppresses inflammatory cytokines, reduces eosinophil infiltration, decreases mast cell mediator release, inhibits leukotriene synthesis, and reduces airway hyperresponsiveness. Effects develop over days to weeks with regular use.',
    pharmacokinetics: {
      absorption: 'Inhaled: 10-25% reaches lower airways depending on device and technique. Absorbed fraction undergoes significant first-pass metabolism. Systemic bioavailability approximately 20% (HFA formulation). Swallowed portion (75-90%) has low bioavailability due to extensive first-pass metabolism.',
      distribution: 'Volume of distribution approximately 20 L/kg. Protein binding 87%. Converted to active metabolite beclomethasone-17-monopropionate (17-BMP) in lungs.',
      metabolism: 'Rapidly converted to active 17-BMP in airways by esterases. Further hepatic metabolism by CYP3A4 to inactive metabolites. High first-pass metabolism (>90%) limits systemic exposure from swallowed drug.',
      elimination: 'Primarily fecal (>90%) and renal (<15%). Elimination half-life of 17-BMP approximately 2.8 hours.',
      halfLife: 'Beclomethasone: 0.5 hours; Active metabolite (17-BMP): 2.8 hours'
    },
    pharmacodynamics: 'Onset of clinical effect: improvement may be seen within 24 hours, but full benefit requires 1-4 weeks of regular use. Effect duration is dependent on continued regular use - not for acute symptom relief. Reduces airway inflammation, decreases bronchial hyperreactivity, and prevents asthma exacerbations.'
  },
  indications: [
    {
      condition: 'Persistent Asthma - Maintenance Therapy',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line controller therapy for persistent asthma (mild, moderate, severe). Most effective maintenance treatment for asthma. GINA recommends ICS-containing therapy for all patients with asthma.'
    },
    {
      condition: 'Asthma - Step-Down from Oral Corticosteroids',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Allows reduction or elimination of oral corticosteroids in many patients while maintaining asthma control.'
    },
    {
      condition: 'COPD with Frequent Exacerbations',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Consider ICS (usually combined with LABA) in COPD patients with history of exacerbations despite bronchodilator therapy. Higher pneumonia risk with ICS in COPD.'
    },
    {
      condition: 'Allergic Rhinitis (Nasal Formulation)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Intranasal beclomethasone highly effective for allergic rhinitis. Different formulation from inhaled product.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Low dose: 100-200 mcg/day; Medium dose: 200-400 mcg/day; High dose: >400 mcg/day (HFA formulation). Dose depends on asthma severity and previous therapy.',
      indicationSpecific: [
        { indication: 'Mild persistent asthma', dose: 'Low dose: 80-160 mcg/day (HFA) in 1-2 divided doses' },
        { indication: 'Moderate persistent asthma', dose: 'Medium dose: 160-320 mcg/day (HFA) in 2 divided doses' },
        { indication: 'Severe persistent asthma', dose: 'High dose: 320-640 mcg/day (HFA) in 2 divided doses; consider add-on therapy' },
        { indication: 'Steroid-dependent asthma', dose: 'High dose: up to 800 mcg/day (HFA); taper oral steroids as tolerated' },
        { indication: 'Allergic rhinitis (nasal)', dose: '100-200 mcg per nostril twice daily initially, reduce to once daily for maintenance' }
      ],
      maxDose: '800 mcg/day (HFA); higher doses have not shown additional benefit and increase systemic effects',
      renalImpairment: {
        mild: 'No adjustment required',
        moderate: 'No adjustment required',
        severe: 'No adjustment required; minimal systemic absorption',
        dialysis: 'No adjustment required'
      },
      hepaticImpairment: 'Use with caution in severe hepatic impairment; may have increased systemic exposure due to reduced first-pass metabolism.'
    },
    pediatric: {
      standard: 'Low dose: 50-100 mcg/day; Medium dose: 100-200 mcg/day; High dose: >200 mcg/day. Always use lowest effective dose.',
      weightBased: false,
      ageSpecific: [
        { age: '5-11 years', dose: 'Low: 50-100 mcg/day; Medium: 100-200 mcg/day; High: >200 mcg/day in 2 divided doses' },
        { age: '≥12 years', dose: 'Adult dosing applies' },
        { age: '<5 years', dose: 'Limited data; 100-200 mcg/day via spacer with mask may be used; nebulized budesonide often preferred' }
      ],
      maxDose: '400 mcg/day in children 5-11 years; use lowest effective dose to minimize growth effects',
      neonatal: 'Not typically used in neonates; budesonide nebulizer solution preferred if ICS needed'
    },
    elderly: 'Standard adult dosing. Monitor for oral candidiasis and systemic effects. Ensure proper inhaler technique.',
    routes: ['Inhalation (MDI)', 'Inhalation (DPI)', 'Nasal (for rhinitis)'],
    administration: [
      'Use spacer device with MDI for optimal lung deposition and reduced oropharyngeal deposition',
      'Rinse mouth with water and spit after each use to prevent oral candidiasis and hoarseness',
      'Regular daily use required - not for acute symptom relief',
      'Take at same times each day for consistent control',
      'Prime MDI before first use and after periods of non-use',
      'Shake MDI well before each use'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to beclomethasone or any component',
      'Primary treatment of acute asthma attack or status asthmaticus (not a rescue medication)',
      'Untreated systemic fungal, bacterial, or viral infections'
    ],
    relative: [
      'Active or quiescent pulmonary tuberculosis',
      'Untreated fungal, bacterial, or viral respiratory infections',
      'Ocular herpes simplex',
      'Transferring from systemic corticosteroids (risk of adrenal insufficiency)',
      'Immunocompromised patients'
    ],
    precautions: [
      'Not for acute bronchospasm relief - always have rescue bronchodilator available',
      'Rinse mouth after use to prevent candidiasis',
      'Monitor growth velocity in children on long-term therapy',
      'Risk of adrenal suppression with high doses or in patients transitioning from oral steroids',
      'May mask symptoms of infection',
      'Increased risk of pneumonia in COPD patients',
      'Paradoxical bronchospasm can occur rarely'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Oral candidiasis (thrush)', frequency: '5-10%', management: 'Rinse mouth and spit after each use; use spacer; treat with nystatin or clotrimazole if occurs' },
      { effect: 'Dysphonia (hoarseness)', frequency: '5-10%', management: 'Rinse mouth after use; use spacer; may need dose reduction or formulation change' },
      { effect: 'Pharyngitis/throat irritation', frequency: '5-10%', management: 'Use spacer; rinse mouth; usually improves with technique correction' },
      { effect: 'Cough after inhalation', frequency: '3-5%', management: 'Inhale slowly; use spacer; consider alternative ICS if persistent' },
      { effect: 'Headache', frequency: '3-5%', management: 'Symptomatic treatment; usually transient' }
    ],
    serious: [
      { effect: 'Adrenal suppression', frequency: 'Rare at low-medium doses', management: 'Use lowest effective dose. If high doses required, consider morning dosing. Stress dose steroids may be needed during illness/surgery.', warning: 'Risk increases with dose and duration; higher risk when transitioning from oral steroids' },
      { effect: 'Growth suppression in children', frequency: 'Variable', management: 'Use lowest effective dose; monitor height regularly (every 6-12 months); growth usually catches up', warning: 'Mean reduction approximately 1 cm in first year; less with continued use' },
      { effect: 'Osteoporosis (high-dose long-term)', frequency: 'Rare', management: 'Use lowest effective dose; consider bone density monitoring with high-dose long-term use; calcium/vitamin D supplementation', warning: 'Risk mainly with high doses >800 mcg/day long-term' },
      { effect: 'Cataracts/glaucoma', frequency: 'Rare', management: 'Periodic eye exams recommended with long-term high-dose use', warning: 'Risk increases with dose and duration of use' },
      { effect: 'Paradoxical bronchospasm', frequency: 'Rare', management: 'Discontinue and use alternative ICS; treat bronchospasm with SABA', warning: 'Stop if breathing worsens immediately after inhalation' },
      { effect: 'Pneumonia (in COPD)', frequency: 'Increased risk', management: 'Monitor for signs of pneumonia; consider ICS withdrawal if recurrent pneumonia', warning: 'COPD patients on ICS have higher pneumonia risk; balance against exacerbation reduction' }
    ],
    rare: [
      'Allergic reactions (rash, urticaria, angioedema)',
      'Cushing syndrome (very high doses)',
      'Hyperglycemia',
      'Psychiatric effects (anxiety, depression, behavioral changes)',
      'Easy bruising',
      'Immunosuppression'
    ]
  },
  interactions: {
    major: [
      { drug: 'Strong CYP3A4 inhibitors (ritonavir, ketoconazole, itraconazole)', effect: 'Increased systemic corticosteroid exposure and risk of adrenal suppression', mechanism: 'CYP3A4 inhibition reduces beclomethasone metabolism', management: 'Avoid combination if possible; if necessary, monitor for systemic corticosteroid effects and consider dose reduction' }
    ],
    moderate: [
      { drug: 'Moderate CYP3A4 inhibitors (erythromycin, diltiazem)', effect: 'Possible increased systemic exposure', management: 'Monitor for systemic effects with prolonged use' },
      { drug: 'Other corticosteroids', effect: 'Additive systemic corticosteroid effects', management: 'Be aware of total corticosteroid burden; monitor for adrenal suppression' }
    ],
    food: [
      'No significant food interactions with inhaled formulation'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'No clear teratogenic risk. Poorly controlled asthma is greater risk to fetus than ICS use. Continue ICS if needed for asthma control.',
      second: 'Continue use for asthma control. Budesonide has most pregnancy safety data among ICS, but beclomethasone also considered acceptable.',
      third: 'Continue use. Inadequately controlled asthma associated with preeclampsia, preterm birth, low birth weight.'
    },
    recommendation: 'Inhaled corticosteroids are recommended for asthma control during pregnancy. Uncontrolled asthma poses greater risk to mother and fetus than ICS. Budesonide has most reassuring human data, but beclomethasone is acceptable if patient well-controlled on it.',
    lactation: {
      safe: true,
      notes: 'Inhaled corticosteroids at usual doses are compatible with breastfeeding. Minimal systemic absorption means minimal excretion in breast milk. Continue if needed for asthma control.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Spirometry (FEV1, FVC)', reason: 'Establish baseline lung function' },
      { test: 'Asthma symptom assessment', reason: 'Document baseline control level' },
      { test: 'Growth (height) in children', reason: 'Baseline for monitoring potential growth effects' }
    ],
    ongoing: [
      { test: 'Asthma control assessment', frequency: 'Every visit (1-3 months initially, then every 3-6 months)', target: 'Good asthma control (minimal symptoms, no exacerbations, normal activities)' },
      { test: 'Spirometry', frequency: 'At least annually; more often if unstable', target: 'FEV1 >80% predicted or personal best' },
      { test: 'Height in children', frequency: 'Every 6-12 months', target: 'Normal growth velocity' },
      { test: 'Oral examination', frequency: 'Periodically', target: 'No oral candidiasis' },
      { test: 'Inhaler technique', frequency: 'Every visit', target: 'Correct technique demonstrated' }
    ],
    therapeutic: {
      range: 'No drug level monitoring. Clinical response (symptom control, lung function, exacerbation frequency) guides therapy.',
      timing: 'Improvement may be seen within days, but 1-4 weeks of regular use needed for full effect. Reassess control at 1-3 months.'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F); do not freeze',
    light: 'Protect from direct sunlight',
    humidity: 'Store in dry place',
    specialInstructions: 'MDI: Do not puncture or expose to heat. Discard after labeled number of actuations or 3 months after opening.',
    shelfLife: '2-3 years unopened; follow manufacturer guidance after opening'
  },
  africanContext: {
    availability: 'Variably available across Africa. WHO Essential Medicine. MDI formulations more common; DPI less available. May not be consistently available at primary care level.',
    affordability: 'Moderate cost; approximately $5-15 USD per month. Cost is barrier for many patients requiring long-term use. Generic formulations improving accessibility.',
    supplyChainNotes: 'MDI formulation requires intact cold chain for optimal stability. Supply interruptions reported in some regions. Generic HFA inhalers increasingly available.',
    localManufacturing: 'Limited local manufacturing. Most imported from India, Europe, and multinational companies. Some availability from South Africa and Egyptian manufacturers.',
    alternatives: [
      'Budesonide (alternative ICS; nebulized form available)',
      'Fluticasone (alternative ICS; variable availability)',
      'Prednisolone (oral corticosteroid for exacerbations; not for maintenance)',
      'No true alternatives for ICS role in asthma - ICS are essential controllers'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'ICS are cornerstone of asthma management but significantly underused across Africa due to availability and cost barriers. Many patients use SABA alone, leading to poor outcomes. Provider and patient education needed about importance of daily controller therapy. Mouth rinsing critical to prevent candidiasis, especially in patients with HIV (high prevalence in parts of Africa). Tuberculosis must be excluded before starting ICS in high-TB-burden settings, as ICS may mask TB symptoms. Generic ICS formulations increasingly available - quality varies, so source from reliable suppliers.'
  },
  patientCounseling: {
    keyPoints: [
      'This is a "preventer" or "controller" medicine - use every day even when feeling well',
      'It does not work immediately for breathing problems - always carry your reliever (blue) inhaler',
      'Use a spacer device if available for better medicine delivery',
      'Rinse your mouth with water and spit after every use to prevent mouth infection',
      'Do not stop using even if you feel better - asthma is a chronic condition',
      'It may take 1-4 weeks of daily use to feel the full benefit'
    ],
    commonMisconceptions: [
      'Misconception: Only use when having symptoms - Truth: Must use every day for prevention; stopping allows inflammation to return',
      'Misconception: Steroid inhalers are dangerous like body-building steroids - Truth: Inhaled steroids are different; very safe at recommended doses',
      'Misconception: Can use this instead of blue inhaler during attack - Truth: This does not work quickly; always use reliever for acute symptoms',
      'Misconception: Can stop once asthma is controlled - Truth: Asthma is chronic; stopping allows it to worsen'
    ],
    adherenceTips: [
      'Link to daily routine - take with morning and evening tooth brushing',
      'Keep inhaler where you will see it (not hidden away)',
      'Use a reminder app or alarm on your phone',
      'Understand that daily use prevents the inflammation that causes asthma attacks',
      'Ask for a written asthma action plan from your doctor'
    ],
    warningSignsToReport: [
      'White patches or soreness in mouth (may be thrush)',
      'Persistent hoarseness that does not improve',
      'Asthma getting worse despite regular use',
      'Needing reliever inhaler more often',
      'Fever, cough with discolored sputum (may indicate infection)'
    ]
  },
  clinicalPearls: [
    'Inhaled corticosteroids are the most effective controller medications for asthma - no other drug class reduces exacerbations, prevents deaths, and improves quality of life as effectively',
    'GINA recommends that ALL patients with asthma should receive ICS-containing treatment - SABA-only treatment is no longer recommended',
    'Mouth rinsing after each use reduces oral candidiasis risk by 50-70% - emphasize this at every visit',
    'Spacer use improves lung deposition by 50% and reduces oropharyngeal side effects - always recommend spacer with MDI',
    'Growth suppression in children is approximately 1 cm in first year but appears to be non-progressive; final adult height usually unaffected',
    'When stepping down therapy, reduce ICS dose by 25-50% every 3 months if asthma well-controlled; do not stop ICS entirely in persistent asthma',
    'In COPD, ICS increase pneumonia risk but reduce exacerbations - use only in appropriate patients (frequent exacerbations, eosinophilic phenotype)',
    'Different ICS have different potencies - beclomethasone HFA is roughly equivalent to fluticasone at half the dose (e.g., 400 mcg BDP ≈ 200 mcg fluticasone)',
    'In high-TB-burden African settings, exclude active TB before starting ICS - ICS can mask TB symptoms and accelerate progression'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Global Initiative for Asthma (GINA) 2023 Report',
    'Global Initiative for Chronic Obstructive Lung Disease (GOLD) 2023',
    'British Thoracic Society/SIGN Asthma Guideline 2019',
    'Pauwels RA et al. Lancet 2003 - Early intervention with budesonide (START trial)',
    'Suissa S et al. NEJM 2000 - Inhaled corticosteroids and risk of death from asthma',
    'Pedersen S. Eur Respir J 2006 - ICS effects on growth in children',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'prednisolone-033',
  genericName: 'Prednisolone',
  brandNames: ['Prelone', 'Orapred', 'Pediapred', 'Deltacortril', 'Millipred', 'Predsol'],
  atcCode: 'H02AB06',
  therapeuticClass: 'Systemic Corticosteroid (Glucocorticoid)',
  pharmacology: {
    mechanism: 'Synthetic glucocorticoid with potent anti-inflammatory and immunosuppressive effects. Binds to cytoplasmic glucocorticoid receptors, forming complexes that translocate to the nucleus and modify gene transcription. Suppresses inflammatory cytokines (IL-1, IL-6, TNF-alpha), inhibits phospholipase A2 (reducing prostaglandins and leukotrienes), stabilizes cell membranes, reduces capillary permeability, and suppresses immune cell function. Also has mineralocorticoid activity causing sodium retention and potassium excretion.',
    pharmacokinetics: {
      absorption: 'Well absorbed orally with bioavailability >80%. Peak plasma concentration in 1-2 hours. Food may delay but does not reduce absorption.',
      distribution: 'Volume of distribution 0.4-0.7 L/kg. Protein binding 70-90% (mainly to corticosteroid-binding globulin and albumin). Crosses placenta and blood-brain barrier.',
      metabolism: 'Hepatic metabolism via 11-beta-hydroxysteroid dehydrogenase and CYP3A4. Prednisone is a prodrug converted to prednisolone (active form) in liver. Multiple inactive metabolites formed.',
      elimination: 'Primarily renal excretion of metabolites. <5% excreted unchanged in urine.',
      halfLife: '2-4 hours (plasma); biological half-life 18-36 hours (duration of physiologic effect)'
    },
    pharmacodynamics: 'Onset of anti-inflammatory effect within hours; peak effect varies by condition. Duration of effect exceeds plasma half-life (biological half-life 18-36 hours). Glucocorticoid potency approximately 4x hydrocortisone; mineralocorticoid potency 0.8x hydrocortisone. HPA axis suppression can occur with prolonged use.'
  },
  indications: [
    {
      condition: 'Acute Asthma Exacerbation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Short course (3-7 days) reduces hospital admissions, improves recovery, and prevents relapse. Oral as effective as IV for most patients.'
    },
    {
      condition: 'COPD Exacerbation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Short course (5 days) improves lung function and reduces treatment failure. Not for stable COPD maintenance.'
    },
    {
      condition: 'Allergic Reactions (Moderate-Severe)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'For severe allergic reactions, drug reactions, contact dermatitis. Short course usually sufficient.'
    },
    {
      condition: 'Autoimmune Diseases',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Including rheumatoid arthritis, SLE, inflammatory bowel disease, vasculitis. Often requires prolonged therapy with steroid-sparing agents.'
    },
    {
      condition: 'Inflammatory Bowel Disease (Acute Flares)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for inducing remission in Crohn disease and ulcerative colitis. Not for maintenance due to side effects.'
    },
    {
      condition: 'Nephrotic Syndrome',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line for steroid-sensitive nephrotic syndrome, especially in children. Prolonged courses often needed.'
    },
    {
      condition: 'Croup (Laryngotracheobronchitis)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Single dose or short course highly effective. Dexamethasone often preferred but prednisolone is alternative.'
    },
    {
      condition: 'Organ Transplant Rejection Prevention',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Component of immunosuppressive regimens. Often tapered to low maintenance dose.'
    },
    {
      condition: 'Bell Palsy',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Started within 72 hours of onset improves complete recovery rate.'
    },
    {
      condition: 'Adrenal Insufficiency (Acute)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: false,
      notes: 'Hydrocortisone preferred for acute crisis (better mineralocorticoid effect), but prednisolone used for maintenance.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Highly variable by indication. Anti-inflammatory: 5-60 mg/day. Immunosuppression: 1-2 mg/kg/day. Always use lowest effective dose for shortest duration.',
      indicationSpecific: [
        { indication: 'Acute asthma exacerbation', dose: '40-50 mg once daily for 5-7 days; no taper needed for short courses' },
        { indication: 'COPD exacerbation', dose: '40 mg once daily for 5 days' },
        { indication: 'Allergic reactions', dose: '30-60 mg/day for 5-14 days depending on severity' },
        { indication: 'Rheumatoid arthritis', dose: '5-10 mg/day maintenance; higher during flares' },
        { indication: 'SLE flare', dose: '1-2 mg/kg/day initially, tapering to lowest effective dose' },
        { indication: 'IBD (moderate-severe)', dose: '40-60 mg/day, tapered over 8-12 weeks' },
        { indication: 'Bell palsy', dose: '60 mg/day for 5 days, then taper over 5 days (or 25 mg twice daily for 10 days)' },
        { indication: 'Giant cell arteritis', dose: '40-60 mg/day initially; taper over months guided by symptoms and ESR/CRP' }
      ],
      maxDose: 'No absolute maximum for short-term use in life-threatening conditions. Chronic use: minimize dose to reduce adverse effects.',
      renalImpairment: {
        mild: 'No adjustment typically required',
        moderate: 'No adjustment typically required; monitor for fluid retention',
        severe: 'Use with caution; may need dose reduction; monitor closely',
        dialysis: 'Supplemental dose generally not required after dialysis'
      },
      hepaticImpairment: 'Prednisone requires hepatic conversion to prednisolone. In severe hepatic impairment, use prednisolone directly (not prednisone). May have prolonged effect due to reduced metabolism.'
    },
    pediatric: {
      standard: 'Varies by indication. Typically 0.5-2 mg/kg/day. Use lowest effective dose.',
      weightBased: true,
      ageSpecific: [
        { age: 'Acute asthma (all ages)', dose: '1-2 mg/kg/day (max 40-60 mg) for 3-5 days' },
        { age: 'Croup', dose: '1-2 mg/kg single dose (max 60 mg); or dexamethasone 0.15-0.6 mg/kg preferred' },
        { age: 'Nephrotic syndrome (initial)', dose: '2 mg/kg/day (max 60 mg) for 4-6 weeks, then alternate-day tapering' },
        { age: 'JIA/Juvenile SLE', dose: '1-2 mg/kg/day initially, taper to lowest effective dose' },
        { age: 'Infantile spasms', dose: '2-3 mg/kg/day divided twice daily for 2-4 weeks, then taper' }
      ],
      maxDose: 'Generally 60 mg/day for most indications; 80 mg/day occasionally used short-term',
      neonatal: 'Limited indications. Bronchopulmonary dysplasia: 0.5-2 mg/kg/day (dexamethasone often preferred). Use shortest duration possible due to neurodevelopmental concerns.'
    },
    elderly: 'Use lowest effective dose. Increased risk of osteoporosis, diabetes, hypertension, and infections. Monitor closely.',
    routes: ['Oral (tablets, liquid)', 'Intravenous (as sodium succinate)', 'Intramuscular', 'Intra-articular'],
    administration: [
      'Take with food or milk to reduce GI irritation',
      'Once-daily dosing in morning mimics physiologic cortisol rhythm and minimizes HPA suppression',
      'For doses >40 mg/day or divided dosing, consider splitting to morning and early afternoon',
      'Do not stop abruptly after prolonged use - taper gradually to allow HPA axis recovery',
      'Enteric-coated tablets should not be crushed',
      'Liquid formulations available for children or those who cannot swallow tablets'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to prednisolone or any component',
      'Systemic fungal infections (unless treating the infection with antifungals)',
      'Live vaccine administration (during immunosuppressive doses)'
    ],
    relative: [
      'Active untreated infections (bacterial, viral, parasitic)',
      'Active tuberculosis (unless on adequate TB treatment)',
      'Uncontrolled diabetes mellitus',
      'Uncontrolled hypertension',
      'Severe osteoporosis',
      'Active peptic ulcer disease',
      'Psychotic disorders (may exacerbate)',
      'Glaucoma or cataracts',
      'Congestive heart failure',
      'Recent GI surgery (risk of perforation)'
    ],
    precautions: [
      'Screen for latent TB before prolonged therapy in high-risk populations',
      'Monitor blood glucose - can cause or worsen diabetes',
      'Monitor blood pressure - sodium retention and hypertension',
      'Consider bone protection (calcium, vitamin D, bisphosphonates) for prolonged use',
      'Increased infection risk - monitor for signs of infection',
      'Adrenal suppression with prolonged use - do not stop abruptly',
      'May mask signs of infection',
      'Risk of GI bleeding increased with concurrent NSAIDs'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Increased appetite and weight gain', frequency: '30-50%', management: 'Dietary counseling; usually reversible on discontinuation' },
      { effect: 'Insomnia', frequency: '20-30%', management: 'Take morning dose; avoid evening dosing' },
      { effect: 'Mood changes (euphoria, irritability)', frequency: '20-30%', management: 'Usually mild; reassure patient; may need dose reduction' },
      { effect: 'Hyperglycemia', frequency: '10-20%', management: 'Monitor glucose; adjust diabetes therapy if needed; usually reversible' },
      { effect: 'Fluid retention/edema', frequency: '10-20%', management: 'Sodium restriction; may need diuretics' },
      { effect: 'Dyspepsia', frequency: '10-15%', management: 'Take with food; consider PPI if high-risk or symptomatic' }
    ],
    serious: [
      { effect: 'Adrenal suppression/insufficiency', frequency: 'Common with prolonged use', management: 'Taper slowly when discontinuing after prolonged use. Stress dose steroids during illness/surgery.', warning: 'Can be life-threatening if abrupt withdrawal after prolonged use' },
      { effect: 'Osteoporosis and fractures', frequency: '30-50% with chronic use', management: 'Calcium and vitamin D supplementation; bisphosphonates for high-risk patients; DEXA monitoring', warning: 'Bone loss begins within first few weeks of therapy' },
      { effect: 'Immunosuppression and infections', frequency: 'Variable', management: 'Vigilant monitoring; low threshold for investigating infection; prophylaxis in some cases (e.g., PCP if high-dose prolonged)', warning: 'Opportunistic infections can be fatal' },
      { effect: 'Diabetes mellitus (steroid-induced)', frequency: '5-15%', management: 'Monitor glucose regularly; treat with diet, oral agents, or insulin as needed', warning: 'May unmask latent diabetes or worsen existing diabetes' },
      { effect: 'Peptic ulcer disease and GI bleeding', frequency: '2-5%', management: 'Avoid concurrent NSAIDs; consider PPI prophylaxis in high-risk patients', warning: 'Risk especially high with concurrent NSAID use' },
      { effect: 'Avascular necrosis (osteonecrosis)', frequency: '1-5% with high-dose prolonged use', management: 'MRI if joint pain; orthopedic referral; may need surgical intervention', warning: 'Most commonly affects femoral head; hip pain in steroid user should raise suspicion' },
      { effect: 'Psychiatric effects (psychosis, severe depression)', frequency: '1-5%', management: 'Reduce dose or discontinue if possible; psychiatric consultation; antipsychotics or antidepressants may be needed', warning: 'More common with high doses; usually reversible' },
      { effect: 'Cataracts', frequency: 'Variable with prolonged use', management: 'Periodic ophthalmologic examination', warning: 'Posterior subcapsular cataracts most common' },
      { effect: 'Glaucoma', frequency: 'Variable', management: 'Monitor intraocular pressure in at-risk patients', warning: 'Can occur with any route including inhaled' }
    ],
    rare: [
      'Pancreatitis',
      'Myopathy (proximal muscle weakness)',
      'Tendon rupture',
      'Growth suppression in children',
      'Pseudotumor cerebri',
      'Kaposi sarcoma exacerbation',
      'Hypersensitivity reactions'
    ]
  },
  interactions: {
    major: [
      { drug: 'Live vaccines (MMR, varicella, yellow fever, BCG)', effect: 'Risk of disseminated infection from vaccine virus', mechanism: 'Immunosuppression allows vaccine virus replication', management: 'Avoid live vaccines during immunosuppressive steroid therapy (generally >20 mg/day for >2 weeks); wait 1 month after stopping before vaccination' },
      { drug: 'NSAIDs', effect: 'Increased risk of GI ulceration and bleeding', mechanism: 'Both damage gastric mucosa; steroids mask symptoms', management: 'Avoid combination if possible; if needed, use PPI prophylaxis' },
      { drug: 'Fluoroquinolones', effect: 'Increased risk of tendon rupture', mechanism: 'Both affect tendon integrity', management: 'Avoid combination if possible; use alternative antibiotics' }
    ],
    moderate: [
      { drug: 'CYP3A4 inducers (rifampicin, phenytoin, carbamazepine)', effect: 'Reduced prednisolone effect', management: 'May need increased prednisolone dose during concurrent therapy' },
      { drug: 'CYP3A4 inhibitors (ketoconazole, itraconazole, ritonavir)', effect: 'Increased prednisolone levels and effects', management: 'Monitor for steroid toxicity; may need dose reduction' },
      { drug: 'Anticoagulants (warfarin)', effect: 'Variable effect on INR; usually decreased', management: 'Monitor INR closely and adjust anticoagulant dose' },
      { drug: 'Antidiabetic agents', effect: 'Reduced hypoglycemic effect', management: 'Monitor blood glucose; increase antidiabetic doses as needed' },
      { drug: 'Potassium-depleting diuretics', effect: 'Additive hypokalemia', management: 'Monitor potassium; supplement as needed' },
      { drug: 'Digoxin', effect: 'Hypokalemia from steroids increases digoxin toxicity', management: 'Monitor potassium and digoxin levels' }
    ],
    food: [
      'Take with food to reduce GI irritation',
      'High sodium intake may worsen fluid retention',
      'Grapefruit juice may increase prednisolone levels (CYP3A4 inhibition)'
    ]
  },
  pregnancy: {
    category: 'C (D for first trimester in some classifications)',
    trimpiesterRisks: {
      first: 'Possible small increased risk of cleft palate with first trimester exposure. Use only if clearly needed.',
      second: 'May affect fetal growth. Use lowest effective dose.',
      third: 'Risk of fetal adrenal suppression with prolonged high-dose use. May cause neonatal hypoglycemia.'
    },
    recommendation: 'Use when benefits outweigh risks. Prednisolone preferred over prednisone (does not require hepatic conversion). Use lowest effective dose. Monitor neonate for adrenal insufficiency if prolonged maternal use. Antenatal corticosteroids (usually betamethasone or dexamethasone) given for fetal lung maturity are different indication.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk. At maternal doses ≤20 mg/day, infant exposure is low. For higher doses, consider waiting 4 hours after dose before breastfeeding to minimize exposure. Monitor infant for growth if prolonged maternal high-dose use.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Blood glucose', reason: 'Steroids cause hyperglycemia; establish baseline' },
      { test: 'Blood pressure', reason: 'Steroids cause sodium retention and hypertension' },
      { test: 'Electrolytes (potassium)', reason: 'Steroids cause hypokalemia' },
      { test: 'Weight', reason: 'Monitor for weight gain' },
      { test: 'Bone density (DEXA)', reason: 'If planning prolonged therapy; baseline for monitoring' },
      { test: 'Eye examination', reason: 'If planning prolonged therapy; baseline for cataracts/glaucoma' },
      { test: 'TB screening (PPD/IGRA)', reason: 'In high-risk populations before prolonged therapy' }
    ],
    ongoing: [
      { test: 'Blood glucose', frequency: 'Weekly initially; less often once stable', target: 'Fasting <126 mg/dL; adjust diabetes therapy if elevated' },
      { test: 'Blood pressure', frequency: 'Each visit', target: '<140/90 mmHg' },
      { test: 'Potassium', frequency: 'Periodically, especially if on diuretics', target: '>3.5 mEq/L' },
      { test: 'Weight', frequency: 'Each visit', target: 'Stable; address if significant gain' },
      { test: 'Bone density', frequency: 'Every 1-2 years if on prolonged therapy', target: 'T-score >-1.0; treat if osteoporotic' },
      { test: 'Signs of infection', frequency: 'Each visit', target: 'No opportunistic infections' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring. Clinical response and adverse effects guide dosing.',
      timing: 'Effect varies by condition; acute inflammation responds within hours; autoimmune conditions may take days to weeks'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Keep in original container. Do not remove desiccant if present.',
    shelfLife: '2-3 years; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa in tablet form. WHO Essential Medicine. Present in most health facilities including primary care.',
    affordability: 'Very affordable; approximately $1-5 USD per month at typical doses. One of most accessible medications.',
    supplyChainNotes: 'Stable supply; heat-stable for storage. Multiple generic manufacturers ensure good availability.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, Evans, May & Baker), South Africa (Aspen, Adcock Ingram), Kenya, Egypt, and across North Africa.',
    alternatives: [
      'Prednisone (requires hepatic conversion; equivalent efficacy)',
      'Dexamethasone (longer-acting; higher potency)',
      'Hydrocortisone (for adrenal insufficiency; more mineralocorticoid effect)',
      'Methylprednisolone (for IV use; pulse therapy)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial). Note: Steroid responsiveness varies in some conditions (steroid-resistant nephrotic syndrome, steroid-refractory asthma).',
    endemicConsiderations: 'Prednisolone is essential and widely used across Africa. Key considerations: (1) TB - screen for latent/active TB before prolonged therapy in high-burden settings; steroids can reactivate TB or mask symptoms; (2) HIV - steroids increase infection risk in immunocompromised patients; consider PCP prophylaxis if high-dose prolonged use; (3) Strongyloides - can cause hyperinfection syndrome with steroids in endemic areas; consider empiric ivermectin before prolonged therapy; (4) Diabetes and hypertension are increasing across Africa - monitor and manage steroid-induced metabolic effects; (5) Limited access to DEXA scanning - clinical risk assessment for osteoporosis important.'
  },
  patientCounseling: {
    keyPoints: [
      'Take with food to reduce stomach upset',
      'Take in the morning if once-daily dosing to mimic your body natural rhythm',
      'Do not stop taking suddenly if you have been on it for more than a few weeks - must taper slowly',
      'Carry a steroid card or wear medical ID if on long-term therapy',
      'Report any signs of infection - steroids can mask symptoms',
      'Attend all follow-up appointments for blood pressure and blood sugar monitoring'
    ],
    commonMisconceptions: [
      'Misconception: Can stop when feeling better - Truth: Must complete course or taper as directed; sudden stopping can be dangerous',
      'Misconception: Steroids will make me look like a bodybuilder - Truth: These are different from anabolic steroids; weight gain is fluid and fat, not muscle',
      'Misconception: Short courses are harmless - Truth: Even short courses can affect blood sugar and mood; but usually safe when needed',
      'Misconception: All steroids are the same - Truth: Different types for different purposes; inhaled steroids for asthma are much safer than oral'
    ],
    adherenceTips: [
      'Take at same time each day, preferably morning',
      'Set alarm if prone to forgetting',
      'If given a tapering schedule, follow it exactly',
      'Do not run out - always have refills arranged',
      'Inform all healthcare providers you are taking steroids'
    ],
    warningSignsToReport: [
      'Fever or signs of infection',
      'Black tarry stools or blood in vomit',
      'Severe mood changes or confusion',
      'Vision changes',
      'Severe headache',
      'Unusual weakness or fatigue (may indicate adrenal crisis if stopping)',
      'Severe bone or joint pain',
      'Swelling of face or legs'
    ]
  },
  clinicalPearls: [
    'Morning dosing mimics physiologic cortisol rhythm and minimizes HPA axis suppression - preferred for once-daily dosing',
    'Short courses (≤3 weeks) generally do not require tapering; longer courses need gradual reduction to allow HPA axis recovery',
    'In severe hepatic impairment, use prednisolone instead of prednisone (prednisone requires hepatic conversion)',
    'Screen for latent TB (IGRA or PPD) before prolonged therapy in high-burden settings - steroids can reactivate TB',
    'Consider empiric ivermectin for Strongyloides in endemic areas before immunosuppressive steroid doses - hyperinfection syndrome can be fatal',
    'Steroid-induced osteoporosis begins within weeks - consider calcium, vitamin D, and bisphosphonates early for those needing prolonged therapy',
    'PPI prophylaxis is NOT routinely needed with steroids alone; reserve for concurrent NSAID use, anticoagulants, or prior GI bleeding',
    'For acute asthma, 3-5 days of steroids is as effective as longer courses and does not require tapering',
    'Steroid-induced hyperglycemia typically improves or resolves when steroids discontinued - may not need lifelong diabetes treatment',
    'Always check vaccination status before starting immunosuppressive steroids - complete live vaccines first if possible'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Global Initiative for Asthma (GINA) 2023 - Systemic corticosteroids in asthma',
    'GOLD 2023 - Corticosteroids in COPD exacerbations',
    'ACR Guidelines for Glucocorticoid-Induced Osteoporosis 2022',
    'Hoes JN et al. Ann Rheum Dis 2007 - EULAR recommendations for systemic corticosteroids',
    'Rhen T, Cidlowski JA. NEJM 2005 - Anti-inflammatory action of glucocorticoids',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'paracetamol-034',
  genericName: 'Paracetamol (Acetaminophen)',
  brandNames: ['Panadol', 'Tylenol', 'Calpol', 'Efferalgan', 'Perfalgan', 'Tempra', 'Mapap'],
  atcCode: 'N02BE01',
  therapeuticClass: 'Analgesic / Antipyretic',
  pharmacology: {
    mechanism: 'Mechanism not fully understood. Primarily acts centrally by inhibiting cyclooxygenase (COX) enzymes in the CNS, reducing prostaglandin synthesis in the hypothalamus (antipyretic effect) and possibly affecting pain perception. May also activate descending serotonergic pathways and interact with endocannabinoid system. Unlike NSAIDs, has minimal peripheral anti-inflammatory effect and does not inhibit platelet function or cause gastric irritation.',
    pharmacokinetics: {
      absorption: 'Rapidly and almost completely absorbed from GI tract (70-90% bioavailability). Peak plasma concentration in 30-60 minutes (immediate-release). Food delays but does not reduce absorption.',
      distribution: 'Volume of distribution 0.9-1.0 L/kg. Protein binding 10-25% (higher in overdose). Uniformly distributed; crosses placenta and blood-brain barrier.',
      metabolism: 'Extensively hepatic metabolism (>90%). Primary pathways: glucuronidation (40-67%) and sulfation (20-46%) to inactive conjugates. Minor pathway via CYP2E1 produces toxic metabolite NAPQI, detoxified by glutathione. In overdose, glutathione depleted, allowing NAPQI accumulation and hepatotoxicity.',
      elimination: 'Renal excretion of metabolites. <5% excreted unchanged. Elimination half-life 1.5-3 hours (prolonged in liver disease and overdose).',
      halfLife: '1.5-3 hours (normal); may exceed 4 hours in hepatic impairment or overdose'
    },
    pharmacodynamics: 'Onset of analgesia 30-60 minutes oral, 5-10 minutes IV. Peak effect 1-3 hours. Duration 4-6 hours. Antipyretic effect onset 30-60 minutes, duration 4-6 hours. Reduces fever by resetting hypothalamic thermostat via prostaglandin inhibition.'
  },
  indications: [
    {
      condition: 'Mild to Moderate Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line analgesic for headache, toothache, musculoskeletal pain, dysmenorrhea. Often combined with opioids for moderate-severe pain.'
    },
    {
      condition: 'Fever (Pyrexia)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line antipyretic in most patients including children. Safer than aspirin in children (no Reye syndrome risk).'
    },
    {
      condition: 'Osteoarthritis Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: true,
      notes: 'First-line oral analgesic per guidelines. Effect size modest; NSAIDs more effective but higher risk.'
    },
    {
      condition: 'Post-Operative Pain (Component of Multimodal Analgesia)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces opioid requirements when used as part of multimodal analgesia. IV formulation used perioperatively.'
    },
    {
      condition: 'Migraine and Tension Headache',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for mild-moderate headache. Often combined with caffeine for enhanced effect.'
    },
    {
      condition: 'Pain and Fever in Children',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Preferred over aspirin (Reye syndrome risk) and NSAIDs (better GI safety). Available in pediatric formulations.'
    }
  ],
  dosing: {
    adults: {
      standard: '500-1000 mg every 4-6 hours as needed. Maximum 4000 mg/day (3000 mg/day in chronic use or at-risk patients).',
      indicationSpecific: [
        { indication: 'Mild-moderate pain', dose: '500-1000 mg every 4-6 hours as needed; max 4000 mg/day' },
        { indication: 'Fever', dose: '500-1000 mg every 4-6 hours as needed' },
        { indication: 'Osteoarthritis', dose: '650-1000 mg every 6 hours; max 4000 mg/day; use regularly for chronic pain' },
        { indication: 'Post-operative (IV)', dose: '1000 mg IV every 6 hours; max 4000 mg/day' },
        { indication: 'Patients <50 kg', dose: '15 mg/kg every 6 hours; max 75 mg/kg/day or 3000 mg/day' }
      ],
      maxDose: '4000 mg/day for short-term use; 3000 mg/day recommended for chronic use or high-risk patients (elderly, hepatic impairment, malnutrition, chronic alcohol use)',
      renalImpairment: {
        mild: 'No adjustment required',
        moderate: 'No adjustment for single doses; extend interval to every 6 hours for chronic use',
        severe: 'CrCl <10 mL/min: Extend interval to every 8 hours; max 3000 mg/day',
        dialysis: 'Give after dialysis on dialysis days; supplement dose may be needed'
      },
      hepaticImpairment: 'Use with caution. Reduce dose and extend interval. Maximum 2000 mg/day in severe hepatic impairment. Avoid in acute hepatic failure.'
    },
    pediatric: {
      standard: '10-15 mg/kg every 4-6 hours; max 5 doses (75 mg/kg) per 24 hours.',
      weightBased: true,
      ageSpecific: [
        { age: 'Neonates (term) 0-28 days', dose: '10-15 mg/kg every 6-8 hours; max 40-60 mg/kg/day' },
        { age: 'Infants 1-3 months', dose: '10-15 mg/kg every 6-8 hours; max 60 mg/kg/day' },
        { age: 'Infants 3-12 months', dose: '10-15 mg/kg every 4-6 hours; max 75 mg/kg/day' },
        { age: 'Children 1-12 years', dose: '10-15 mg/kg every 4-6 hours; max 75 mg/kg/day or 4000 mg, whichever is less' },
        { age: '>12 years or >40 kg', dose: 'Adult dosing' },
        { age: 'Rectal (children)', dose: '15-20 mg/kg every 4-6 hours; slower absorption' }
      ],
      maxDose: '75 mg/kg/day or 4000 mg/day, whichever is less',
      neonatal: 'Term neonates: 10-15 mg/kg every 6-8 hours; max 40-60 mg/kg/day. Preterm: 10 mg/kg every 8-12 hours. IV: 7.5 mg/kg every 6 hours in neonates <10 days.'
    },
    elderly: 'Consider lower doses (max 3000 mg/day) especially with hepatic impairment, malnutrition, or dehydration. Risk of unintentional overdose from multiple products.',
    routes: ['Oral (tablets, capsules, liquid, dispersible)', 'Rectal (suppository)', 'Intravenous'],
    administration: [
      'May take with or without food; food may delay absorption',
      'Dispersible/effervescent tablets: dissolve in water before taking',
      'IV: Infuse over 15 minutes; for patients <50 kg, use weight-based dosing',
      'Suppositories: Higher doses may be needed (slower, less predictable absorption)',
      'Check all medications for paracetamol content to avoid unintentional overdose (common in combination products)'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to paracetamol or any component',
      'Severe hepatic impairment or acute hepatic failure',
      'Severe active liver disease'
    ],
    relative: [
      'Chronic alcohol use (>3 drinks/day) - increased hepatotoxicity risk',
      'Mild-moderate hepatic impairment (reduce dose)',
      'Malnutrition or fasting (depleted glutathione)',
      'G6PD deficiency (rare hemolysis reported at high doses)',
      'Severe renal impairment (extend interval)'
    ],
    precautions: [
      'Do not exceed recommended dose - hepatotoxicity risk',
      'Check other medications for paracetamol content (combination products common)',
      'Caution in chronic alcohol users - reduce maximum daily dose',
      'May cause false positive for glucose monitoring with some strips',
      'Inform patients about OTC products containing paracetamol',
      'Do not use to treat underlying disease - address cause of pain/fever'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Generally well tolerated at therapeutic doses', frequency: 'Rare (<1%)', management: 'Paracetamol is one of the safest analgesics when used correctly' }
    ],
    serious: [
      { effect: 'Hepatotoxicity (overdose)', frequency: 'Dose-related', management: 'N-acetylcysteine (NAC) is antidote; most effective within 8 hours of ingestion but beneficial up to 24+ hours. Liver transplant may be needed for fulminant failure.', warning: 'Single dose >150 mg/kg (7.5g in adult) is potentially hepatotoxic; risk begins at 10-15g. Check serum paracetamol and ALT.' },
      { effect: 'Acute liver failure (overdose)', frequency: 'Overdose-related', management: 'NAC antidote; supportive care; hepatology/transplant consultation for severe cases', warning: 'Leading cause of acute liver failure in developed countries; often unintentional from multiple products' },
      { effect: 'Severe skin reactions (SJS, TEN, AGEP)', frequency: 'Very rare', management: 'Stop immediately; supportive care; dermatology consultation', warning: 'Discontinue at first sign of rash or blistering' },
      { effect: 'Anaphylaxis', frequency: 'Very rare', management: 'Standard anaphylaxis treatment; epinephrine, antihistamines, steroids', warning: 'True allergy rare but can occur' },
      { effect: 'Thrombocytopenia, neutropenia', frequency: 'Very rare', management: 'Discontinue; supportive care; usually reversible', warning: 'Idiosyncratic reaction' }
    ],
    rare: [
      'Renal papillary necrosis (chronic high-dose use)',
      'Methemoglobinemia (massive overdose)',
      'Hemolytic anemia (G6PD deficiency, high doses)',
      'Bronchospasm in aspirin-sensitive asthmatics (rare cross-reactivity)',
      'Hypotension (with IV formulation)'
    ]
  },
  interactions: {
    major: [
      { drug: 'Chronic alcohol use (>3 drinks/day)', effect: 'Increased hepatotoxicity risk', mechanism: 'Alcohol induces CYP2E1 and depletes glutathione, increasing NAPQI formation and reducing detoxification', management: 'Reduce max dose to 2000-3000 mg/day in chronic heavy drinkers; caution patients' },
      { drug: 'Warfarin', effect: 'Increased INR with regular paracetamol use (>2g/day for >1 week)', mechanism: 'Unclear; may inhibit vitamin K-dependent clotting factor synthesis', management: 'Monitor INR more frequently; occasional use unlikely to be significant' }
    ],
    moderate: [
      { drug: 'Isoniazid', effect: 'Increased hepatotoxicity risk', management: 'Use with caution; monitor liver function' },
      { drug: 'Carbamazepine, phenytoin, phenobarbital', effect: 'May increase paracetamol metabolism (CYP induction) and reduce effect; may increase hepatotoxicity risk', management: 'Monitor for efficacy; use with caution regarding hepatotoxicity' },
      { drug: 'Probenecid', effect: 'Reduced paracetamol clearance', management: 'May need to reduce paracetamol dose or extend interval' },
      { drug: 'Metoclopramide, domperidone', effect: 'Increased paracetamol absorption rate', management: 'May enhance onset; useful in migraine' }
    ],
    food: [
      'Food delays but does not reduce absorption',
      'High-carbohydrate meal may slow absorption',
      'Fasting may increase hepatotoxicity risk (depleted glutathione)'
    ]
  },
  pregnancy: {
    category: 'B',
    trimpiesterRisks: {
      first: 'Extensive human data; no clear teratogenic risk. Preferred analgesic/antipyretic in pregnancy.',
      second: 'Safe for use when needed. Preferred over NSAIDs.',
      third: 'Safe for use when needed. Does not affect fetal ductus arteriosus (unlike NSAIDs).'
    },
    recommendation: 'Paracetamol is the preferred analgesic and antipyretic during pregnancy at all stages. Use lowest effective dose for shortest duration. No evidence of teratogenicity in extensive human experience.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in small amounts. Considered safe and compatible with breastfeeding. Widely used by breastfeeding mothers. Infant dose is <5% of maternal dose.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Liver function (if prolonged use planned)', reason: 'Baseline for monitoring; avoid in significant hepatic impairment' },
      { test: 'Assessment of other paracetamol-containing products', reason: 'Avoid unintentional overdose from multiple sources' }
    ],
    ongoing: [
      { test: 'Liver function', frequency: 'Not routinely needed for short-term use at therapeutic doses; consider periodic LFTs if chronic use', target: 'Normal transaminases' },
      { test: 'Pain/fever assessment', frequency: 'As needed', target: 'Symptom relief' },
      { test: 'Signs of hepatotoxicity', frequency: 'If any concern for overdose', target: 'No nausea, vomiting, RUQ pain, jaundice' }
    ],
    therapeutic: {
      range: 'No routine level monitoring in therapeutic use. In suspected overdose: serum paracetamol level at 4+ hours post-ingestion.',
      timing: 'For overdose: 4-hour level plotted on Rumack-Matthew nomogram determines treatment need'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture; keep container tightly closed',
    specialInstructions: 'Keep out of reach of children (overdose risk). Suppositories may need refrigeration.',
    shelfLife: '3-5 years for tablets; check expiry date'
  },
  africanContext: {
    availability: 'Universally available across Africa. WHO Essential Medicine. Present at all levels of healthcare including community pharmacies, patent medicine vendors, and markets.',
    affordability: 'Extremely affordable; often <$0.50 USD for multiple doses. One of the most accessible medications.',
    supplyChainNotes: 'Very stable supply. Heat-stable for storage. Multiple formulations available (tablets, liquid, suppositories). Watch for substandard products - source from reputable suppliers.',
    localManufacturing: 'Manufactured extensively across Africa: Nigeria (Emzor, Fidson, May & Baker, GSK Nigeria, and many others), South Africa, Kenya, Egypt, Morocco, Ghana, Tanzania, and virtually all countries with pharmaceutical manufacturing.',
    alternatives: [
      'Ibuprofen (NSAID - more GI side effects but stronger anti-inflammatory)',
      'Aspirin (avoid in children; GI effects)',
      'Diclofenac (NSAID; more potent but more side effects)',
      'No direct alternative with same safety profile'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Paracetamol is the most commonly used analgesic/antipyretic across Africa. Key considerations: (1) Self-medication is widespread - educate about maximum doses to prevent unintentional overdose; (2) Combination products (paracetamol + caffeine, paracetamol + codeine, paracetamol + chlorpheniramine) are common - patients may take multiple products containing paracetamol; (3) Quality issues exist with some generic products - source from reliable suppliers; (4) Hepatitis B and C are prevalent in some regions - assess liver function in at-risk patients; (5) Alcohol use may increase hepatotoxicity risk; (6) N-acetylcysteine (NAC) antidote may not be available in all facilities - prevention through education is critical.'
  },
  patientCounseling: {
    keyPoints: [
      'Do not exceed the recommended dose - overdose damages the liver',
      'Check other medicines for paracetamol content - many cold and pain medicines contain it',
      'Maximum 8 tablets (500 mg each) or 4g per day in adults',
      'Leave at least 4 hours between doses',
      'Seek medical help immediately if you take more than the recommended dose',
      'Do not drink alcohol heavily while taking paracetamol regularly'
    ],
    commonMisconceptions: [
      'Misconception: Paracetamol is completely safe because it is available without prescription - Truth: Overdose is dangerous and can cause liver failure',
      'Misconception: If one dose does not work, take more - Truth: Never exceed recommended dose; more is not better',
      'Misconception: Different brands can be added together - Truth: All paracetamol counts toward daily maximum regardless of brand',
      'Misconception: Paracetamol treats inflammation - Truth: It relieves pain and fever but has minimal anti-inflammatory effect'
    ],
    adherenceTips: [
      'Keep track of doses taken - write down time of each dose',
      'Read labels on all medications to check for paracetamol content',
      'Use measuring cup or syringe for liquid formulations - not household spoons',
      'Do not give adult formulations to children without checking dose',
      'Store safely away from children'
    ],
    warningSignsToReport: [
      'Nausea, vomiting, or abdominal pain after taking paracetamol',
      'Yellowing of eyes or skin (jaundice)',
      'Dark urine or pale stools',
      'Unusual tiredness',
      'Any skin rash or allergic reaction',
      'Symptoms not improving after 3 days of treatment'
    ]
  },
  clinicalPearls: [
    'Paracetamol is the safest first-line analgesic/antipyretic when used at recommended doses - it lacks the GI, renal, and cardiovascular risks of NSAIDs',
    'Hepatotoxicity threshold is approximately 150 mg/kg (about 10g in average adult) in single dose, but lower in risk factors (alcohol, malnutrition, liver disease)',
    'N-acetylcysteine is highly effective antidote for paracetamol overdose; most effective within 8 hours but beneficial even at 24+ hours - do not delay treatment',
    'Maximum 3000 mg/day recommended for chronic use, elderly, or those with risk factors (vs 4000 mg/day for short-term use in healthy adults)',
    'Unintentional overdose from multiple products is common - always check what other medications patient is taking',
    'Combining paracetamol with NSAIDs provides better analgesia than either alone (multimodal approach) with lower risk than high-dose NSAID',
    'Rectal absorption is slower and more variable than oral - may need 25-50% higher rectal dose for equivalent effect',
    'IV paracetamol has faster onset (5-10 minutes) but no advantage in efficacy over oral in patients who can swallow',
    'Paracetamol can affect INR with warfarin when used regularly at doses >2g/day for more than a week - monitor INR',
    'In Africa, emphasize safe dosing and checking labels - unintentional overdose is preventable tragedy'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Rumack BH. Pediatrics 1975 - Acetaminophen overdose',
    'Prescott LF. Am J Ther 2000 - Paracetamol: past, present, and future',
    'NICE Clinical Knowledge Summary - Analgesia 2023',
    'American College of Rheumatology OA Guidelines 2019',
    'Cochrane Review - Paracetamol for acute pain',
    'FDA Drug Safety Communication - Acetaminophen and liver injury',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'ibuprofen-035',
  genericName: 'Ibuprofen',
  brandNames: ['Advil', 'Motrin', 'Brufen', 'Nurofen', 'Caldolor', 'Ibuflam'],
  atcCode: 'M01AE01',
  therapeuticClass: 'Non-Steroidal Anti-Inflammatory Drug (NSAID) - Propionic Acid Derivative',
  pharmacology: {
    mechanism: 'Non-selective inhibitor of cyclooxygenase (COX-1 and COX-2) enzymes, reducing prostaglandin synthesis. Decreased prostaglandins lead to: reduced inflammation (by decreasing vasodilation, vascular permeability, and inflammatory cell recruitment), analgesia (by reducing peripheral and central pain sensitization), antipyresis (by resetting hypothalamic thermostat). COX-1 inhibition also reduces gastric mucosal protection and platelet aggregation.',
    pharmacokinetics: {
      absorption: 'Rapidly and well absorbed from GI tract (80% bioavailability). Peak plasma concentration in 1-2 hours. Food delays absorption but does not reduce extent.',
      distribution: 'Volume of distribution 0.12-0.2 L/kg. Highly protein bound (99%), mainly to albumin. Distributes into synovial fluid. Crosses placenta; minimal CNS penetration.',
      metabolism: 'Extensively hepatic metabolism via CYP2C9 (major) and CYP2C8 to inactive hydroxyl and carboxyl metabolites. No active metabolites.',
      elimination: 'Primarily renal excretion of metabolites (90%); <10% unchanged. Complete excretion within 24 hours.',
      halfLife: '2-4 hours; extended in elderly and renal impairment'
    },
    pharmacodynamics: 'Onset of analgesia 30-60 minutes, peak 1-2 hours, duration 4-6 hours. Anti-inflammatory effect requires higher doses and several days of regular use for maximal effect. Antipyretic onset 30-60 minutes.'
  },
  indications: [
    {
      condition: 'Mild to Moderate Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for headache, dental pain, dysmenorrhea, musculoskeletal pain. More effective than paracetamol for inflammatory pain.'
    },
    {
      condition: 'Inflammatory Conditions (Arthritis)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for rheumatoid arthritis, osteoarthritis, juvenile arthritis. Regular dosing needed for anti-inflammatory effect.'
    },
    {
      condition: 'Dysmenorrhea',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Highly effective; start at onset of menses or just before. Reduces prostaglandin-mediated uterine contractions.'
    },
    {
      condition: 'Fever',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective antipyretic. In children, alternating with paracetamol no longer routinely recommended.'
    },
    {
      condition: 'Migraine',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for acute migraine, especially early in attack. 400 mg shown effective; often combined with antiemetic.'
    },
    {
      condition: 'Acute Gout',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'High-dose NSAIDs are first-line for acute gout. Continue until attack resolves (typically 5-7 days).'
    },
    {
      condition: 'Patent Ductus Arteriosus (IV formulation in neonates)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV ibuprofen used to close hemodynamically significant PDA in premature infants. Similar efficacy to indomethacin with fewer renal side effects.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Analgesia: 200-400 mg every 4-6 hours as needed. Anti-inflammatory: 400-800 mg three times daily with food. Maximum 3200 mg/day (2400 mg/day for OTC use).',
      indicationSpecific: [
        { indication: 'Mild-moderate pain', dose: '200-400 mg every 4-6 hours as needed; max 1200-2400 mg/day' },
        { indication: 'Inflammatory arthritis', dose: '400-800 mg three times daily with food; max 3200 mg/day' },
        { indication: 'Dysmenorrhea', dose: '400 mg at onset, then 200-400 mg every 4-6 hours; max 1200 mg/day' },
        { indication: 'Migraine', dose: '400 mg at onset; may repeat after 4-6 hours; max 1200 mg/day for self-treatment' },
        { indication: 'Acute gout', dose: '800 mg three times daily until attack resolves (typically 5-7 days)' },
        { indication: 'Fever', dose: '200-400 mg every 4-6 hours as needed' }
      ],
      maxDose: '3200 mg/day (prescription); 1200 mg/day (OTC self-care)',
      renalImpairment: {
        mild: 'CrCl 50-80 mL/min: Use with caution; may reduce dose',
        moderate: 'CrCl 30-50 mL/min: Reduce dose; use lowest effective dose for shortest duration',
        severe: 'CrCl <30 mL/min: Avoid if possible; if essential, use minimal dose with close monitoring',
        dialysis: 'Avoid if possible; not removed by dialysis'
      },
      hepaticImpairment: 'Avoid in severe hepatic impairment (Child-Pugh C). Use with caution in mild-moderate impairment; reduced dose may be needed.'
    },
    pediatric: {
      standard: '5-10 mg/kg every 6-8 hours. Maximum 40 mg/kg/day.',
      weightBased: true,
      ageSpecific: [
        { age: '3-6 months (only if >5 kg)', dose: '5 mg/kg every 6-8 hours; max 20 mg/kg/day (fever only, max 24h without medical advice)' },
        { age: '6-12 months', dose: '5-10 mg/kg every 6-8 hours; max 30-40 mg/kg/day' },
        { age: '1-12 years', dose: '5-10 mg/kg every 6-8 hours; max 40 mg/kg/day or 2400 mg/day, whichever less' },
        { age: '>12 years or >40 kg', dose: 'Adult dosing' },
        { age: 'Juvenile arthritis', dose: '30-40 mg/kg/day in 3-4 divided doses; max 2400 mg/day' },
        { age: 'PDA closure (IV, preterm neonate)', dose: '10 mg/kg, then 5 mg/kg at 24h and 48h (total 3 doses)' }
      ],
      maxDose: '40 mg/kg/day or 2400 mg/day, whichever is less',
      neonatal: 'IV ibuprofen for PDA: 10 mg/kg initial, then 5 mg/kg at 24h and 48h. Contraindicated for other indications due to renal and GI immaturity.'
    },
    elderly: 'Use lowest effective dose for shortest duration. Increased risk of GI bleeding, renal impairment, and cardiovascular events. Consider GI protection (PPI).',
    routes: ['Oral (tablets, capsules, liquid)', 'Topical (gel, cream)', 'Intravenous', 'Rectal'],
    administration: [
      'Take with food, milk, or antacid to reduce GI irritation',
      'Tablets can be taken whole or with water',
      'Liquid formulations: shake well; use appropriate measuring device',
      'IV (Caldolor): Infuse over at least 30 minutes; not for bolus injection',
      'Topical: Apply to affected area; do not use on broken skin or with occlusive dressings'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to ibuprofen or other NSAIDs',
      'History of asthma, urticaria, or allergic reactions to aspirin or NSAIDs (NSAID-exacerbated respiratory disease)',
      'Active peptic ulcer disease or GI bleeding',
      'Severe heart failure (NYHA III-IV)',
      'Third trimester of pregnancy',
      'Severe hepatic impairment',
      'Severe renal impairment (CrCl <30 mL/min) for prolonged use',
      'Perioperative use in coronary artery bypass graft (CABG) surgery'
    ],
    relative: [
      'History of peptic ulcer disease or GI bleeding',
      'Cardiovascular disease (increased CV risk with NSAIDs)',
      'Hypertension (may worsen)',
      'Heart failure (fluid retention, worsening)',
      'Renal impairment',
      'Asthma (may exacerbate in NSAID-sensitive patients)',
      'Concurrent anticoagulant or antiplatelet therapy',
      'Elderly (increased risk of all adverse effects)',
      'First and second trimester pregnancy (use only if clearly needed)'
    ],
    precautions: [
      'Use lowest effective dose for shortest duration',
      'Monitor for GI bleeding symptoms',
      'Monitor blood pressure and renal function with prolonged use',
      'Consider GI protection (PPI) in high-risk patients',
      'May mask signs and symptoms of infection',
      'Avoid dehydration - adequate fluid intake important',
      'Risk of cardiovascular thrombotic events (MI, stroke) - use with caution in CV disease'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Dyspepsia/epigastric pain', frequency: '10-15%', management: 'Take with food; consider PPI if needed; use lowest effective dose' },
      { effect: 'Nausea', frequency: '3-9%', management: 'Take with food; usually mild' },
      { effect: 'Headache', frequency: '3-5%', management: 'Usually transient; symptomatic treatment' },
      { effect: 'Dizziness', frequency: '3-5%', management: 'Use caution with activities requiring alertness; usually transient' },
      { effect: 'Rash', frequency: '1-3%', management: 'Discontinue if significant; antihistamines for mild cases' },
      { effect: 'Fluid retention/edema', frequency: '1-3%', management: 'Monitor weight; may worsen hypertension or heart failure' }
    ],
    serious: [
      { effect: 'GI bleeding/ulceration/perforation', frequency: '1-4% per year', management: 'Stop NSAID; PPI; endoscopy if needed; blood transfusion for severe bleeding', warning: 'Risk increases with age >65, prior GI history, concurrent anticoagulants/steroids/aspirin, high doses, prolonged use' },
      { effect: 'Cardiovascular thrombotic events (MI, stroke)', frequency: 'Increased risk', management: 'Use lowest dose for shortest duration; avoid in established CVD if possible', warning: 'FDA black box warning. Risk increases with dose and duration. All NSAIDs carry this risk.' },
      { effect: 'Acute kidney injury', frequency: '1-5%', management: 'Stop NSAID; IV fluids; most cases reversible. Avoid in volume-depleted patients.', warning: 'Risk factors: elderly, dehydration, concurrent ACE-I/ARB/diuretics, pre-existing renal disease' },
      { effect: 'Severe allergic reactions (anaphylaxis, angioedema)', frequency: 'Rare', management: 'Stop immediately; epinephrine, antihistamines, steroids as needed', warning: 'Cross-reactivity between NSAIDs possible but not universal' },
      { effect: 'NSAID-exacerbated respiratory disease', frequency: '5-10% of asthmatics', management: 'Stop NSAID; bronchodilators; epinephrine if severe. Avoid all NSAIDs in affected patients.', warning: 'Classic triad: asthma, nasal polyps, NSAID sensitivity. Can be life-threatening.' },
      { effect: 'Severe skin reactions (SJS, TEN)', frequency: 'Very rare', management: 'Stop immediately; hospitalization; dermatology/burn unit care', warning: 'Highest risk in first weeks of use; discontinue at first sign of rash' },
      { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Stop NSAID; supportive care; usually reversible', warning: 'Monitor if symptoms of hepatotoxicity develop' }
    ],
    rare: [
      'Aseptic meningitis',
      'Blood dyscrasias (agranulocytosis, aplastic anemia, thrombocytopenia)',
      'Interstitial nephritis',
      'Hyperkalemia',
      'Hypertension',
      'Heart failure exacerbation',
      'Tinnitus (high doses)'
    ]
  },
  interactions: {
    major: [
      { drug: 'Anticoagulants (warfarin, heparin, DOACs)', effect: 'Increased bleeding risk', mechanism: 'NSAIDs inhibit platelet function and may displace warfarin from protein binding; GI ulceration risk', management: 'Avoid combination if possible; if needed, use lowest NSAID dose, add PPI, monitor closely' },
      { drug: 'Aspirin (antiplatelet doses)', effect: 'Ibuprofen may reduce aspirin antiplatelet effect; increased GI bleeding', mechanism: 'Ibuprofen competitively inhibits aspirin binding to COX-1', management: 'Take aspirin 30-60 minutes before ibuprofen; avoid regular concurrent use; use alternative analgesic' },
      { drug: 'Lithium', effect: 'Increased lithium levels and toxicity', mechanism: 'NSAIDs reduce renal lithium clearance', management: 'Monitor lithium levels closely; may need dose reduction' },
      { drug: 'Methotrexate (high-dose)', effect: 'Increased methotrexate toxicity', mechanism: 'Reduced renal clearance of methotrexate', management: 'Avoid NSAIDs with high-dose methotrexate; caution with low-dose; monitor levels' },
      { drug: 'ACE inhibitors/ARBs + diuretics ("triple whammy")', effect: 'Significantly increased acute kidney injury risk', mechanism: 'NSAID + ACE-I/ARB + diuretic severely compromises renal hemodynamics', management: 'Avoid triple combination; if NSAID needed, ensure adequate hydration, monitor renal function' }
    ],
    moderate: [
      { drug: 'Antihypertensives (ACE-I, ARB, beta-blockers, diuretics)', effect: 'Reduced antihypertensive effect', management: 'Monitor blood pressure; may need increased antihypertensive dose' },
      { drug: 'Corticosteroids', effect: 'Increased GI ulcer and bleeding risk', management: 'Avoid combination if possible; use PPI prophylaxis if needed' },
      { drug: 'SSRIs/SNRIs', effect: 'Increased GI bleeding risk', management: 'Use PPI prophylaxis; monitor for bleeding' },
      { drug: 'Cyclosporine', effect: 'Increased nephrotoxicity', management: 'Monitor renal function closely' },
      { drug: 'Quinolone antibiotics', effect: 'Possible increased seizure risk', management: 'Use with caution, especially in patients with seizure history' }
    ],
    food: [
      'Take with food to reduce GI irritation',
      'Food delays absorption but does not reduce total absorption',
      'Alcohol increases GI bleeding risk - avoid or limit intake'
    ]
  },
  pregnancy: {
    category: 'C (first/second trimester); D (third trimester)',
    trimpiesterRisks: {
      first: 'Some studies suggest possible increased miscarriage risk. Use only if clearly needed.',
      second: 'May affect fetal renal function. Use only if clearly needed and for shortest duration.',
      third: 'CONTRAINDICATED. Causes premature closure of ductus arteriosus, oligohydramnios, and may prolong labor.'
    },
    recommendation: 'Avoid in third trimester (contraindicated after 30 weeks). Use in first/second trimester only if clearly needed. Paracetamol is preferred analgesic/antipyretic in pregnancy.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in very low amounts. Short-term use compatible with breastfeeding. Considered one of the preferred NSAIDs for breastfeeding mothers if NSAID needed.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Blood pressure', reason: 'NSAIDs can raise BP' },
      { test: 'Renal function (creatinine, eGFR)', reason: 'NSAIDs reduce renal function' },
      { test: 'CBC', reason: 'Baseline for monitoring GI blood loss' },
      { test: 'Hepatic function', reason: 'If prolonged use planned' }
    ],
    ongoing: [
      { test: 'Blood pressure', frequency: 'Within first few weeks, then periodically', target: 'No significant increase' },
      { test: 'Renal function', frequency: 'Within 1-2 weeks if at risk, then periodically', target: 'Stable creatinine' },
      { test: 'Hemoglobin', frequency: 'If prolonged use or symptoms of GI bleeding', target: 'Stable; no occult blood loss' },
      { test: 'Signs of GI bleeding', frequency: 'Each visit (clinical assessment)', target: 'No melena, hematemesis, or abdominal pain' },
      { test: 'Edema/weight', frequency: 'Periodically', target: 'No significant fluid retention' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring. Clinical response guides therapy.',
      timing: 'Analgesic effect within 30-60 minutes; full anti-inflammatory effect may take several days to weeks of regular dosing'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Keep container tightly closed',
    shelfLife: '3-5 years; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa. WHO Essential Medicine. Present in most health facilities and pharmacies; also available OTC.',
    affordability: 'Very affordable; approximately $0.50-3 USD per pack. Generic formulations widely available.',
    supplyChainNotes: 'Stable supply; heat-stable for storage. Multiple generic manufacturers ensure consistent availability.',
    localManufacturing: 'Manufactured across Africa: Nigeria (Emzor, Fidson, May & Baker), South Africa (Aspen, Adcock Ingram), Kenya, Egypt, Morocco, and many other countries.',
    alternatives: [
      'Diclofenac (more potent; similar GI risks)',
      'Naproxen (longer-acting NSAID)',
      'Paracetamol (safer GI profile but no anti-inflammatory effect)',
      'Aspirin (similar risks; also antiplatelet)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial)',
    endemicConsiderations: 'Ibuprofen is widely used across Africa. Key considerations: (1) GI bleeding risk - H. pylori infection is highly prevalent in Africa; eradication may reduce NSAID-related ulcer risk; (2) Renal disease - hypertensive and diabetic nephropathy are common; use with caution and monitor; (3) Self-medication is widespread - educate about maximum doses and duration; (4) Dehydration risk in hot climates - ensure adequate fluid intake to reduce renal effects; (5) Cardiovascular disease is increasing - consider CV risk before prescribing; (6) In malaria and viral infections, NSAIDs may mask fever and symptoms - use judiciously in febrile illness of unknown origin.'
  },
  patientCounseling: {
    keyPoints: [
      'Take with food, milk, or water to protect your stomach',
      'Do not exceed the recommended dose or duration',
      'Report any stomach pain, black stools, or vomiting blood immediately',
      'Drink plenty of fluids to protect your kidneys',
      'Avoid alcohol while taking ibuprofen',
      'Tell your doctor if you have heart disease, kidney disease, or stomach problems'
    ],
    commonMisconceptions: [
      'Misconception: OTC means completely safe - Truth: Can cause serious GI, kidney, and heart problems',
      'Misconception: More is better for severe pain - Truth: Higher doses increase risks without much additional benefit; max dose exists for safety',
      'Misconception: Can use long-term without problems - Truth: Risks increase with duration; use for shortest time needed',
      'Misconception: All pain relievers are the same - Truth: Ibuprofen has specific risks and benefits different from paracetamol'
    ],
    adherenceTips: [
      'Take with meals to remember and reduce stomach upset',
      'Do not lie down for 30 minutes after taking to prevent reflux',
      'Set limits on how long you will take it - see doctor if pain persists',
      'Read labels on other medicines to avoid taking multiple NSAIDs',
      'Keep a pain diary to assess if medication is helping'
    ],
    warningSignsToReport: [
      'Stomach pain, heartburn, or nausea that persists',
      'Black tarry stools or blood in vomit',
      'Chest pain, shortness of breath, or weakness on one side',
      'Swelling of face, lips, or throat (allergic reaction)',
      'Skin rash or blistering',
      'Unusual weight gain or ankle swelling',
      'Decreased urination'
    ]
  },
  clinicalPearls: [
    'Ibuprofen at OTC doses (≤1200 mg/day) has the best cardiovascular safety profile among NSAIDs; risk increases at higher doses',
    'GI risk is dose-dependent - always use lowest effective dose for shortest duration; add PPI if high-risk',
    'Ibuprofen may interfere with aspirin cardioprotection if taken before aspirin - advise taking aspirin 30-60 min before ibuprofen if both needed',
    'For dysmenorrhea, starting ibuprofen at or just before menses onset is most effective; prostaglandins are already elevated once pain begins',
    'Triple whammy (NSAID + ACE-I/ARB + diuretic) significantly increases AKI risk - avoid this combination when possible',
    'Topical ibuprofen provides local pain relief with minimal systemic absorption - good option for localized musculoskeletal pain in patients at risk for systemic effects',
    'NSAIDs may mask fever and inflammatory markers - use with caution when infection diagnosis is uncertain',
    'In pediatrics, ibuprofen is NOT recommended for use in dehydration (e.g., gastroenteritis with fever) due to AKI risk',
    'For acute gout, full anti-inflammatory doses (2400-3200 mg/day) are needed for efficacy - short course is acceptable',
    'In Africa, consider H. pylori testing/treatment in patients requiring chronic NSAIDs to reduce GI complication risk'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'FDA Black Box Warning - NSAIDs Cardiovascular and GI Risks',
    'Bhala N et al. Lancet 2013 - Coxib and traditional NSAID Trialists meta-analysis',
    'NICE Clinical Guideline - NSAIDs prescribing 2023',
    'American College of Gastroenterology Guidelines - NSAID ulcer prevention',
    'American College of Rheumatology OA Guidelines 2019',
    'Cochrane Review - Ibuprofen for acute pain',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'morphine-036',
  genericName: 'Morphine',
  brandNames: ['MS Contin', 'Kadian', 'Oramorph', 'Sevredol', 'MST Continus', 'Morphgesic'],
  atcCode: 'N02AA01',
  therapeuticClass: 'Opioid Analgesic (Strong Opioid Agonist)',
  pharmacology: {
    mechanism: 'Binds primarily to mu-opioid receptors in the central nervous system and peripheral tissues. Mu receptor activation inhibits adenylyl cyclase, opens potassium channels, and inhibits calcium channels, leading to neuronal hyperpolarization and reduced neurotransmitter release. Results in analgesia, euphoria, sedation, respiratory depression, decreased GI motility, and physical dependence with prolonged use.',
    pharmacokinetics: {
      absorption: 'Oral bioavailability 20-40% due to extensive first-pass metabolism. Peak plasma concentration: immediate-release 0.5-1 hour; sustained-release 3-4 hours. IM/SC well absorbed; IV immediate effect.',
      distribution: 'Volume of distribution 3-4 L/kg. Protein binding 30-35%. Crosses blood-brain barrier (slowly due to hydrophilicity). Crosses placenta; excreted in breast milk.',
      metabolism: 'Hepatic glucuronidation to morphine-3-glucuronide (M3G, inactive, neuroexcitatory) and morphine-6-glucuronide (M6G, active, more potent than morphine). M6G accumulates in renal impairment.',
      elimination: 'Primarily renal excretion of glucuronide metabolites (90%). 10% biliary excretion. Enterohepatic recirculation occurs.',
      halfLife: '2-4 hours (morphine); M6G half-life 4-6 hours (prolonged to 50+ hours in renal failure)'
    },
    pharmacodynamics: 'Onset: IV 5-10 minutes, IM/SC 15-30 minutes, oral immediate-release 30-60 minutes. Peak effect: IV 20 minutes, oral 1-2 hours. Duration: 3-5 hours (immediate-release). Dose-response relationship is linear until high doses where respiratory depression becomes limiting factor.'
  },
  indications: [
    {
      condition: 'Severe Acute Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Gold standard for severe acute pain including post-operative, trauma, burns, MI. Titrate to effect.'
    },
    {
      condition: 'Chronic Cancer Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Foundation of WHO analgesic ladder step 3. Essential for cancer pain management. Use sustained-release for background pain with immediate-release for breakthrough.'
    },
    {
      condition: 'Palliative Care / End-of-Life Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Essential medicine for palliative care. Also relieves dyspnea in terminal illness. Doses should not be limited by fear of hastening death when used appropriately.'
    },
    {
      condition: 'Acute Pulmonary Edema',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'IV morphine reduces preload, anxiety, and dyspnea. Use with caution; not first-line if hypotensive.'
    },
    {
      condition: 'Myocardial Infarction Pain',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: true,
      notes: 'Relieves pain and anxiety; reduces oxygen demand. Some evidence of increased adverse outcomes - use judiciously with monitoring.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Acute pain: IV 2-4 mg every 5-15 min until pain controlled, then every 2-4 hours. Oral immediate-release: 5-15 mg every 4 hours. Chronic pain: individualized titration.',
      indicationSpecific: [
        { indication: 'Acute severe pain (IV)', dose: 'Initial 2-4 mg IV every 5-15 min; maintenance 2-10 mg every 2-4 hours; PCA 1-2 mg bolus, lockout 6-10 min' },
        { indication: 'Acute severe pain (oral)', dose: 'Opioid-naive: 5-15 mg immediate-release every 4 hours; titrate to effect' },
        { indication: 'Chronic cancer pain', dose: 'Start 5-15 mg IR every 4 hours; convert to sustained-release once stable; breakthrough = 10-15% of 24h dose' },
        { indication: 'Acute pulmonary edema', dose: '2-5 mg IV slowly; may repeat every 5-15 minutes with careful monitoring' },
        { indication: 'Palliative dyspnea', dose: 'Start 2.5-5 mg oral/SC every 4 hours; titrate to comfort' }
      ],
      maxDose: 'No absolute ceiling for cancer/palliative pain - titrate to effect balanced against side effects.',
      renalImpairment: {
        mild: 'CrCl 50-80 mL/min: Reduce dose by 25%; extend interval',
        moderate: 'CrCl 10-50 mL/min: Reduce dose by 50%; extend interval; M6G accumulates',
        severe: 'CrCl <10 mL/min: Reduce dose by 75%; consider alternative opioid (fentanyl, methadone)',
        dialysis: 'Avoid if possible; if needed, give after dialysis; use reduced dose'
      },
      hepaticImpairment: 'Reduce dose and extend interval. Severe impairment: reduce dose by 50% and monitor closely.'
    },
    pediatric: {
      standard: 'IV: 0.05-0.1 mg/kg every 2-4 hours. Oral: 0.2-0.5 mg/kg every 4-6 hours. Always start at low end and titrate.',
      weightBased: true,
      ageSpecific: [
        { age: 'Neonates (<7 days)', dose: 'IV: 0.025-0.05 mg/kg every 6-8 hours; use with extreme caution' },
        { age: 'Infants 7 days - 6 months', dose: 'IV: 0.025-0.05 mg/kg every 4-6 hours' },
        { age: 'Children 1-12 years', dose: 'IV: 0.05-0.1 mg/kg every 2-4 hours; Oral: 0.2-0.5 mg/kg every 4 hours' },
        { age: 'Adolescents >12 years', dose: 'Approach adult dosing with weight-based calculations' }
      ],
      maxDose: '0.1 mg/kg per dose IV; 0.5 mg/kg per dose oral; titrate carefully to effect',
      neonatal: 'IV: 0.025-0.05 mg/kg every 6-8 hours. Neonates extremely sensitive. Monitor respiratory rate closely.'
    },
    elderly: 'Start at 25-50% of usual adult dose. Increased sensitivity and prolonged duration. Titrate slowly.',
    routes: ['Oral (immediate and sustained-release)', 'Intravenous', 'Subcutaneous', 'Intramuscular', 'Rectal', 'Epidural', 'Intrathecal'],
    administration: [
      'IV: Give slowly over 4-5 minutes; rapid injection increases adverse effects',
      'Sustained-release tablets: swallow whole; do not crush, chew, or dissolve',
      'SC: Preferred route in palliative care when oral not possible',
      'Oral solution: measure carefully using appropriate device',
      'Converting routes: oral to IV/SC ratio approximately 3:1 (30 mg oral = 10 mg IV/SC)'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to morphine or any opioid',
      'Severe respiratory depression (without ventilatory support)',
      'Acute or severe bronchial asthma (without monitoring)',
      'Known or suspected GI obstruction including paralytic ileus',
      'Concurrent MAO inhibitor use or within 14 days'
    ],
    relative: [
      'Moderate respiratory depression',
      'COPD or cor pulmonale',
      'Head injury or raised intracranial pressure',
      'Hypotension or hypovolemia',
      'Acute pancreatitis',
      'Severe renal impairment (M6G accumulation)',
      'Severe hepatic impairment',
      'History of substance use disorder'
    ],
    precautions: [
      'Always have naloxone available when initiating therapy',
      'Monitor respiratory rate and sedation level',
      'Start low and titrate slowly in opioid-naive patients',
      'Risk of tolerance, dependence, and opioid use disorder',
      'Avoid abrupt discontinuation after prolonged use',
      'Constipation is near-universal - initiate prophylaxis'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Constipation', frequency: '40-95%', management: 'Prophylactic laxatives for all patients on regular opioids. Tolerance does NOT develop.' },
      { effect: 'Nausea and vomiting', frequency: '20-60%', management: 'Often improves after first few days; antiemetics as needed' },
      { effect: 'Sedation/drowsiness', frequency: '20-50%', management: 'Usually improves with tolerance; reduce dose if excessive' },
      { effect: 'Pruritus', frequency: '10-20%', management: 'Antihistamines; opioid rotation if severe' },
      { effect: 'Dizziness', frequency: '10-20%', management: 'Advise caution with position changes' }
    ],
    serious: [
      { effect: 'Respiratory depression', frequency: 'Dose-related', management: 'Reduce dose; naloxone 0.4-2 mg IV if severe; ventilatory support', warning: 'Risk highest in opioid-naive, elderly, renal impairment, concurrent CNS depressants' },
      { effect: 'Hypotension', frequency: '5-10%', management: 'IV fluids; reduce dose', warning: 'Due to histamine release and vasodilation' },
      { effect: 'Urinary retention', frequency: '5-15%', management: 'Catheterization if needed; reduce dose', warning: 'More common in elderly men' },
      { effect: 'Physical dependence', frequency: 'Universal with prolonged use', management: 'Never stop abruptly; taper gradually', warning: 'Withdrawal uncomfortable but not life-threatening' },
      { effect: 'Opioid use disorder', frequency: 'Variable', management: 'Careful patient selection; monitor for aberrant behaviors', warning: 'Risk factors: history of substance abuse, psychiatric disorders' }
    ],
    rare: [
      'Seizures (more common in renal failure due to M3G)',
      'Serotonin syndrome (with concurrent serotonergic drugs)',
      'Adrenal insufficiency (with prolonged use)',
      'QT prolongation (less than with methadone)'
    ]
  },
  interactions: {
    major: [
      { drug: 'MAO inhibitors', effect: 'Severe reactions (hyperpyrexia, coma, respiratory depression)', mechanism: 'Altered opioid metabolism', management: 'CONTRAINDICATED. Wait 14 days after stopping MAOI.' },
      { drug: 'Benzodiazepines and other CNS depressants', effect: 'Enhanced respiratory depression, sedation, death', mechanism: 'Additive CNS depression', management: 'FDA black box warning. Avoid if possible; if needed, use lowest doses.' },
      { drug: 'Alcohol', effect: 'Enhanced CNS depression; fatal respiratory depression', mechanism: 'Additive CNS effects', management: 'Warn patients to avoid alcohol.' }
    ],
    moderate: [
      { drug: 'CYP3A4 inhibitors (ketoconazole, ritonavir)', effect: 'Possible increased morphine effects', management: 'Monitor for increased opioid effect' },
      { drug: 'Anticholinergic drugs', effect: 'Additive constipation, urinary retention', management: 'Aggressive bowel regimen' },
      { drug: 'Serotonergic drugs (SSRIs, tramadol)', effect: 'Risk of serotonin syndrome', management: 'Monitor; use with caution' }
    ],
    food: [
      'Food does not significantly affect absorption',
      'Avoid alcohol - dangerous interaction'
    ]
  },
  pregnancy: {
    category: 'C (D if prolonged use at term)',
    trimpiesterRisks: {
      first: 'No clear teratogenic risk. Use if benefits outweigh risks.',
      second: 'Use for severe pain when necessary. Monitor fetal development.',
      third: 'Prolonged use causes neonatal opioid withdrawal syndrome. Use near term may cause neonatal respiratory depression.'
    },
    recommendation: 'Use for severe pain when benefits outweigh risks. Prepare for neonatal withdrawal if prolonged use. Avoid high doses near delivery.',
    lactation: {
      safe: false,
      notes: 'Excreted in breast milk. Repeated doses may cause infant sedation and respiratory depression. Monitor infant closely if breastfeeding.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Pain assessment', reason: 'Establish baseline and treatment goals' },
      { test: 'Respiratory rate and sedation level', reason: 'Baseline for monitoring' },
      { test: 'Renal function', reason: 'M6G accumulates in renal impairment' },
      { test: 'Risk assessment for opioid use disorder', reason: 'Identify patients needing closer monitoring' }
    ],
    ongoing: [
      { test: 'Pain score', frequency: 'Every dose in acute setting; each visit in chronic use', target: 'Pain at acceptable level' },
      { test: 'Respiratory rate', frequency: 'Every 1-2 hours initially; before each PRN dose', target: 'RR >8-10/min' },
      { test: 'Sedation score', frequency: 'With respiratory rate monitoring', target: 'Alert or easily rousable' },
      { test: 'Bowel function', frequency: 'Daily assessment', target: 'Regular bowel movements' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring. Titrate to clinical effect.',
      timing: 'Reassess pain 15-30 min after IV; 60 min after oral'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'CONTROLLED SUBSTANCE - secure storage required. Keep accurate records.',
    shelfLife: '2-5 years depending on formulation'
  },
  africanContext: {
    availability: 'Limited availability across Africa despite being WHO Essential Medicine. Regulatory barriers and supply chain challenges limit access.',
    affordability: 'When available, generic morphine is relatively affordable. Regulatory issues predominate over cost.',
    supplyChainNotes: 'Controlled substance requiring special import licenses. Many African countries have inadequate systems for opioid supply.',
    localManufacturing: 'Very limited. Some countries (Uganda, Kenya) have made progress with local oral morphine solutions.',
    alternatives: [
      'Tramadol (weaker opioid; often more available)',
      'Codeine (weaker; in some combination products)',
      'Fentanyl (for severe pain)'
    ],
    resistancePatterns: 'Not applicable. Note: Tolerance develops with chronic use.',
    endemicConsiderations: 'Access to morphine is a major public health issue. Millions suffer from severe pain without opioid access. Key issues: regulatory barriers, healthcare worker fear, limited prescriber pool, supply chain challenges. Oral morphine solution is most practical for low-resource settings.'
  },
  patientCounseling: {
    keyPoints: [
      'Take exactly as prescribed - do not take more than directed',
      'This medicine causes constipation - take laxatives as directed',
      'Do not drive or operate machinery until stable on medication',
      'Do not drink alcohol',
      'Do not stop suddenly after prolonged use',
      'Store securely away from others'
    ],
    commonMisconceptions: [
      'Misconception: Taking morphine for pain means becoming an addict - Truth: When used appropriately, addiction is uncommon',
      'Misconception: Should wait until pain is severe - Truth: Take on schedule for chronic pain',
      'Misconception: Morphine means I am dying - Truth: Used for severe pain of any cause'
    ],
    adherenceTips: [
      'Take sustained-release at same times each day',
      'Use immediate-release for breakthrough pain as directed',
      'Take laxatives every day',
      'Keep a pain diary'
    ],
    warningSignsToReport: [
      'Severe drowsiness or difficulty staying awake',
      'Very slow or shallow breathing',
      'Confusion or hallucinations',
      'Severe constipation not relieved by laxatives',
      'Difficulty urinating'
    ]
  },
  clinicalPearls: [
    'Morphine is the gold standard opioid - essential for severe pain management',
    'In renal impairment, M6G accumulates - reduce dose or use fentanyl/methadone',
    'Always initiate laxative prophylaxis - tolerance does NOT develop to constipation',
    'Oral to parenteral conversion: approximately 3:1 (30 mg oral = 10 mg IV/SC)',
    'For cancer pain, there is no ceiling dose - titrate to comfort',
    'Respiratory depression risk highest at initiation and dose increases',
    'Fear of addiction should not prevent adequate pain treatment',
    'In Africa, oral morphine solution is most cost-effective formulation',
    'Advocate for improved opioid access - pain relief is a human right'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'WHO Guidelines for Pharmacological Treatment of Persisting Pain 2012',
    'Lancet Commission on Palliative Care and Pain Relief 2018',
    'African Palliative Care Association Standards',
    'CDC Guideline for Prescribing Opioids 2022',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'tramadol-037',
  genericName: 'Tramadol',
  brandNames: ['Ultram', 'Tramal', 'Zydol', 'Contramal', 'Tramacet', 'Ryzolt'],
  atcCode: 'N02AX02',
  therapeuticClass: 'Atypical Opioid Analgesic (Weak Opioid Agonist with Monoaminergic Activity)',
  pharmacology: {
    mechanism: 'Dual mechanism of action: (1) Weak mu-opioid receptor agonist (affinity 6000x less than morphine); active metabolite O-desmethyltramadol (M1) has higher opioid affinity. (2) Inhibits reuptake of norepinephrine and serotonin, contributing to analgesia through descending pain inhibition pathways. Both mechanisms contribute approximately equally to analgesic effect.',
    pharmacokinetics: {
      absorption: 'Well absorbed orally with bioavailability ~70% (increases to ~90% with multiple doses). Peak plasma concentration: immediate-release 1.5-2 hours; extended-release 4-5 hours. Food does not significantly affect absorption.',
      distribution: 'Volume of distribution approximately 2.5-3 L/kg. Protein binding 20%. Crosses blood-brain barrier and placenta.',
      metabolism: 'Hepatic metabolism via CYP2D6 to active M1 metabolite (O-desmethyltramadol) and CYP3A4 to inactive N-desmethyltramadol. M1 has 200x higher opioid receptor affinity than tramadol. CYP2D6 polymorphism significantly affects efficacy.',
      elimination: 'Primarily renal excretion (90%); 30% as unchanged drug, remainder as metabolites.',
      halfLife: 'Tramadol: 5-6 hours; M1 metabolite: 7-9 hours'
    },
    pharmacodynamics: 'Onset of analgesia 30-60 minutes oral. Peak effect 2-4 hours. Duration 4-6 hours (immediate-release), 12-24 hours (extended-release). CYP2D6 poor metabolizers have reduced M1 formation and may experience less opioid effect but increased serotonergic effects.'
  },
  indications: [
    {
      condition: 'Moderate to Moderately Severe Pain',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'WHO analgesic ladder step 2 (weak opioid). Alternative to codeine. Less potent than morphine but useful for moderate pain.'
    },
    {
      condition: 'Chronic Pain (Non-Cancer)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Extended-release formulations for chronic pain. Still has abuse potential; use with caution.'
    },
    {
      condition: 'Post-Operative Pain (Moderate)',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Often used as part of multimodal analgesia. May be step-down from stronger opioids.'
    },
    {
      condition: 'Neuropathic Pain',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Dual mechanism may benefit neuropathic pain. Often used when first-line agents insufficient.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Immediate-release: 50-100 mg every 4-6 hours as needed. Extended-release: 100-300 mg once daily. Maximum 400 mg/day.',
      indicationSpecific: [
        { indication: 'Acute moderate pain', dose: '50-100 mg every 4-6 hours as needed; start 25 mg in opioid-naive or sensitive patients' },
        { indication: 'Chronic pain (ER formulation)', dose: 'Start 100 mg once daily; titrate by 100 mg every 5 days; max 300 mg/day' },
        { indication: 'Elderly or hepatic impairment', dose: 'Start 25 mg every 6 hours; max 200-300 mg/day' },
        { indication: 'Post-operative', dose: '50-100 mg every 4-6 hours; often combined with paracetamol' }
      ],
      maxDose: '400 mg/day (300 mg/day for ER formulations and elderly)',
      renalImpairment: {
        mild: 'No adjustment typically needed',
        moderate: 'CrCl 30-50 mL/min: Increase interval to every 12 hours; max 200 mg/day',
        severe: 'CrCl <30 mL/min: 50 mg every 12 hours; max 100 mg/day',
        dialysis: 'Give after dialysis; max 50 mg every 12 hours'
      },
      hepaticImpairment: 'Reduce dose to 50 mg every 12 hours in cirrhosis. Avoid extended-release in severe impairment.'
    },
    pediatric: {
      standard: 'Generally not recommended in children <12 years. Adolescents ≥12 years: 50-100 mg every 4-6 hours; max 400 mg/day.',
      weightBased: false,
      ageSpecific: [
        { age: '<12 years', dose: 'Not recommended - safety not established' },
        { age: '12-17 years', dose: '50-100 mg every 4-6 hours as needed; max 400 mg/day' },
        { age: '≥18 years', dose: 'Adult dosing' }
      ],
      maxDose: '400 mg/day in adolescents ≥12 years',
      neonatal: 'Contraindicated in neonates and infants'
    },
    elderly: 'Start 25 mg every 6 hours. Maximum 300 mg/day. Increased sensitivity and fall risk.',
    routes: ['Oral (immediate-release, extended-release)', 'Intravenous', 'Intramuscular'],
    administration: [
      'May take with or without food',
      'Extended-release tablets: swallow whole; do not crush, break, or chew',
      'For patients with difficulty swallowing, immediate-release can be used',
      'IV: Give slowly over 2-3 minutes',
      'Can be combined with paracetamol for enhanced effect'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to tramadol or opioids',
      'Concurrent or recent (within 14 days) MAO inhibitor use',
      'Acute intoxication with alcohol, hypnotics, opioids, or psychotropic drugs',
      'Patients receiving or who have received MAOIs within 14 days',
      'Children <12 years for any formulation',
      'Children <18 years for post-tonsillectomy/adenoidectomy pain'
    ],
    relative: [
      'Seizure disorders (lowers seizure threshold)',
      'Head injury or increased intracranial pressure',
      'Respiratory depression',
      'Concurrent serotonergic drugs (serotonin syndrome risk)',
      'CYP2D6 ultra-rapid metabolizers (increased toxicity risk)',
      'Severe renal impairment',
      'Severe hepatic impairment',
      'History of substance use disorder'
    ],
    precautions: [
      'Risk of seizures - especially >400 mg/day or with serotonergic drugs',
      'Serotonin syndrome risk with serotonergic medications',
      'CYP2D6 polymorphism affects efficacy and toxicity',
      'Still has abuse and dependence potential',
      'Avoid abrupt discontinuation after prolonged use',
      'May impair ability to drive or operate machinery'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Nausea', frequency: '15-40%', management: 'Take with food; antiemetic if needed; often improves with time' },
      { effect: 'Dizziness', frequency: '10-30%', management: 'Advise caution; usually improves with continued use' },
      { effect: 'Constipation', frequency: '10-25%', management: 'Laxatives; less severe than with stronger opioids' },
      { effect: 'Headache', frequency: '10-20%', management: 'Symptomatic treatment' },
      { effect: 'Somnolence', frequency: '10-25%', management: 'Take at bedtime if possible; avoid driving until stable' },
      { effect: 'Dry mouth', frequency: '5-10%', management: 'Sips of water; saliva substitutes' }
    ],
    serious: [
      { effect: 'Seizures', frequency: '0.5-1%', management: 'Stop tramadol; benzodiazepines for seizure; supportive care', warning: 'Risk increases >400 mg/day, with SSRIs/SNRIs, TCAs, history of epilepsy, head trauma' },
      { effect: 'Serotonin syndrome', frequency: 'Rare', management: 'Stop serotonergic drugs; supportive care; cyproheptadine in severe cases', warning: 'Risk with concurrent SSRIs, SNRIs, MAOIs, triptans, TCAs' },
      { effect: 'Respiratory depression', frequency: 'Rare (less than strong opioids)', management: 'Naloxone may partially reverse; supportive care', warning: 'Risk increased with CNS depressants, ultra-rapid CYP2D6 metabolizers' },
      { effect: 'Anaphylaxis', frequency: 'Rare', management: 'Standard anaphylaxis treatment', warning: 'Cross-reactivity with other opioids possible' },
      { effect: 'Dependence and withdrawal', frequency: 'Variable', management: 'Taper gradually; withdrawal may include atypical symptoms (anxiety, panic, paresthesias)', warning: 'Withdrawal may be more complex than typical opioid withdrawal due to monoaminergic effects' }
    ],
    rare: [
      'Hypoglycemia',
      'Hyponatremia/SIADH',
      'Stevens-Johnson syndrome',
      'Hallucinations',
      'QT prolongation (rare)',
      'Adrenal insufficiency'
    ]
  },
  interactions: {
    major: [
      { drug: 'MAO inhibitors', effect: 'Serotonin syndrome; potentially fatal', mechanism: 'Increased serotonin accumulation', management: 'CONTRAINDICATED. Wait 14 days after stopping MAOI.' },
      { drug: 'SSRIs, SNRIs (sertraline, venlafaxine, etc.)', effect: 'Serotonin syndrome; lowered seizure threshold', mechanism: 'Additive serotonergic effects', management: 'Use with caution; lowest doses; monitor for serotonin syndrome' },
      { drug: 'Carbamazepine', effect: 'Significantly reduced tramadol effect', mechanism: 'CYP3A4 induction increases tramadol metabolism', management: 'May need alternative analgesic; higher tramadol doses often inadequate' },
      { drug: 'Benzodiazepines and CNS depressants', effect: 'Enhanced respiratory depression and sedation', mechanism: 'Additive CNS depression', management: 'FDA warning; avoid if possible; if used, lowest doses and monitor' }
    ],
    moderate: [
      { drug: 'CYP2D6 inhibitors (paroxetine, fluoxetine, quinidine)', effect: 'Reduced M1 (active metabolite) formation; reduced opioid effect', management: 'May have reduced efficacy but increased serotonergic effects' },
      { drug: 'CYP3A4 inhibitors (ketoconazole, erythromycin)', effect: 'Increased tramadol levels', management: 'Monitor for increased effects and toxicity' },
      { drug: 'Tricyclic antidepressants', effect: 'Serotonin syndrome; lowered seizure threshold', management: 'Use with caution; monitor closely' },
      { drug: 'Warfarin', effect: 'Possible increased INR', management: 'Monitor INR when starting or stopping tramadol' }
    ],
    food: [
      'Food does not significantly affect absorption',
      'Avoid alcohol - additive CNS depression'
    ]
  },
  pregnancy: {
    category: 'C',
    trimpiesterRisks: {
      first: 'Limited data. Use only if clearly needed.',
      second: 'May affect fetal development. Use with caution.',
      third: 'Risk of neonatal withdrawal syndrome with prolonged use. Neonatal respiratory depression possible.'
    },
    recommendation: 'Avoid if possible, especially prolonged use. If needed for severe pain, use lowest dose for shortest duration. Monitor neonate for withdrawal if used near term.',
    lactation: {
      safe: false,
      notes: 'Excreted in breast milk. Risk of sedation and respiratory depression in infant, especially with CYP2D6 ultra-rapid metabolizer mothers. Avoid or pump and discard during treatment and 24 hours after.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Pain assessment', reason: 'Establish baseline' },
      { test: 'Renal and hepatic function', reason: 'Dose adjustment needed in impairment' },
      { test: 'Seizure history', reason: 'Tramadol lowers seizure threshold' },
      { test: 'Medication review for serotonergic drugs', reason: 'Serotonin syndrome risk' }
    ],
    ongoing: [
      { test: 'Pain score', frequency: 'Each visit', target: 'Adequate pain control' },
      { test: 'Signs of serotonin syndrome', frequency: 'Especially with serotonergic comedications', target: 'No hyperthermia, rigidity, clonus, agitation' },
      { test: 'Signs of misuse', frequency: 'Each visit for chronic use', target: 'No aberrant behaviors' },
      { test: 'Blood glucose (diabetics)', frequency: 'Periodically', target: 'Stable; tramadol may cause hypoglycemia' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring.',
      timing: 'Assess response at 1-2 hours post-dose for immediate-release'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Controlled substance in many countries - secure storage may be required.',
    shelfLife: '3-5 years; check expiry date'
  },
  africanContext: {
    availability: 'More widely available than morphine across Africa due to less restrictive regulations in many countries. Present in most pharmacies and health facilities.',
    affordability: 'Relatively affordable; approximately $2-10 USD per pack. Generic formulations available.',
    supplyChainNotes: 'Easier to procure than Schedule II opioids. However, increasing regulatory control in some countries due to abuse concerns.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, May & Baker), South Africa (Aspen), Kenya, Egypt, and imported from India.',
    alternatives: [
      'Codeine (weaker; often in combinations)',
      'Morphine (stronger; more restricted)',
      'Paracetamol + codeine combinations',
      'NSAIDs (for inflammatory pain)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial).',
    endemicConsiderations: 'Tramadol has become one of the most abused drugs in parts of Africa (particularly West Africa). Important considerations: (1) Prescribe responsibly - avoid large quantities; (2) Educate patients about abuse potential; (3) Watch for drug-seeking behavior; (4) Counterfeit/substandard tramadol is common - source from reliable suppliers; (5) Illicit high-dose tramadol (120mg+ tablets) is trafficked - these are not pharmaceutical grade; (6) Despite abuse concerns, tramadol remains important analgesic given limited access to stronger opioids.'
  },
  patientCounseling: {
    keyPoints: [
      'Take exactly as prescribed - do not exceed recommended dose',
      'Do not drink alcohol while taking this medicine',
      'May cause dizziness and drowsiness - avoid driving until you know how it affects you',
      'Do not stop suddenly after taking for more than a few weeks',
      'Tell your doctor about all other medicines, especially antidepressants',
      'Seek help if you develop seizures, confusion, or high fever'
    ],
    commonMisconceptions: [
      'Misconception: Tramadol is completely safe because it is weaker than morphine - Truth: Still has risks including seizures, serotonin syndrome, and dependence',
      'Misconception: Cannot become dependent on tramadol - Truth: Physical dependence and abuse can occur',
      'Misconception: Can take with any antidepressant safely - Truth: Dangerous interaction with some antidepressants'
    ],
    adherenceTips: [
      'Take at regular intervals for chronic pain',
      'If using as-needed, take at first sign of pain',
      'Extended-release: take at same time daily',
      'Do not crush or chew extended-release tablets'
    ],
    warningSignsToReport: [
      'Seizures or convulsions',
      'Confusion, agitation, high fever, sweating (serotonin syndrome)',
      'Severe dizziness or fainting',
      'Difficulty breathing',
      'Symptoms of low blood sugar',
      'Thoughts of self-harm or unusual mood changes'
    ]
  },
  clinicalPearls: [
    'Tramadol analgesic effect depends on CYP2D6 metabolism to active M1 metabolite - poor metabolizers (5-10% of Caucasians, higher in some African populations) may have reduced efficacy',
    'Unique dual mechanism (opioid + monoaminergic) makes it useful for neuropathic pain but increases serotonin syndrome and seizure risk',
    'Seizure risk increases significantly above 400 mg/day and with concurrent serotonergic drugs - respect dose limits',
    'Tramadol abuse is a significant problem in parts of Africa - prescribe responsibly and monitor for misuse',
    'Withdrawal from tramadol may include atypical symptoms (anxiety, panic attacks, paresthesias) due to monoaminergic discontinuation effects',
    'Less respiratory depression than morphine at equianalgesic doses, but still present - caution with CNS depressants',
    'FDA warns against use in children <12 and for post-tonsillectomy pain in children <18 due to ultra-rapid metabolizer risk',
    'Hypoglycemia is a recognized risk - monitor diabetic patients',
    'Carbamazepine dramatically reduces tramadol effect through enzyme induction - alternative analgesic usually needed'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'FDA Drug Safety Communications - Tramadol',
    'UNODC Report on Tramadol Trafficking 2021',
    'Grond S, Sablotzki A. Clin Pharmacokinet 2004 - Clinical pharmacology of tramadol',
    'Miotto K et al. Drug Alcohol Depend 2017 - Tramadol: risks, mechanisms, and alternatives',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'diazepam-038',
  genericName: 'Diazepam',
  brandNames: ['Valium', 'Diastat', 'Stesolid', 'Diazemuls', 'Relanium', 'Apaurin'],
  atcCode: 'N05BA01',
  therapeuticClass: 'Benzodiazepine (Anxiolytic, Anticonvulsant, Muscle Relaxant, Sedative)',
  pharmacology: {
    mechanism: 'Enhances the effect of gamma-aminobutyric acid (GABA) at GABA-A receptors by binding to a specific benzodiazepine site. This increases chloride ion conductance, hyperpolarizes neurons, and inhibits neuronal excitability. Produces anxiolytic, sedative-hypnotic, anticonvulsant, and muscle relaxant effects through actions on different brain regions.',
    pharmacokinetics: {
      absorption: 'Rapidly and completely absorbed orally (bioavailability >90%). Peak plasma concentration in 30-90 minutes. IM absorption erratic and slower than oral. Rectal absorption rapid and reliable.',
      distribution: 'Volume of distribution 1-2 L/kg. Highly lipophilic - rapidly crosses blood-brain barrier. Protein binding 98%. Redistributes from brain to peripheral tissues, causing rapid offset of single-dose effect.',
      metabolism: 'Hepatic metabolism via CYP2C19 and CYP3A4 to active metabolites: desmethyldiazepam (nordiazepam), temazepam, and oxazepam. Active metabolites extend duration of effect.',
      elimination: 'Renal excretion of glucuronide conjugates. Very long elimination due to active metabolites.',
      halfLife: 'Diazepam: 20-100 hours; Desmethyldiazepam (active metabolite): 36-200 hours'
    },
    pharmacodynamics: 'Onset: IV 1-5 minutes, oral 15-60 minutes, rectal 10-15 minutes. Peak effect: IV immediate, oral 1-2 hours. Duration highly variable due to redistribution (acute effects) and long half-life of metabolites (prolonged with repeated dosing).'
  },
  indications: [
    {
      condition: 'Status Epilepticus / Acute Seizures',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line emergency treatment for active seizures. IV or rectal formulations. Followed by longer-acting anticonvulsant.'
    },
    {
      condition: 'Acute Anxiety / Panic Attacks',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Rapid relief of severe acute anxiety. Not for long-term use due to dependence. SSRIs preferred for chronic anxiety.'
    },
    {
      condition: 'Alcohol Withdrawal Syndrome',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Standard of care for alcohol withdrawal. Prevents seizures and delirium tremens. Symptom-triggered or fixed-dose protocols.'
    },
    {
      condition: 'Muscle Spasm / Spasticity',
      evidenceLevel: 'A',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Useful for acute muscle spasm. Less effective for chronic spasticity. Alternatives (baclofen, tizanidine) often preferred for chronic use.'
    },
    {
      condition: 'Procedural Sedation',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'IV diazepam for endoscopy, cardioversion, and other procedures. Midazolam often preferred (shorter acting).'
    },
    {
      condition: 'Preoperative Anxiolysis',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Reduces preoperative anxiety. Oral or IV.'
    },
    {
      condition: 'Tetanus (Muscle Spasm Control)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'High doses required. Often continuous IV infusion. Essential component of tetanus management.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Anxiety: 2-10 mg 2-4 times daily. Seizures: 5-10 mg IV every 5-10 min (max 30 mg). Alcohol withdrawal: 10-20 mg initially, then symptom-triggered.',
      indicationSpecific: [
        { indication: 'Status epilepticus', dose: 'IV: 5-10 mg at 2-5 mg/min; repeat every 5-10 min if needed; max 30 mg. Rectal: 10-20 mg' },
        { indication: 'Anxiety (acute)', dose: '5-10 mg oral/IV; repeat in 3-4 hours if needed' },
        { indication: 'Alcohol withdrawal', dose: 'Loading: 10-20 mg IV/oral every 1-2 hours until calm; then symptom-triggered 10-20 mg every 1-2 hours for CIWA-Ar >8-10' },
        { indication: 'Muscle spasm', dose: '5-10 mg 3-4 times daily initially' },
        { indication: 'Procedural sedation', dose: 'IV: 2.5-10 mg slowly; titrate to effect' },
        { indication: 'Tetanus', dose: 'IV: 0.1-0.3 mg/kg every 1-4 hours or continuous infusion up to 10-15 mg/kg/day' }
      ],
      maxDose: '40 mg/day oral for chronic use; higher doses for status epilepticus and tetanus under monitoring',
      renalImpairment: {
        mild: 'No adjustment typically needed',
        moderate: 'Reduce dose; monitor for excessive sedation',
        severe: 'Reduce dose by 50%; active metabolites may accumulate',
        dialysis: 'Not removed by hemodialysis; use with caution'
      },
      hepaticImpairment: 'Reduce dose by 50% or more. Metabolites accumulate. Half-life markedly prolonged. Use short-acting alternatives (lorazepam, oxazepam) if possible.'
    },
    pediatric: {
      standard: 'Seizures: 0.1-0.3 mg/kg IV (max 10 mg) or 0.5 mg/kg rectal. Anxiety rarely indicated in children.',
      weightBased: true,
      ageSpecific: [
        { age: 'Infants 30 days - 5 years (seizures)', dose: '0.2-0.5 mg/kg rectal; IV: 0.1-0.3 mg/kg slowly' },
        { age: '5-12 years (seizures)', dose: 'Rectal: 0.3 mg/kg; IV: 0.1-0.3 mg/kg (max 5 mg)' },
        { age: '>12 years (seizures)', dose: 'Rectal: 0.2 mg/kg (max 20 mg); IV: 5-10 mg' },
        { age: 'Tetanus (children)', dose: '0.1-0.2 mg/kg IV every 4-8 hours or continuous infusion' }
      ],
      maxDose: 'Seizures: <5 years max 5 mg; ≥5 years max 10 mg per dose',
      neonatal: 'IV: 0.1-0.3 mg/kg for seizures. Neonates very sensitive; prolonged half-life. Use with caution.'
    },
    elderly: 'Start 2-2.5 mg 1-2 times daily. Significantly increased sensitivity. Prolonged half-life. Increased fall risk. Avoid if possible.',
    routes: ['Oral', 'Intravenous', 'Intramuscular (avoid - erratic absorption)', 'Rectal'],
    administration: [
      'IV: Give slowly at rate not exceeding 5 mg/min (adults) to avoid apnea and hypotension',
      'IV formulation contains propylene glycol - rapid injection can cause hypotension',
      'Do not dilute IV injection or mix with other drugs',
      'Rectal gel (Diastat) useful for home seizure management',
      'IM injection painful and absorbed erratically - avoid when IV or rectal available',
      'Oral may be taken with or without food'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to benzodiazepines',
      'Severe respiratory depression',
      'Acute pulmonary insufficiency',
      'Sleep apnea syndrome (relative for chronic use)',
      'Severe hepatic impairment (for chronic use)',
      'Myasthenia gravis',
      'Acute narrow-angle glaucoma'
    ],
    relative: [
      'Chronic obstructive pulmonary disease',
      'Moderate hepatic impairment',
      'Elderly or debilitated patients',
      'History of substance use disorder',
      'Depression (may worsen)',
      'Concurrent opioid use'
    ],
    precautions: [
      'Monitor for respiratory depression, especially with IV use',
      'Risk of dependence and withdrawal with chronic use',
      'Increased fall risk in elderly',
      'May cause paradoxical excitation in some patients',
      'Avoid abrupt discontinuation after prolonged use',
      'Have flumazenil available when using IV for sedation',
      'May cause anterograde amnesia'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Sedation/drowsiness', frequency: '30-50%', management: 'Dose at bedtime if possible; reduce dose; warn about driving' },
      { effect: 'Ataxia/incoordination', frequency: '10-20%', management: 'Reduce dose; fall precautions especially in elderly' },
      { effect: 'Confusion', frequency: '5-15%', management: 'Reduce dose; more common in elderly' },
      { effect: 'Anterograde amnesia', frequency: '5-10%', management: 'Expected effect; inform patient' },
      { effect: 'Fatigue', frequency: '10-20%', management: 'Usually improves with continued use or dose adjustment' },
      { effect: 'Muscle weakness', frequency: '5-10%', management: 'Reduce dose if troublesome' }
    ],
    serious: [
      { effect: 'Respiratory depression', frequency: 'Rare (more common with IV)', management: 'Support ventilation; flumazenil if severe (cautiously in chronic users)', warning: 'Risk highest with IV administration, concurrent opioids/CNS depressants, elderly, COPD' },
      { effect: 'Dependence and withdrawal', frequency: 'Common with prolonged use', management: 'Gradual taper (reduce by 10-25% every 1-2 weeks); withdrawal can be life-threatening', warning: 'Physical dependence develops within 2-4 weeks of continuous use. Abrupt cessation can cause seizures.' },
      { effect: 'Paradoxical reactions (agitation, aggression)', frequency: '1-2%', management: 'Discontinue; may need alternative sedation method', warning: 'More common in children, elderly, and patients with psychiatric disorders' },
      { effect: 'Hypotension (IV use)', frequency: 'Variable', management: 'Slow IV push; IV fluids if needed', warning: 'Due to propylene glycol vehicle and vasodilation' },
      { effect: 'Falls and fractures (elderly)', frequency: 'Increased risk', management: 'Lowest dose possible; fall precautions; avoid if possible in elderly', warning: 'AGS Beers Criteria recommends avoiding in older adults' }
    ],
    rare: [
      'Blood dyscrasias',
      'Hepatotoxicity',
      'Skin reactions',
      'Depression',
      'Incontinence',
      'Changes in libido',
      'Diplopia/blurred vision'
    ]
  },
  interactions: {
    major: [
      { drug: 'Opioids', effect: 'Profound sedation, respiratory depression, coma, death', mechanism: 'Additive CNS depression', management: 'FDA black box warning. Avoid combination if possible. If needed, use lowest doses and monitor closely.' },
      { drug: 'Alcohol', effect: 'Enhanced CNS depression; potentially fatal', mechanism: 'Additive effects', management: 'Absolute avoidance advised' },
      { drug: 'Other CNS depressants (barbiturates, antihistamines)', effect: 'Enhanced sedation and respiratory depression', mechanism: 'Additive CNS effects', management: 'Avoid or use reduced doses with close monitoring' }
    ],
    moderate: [
      { drug: 'CYP3A4 inhibitors (ketoconazole, erythromycin, ritonavir)', effect: 'Increased diazepam levels and effects', management: 'Reduce diazepam dose; monitor for excessive sedation' },
      { drug: 'CYP2C19 inhibitors (omeprazole, fluoxetine)', effect: 'Increased diazepam levels', management: 'Monitor for increased effects; may need dose reduction' },
      { drug: 'CYP3A4 inducers (rifampicin, phenytoin)', effect: 'Reduced diazepam effect', management: 'May need increased dose; monitor efficacy' },
      { drug: 'Valproate', effect: 'Displaces diazepam from protein binding; increased free diazepam', management: 'Monitor for increased effects' }
    ],
    food: [
      'Food may delay absorption slightly but does not reduce extent',
      'Avoid alcohol - dangerous combination',
      'Grapefruit juice may increase diazepam levels'
    ]
  },
  pregnancy: {
    category: 'D',
    trimpiesterRisks: {
      first: 'Possible increased risk of oral clefts (controversial). Avoid if possible.',
      second: 'May affect fetal development. Use only if clearly needed.',
      third: 'Risk of neonatal withdrawal syndrome and floppy infant syndrome. Avoid near delivery.'
    },
    recommendation: 'Avoid during pregnancy, especially first trimester and near delivery. If essential (e.g., status epilepticus), use with caution. Neonatal monitoring required if used near term. For chronic anxiety, consider non-pharmacologic approaches or safer alternatives.',
    lactation: {
      safe: false,
      notes: 'Diazepam and active metabolites excreted in breast milk. Can cause sedation and feeding difficulties in infant. Accumulates due to long half-life. Avoid if possible; if single dose needed, pump and discard for 24-48 hours.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Respiratory status', reason: 'Risk of respiratory depression, especially IV' },
      { test: 'Mental status', reason: 'Assess baseline for monitoring sedation and confusion' },
      { test: 'Hepatic function', reason: 'Metabolized hepatically; dose adjustment in impairment' },
      { test: 'Fall risk assessment', reason: 'Especially in elderly' }
    ],
    ongoing: [
      { test: 'Respiratory rate and oxygen saturation', frequency: 'Continuous during IV sedation; regularly in hospital', target: 'RR >10-12/min; SpO2 >94%' },
      { test: 'Level of sedation', frequency: 'Regularly during and after IV administration', target: 'Appropriate sedation without respiratory compromise' },
      { test: 'Signs of dependence (chronic use)', frequency: 'Each visit', target: 'No tolerance, dose escalation, or drug-seeking' },
      { test: 'Functional status (chronic use)', frequency: 'Each visit', target: 'No impairment from medication' }
    ],
    therapeutic: {
      range: 'No routine drug level monitoring in clinical practice.',
      timing: 'For status epilepticus, assess seizure termination; repeat if ongoing'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light',
    humidity: 'Protect from moisture',
    specialInstructions: 'Controlled substance - secure storage required. Injectable may adsorb to plastic; use glass syringes or administer immediately.',
    shelfLife: '3-5 years; check expiry date'
  },
  africanContext: {
    availability: 'Widely available across Africa. WHO Essential Medicine. Present in most health facilities. Injectable formulation essential for seizure management.',
    affordability: 'Very affordable; among cheapest benzodiazepines. Generic formulations widely available.',
    supplyChainNotes: 'Controlled substance but less restricted than opioids. Generally stable supply. Injectable may have cold chain requirements for some formulations.',
    localManufacturing: 'Manufactured in Nigeria (Emzor, May & Baker), South Africa (Aspen), Kenya, Egypt, and imported from India.',
    alternatives: [
      'Lorazepam (shorter acting; preferred in hepatic impairment)',
      'Midazolam (shorter acting; IM absorption better)',
      'Phenobarbital (alternative for seizures)',
      'Clonazepam (alternative anticonvulsant)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial). Note: Tolerance develops with chronic use.',
    endemicConsiderations: 'Diazepam is essential for status epilepticus and alcohol withdrawal across Africa. Key considerations: (1) Rectal diazepam is invaluable for community/home management of prolonged seizures; (2) Essential for tetanus management - tetanus remains significant in Africa; (3) Abuse potential exists but generally less problematic than opioids; (4) Alcohol use disorder is common in many African communities - diazepam is critical for safe withdrawal; (5) Injectable formulation must be available at all levels for seizure emergencies.'
  },
  patientCounseling: {
    keyPoints: [
      'Do not drive or operate machinery while taking this medicine',
      'Do not drink alcohol - dangerous combination',
      'Do not stop suddenly after taking for more than a few weeks - must taper gradually',
      'May cause drowsiness - take at bedtime if once-daily',
      'Can become habit-forming - use only as prescribed',
      'Keep out of reach of children'
    ],
    commonMisconceptions: [
      'Misconception: Can take as much as needed when anxious - Truth: Take only prescribed dose; more is dangerous',
      'Misconception: Can stop anytime without problems - Truth: Sudden stopping can cause seizures',
      'Misconception: Safe to take with alcohol - Truth: This combination can be fatal',
      'Misconception: Not addictive because prescribed by doctor - Truth: Dependence develops with regular use'
    ],
    adherenceTips: [
      'Take at same times each day',
      'Do not increase dose without consulting doctor',
      'If using for sleep, take 30 minutes before bed',
      'Keep a supply of medication for emergencies (seizure patients)'
    ],
    warningSignsToReport: [
      'Severe drowsiness or confusion',
      'Difficulty breathing',
      'Severe weakness or incoordination',
      'Worsening anxiety or agitation (paradoxical reaction)',
      'Memory problems',
      'Signs of withdrawal if you miss doses'
    ]
  },
  clinicalPearls: [
    'IV diazepam must be given slowly (max 5 mg/min) - rapid injection causes apnea and hypotension',
    'Active metabolites have very long half-lives (36-200 hours) - accumulation occurs with repeated dosing, especially in elderly',
    'For status epilepticus, if IV not available, rectal diazepam is highly effective and rapidly absorbed',
    'In hepatic impairment, prefer lorazepam or oxazepam (no active metabolites, glucuronidated only)',
    'Alcohol withdrawal: symptom-triggered dosing (using CIWA-Ar score) uses less medication than fixed-dose schedules',
    'Flumazenil reverses benzodiazepine effects but can precipitate seizures in chronic users - use with caution',
    'AGS Beers Criteria strongly recommends avoiding benzodiazepines in older adults due to fall risk and cognitive effects',
    'For tetanus, very high doses may be needed - titrate to muscle relaxation; mechanical ventilation often required',
    'Dependence develops within 2-4 weeks of daily use - limit duration when possible',
    'Rectal diazepam gel (Diastat) is essential for home management of pediatric seizure emergencies'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'Brodie MJ et al. Epilepsia 2012 - Management of Status Epilepticus',
    'American Society of Addiction Medicine - Alcohol Withdrawal Guidelines',
    'American Geriatrics Society Beers Criteria 2023',
    'WHO Guidelines on Tetanus Management',
    'NICE Guidelines - Epilepsy Management',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'phenytoin-039',
  genericName: 'Phenytoin',
  brandNames: ['Dilantin', 'Epanutin', 'Phenytek', 'Eptoin', 'Phenhydan'],
  atcCode: 'N03AB02',
  therapeuticClass: 'Hydantoin Anticonvulsant (Antiepileptic Drug)',
  pharmacology: {
    mechanism: 'Stabilizes neuronal membranes by blocking voltage-gated sodium channels in their inactivated state. Prolongs the inactivated state, reducing the ability of neurons to fire high-frequency action potentials. This limits the spread of seizure activity without affecting normal neuronal firing. Also has effects on calcium channels and neurotransmitter release.',
    pharmacokinetics: {
      absorption: 'Slowly absorbed orally; bioavailability ~90% (varies between formulations). Peak levels: immediate-release 3-12 hours, extended-release 4-12 hours. Food may increase absorption. IM absorption erratic and painful - avoid.',
      distribution: 'Volume of distribution 0.6-0.8 L/kg. Highly protein bound (90%), primarily to albumin. Crosses blood-brain barrier. Free (unbound) fraction increases in hypoalbuminemia, renal failure, pregnancy.',
      metabolism: 'Extensive hepatic metabolism via CYP2C9 (major) and CYP2C19. Saturable (zero-order) kinetics at therapeutic doses - small dose increases cause disproportionate increases in plasma levels. Major enzyme inducer.',
      elimination: '<5% excreted unchanged in urine. Metabolites excreted renally.',
      halfLife: '7-42 hours (average 22 hours); highly variable due to saturable kinetics; increases with higher concentrations'
    },
    pharmacodynamics: 'Anticonvulsant effect correlates with plasma concentration. Therapeutic range 10-20 mcg/mL (total) or 1-2 mcg/mL (free). Effect onset after oral loading in 1-3 hours. Steady state with maintenance dosing takes 7-10 days (longer due to saturable kinetics).'
  },
  indications: [
    {
      condition: 'Generalized Tonic-Clonic Seizures',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective first-line agent. Widely used globally. Narrow therapeutic index requires monitoring.'
    },
    {
      condition: 'Focal (Partial) Seizures',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective for focal seizures with or without secondary generalization.'
    },
    {
      condition: 'Status Epilepticus',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Second-line after benzodiazepines fail. IV loading dose. Fosphenytoin preferred if available (less tissue toxicity).'
    },
    {
      condition: 'Seizure Prophylaxis (Neurosurgery, Severe TBI)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: true,
      notes: 'Short-term prophylaxis (7 days) reduces early seizures. Long-term prophylaxis not clearly beneficial.'
    },
    {
      condition: 'Trigeminal Neuralgia',
      evidenceLevel: 'B',
      recommendationStrength: 'Weak',
      firstLine: false,
      notes: 'Alternative when carbamazepine not tolerated. Less evidence than carbamazepine.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Loading (status epilepticus): 15-20 mg/kg IV at max 50 mg/min. Maintenance: 300-400 mg/day oral in 1-3 divided doses (extended-release can be once daily).',
      indicationSpecific: [
        { indication: 'Status epilepticus', dose: 'IV: 15-20 mg/kg at max 50 mg/min (max 25 mg/min in elderly/cardiac disease); follow with maintenance 5-6 mg/kg/day' },
        { indication: 'Oral loading (non-emergency)', dose: '1 g in 3 divided doses (400 mg, 300 mg, 300 mg) at 2-hour intervals' },
        { indication: 'Maintenance therapy', dose: 'Start 300 mg/day (3-5 mg/kg/day); adjust based on levels; usual range 200-500 mg/day' },
        { indication: 'Seizure prophylaxis', dose: 'Loading 15-20 mg/kg IV, then 5 mg/kg/day maintenance' }
      ],
      maxDose: '600 mg/day typically; higher doses rarely needed and increase toxicity risk',
      renalImpairment: {
        mild: 'No dose adjustment; monitor free levels (increased free fraction)',
        moderate: 'No dose adjustment; free fraction increased - monitor free phenytoin levels',
        severe: 'No dose adjustment; interpret total levels cautiously; measure free levels',
        dialysis: 'Supplemental dose may be needed after dialysis (modest removal)'
      },
      hepaticImpairment: 'Reduce dose; metabolism impaired. Monitor levels closely. Free fraction may increase.'
    },
    pediatric: {
      standard: 'Loading: 15-20 mg/kg IV. Maintenance: varies by age; generally 4-8 mg/kg/day.',
      weightBased: true,
      ageSpecific: [
        { age: 'Neonates', dose: 'Loading: 15-20 mg/kg IV slowly; Maintenance: 5-8 mg/kg/day divided every 8-12 hours' },
        { age: 'Infants/Children <6 years', dose: 'Maintenance: 5-10 mg/kg/day divided 2-3 times daily' },
        { age: 'Children 6-12 years', dose: 'Maintenance: 4-8 mg/kg/day divided 2-3 times daily' },
        { age: 'Adolescents', dose: 'Approach adult dosing' }
      ],
      maxDose: 'Based on serum levels; children may require higher mg/kg doses due to faster metabolism',
      neonatal: 'Loading: 15-20 mg/kg IV slowly (max 1-3 mg/kg/min). Maintenance: 5-8 mg/kg/day. Neonates have variable and prolonged half-life.'
    },
    elderly: 'Start with lower doses (200 mg/day). Increased free fraction due to decreased albumin. More susceptible to CNS effects. Give IV slowly (max 25 mg/min).',
    routes: ['Oral (capsules, tablets, suspension)', 'Intravenous'],
    administration: [
      'IV: Give slowly - max 50 mg/min in adults (25 mg/min in elderly, cardiac patients). Infusion rate-related hypotension and arrhythmias.',
      'IV must use in-line filter; incompatible with dextrose solutions; flush with saline',
      'Extended-release capsules: swallow whole; can be opened and sprinkled on food but do not chew',
      'Oral suspension: shake well; measure carefully; do not substitute for capsules mg-for-mg (different bioavailability)',
      'IM route avoided - erratic absorption, crystallization, muscle necrosis',
      'IV extravasation causes severe tissue damage (purple glove syndrome)'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to phenytoin or other hydantoins',
      'Sinus bradycardia, sinoatrial block, second/third-degree AV block (IV use)',
      'Adams-Stokes syndrome',
      'Concurrent use with delavirdine'
    ],
    relative: [
      'Hepatic impairment',
      'Cardiac disease (for IV use)',
      'Hypotension (for IV use)',
      'Porphyria',
      'Asian ancestry with HLA-B*1502 (increased SJS/TEN risk)',
      'Diabetes mellitus (may impair glucose control)'
    ],
    precautions: [
      'Narrow therapeutic index - monitor levels',
      'Multiple drug interactions due to enzyme induction',
      'May cause severe skin reactions (SJS/TEN) - stop at first sign of rash',
      'Purple glove syndrome with IV extravasation',
      'IV administration requires cardiac monitoring',
      'May cause hyperglycemia',
      'Long-term use: osteoporosis, gingival hyperplasia, hirsutism'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Nystagmus', frequency: '10-20%', management: 'Indicates level near/above therapeutic range; reduce dose if persistent' },
      { effect: 'Ataxia', frequency: '5-15%', management: 'Reduce dose; usually indicates toxicity' },
      { effect: 'Drowsiness/sedation', frequency: '5-15%', management: 'Often improves with time; check levels' },
      { effect: 'Gingival hyperplasia', frequency: '20-50%', management: 'Good oral hygiene; dental care; may require gingivectomy or drug switch' },
      { effect: 'Hirsutism', frequency: '5-10%', management: 'Cosmetic concern; consider alternative if bothersome' },
      { effect: 'Nausea', frequency: '5-10%', management: 'Take with food' }
    ],
    serious: [
      { effect: 'Stevens-Johnson syndrome / TEN', frequency: 'Rare (1:1000 to 1:10000)', management: 'STOP immediately at first sign of rash. Hospitalize; supportive care; dermatology consultation.', warning: 'Increased risk in HLA-B*1502 positive individuals (common in Asians). Screen before initiating in at-risk populations.' },
      { effect: 'DRESS syndrome (Drug Reaction with Eosinophilia)', frequency: 'Rare', management: 'Stop phenytoin; systemic corticosteroids; supportive care', warning: 'Typically occurs 2-6 weeks after starting. Multi-organ involvement.' },
      { effect: 'Cardiac arrhythmias (IV use)', frequency: 'Uncommon', management: 'Slow infusion rate; cardiac monitoring; stop if arrhythmias occur', warning: 'Risk increases with rapid infusion; pre-existing cardiac disease' },
      { effect: 'Hypotension (IV use)', frequency: '5-10%', management: 'Slow infusion rate; IV fluids; support BP', warning: 'Due to propylene glycol vehicle and direct cardiac effects' },
      { effect: 'Purple glove syndrome (IV extravasation)', frequency: 'Rare', management: 'Stop infusion immediately; elevate limb; may need surgical consultation', warning: 'Can cause limb ischemia and necrosis; use large vein, monitor site' },
      { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Stop phenytoin; supportive care', warning: 'Can be fatal; monitor LFTs if symptoms develop' },
      { effect: 'Megaloblastic anemia', frequency: 'Rare', management: 'Folic acid supplementation', warning: 'Due to interference with folate metabolism' }
    ],
    rare: [
      'Lupus-like syndrome',
      'Lymphadenopathy (pseudolymphoma)',
      'Peripheral neuropathy',
      'Osteomalacia/osteoporosis (chronic use)',
      'Coarsening of facial features',
      'Cerebellar atrophy (chronic toxicity)',
      'Immunoglobulin deficiency'
    ]
  },
  interactions: {
    major: [
      { drug: 'Warfarin', effect: 'Complex: initially increased anticoagulation, then reduced effect with chronic phenytoin', mechanism: 'Initially inhibits warfarin metabolism, then induces CYP2C9', management: 'Monitor INR frequently when starting, stopping, or changing phenytoin dose' },
      { drug: 'Oral contraceptives', effect: 'Reduced contraceptive efficacy; contraceptive failure', mechanism: 'CYP3A4 induction increases metabolism of estrogen/progestin', management: 'Use higher-dose OC or alternative contraception (IUD, depot medroxyprogesterone)' },
      { drug: 'Carbamazepine', effect: 'Mutual induction; complex interactions', mechanism: 'Both are CYP inducers; affect each others metabolism', management: 'Monitor levels of both; dose adjustments often needed' },
      { drug: 'Valproate', effect: 'Increased free phenytoin; decreased total levels', mechanism: 'Valproate displaces phenytoin from albumin and inhibits metabolism', management: 'Monitor free phenytoin levels; clinical signs of toxicity' }
    ],
    moderate: [
      { drug: 'CYP2C9 inhibitors (fluconazole, amiodarone)', effect: 'Increased phenytoin levels', management: 'Monitor levels; reduce phenytoin dose' },
      { drug: 'CYP2C9 inducers (rifampicin)', effect: 'Reduced phenytoin levels', management: 'May need increased phenytoin dose; monitor levels' },
      { drug: 'Corticosteroids', effect: 'Reduced corticosteroid efficacy', management: 'May need higher corticosteroid doses' },
      { drug: 'Antifungals (azoles)', effect: 'Increased phenytoin; decreased antifungal', management: 'Monitor both drug levels and efficacy' },
      { drug: 'Enteral feeds', effect: 'Reduced phenytoin absorption', management: 'Hold feeds 1-2 hours before and after phenytoin; monitor levels' }
    ],
    food: [
      'May be taken with food to reduce GI upset',
      'Enteral nutrition significantly reduces absorption - hold feeds around doses',
      'Chronic alcohol use increases metabolism; acute intoxication may increase levels'
    ]
  },
  pregnancy: {
    category: 'D',
    trimpiesterRisks: {
      first: 'Risk of fetal hydantoin syndrome (craniofacial abnormalities, limb defects, growth retardation, developmental delay). Risk ~5-10%.',
      second: 'Continued teratogenic risk. Monitor fetal development.',
      third: 'Risk of neonatal coagulation defects (vitamin K deficiency). Give maternal vitamin K before delivery.'
    },
    recommendation: 'Avoid if possible. If essential for seizure control, use lowest effective dose; consider monotherapy. Preconception folic acid (4-5 mg/day). High-resolution ultrasound for fetal anomalies. Vitamin K to mother before delivery and to neonate. Breastfeeding generally acceptable.',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk in low concentrations. Generally considered compatible with breastfeeding. Infant receives ~5% of maternal dose. Monitor infant for sedation.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Phenytoin level', reason: 'Establish baseline if converting from another formulation' },
      { test: 'Complete blood count', reason: 'Baseline for monitoring hematologic effects' },
      { test: 'Liver function tests', reason: 'Hepatic metabolism; risk of hepatotoxicity' },
      { test: 'Albumin', reason: 'Highly protein bound; affects interpretation of levels' },
      { test: 'HLA-B*1502 (in Asians)', reason: 'Identify risk of severe cutaneous reactions' }
    ],
    ongoing: [
      { test: 'Phenytoin level (total)', frequency: 'After loading; 2-4 weeks after dose change; periodically when stable', target: '10-20 mcg/mL (total); interpret with albumin' },
      { test: 'Free phenytoin level', frequency: 'If hypoalbuminemia, renal failure, or pregnancy', target: '1-2 mcg/mL (free)' },
      { test: 'CBC', frequency: 'Every 6-12 months', target: 'No significant cytopenias' },
      { test: 'LFTs', frequency: 'Every 6-12 months; if symptoms', target: 'Normal' },
      { test: 'Signs of toxicity (nystagmus, ataxia, lethargy)', frequency: 'Each visit', target: 'None present' },
      { test: 'Vitamin D and bone density', frequency: 'Consider with long-term use', target: 'Normal vitamin D; stable bone density' }
    ],
    therapeutic: {
      range: 'Total: 10-20 mcg/mL (40-80 micromol/L); Free: 1-2 mcg/mL. Toxicity usually >20 mcg/mL total.',
      timing: 'Trough level (just before next dose) preferred. Steady state in 7-10 days with maintenance dosing.'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light and moisture',
    humidity: 'Store in tight container',
    specialInstructions: 'Do not freeze oral suspension. IV solution precipitates if refrigerated or mixed with dextrose.',
    shelfLife: '2-5 years depending on formulation; check expiry'
  },
  africanContext: {
    availability: 'Widely available across Africa. WHO Essential Medicine. One of the most accessible anticonvulsants at all healthcare levels.',
    affordability: 'Very affordable; among the cheapest anticonvulsants. Generic formulations widely available.',
    supplyChainNotes: 'Stable supply due to multiple generic manufacturers. Formulations generally heat-stable.',
    localManufacturing: 'Manufactured in Nigeria, South Africa, Kenya, Egypt, and many other African countries.',
    alternatives: [
      'Carbamazepine (alternative first-line for focal seizures)',
      'Phenobarbital (older agent; very cheap and available)',
      'Valproate (broader spectrum)',
      'Newer agents (levetiracetam) - limited availability'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial).',
    endemicConsiderations: 'Phenytoin remains a cornerstone of epilepsy treatment in Africa due to availability and cost. Key considerations: (1) Therapeutic drug monitoring often unavailable - clinical monitoring essential; (2) Non-linear kinetics makes dose adjustment challenging without levels; (3) Formulation switching problematic - generic substitution can alter levels; (4) Gingival hyperplasia and cosmetic effects can affect adherence, especially in young women; (5) Drug interactions common with TB therapy (rifampicin), HIV therapy, and traditional medicines; (6) HLA-B*1502 testing unavailable in most settings - be vigilant for early rash.'
  },
  patientCounseling: {
    keyPoints: [
      'Take exactly as prescribed at the same time(s) each day',
      'Do not stop suddenly - can cause seizures',
      'Report any skin rash immediately - may need to stop medication',
      'Maintain good dental hygiene - brush and floss regularly',
      'Avoid alcohol - increases side effects and affects levels',
      'Tell all doctors you take phenytoin - many drug interactions'
    ],
    commonMisconceptions: [
      'Misconception: Can skip doses if feeling well - Truth: Must take consistently to prevent seizures',
      'Misconception: All brands are the same - Truth: Switching brands can change drug levels',
      'Misconception: Side effects mean the drug is not working - Truth: Some side effects normal; toxicity symptoms need dose adjustment'
    ],
    adherenceTips: [
      'Take at same time(s) daily',
      'Use pill organizer',
      'Do not double up if missed dose - take next regular dose',
      'Keep regular dental appointments',
      'Carry medical identification'
    ],
    warningSignsToReport: [
      'Any skin rash, blistering, or mouth sores',
      'Fever, sore throat, unusual bleeding or bruising',
      'Severe nausea or vomiting, abdominal pain, yellowing of skin',
      'Unsteadiness, double vision, slurred speech (toxicity signs)',
      'Swelling or pain at IV site'
    ]
  },
  clinicalPearls: [
    'Saturable (zero-order) kinetics means small dose changes cause disproportionate level changes - increase in small increments (25-50 mg) and recheck levels',
    'Free (unbound) phenytoin levels should be measured in hypoalbuminemia, renal failure, and pregnancy - total levels are misleading',
    'IV phenytoin must be given slowly (max 50 mg/min) with cardiac monitoring - faster rates cause hypotension and arrhythmias',
    'HLA-B*1502 screening before initiation in Asian patients can identify those at high risk for SJS/TEN',
    'Fosphenytoin is a prodrug that avoids IV phenytoin complications (purple glove syndrome) - use if available',
    'Enzyme induction affects many drugs including contraceptives, warfarin, and other AEDs - review all medications',
    'Gingival hyperplasia occurs in 20-50% of patients - good oral hygiene and regular dental care essential',
    'In African settings without drug level monitoring, titrate based on clinical response and signs of toxicity (nystagmus is early sign)',
    'Formulation matters - do not substitute capsules for suspension mg-for-mg; different bioavailability',
    'Long-term use causes vitamin D deficiency and osteoporosis - supplement calcium and vitamin D'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'ILAE Guidelines for Treatment of Epilepsy 2022',
    'Brodie MJ et al. Epilepsia 2012 - Management of Epilepsy',
    'FDA Drug Safety Communication - Phenytoin and HLA-B*1502',
    'American Epilepsy Society Status Epilepticus Guidelines',
    'Zaccara G et al. CNS Drugs 2017 - Phenytoin review',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  {
  id: 'carbamazepine-040',
  genericName: 'Carbamazepine',
  brandNames: ['Tegretol', 'Carbatrol', 'Epitol', 'Equetro', 'Tegrital', 'Timonil'],
  atcCode: 'N03AF01',
  therapeuticClass: 'Iminostilbene Anticonvulsant (Antiepileptic Drug)',
  pharmacology: {
    mechanism: 'Primary mechanism is blockade of voltage-gated sodium channels, stabilizing the inactivated state and reducing neuronal firing. Limits spread of seizure activity and reduces post-tetanic potentiation. Also affects adenosine receptors, and has effects on calcium channels and neurotransmitter release. Mechanism in trigeminal neuralgia similar - reduces abnormal neuronal firing.',
    pharmacokinetics: {
      absorption: 'Slowly but well absorbed from GI tract. Bioavailability 75-85%. Peak levels: immediate-release 4-8 hours, extended-release 3-12 hours. Absorption improved with food.',
      distribution: 'Volume of distribution 0.8-2 L/kg. Protein binding 70-80%. Crosses blood-brain barrier. Active metabolite (carbamazepine-10,11-epoxide) also contributes to effect and toxicity.',
      metabolism: 'Extensive hepatic metabolism via CYP3A4 (major) to active epoxide metabolite (CBZ-E). Epoxide further metabolized. Auto-induction: carbamazepine induces its own metabolism - half-life decreases with chronic dosing.',
      elimination: '1-3% excreted unchanged in urine. Metabolites renally excreted.',
      halfLife: 'Initial: 25-65 hours; After auto-induction (2-4 weeks): 12-17 hours; CBZ-E half-life: 5-8 hours'
    },
    pharmacodynamics: 'Anticonvulsant effect correlates with total and epoxide concentrations. Therapeutic range 4-12 mcg/mL. Effect onset over days to weeks. Steady state takes 2-4 weeks due to auto-induction. Maximum effect may not be seen until 1 month after stable dosing.'
  },
  indications: [
    {
      condition: 'Focal (Partial) Seizures',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'First-line agent for focal seizures with or without secondary generalization. Comparable efficacy to phenytoin with better tolerability for some.'
    },
    {
      condition: 'Generalized Tonic-Clonic Seizures',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Effective first-line agent. Not for absence or myoclonic seizures (may worsen).'
    },
    {
      condition: 'Trigeminal Neuralgia',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: true,
      notes: 'Drug of choice. 70-80% of patients respond. Start low and titrate. May need to increase over time due to auto-induction.'
    },
    {
      condition: 'Bipolar Disorder (Acute Mania, Maintenance)',
      evidenceLevel: 'A',
      recommendationStrength: 'Strong',
      firstLine: false,
      notes: 'Alternative mood stabilizer. Particularly useful for rapid cycling and mixed states. Second-line to lithium and valproate.'
    },
    {
      condition: 'Neuropathic Pain (Diabetic Neuropathy, Postherpetic Neuralgia)',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: false,
      notes: 'Off-label but effective. Second-line to gabapentin, pregabalin, or duloxetine.'
    },
    {
      condition: 'Glossopharyngeal Neuralgia',
      evidenceLevel: 'B',
      recommendationStrength: 'Moderate',
      firstLine: true,
      notes: 'Similar efficacy as for trigeminal neuralgia.'
    }
  ],
  dosing: {
    adults: {
      standard: 'Start 100-200 mg once or twice daily. Increase by 200 mg/day at weekly intervals. Usual maintenance 800-1200 mg/day in 2-4 divided doses.',
      indicationSpecific: [
        { indication: 'Epilepsy', dose: 'Start 100-200 mg twice daily; increase weekly by 200 mg/day; usual 800-1200 mg/day; max 1600-2400 mg/day' },
        { indication: 'Trigeminal neuralgia', dose: 'Start 100 mg twice daily; increase by 100-200 mg every 2-3 days until pain relief; usual 400-800 mg/day; max 1200 mg/day' },
        { indication: 'Bipolar disorder', dose: 'Start 200 mg twice daily; increase by 200 mg/day every 3-7 days; usual 800-1200 mg/day' },
        { indication: 'Neuropathic pain', dose: 'Start 100-200 mg/day; titrate as tolerated; usual 400-800 mg/day' }
      ],
      maxDose: '1600 mg/day typical; up to 2400 mg/day in some cases (monitor levels)',
      renalImpairment: {
        mild: 'No adjustment; monitor levels',
        moderate: 'Consider dose reduction; reduce by 25%',
        severe: 'Reduce dose; monitor levels and toxicity closely',
        dialysis: 'Supplement dose may be needed after dialysis'
      },
      hepaticImpairment: 'Reduce dose; metabolism impaired. Use with caution in severe impairment.'
    },
    pediatric: {
      standard: 'Children <12 years: Start 10-20 mg/kg/day divided 2-4 times daily; increase weekly. Adolescents: approach adult dosing.',
      weightBased: true,
      ageSpecific: [
        { age: '<6 years', dose: 'Start 10-20 mg/kg/day in 2-3 divided doses; increase weekly by 5-10 mg/kg/day; max 35 mg/kg/day' },
        { age: '6-12 years', dose: 'Start 100 mg twice daily (10-20 mg/kg/day); increase weekly; max 1000-1200 mg/day' },
        { age: '>12 years', dose: 'Adult dosing' }
      ],
      maxDose: '35 mg/kg/day or 1200 mg/day in children <12 years',
      neonatal: 'Limited data. Rarely used. If needed: 5-10 mg/kg/day divided 2-3 times daily.'
    },
    elderly: 'Start lower (100 mg daily or twice daily). Titrate slowly. More susceptible to CNS effects. Auto-induction may be less pronounced.',
    routes: ['Oral (tablets, chewable tablets, extended-release, suspension)'],
    administration: [
      'Take with food to enhance absorption and reduce GI upset',
      'Extended-release: swallow whole; do not crush or chew',
      'Suspension: shake well; do not administer simultaneously with other liquid medications',
      'Tablets can be halved but extended-release must not be crushed',
      'Auto-induction occurs over 2-4 weeks - may need dose increases',
      'Divide daily dose into multiple doses for immediate-release (reduce fluctuation)'
    ]
  },
  contraindications: {
    absolute: [
      'Hypersensitivity to carbamazepine or tricyclic compounds',
      'History of bone marrow depression',
      'Concurrent or recent (within 14 days) MAO inhibitor use',
      'Concurrent use with nefazodone',
      'Atrioventricular heart block'
    ],
    relative: [
      'HLA-B*1502 positive (common in Asian populations) - high SJS/TEN risk',
      'HLA-A*3101 positive - increased hypersensitivity risk',
      'Hepatic disease',
      'Renal impairment',
      'History of hematologic reactions to any drug',
      'Mixed seizure disorders including absence seizures (may worsen)',
      'Glaucoma',
      'Cardiac conduction defects'
    ],
    precautions: [
      'Screen for HLA-B*1502 in Asian patients before initiating',
      'Monitor CBC due to risk of aplastic anemia and agranulocytosis',
      'Multiple drug interactions due to enzyme induction',
      'Auto-induction - levels decrease over first weeks, may need dose increase',
      'Severe skin reactions (SJS/TEN, DRESS) - stop immediately if rash',
      'Hyponatremia, especially in elderly',
      'May exacerbate absence and myoclonic seizures'
    ]
  },
  adverseEffects: {
    common: [
      { effect: 'Dizziness', frequency: '15-30%', management: 'Start low, titrate slowly; often improves with time' },
      { effect: 'Drowsiness/sedation', frequency: '10-25%', management: 'Take larger portion at bedtime; usually improves' },
      { effect: 'Nausea/vomiting', frequency: '10-20%', management: 'Take with food; often improves with continued use' },
      { effect: 'Ataxia', frequency: '5-15%', management: 'Reduce dose; may indicate toxicity' },
      { effect: 'Diplopia/blurred vision', frequency: '5-15%', management: 'Often dose-related; reduce dose if troublesome' },
      { effect: 'Rash (non-serious)', frequency: '5-10%', management: 'Evaluate severity; mild rash may resolve; discontinue if suspicious' }
    ],
    serious: [
      { effect: 'Stevens-Johnson syndrome / TEN', frequency: 'Rare (1:1000 to 1:10000)', management: 'STOP immediately. Hospitalize; supportive care.', warning: 'HLA-B*1502 (Asian populations) increases risk 10-fold. Consider screening.' },
      { effect: 'DRESS syndrome', frequency: 'Rare', management: 'Stop carbamazepine; systemic corticosteroids; supportive care', warning: 'Onset 2-6 weeks. May involve liver, kidneys, heart.' },
      { effect: 'Aplastic anemia', frequency: 'Very rare (2-5 per million)', management: 'Stop drug; hematology consultation; supportive care', warning: 'Monitor CBC; higher risk in first months' },
      { effect: 'Agranulocytosis', frequency: 'Very rare', management: 'Stop drug; G-CSF may be used; infection prevention', warning: 'Fatal if not recognized early' },
      { effect: 'Hyponatremia (SIADH)', frequency: '5-15%', management: 'Monitor sodium; fluid restriction; may need dose reduction or switch', warning: 'More common in elderly; can cause confusion, seizures' },
      { effect: 'Hepatotoxicity', frequency: 'Rare', management: 'Stop drug; supportive care', warning: 'Monitor LFTs if symptoms; can be fatal' },
      { effect: 'Cardiac conduction abnormalities', frequency: 'Rare', management: 'ECG monitoring; stop if significant abnormalities', warning: 'Risk in patients with pre-existing conduction disease' }
    ],
    rare: [
      'Lupus-like syndrome',
      'Pancreatitis',
      'Renal failure',
      'Thrombocytopenia',
      'Pulmonary hypersensitivity',
      'Aseptic meningitis',
      'Osteoporosis (chronic use)',
      'Alopecia'
    ]
  },
  interactions: {
    major: [
      { drug: 'Oral contraceptives', effect: 'Contraceptive failure', mechanism: 'CYP3A4 induction accelerates metabolism', management: 'Use high-dose OC (≥50 mcg ethinyl estradiol) or non-hormonal method (IUD)' },
      { drug: 'Warfarin', effect: 'Reduced anticoagulant effect', mechanism: 'CYP3A4/1A2 induction', management: 'Monitor INR closely; increase warfarin dose as needed' },
      { drug: 'Phenytoin', effect: 'Complex mutual interaction', mechanism: 'Both are inducers; affect each others metabolism', management: 'Monitor levels of both; adjust doses' },
      { drug: 'Valproate', effect: 'Increased CBZ-epoxide (toxic metabolite); decreased valproate levels', mechanism: 'Complex inhibition and induction', management: 'Monitor for toxicity (epoxide); monitor valproate levels' },
      { drug: 'MAO inhibitors', effect: 'Serotonin syndrome and hypertensive crisis risk', mechanism: 'Structural similarity to TCAs', management: 'CONTRAINDICATED. Wait 14 days after MAOI.' }
    ],
    moderate: [
      { drug: 'CYP3A4 inhibitors (erythromycin, ketoconazole, grapefruit)', effect: 'Increased carbamazepine levels', management: 'Monitor for toxicity; may need dose reduction' },
      { drug: 'CYP3A4 inducers (rifampicin, phenobarbital)', effect: 'Reduced carbamazepine levels', management: 'Monitor levels; may need dose increase' },
      { drug: 'Corticosteroids', effect: 'Reduced steroid efficacy', management: 'May need higher steroid doses' },
      { drug: 'Antipsychotics (haloperidol, risperidone)', effect: 'Reduced antipsychotic levels', management: 'Monitor response; increase antipsychotic dose if needed' },
      { drug: 'Lamotrigine', effect: 'Reduced lamotrigine levels', management: 'Higher lamotrigine doses needed with carbamazepine' },
      { drug: 'Tramadol', effect: 'Greatly reduced tramadol efficacy (CYP3A4 induction)', management: 'Alternative analgesic often needed' }
    ],
    food: [
      'Take with food to enhance absorption and reduce GI upset',
      'Grapefruit juice increases levels - avoid or use consistently',
      'Alcohol may enhance CNS depression'
    ]
  },
  pregnancy: {
    category: 'D',
    trimpiesterRisks: {
      first: 'Teratogenic: neural tube defects (0.5-1%), craniofacial abnormalities, cardiovascular defects, hypospadias. Risk of major malformations ~2-3x baseline.',
      second: 'Continued risk. Monitor fetal development.',
      third: 'Risk of neonatal hemorrhagic disease (vitamin K deficiency). Give maternal vitamin K before delivery.'
    },
    recommendation: 'Avoid if possible, especially polytherapy. If essential: use lowest effective dose, monotherapy preferred, preconception folic acid 4-5 mg/day, high-resolution fetal ultrasound, vitamin K before delivery. Consider alternative AED with better pregnancy profile (lamotrigine, levetiracetam).',
    lactation: {
      safe: true,
      notes: 'Excreted in breast milk. Infant receives ~5-10% of maternal dose. Generally compatible with breastfeeding. Monitor infant for sedation, poor feeding, jaundice. CBZ-epoxide also transferred.'
    }
  },
  monitoring: {
    baseline: [
      { test: 'Complete blood count', reason: 'Risk of aplastic anemia, agranulocytosis' },
      { test: 'Liver function tests', reason: 'Hepatic metabolism; risk of hepatotoxicity' },
      { test: 'Sodium', reason: 'Risk of hyponatremia' },
      { test: 'HLA-B*1502 (Asian patients)', reason: 'Identify SJS/TEN risk' },
      { test: 'ECG (if cardiac history)', reason: 'Risk of conduction abnormalities' }
    ],
    ongoing: [
      { test: 'Carbamazepine level', frequency: 'After titration complete; periodically when stable; with suspected toxicity', target: '4-12 mcg/mL' },
      { test: 'CBC', frequency: 'Weekly for first month, then monthly for 2-3 months, then every 6-12 months', target: 'No significant cytopenias; stop if WBC <3000 or ANC <1500' },
      { test: 'Sodium', frequency: 'Periodically; more often in elderly', target: '>130 mEq/L' },
      { test: 'LFTs', frequency: 'Periodically; if symptoms develop', target: 'Normal' },
      { test: 'Signs of toxicity (diplopia, ataxia, nystagmus)', frequency: 'Each visit', target: 'None present' }
    ],
    therapeutic: {
      range: '4-12 mcg/mL (17-51 micromol/L). Some patients require higher levels; balance efficacy against toxicity.',
      timing: 'Trough level preferred. Allow 2-4 weeks for auto-induction before expecting stable levels.'
    }
  },
  storage: {
    temperature: '15-30°C (59-86°F)',
    light: 'Protect from light and moisture',
    humidity: 'Store in tight container; moisture can affect tablet integrity',
    specialInstructions: 'Do not store in bathroom. Keep suspension away from heat.',
    shelfLife: '2-5 years depending on formulation; check expiry'
  },
  africanContext: {
    availability: 'Widely available across Africa. WHO Essential Medicine for epilepsy and trigeminal neuralgia. Present in most health facilities.',
    affordability: 'Affordable; generic formulations widely available. More expensive than phenytoin but still accessible.',
    supplyChainNotes: 'Generally stable supply. Extended-release formulations may be less available than immediate-release.',
    localManufacturing: 'Manufactured in Nigeria, South Africa, Egypt, Kenya, and other African countries.',
    alternatives: [
      'Phenytoin (alternative for seizures; cheaper)',
      'Valproate (broader spectrum; alternative for epilepsy and bipolar)',
      'Oxcarbazepine (better tolerated; limited availability)',
      'Gabapentin (for neuropathic pain; variable availability)'
    ],
    resistancePatterns: 'Not applicable (non-antimicrobial).',
    endemicConsiderations: 'Carbamazepine is essential for epilepsy and trigeminal neuralgia in Africa. Key considerations: (1) HLA-B*1502 testing unavailable in most settings - monitor closely for rash in first weeks; (2) CBC monitoring may be limited - educate patients about fever, sore throat, bleeding; (3) Drug interactions with HIV medications (efavirenz, nevirapine) and TB treatment (rifampicin) are common; (4) Auto-induction complicates dosing without level monitoring - titrate based on clinical response; (5) Trigeminal neuralgia can be debilitating - carbamazepine often life-changing when available.'
  },
  patientCounseling: {
    keyPoints: [
      'Take with food to improve absorption and reduce upset stomach',
      'Do not stop suddenly - can cause seizures',
      'Report any skin rash immediately - may need to stop medication',
      'Report fever, sore throat, mouth ulcers, or unusual bleeding (blood problems)',
      'Many drug interactions - tell all doctors what medications you take',
      'Use effective contraception - this medicine can cause birth defects'
    ],
    commonMisconceptions: [
      'Misconception: Can stop when seizure-free - Truth: Usually need to continue; stopping can cause seizures',
      'Misconception: All side effects mean allergic reaction - Truth: Some (dizziness, nausea) are common and improve; rash and blood problems are serious'
    ],
    adherenceTips: [
      'Take at same times daily with food',
      'Do not switch brands without consulting doctor',
      'Extended-release: swallow whole, do not crush',
      'Keep medication diary if helpful'
    ],
    warningSignsToReport: [
      'Any skin rash, blistering, or mouth sores',
      'Fever, sore throat, mouth ulcers (infection signs)',
      'Unusual bleeding or bruising',
      'Yellowing of skin or eyes, dark urine',
      'Severe nausea, vomiting, loss of appetite',
      'Confusion, severe drowsiness',
      'Double vision, unsteadiness (toxicity signs)'
    ]
  },
  clinicalPearls: [
    'Auto-induction occurs over 2-4 weeks - start low, recheck levels after 4 weeks, dose often needs to increase',
    'HLA-B*1502 screening recommended in Asian patients before starting - 10-fold increased SJS/TEN risk if positive',
    'Carbamazepine-10,11-epoxide is active metabolite - contributes to efficacy and toxicity; levels may help in unexplained toxicity',
    'Contraindicated in absence and myoclonic seizures - may worsen these seizure types',
    'First-line for trigeminal neuralgia - 70-80% of patients respond; start very low (100 mg) and titrate slowly',
    'Hyponatremia occurs in 5-15%, more common in elderly - monitor sodium, especially if confusion develops',
    'Major enzyme inducer - reduces levels of many drugs including contraceptives, warfarin, and other AEDs',
    'If cross-sensitivity to phenytoin suspected (~70% cross-reactivity), consider valproate or levetiracetam instead',
    'Extended-release formulations reduce peak-related side effects and allow once or twice daily dosing',
    'In African settings without level monitoring, titrate based on clinical response and early signs of toxicity (diplopia, ataxia)'
  ],
  references: [
    'WHO Model List of Essential Medicines 2023 (23rd list)',
    'ILAE Guidelines for Treatment of Epilepsy 2022',
    'AAN/AHS Guideline: Trigeminal Neuralgia 2019',
    'FDA Drug Safety Communication - Carbamazepine and HLA-B*1502',
    'Cruccu G et al. Eur J Neurol 2008 - AAN/EFNS guidelines on trigeminal neuralgia',
    'Perucca E. CNS Drugs 2005 - Carbamazepine pharmacology review',
    'British National Formulary (BNF) 2024',
    'Lexicomp Drug Information 2024'
  ],
  lastUpdated: '2025-01-20',
  medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
}
,
  // ═══════════════════════════════════════════════════════════════════════════
  // 41. HALOPERIDOL
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'haloperidol-041',
    genericName: 'Haloperidol',
    brandNames: ['Haldol', 'Serenace', 'Peridol', 'Dozic'],
    atcCode: 'N05AD01',
    therapeuticClass: 'Typical Antipsychotic (Butyrophenone)',
    pharmacology: {
      mechanism: 'Potent dopamine D2 receptor antagonist in the mesolimbic and mesocortical pathways, reducing positive psychotic symptoms. Also blocks alpha-1 adrenergic receptors (causing orthostatic hypotension) and has weak anticholinergic activity. High D2 affinity contributes to efficacy but also to extrapyramidal side effects.',
      pharmacokinetics: {
        absorption: 'Oral bioavailability 60-70% due to first-pass metabolism; IM absorption rapid with peak at 20-40 minutes; decanoate depot reaches peak in 6 days',
        distribution: 'Vd 8-18 L/kg; highly lipophilic with extensive tissue distribution; 92% protein bound; crosses blood-brain barrier and placenta',
        metabolism: 'Extensive hepatic metabolism via CYP3A4 (major) and CYP2D6; glucuronidation also significant; active metabolite reduced haloperidol has lower activity',
        elimination: '60% excreted in urine (1% unchanged), 15% in feces via biliary excretion',
        halfLife: 'Oral: 14-26 hours (up to 36 hours); IM: 21 hours; Decanoate: 3 weeks; prolonged in elderly and hepatic impairment'
      },
      pharmacodynamics: 'Antipsychotic effect correlates with D2 receptor occupancy of 65-80%; >80% occupancy associated with extrapyramidal symptoms. Clinical effect for acute agitation within 30-60 minutes IM; full antipsychotic effect may take days to weeks.'
    },
    indications: [
      { condition: 'Acute psychosis and schizophrenia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Effective for positive symptoms; less effective for negative symptoms' },
      { condition: 'Acute agitation and behavioral emergencies', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'IM formulation for rapid control; often combined with benzodiazepines' },
      { condition: 'Delirium', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Low-dose haloperidol preferred; monitor QTc; avoid in alcohol withdrawal delirium' },
      { condition: 'Tourette syndrome', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Effective for tics; use lowest effective dose' },
      { condition: 'Nausea and vomiting (intractable)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Low doses effective; useful in palliative care' },
      { condition: 'Bipolar disorder (acute mania)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Adjunctive therapy; mood stabilizer preferred for maintenance' }
    ],
    dosing: {
      adults: {
        standard: 'Oral: 0.5-5 mg 2-3 times daily; usual range 2-20 mg/day',
        indicationSpecific: [
          { indication: 'Acute psychosis', dose: 'Oral: 2-5 mg q4-8h; IM: 2-5 mg, may repeat q1h PRN (max 20 mg/day)' },
          { indication: 'Acute agitation', dose: 'IM: 2-5 mg, may repeat q30-60min; often combined with lorazepam 2 mg IM' },
          { indication: 'Delirium', dose: '0.5-2 mg IV/IM q4-6h PRN; start low in elderly (0.25-0.5 mg)' },
          { indication: 'Maintenance schizophrenia', dose: 'Oral: 2-20 mg/day in divided doses; Decanoate: 50-200 mg IM q4 weeks' },
          { indication: 'Nausea (palliative)', dose: '0.5-2 mg oral/SC q8-12h' }
        ],
        maxDose: '100 mg/day oral (rarely needed); 20 mg/day IM for acute agitation',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment; monitor for accumulation',
          severe: 'Start with lower doses; haloperidol minimally renally excreted',
          dialysis: 'Not dialyzable; no supplemental dose needed'
        },
        hepaticImpairment: 'Reduce dose by 50% in moderate-severe impairment; hepatic metabolism significantly reduced'
      },
      pediatric: {
        standard: 'Children 3-12 years: 0.01-0.03 mg/kg/day divided BID-TID; adolescents: adult dosing',
        weightBased: true,
        ageSpecific: [
          { age: 'Children 3-12 years (psychosis)', dose: '0.05-0.15 mg/kg/day in 2-3 divided doses' },
          { age: 'Children 3-12 years (Tourette)', dose: '0.05-0.075 mg/kg/day in 2-3 divided doses' },
          { age: 'Adolescents 13-17 years', dose: 'Start 0.5-2 mg 2-3 times daily; increase gradually' }
        ],
        maxDose: 'Children: 0.15 mg/kg/day or 6 mg/day; adolescents: similar to adults',
        neonatal: 'Not recommended in neonates; limited data; risk of extrapyramidal effects'
      },
      elderly: 'Start 0.25-0.5 mg once or twice daily; increase slowly; high risk of extrapyramidal symptoms, falls, and over-sedation; BLACK BOX WARNING: increased mortality in elderly with dementia-related psychosis',
      routes: ['Oral', 'IM', 'IV', 'Subcutaneous'],
      administration: [
        'Oral: May give with or without food; liquid concentrate can be diluted in water or juice (not tea or coffee)',
        'IM: Deep gluteal injection; rotate sites for decanoate',
        'IV: Reserved for severe agitation; dilute and give slowly over 5 minutes; requires cardiac monitoring',
        'Decanoate: IM only (gluteal); never give IV'
      ]
    },
    contraindications: {
      absolute: [
        'Known hypersensitivity to haloperidol or butyrophenones',
        'Severe CNS depression or comatose states',
        "Parkinson's disease",
        'Dementia with Lewy bodies',
        'QTc prolongation >500 ms or history of torsades de pointes',
        'Uncorrected hypokalemia or hypomagnesemia'
      ],
      relative: [
        'History of QTc prolongation or cardiac arrhythmias',
        'Concurrent QT-prolonging drugs',
        'Hepatic impairment',
        'Seizure disorder (lowers seizure threshold)',
        'Elderly with dementia (increased mortality)',
        'Thyrotoxicosis',
        'Pheochromocytoma'
      ],
      precautions: [
        'Monitor ECG at baseline and during treatment especially with IV use',
        'Risk of neuroleptic malignant syndrome - discontinue if suspected',
        'Risk of tardive dyskinesia with long-term use',
        'May mask signs of overdose of other drugs or intestinal obstruction',
        'Avoid abrupt discontinuation after prolonged use',
        'May impair temperature regulation - caution in extreme heat'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Extrapyramidal symptoms (dystonia, akathisia, parkinsonism)', frequency: '25-60%', management: 'Dose reduction; add anticholinergic (benztropine); consider switching to atypical antipsychotic' },
        { effect: 'Sedation and drowsiness', frequency: '10-30%', management: 'Dose at bedtime; usually improves with time; dose reduction if severe' },
        { effect: 'Orthostatic hypotension', frequency: '5-15%', management: 'Rise slowly; adequate hydration; dose reduction' },
        { effect: 'Hyperprolactinemia', frequency: '30-70%', management: 'Symptoms include galactorrhea, amenorrhea, gynecomastia; consider dose reduction or switch' },
        { effect: 'Weight gain', frequency: '10-20%', management: 'Less than atypical antipsychotics; dietary counseling; exercise' }
      ],
      serious: [
        { effect: 'Neuroleptic malignant syndrome', frequency: '<1%', management: 'STOP drug immediately; supportive care; dantrolene; bromocriptine; ICU admission', warning: 'Life-threatening: fever, rigidity, altered mental status, autonomic instability' },
        { effect: 'QTc prolongation and torsades de pointes', frequency: '1-5%', management: 'ECG monitoring; correct electrolytes; discontinue if QTc >500 ms', warning: 'Risk increased with IV use, high doses, and concurrent QT-prolonging drugs' },
        { effect: 'Tardive dyskinesia', frequency: '5-30% with chronic use', management: 'AIMS assessment regularly; reduce/discontinue if possible; valbenazine for treatment', warning: 'May be irreversible; risk increases with duration of treatment' },
        { effect: 'Seizures', frequency: '<1%', management: 'Use with caution in seizure disorders; standard seizure management' }
      ],
      rare: [
        'Agranulocytosis and leukopenia',
        'Hepatotoxicity',
        'Laryngospasm',
        'Bronchospasm',
        'Priapism',
        'Sudden cardiac death'
      ]
    },
    interactions: {
      major: [
        { drug: 'QTc-prolonging drugs (amiodarone, sotalol, quinidine, macrolides)', effect: 'Additive QTc prolongation with risk of torsades de pointes', mechanism: 'Pharmacodynamic - cardiac ion channel effects', management: 'Avoid combination if possible; if necessary, monitor ECG closely; correct electrolytes' },
        { drug: 'Dopamine agonists (levodopa, pramipexole)', effect: 'Mutual antagonism - reduced efficacy of both drugs', mechanism: 'Pharmacodynamic - opposing dopamine receptor effects', management: 'Avoid combination; use alternative antipsychotic if needed in Parkinson psychosis (quetiapine, clozapine)' },
        { drug: 'CNS depressants (opioids, benzodiazepines, alcohol)', effect: 'Enhanced sedation, respiratory depression, hypotension', mechanism: 'Additive CNS depression', management: 'Reduce doses of both; monitor closely; beneficial combination for acute agitation' },
        { drug: 'CYP3A4 inhibitors (ketoconazole, ritonavir, clarithromycin)', effect: 'Increased haloperidol levels by 50-100%', mechanism: 'Inhibition of hepatic metabolism', management: 'Reduce haloperidol dose; monitor for toxicity' }
      ],
      moderate: [
        { drug: 'CYP3A4 inducers (carbamazepine, phenytoin, rifampicin)', effect: 'Reduced haloperidol levels by 50-70%', management: 'May need to increase haloperidol dose; monitor efficacy' },
        { drug: 'Lithium', effect: 'Rare reports of encephalopathy, EPS, and NMS', management: 'Monitor closely; use lowest effective doses of both' },
        { drug: 'Antihypertensives', effect: 'Enhanced hypotensive effects', management: 'Monitor blood pressure; adjust antihypertensive dose if needed' },
        { drug: 'Anticholinergics', effect: 'Additive anticholinergic effects', management: 'Monitor for constipation, urinary retention, cognitive impairment' }
      ],
      food: [
        'Grapefruit juice may increase haloperidol levels (CYP3A4 inhibition)',
        'Caffeine may reduce sedative effects'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Limited data; possible association with limb malformations at high doses in animal studies; use only if clearly needed',
        second: 'Third-trimester exposure associated with extrapyramidal symptoms and withdrawal in neonates',
        third: 'Risk of neonatal extrapyramidal symptoms, sedation, respiratory distress, feeding difficulties; symptoms usually self-limiting'
      },
      recommendation: 'Use only if benefits outweigh risks; prefer agents with more safety data in pregnancy; monitor neonate for extrapyramidal symptoms and withdrawal',
      lactation: { safe: false, notes: 'Excreted in breast milk; may cause sedation and developmental effects in infant; avoid breastfeeding or discontinue drug' }
    },
    monitoring: {
      baseline: [
        { test: 'ECG', reason: 'Assess QTc interval before starting therapy' },
        { test: 'Electrolytes (K+, Mg2+)', reason: 'Hypokalemia/hypomagnesemia increase QTc prolongation risk' },
        { test: 'Liver function tests', reason: 'Baseline for monitoring hepatotoxicity' },
        { test: 'Fasting glucose and lipids', reason: 'Metabolic monitoring' },
        { test: 'AIMS assessment', reason: 'Baseline for tardive dyskinesia monitoring' }
      ],
      ongoing: [
        { test: 'ECG', frequency: 'After dose changes, then periodically; more frequently with IV use', target: 'QTc <500 ms' },
        { test: 'AIMS assessment', frequency: 'Every 6-12 months', target: 'Early detection of tardive dyskinesia' },
        { test: 'Fasting glucose and lipids', frequency: 'Annually', target: 'Metabolic monitoring' },
        { test: 'Prolactin (if symptomatic)', frequency: 'As needed for galactorrhea, amenorrhea, gynecomastia' },
        { test: 'Extrapyramidal symptom assessment', frequency: 'Each visit' }
      ],
      therapeutic: { range: '5-20 ng/mL (therapeutic drug monitoring rarely needed)', timing: 'Trough level; steady state in 5-7 days' }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F); protect from excessive heat',
      light: 'Protect from light; slight yellowing of oral solution does not affect potency',
      humidity: 'Store in dry place',
      specialInstructions: 'Decanoate is viscous - warm to room temperature before injection',
      shelfLife: 'Tablets/solution: 3 years; Injection: 2-3 years; check manufacturer specifications'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; available throughout sub-Saharan Africa; both oral and injectable forms accessible',
      affordability: 'VERY AFFORDABLE - Generic haloperidol extremely low cost; approximately $0.50-2 USD per month of treatment; one of most affordable antipsychotics',
      supplyChainNotes: 'Stable supply in most countries; tablet form more consistently available than injectable; decanoate may have limited availability',
      localManufacturing: 'Manufactured locally in Nigeria, South Africa, Kenya, Egypt, Morocco; multiple generic manufacturers',
      alternatives: ['Chlorpromazine', 'Fluphenazine', 'Risperidone'],
      resistancePatterns: 'Not applicable - antipsychotic',
      endemicConsiderations: 'Important for managing acute psychosis in resource-limited settings; useful for acute agitation in infectious disease presentations (meningitis, cerebral malaria); caution with QTc in patients on antimalarials'
    },
    patientCounseling: {
      keyPoints: [
        'Take medication exactly as prescribed; do not stop suddenly',
        'May cause drowsiness - avoid driving or operating machinery until effects known',
        'Rise slowly from sitting/lying to prevent dizziness',
        'Report any involuntary movements of face, tongue, or limbs immediately'
      ],
      commonMisconceptions: [
        'Misconception: "This medication is only for crazy people" - Truth: Used for various conditions including severe nausea and agitation',
        'Misconception: "I can stop when I feel better" - Truth: Stopping suddenly can cause withdrawal and relapse'
      ],
      adherenceTips: [
        'Use pill organizers or alarms to remember doses',
        'Long-acting injection (decanoate) available if daily pills are difficult',
        'Discuss side effects openly with your doctor - most can be managed'
      ],
      warningSignsToReport: [
        'Muscle stiffness, fever, confusion (may indicate NMS - seek emergency care)',
        'Involuntary movements of tongue, face, or body',
        'Fainting or irregular heartbeat',
        'Severe restlessness or inability to sit still',
        'Difficulty swallowing or breathing'
      ]
    },
    clinicalPearls: [
      'Haloperidol 5 mg IM + lorazepam 2 mg IM is a classic combination for acute agitation; reduces dose of each needed',
      'IV haloperidol has paradoxically higher QTc prolongation risk than IM - always monitor ECG with IV use',
      'For delirium, use lowest effective dose (0.5-2 mg); high doses may worsen confusion in elderly',
      'Akathisia (restlessness) is often misdiagnosed as worsening psychosis - leading to inappropriate dose increases',
      'In African settings, haloperidol remains first-line due to cost and availability; reserve atypical antipsychotics for EPS intolerance'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'Stahl SM. Stahls Essential Psychopharmacology. 5th ed. Cambridge University Press; 2021',
      'American Psychiatric Association Practice Guidelines for Schizophrenia 2020',
      'NICE Guidelines: Psychosis and Schizophrenia in Adults 2014 (updated 2024)',
      'Cochrane Review: Haloperidol for Schizophrenia 2017'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 42. AMITRIPTYLINE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'amitriptyline-042',
    genericName: 'Amitriptyline',
    brandNames: ['Elavil', 'Endep', 'Tryptomer', 'Laroxyl'],
    atcCode: 'N06AA09',
    therapeuticClass: 'Tricyclic Antidepressant (TCA)',
    pharmacology: {
      mechanism: 'Inhibits reuptake of serotonin and norepinephrine at presynaptic neurons, increasing monoamine availability in synaptic cleft. Also blocks muscarinic acetylcholine receptors (anticholinergic effects), histamine H1 receptors (sedation, weight gain), and alpha-1 adrenergic receptors (orthostatic hypotension). Sodium channel blockade contributes to cardiac toxicity in overdose.',
      pharmacokinetics: {
        absorption: 'Well absorbed orally; bioavailability 30-60% due to first-pass metabolism; peak plasma levels 2-6 hours',
        distribution: 'Vd 6-36 L/kg; highly lipophilic with extensive tissue distribution; 95% protein bound; crosses placenta and blood-brain barrier',
        metabolism: 'Extensive hepatic metabolism via CYP2D6, CYP2C19, CYP3A4; active metabolite nortriptyline (also a TCA) contributes to therapeutic effect',
        elimination: 'Renal excretion of metabolites; <5% excreted unchanged; enterohepatic recirculation',
        halfLife: '10-28 hours (parent); nortriptyline half-life 16-80 hours; steady state in 1-2 weeks'
      },
      pharmacodynamics: 'Antidepressant effect develops over 2-4 weeks; analgesic effect for neuropathic pain may occur earlier (1-2 weeks). Effect on serotonin > norepinephrine reuptake inhibition.'
    },
    indications: [
      { condition: 'Major depressive disorder', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Effective but not first-line due to side effects and overdose risk; SSRIs preferred' },
      { condition: 'Neuropathic pain', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line for diabetic neuropathy, postherpetic neuralgia; effective at lower doses than antidepressant' },
      { condition: 'Migraine prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Low-dose effective; 10-25 mg at bedtime; may take 4-6 weeks for effect' },
      { condition: 'Tension-type headache prophylaxis', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Similar dosing to migraine prophylaxis' },
      { condition: 'Fibromyalgia', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Low doses improve pain and sleep; alternatives may be better tolerated' },
      { condition: 'Insomnia (with depression or pain)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Sedating properties beneficial; not recommended for primary insomnia' }
    ],
    dosing: {
      adults: {
        standard: 'Depression: Start 25-50 mg at bedtime, increase by 25-50 mg weekly to 150-300 mg/day; Pain: 10-25 mg at bedtime, increase to 75-150 mg/day',
        indicationSpecific: [
          { indication: 'Depression', dose: 'Start 25-50 mg at bedtime; titrate to 150-300 mg/day in divided doses or single bedtime dose' },
          { indication: 'Neuropathic pain', dose: 'Start 10-25 mg at bedtime; increase by 10-25 mg weekly; usual effective dose 25-75 mg; max 150 mg' },
          { indication: 'Migraine prophylaxis', dose: 'Start 10 mg at bedtime; increase to 25-50 mg; max 150 mg' },
          { indication: 'Fibromyalgia', dose: '10-50 mg at bedtime' }
        ],
        maxDose: '300 mg/day for depression (inpatient); 150 mg/day outpatient; lower for pain indications',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment; metabolites may accumulate',
          severe: 'Use with caution; start low; metabolites renally excreted',
          dialysis: 'Not removed by hemodialysis; no supplemental dose'
        },
        hepaticImpairment: 'Reduce dose significantly; start 10-25 mg; metabolism substantially impaired'
      },
      pediatric: {
        standard: 'Not recommended for depression in children; limited use for chronic pain and nocturnal enuresis',
        weightBased: true,
        ageSpecific: [
          { age: 'Children 6-12 years (enuresis)', dose: '10-25 mg at bedtime; max 50 mg' },
          { age: 'Adolescents (depression)', dose: 'If used: start 25-50 mg/day; max 100 mg/day; NOT first-line' },
          { age: 'Adolescents (pain)', dose: '10-25 mg at bedtime; max 75 mg' }
        ],
        maxDose: 'Children: 50 mg/day; Adolescents: 100 mg/day',
        neonatal: 'Contraindicated in neonates'
      },
      elderly: 'Start very low: 10 mg at bedtime; increase slowly; max 75-100 mg/day; high anticholinergic burden increases fall risk and cognitive impairment; consider alternatives',
      routes: ['Oral'],
      administration: [
        'Usually given as single dose at bedtime due to sedation',
        'May divide dose if daytime sedation problematic at higher doses',
        'Take with food to reduce GI upset',
        'Do not stop abruptly - taper over 2-4 weeks to avoid discontinuation syndrome'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to amitriptyline or other TCAs',
        'Recent myocardial infarction (within 2 weeks)',
        'Concurrent or recent (within 14 days) MAOI therapy',
        'Acute recovery phase post-MI',
        'Uncontrolled narrow-angle glaucoma'
      ],
      relative: [
        'Cardiac conduction disorders (bundle branch block, QTc prolongation)',
        'History of seizures (lowers seizure threshold)',
        'Urinary retention or prostatic hypertrophy',
        'Severe constipation or ileus',
        'Hyperthyroidism',
        'Elderly patients (high anticholinergic burden)',
        'History of bipolar disorder (may trigger mania)'
      ],
      precautions: [
        'BLACK BOX WARNING: Increased suicidality in children, adolescents, and young adults',
        'ECG recommended before starting in patients >40 years or with cardiac history',
        'Avoid in patients at high suicide risk due to lethality in overdose',
        'Gradual taper required to avoid discontinuation symptoms',
        'May impair driving and cognitive function',
        'Avoid alcohol'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Sedation and drowsiness', frequency: '30-50%', management: 'Dose at bedtime; tolerance usually develops; may reduce dose' },
        { effect: 'Dry mouth', frequency: '30-50%', management: 'Sugar-free gum/candy; frequent sips of water; saliva substitutes' },
        { effect: 'Constipation', frequency: '20-40%', management: 'Increase fiber and fluids; stool softeners; avoid in severe cases' },
        { effect: 'Weight gain', frequency: '20-40%', management: 'Dietary counseling; may be significant (5-10 kg); consider alternative' },
        { effect: 'Blurred vision', frequency: '15-25%', management: 'Usually transient; avoid driving if significant' },
        { effect: 'Urinary retention', frequency: '10-20%', management: 'More common in elderly and men with BPH; may require discontinuation' },
        { effect: 'Orthostatic hypotension', frequency: '10-20%', management: 'Rise slowly; adequate hydration; dose reduction' }
      ],
      serious: [
        { effect: 'Cardiac arrhythmias and conduction defects', frequency: '1-5%', management: 'ECG monitoring; avoid in pre-existing conduction disease; discontinue if QTc prolongation', warning: 'Potentially fatal in overdose; sodium channel blockade causes wide QRS' },
        { effect: 'Seizures', frequency: '<1%', management: 'Use with caution in epilepsy; dose-related risk' },
        { effect: 'Serotonin syndrome (with serotonergic drugs)', frequency: 'Rare', management: 'Discontinue offending agents; supportive care; cyproheptadine', warning: 'Risk with concurrent SSRIs, SNRIs, tramadol, triptans' },
        { effect: 'Suicidal ideation', frequency: 'Increased in young adults', management: 'Close monitoring first 1-2 months; weekly visits initially', warning: 'BLACK BOX WARNING: Monitor closely for emergence of suicidality' }
      ],
      rare: [
        'Agranulocytosis',
        'Hepatotoxicity',
        'Hyponatremia (SIADH)',
        'Paralytic ileus',
        'Angle-closure glaucoma precipitation'
      ]
    },
    interactions: {
      major: [
        { drug: 'MAOIs', effect: 'Potentially fatal serotonin syndrome and hypertensive crisis', mechanism: 'Combined monoamine potentiation', management: 'CONTRAINDICATED; 14-day washout required between drugs' },
        { drug: 'QTc-prolonging drugs', effect: 'Additive QTc prolongation and arrhythmia risk', mechanism: 'Cardiac ion channel effects', management: 'Avoid combination; ECG monitoring if unavoidable' },
        { drug: 'Anticholinergic drugs', effect: 'Additive anticholinergic toxicity', mechanism: 'Pharmacodynamic synergy', management: 'Avoid combination especially in elderly; monitor for confusion, urinary retention, ileus' },
        { drug: 'CNS depressants (alcohol, opioids, benzodiazepines)', effect: 'Enhanced sedation and respiratory depression', mechanism: 'Additive CNS depression', management: 'Avoid alcohol; reduce doses if combination necessary' }
      ],
      moderate: [
        { drug: 'CYP2D6 inhibitors (fluoxetine, paroxetine, bupropion)', effect: 'Increased amitriptyline levels 2-4 fold', management: 'Reduce amitriptyline dose; monitor for toxicity' },
        { drug: 'Sympathomimetics (epinephrine, norepinephrine)', effect: 'Enhanced pressor response', management: 'Avoid direct-acting sympathomimetics if possible' },
        { drug: 'Cimetidine', effect: 'Increased TCA levels', management: 'Use alternative H2 blocker (ranitidine) or PPI' },
        { drug: 'Thyroid hormones', effect: 'Enhanced effects of both; increased arrhythmia risk', management: 'Monitor closely; adjust doses as needed' }
      ],
      food: [
        'Grapefruit juice may increase levels (CYP3A4 inhibition)',
        'Alcohol enhances CNS depression - avoid',
        'High-fiber diet may reduce absorption'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Limited human data; animal studies show some teratogenicity at high doses; generally compatible but not first-line',
        second: 'Crosses placenta; consider risk-benefit; SSRIs generally preferred',
        third: 'Neonatal withdrawal syndrome: irritability, feeding difficulties, respiratory distress; taper before delivery if possible'
      },
      recommendation: 'Use only if benefits outweigh risks; SSRIs generally preferred for depression in pregnancy; for neuropathic pain, consider physical therapy first',
      lactation: { safe: false, notes: 'Excreted in breast milk; active metabolite nortriptyline present; may cause sedation in infant; avoid or use with caution' }
    },
    monitoring: {
      baseline: [
        { test: 'ECG', reason: 'Assess QTc and conduction intervals in patients >40 or with cardiac risk' },
        { test: 'Blood pressure (orthostatic)', reason: 'Baseline for hypotension monitoring' },
        { test: 'Weight', reason: 'Monitor for weight gain' }
      ],
      ongoing: [
        { test: 'Mood and suicidality assessment', frequency: 'Weekly for first month, then every 2-4 weeks', target: 'Early detection of worsening' },
        { test: 'Weight', frequency: 'Monthly for first 3 months, then quarterly', target: 'Monitor for significant gain' },
        { test: 'Anticholinergic symptom assessment', frequency: 'Each visit', target: 'Constipation, urinary retention, dry mouth, confusion' },
        { test: 'ECG', frequency: 'After dose changes or if symptomatic', target: 'QTc <500 ms; QRS stable' }
      ],
      therapeutic: { range: 'Combined amitriptyline + nortriptyline: 100-250 ng/mL for depression', timing: 'Trough level 12 hours post-dose; steady state 1-2 weeks; TDM optional' }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F)',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Keep out of reach of children - highly toxic in overdose',
      shelfLife: '3-5 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; available throughout Africa; one of most accessible antidepressants',
      affordability: 'VERY AFFORDABLE - Generic widely available; approximately $1-3 USD per month; most affordable option for neuropathic pain',
      supplyChainNotes: 'Stable supply across most countries; tablet formulations widely available; oral solution less common',
      localManufacturing: 'Manufactured in Nigeria, South Africa, Kenya, Egypt; multiple generic sources',
      alternatives: ['Nortriptyline', 'Imipramine', 'Fluoxetine (for depression)'],
      resistancePatterns: 'Not applicable - antidepressant',
      endemicConsiderations: 'Important for HIV-associated neuropathy; effective for diabetic neuropathy which is common; consider for post-herpetic neuralgia; valuable in palliative care settings'
    },
    patientCounseling: {
      keyPoints: [
        'Takes 2-4 weeks for full antidepressant effect; pain relief may occur sooner',
        'Take at bedtime due to sedating effects; drowsiness usually improves over time',
        'Do not stop suddenly - must taper gradually to avoid withdrawal',
        'Avoid alcohol completely - dangerous interaction'
      ],
      commonMisconceptions: [
        'Misconception: "I can stop when I feel better" - Truth: Treatment should continue as directed; discuss any changes with doctor',
        'Misconception: "This drug is addictive" - Truth: Not addictive, but stopping suddenly causes withdrawal symptoms'
      ],
      adherenceTips: [
        'Take at same time each night; bedtime dosing helps with sleep and reduces daytime drowsiness',
        'Keep medication in a consistent, visible location as reminder',
        'Use sugar-free gum or candy for dry mouth rather than sugary alternatives'
      ],
      warningSignsToReport: [
        'New or worsening depression, suicidal thoughts, or unusual behavior changes',
        'Fast or irregular heartbeat, fainting, chest pain',
        'Severe constipation or inability to urinate',
        'High fever with muscle stiffness and confusion',
        'Unusual bleeding or bruising'
      ]
    },
    clinicalPearls: [
      'For neuropathic pain, low doses (10-75 mg) are effective - do not need antidepressant doses',
      'NNT for neuropathic pain is approximately 3.6 - one of the most effective options available',
      'Weight gain can be substantial (10+ kg) - warn patients and monitor; may limit long-term adherence',
      'In overdose, QRS widening >100 ms predicts seizures; >160 ms predicts arrhythmias - use sodium bicarbonate',
      'In resource-limited settings, amitriptyline is often the only available option for neuropathic pain - highly valuable despite side effect profile'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'NICE Guidelines: Neuropathic Pain in Adults 2020',
      'Cochrane Review: Amitriptyline for Neuropathic Pain 2015',
      'American Headache Society Guidelines: Migraine Prevention 2021',
      'Finnerup NB, et al. Pharmacotherapy for neuropathic pain in adults. Lancet Neurol 2015'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 43. OMEPRAZOLE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'omeprazole-043',
    genericName: 'Omeprazole',
    brandNames: ['Prilosec', 'Losec', 'Omez', 'Gastrozole'],
    atcCode: 'A02BC01',
    therapeuticClass: 'Proton Pump Inhibitor (PPI)',
    pharmacology: {
      mechanism: 'Irreversibly inhibits gastric H+/K+-ATPase (proton pump) on parietal cell secretory surface, blocking the final step of acid production. As a prodrug, omeprazole requires activation in the acidic canaliculi of parietal cells. Suppresses both basal and stimulated gastric acid secretion regardless of stimulus.',
      pharmacokinetics: {
        absorption: 'Acid-labile; enteric-coated formulations protect from gastric degradation; bioavailability 30-40% (increases to 65% with repeated dosing); peak 0.5-3.5 hours',
        distribution: 'Vd 0.13-0.35 L/kg; 95% protein bound; minimal CNS penetration; concentrated in parietal cells',
        metabolism: 'Extensive hepatic metabolism via CYP2C19 (major) and CYP3A4; genetic polymorphism in CYP2C19 affects metabolism significantly',
        elimination: '80% renally excreted as metabolites; 20% fecal; negligible unchanged drug in urine',
        halfLife: '0.5-1 hour (but duration of effect 24+ hours due to irreversible enzyme inhibition); prolonged in hepatic impairment'
      },
      pharmacodynamics: 'Onset of acid suppression within 1 hour; maximal effect in 2-4 days of daily dosing; gastric pH maintained >4 for 15-21 hours/day. After stopping, acid secretion returns to baseline in 3-5 days as new proton pumps are synthesized.'
    },
    indications: [
      { condition: 'Gastroesophageal reflux disease (GERD)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Most effective medical therapy; 8 weeks for healing' },
      { condition: 'Peptic ulcer disease (H. pylori-associated)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Part of triple/quadruple therapy for H. pylori eradication' },
      { condition: 'NSAID-induced ulcer prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'For patients requiring chronic NSAID therapy with GI risk factors' },
      { condition: 'Zollinger-Ellison syndrome', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'High doses often required; may need lifelong therapy' },
      { condition: 'Stress ulcer prophylaxis (ICU)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'For mechanically ventilated or high-risk ICU patients' },
      { condition: 'Dyspepsia/functional dyspepsia', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Empiric trial reasonable; less effective than for GERD' }
    ],
    dosing: {
      adults: {
        standard: 'GERD/ulcer healing: 20 mg once daily; maintenance: 20 mg daily; H. pylori eradication: 20 mg twice daily',
        indicationSpecific: [
          { indication: 'GERD (erosive)', dose: '20 mg once daily for 4-8 weeks; may increase to 40 mg if inadequate response' },
          { indication: 'GERD maintenance', dose: '20 mg once daily long-term' },
          { indication: 'Duodenal ulcer', dose: '20 mg once daily for 4 weeks' },
          { indication: 'Gastric ulcer', dose: '40 mg once daily for 4-8 weeks' },
          { indication: 'H. pylori eradication', dose: '20 mg twice daily with antibiotics for 10-14 days' },
          { indication: 'NSAID prophylaxis', dose: '20 mg once daily' },
          { indication: 'Zollinger-Ellison', dose: 'Start 60 mg once daily; may need up to 120 mg three times daily' }
        ],
        maxDose: '360 mg/day (Zollinger-Ellison); 40 mg/day for most indications',
        renalImpairment: {
          mild: 'No adjustment required',
          moderate: 'No adjustment required',
          severe: 'No adjustment required; not significantly renally eliminated',
          dialysis: 'No adjustment; not removed by hemodialysis'
        },
        hepaticImpairment: 'Reduce dose to 10-20 mg daily in severe impairment; metabolism significantly reduced'
      },
      pediatric: {
        standard: 'Children ≥1 year: 0.7-1.4 mg/kg once daily (max 20 mg); adolescents: adult dosing',
        weightBased: true,
        ageSpecific: [
          { age: 'Infants 1-12 months', dose: '0.7 mg/kg once daily (limited data; use only if necessary)' },
          { age: 'Children 1-4 years (10-20 kg)', dose: '10 mg once daily; may increase to 20 mg' },
          { age: 'Children 5-12 years (>20 kg)', dose: '20 mg once daily' },
          { age: 'Adolescents', dose: 'Adult dosing' }
        ],
        maxDose: '1.4 mg/kg/day or 40 mg/day',
        neonatal: 'Safety not established; limited data; use only if clearly needed at 0.5-1 mg/kg/day'
      },
      elderly: 'No dosage adjustment required; caution with long-term use due to fracture risk and B12/magnesium deficiency',
      routes: ['Oral', 'IV'],
      administration: [
        'Oral: Take 30-60 minutes before meals for optimal absorption; morning dosing preferred',
        'Capsules should be swallowed whole; do not crush or chew enteric-coated granules',
        'For patients unable to swallow: open capsule and mix granules with applesauce; do not crush granules',
        'IV: Infuse over 20-30 minutes; use within 4 hours of reconstitution'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to omeprazole or other PPIs (including substituted benzimidazoles)',
        'Concurrent rilpivirine (HIV medication) - reduced rilpivirine absorption'
      ],
      relative: [
        'Hepatic impairment (dose reduction needed)',
        'Osteoporosis or risk factors for fractures',
        'Concurrent clopidogrel (controversial interaction)',
        'Hypomagnesemia'
      ],
      precautions: [
        'Long-term use associated with increased fracture risk, hypomagnesemia, B12 deficiency',
        'May mask symptoms of gastric malignancy - endoscopy recommended for alarm symptoms',
        'Risk of C. difficile infection with prolonged use',
        'May interfere with absorption of drugs requiring acidic environment',
        'CYP2C19 poor metabolizers have higher drug exposure',
        'Consider periodic reassessment of need for continued therapy'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Headache', frequency: '7-8%', management: 'Usually mild and transient; simple analgesics if needed' },
        { effect: 'Diarrhea', frequency: '3-5%', management: 'Usually self-limiting; consider C. diff if persistent' },
        { effect: 'Abdominal pain', frequency: '3-5%', management: 'Usually transient; take with food if bothersome' },
        { effect: 'Nausea', frequency: '2-4%', management: 'Take with food; usually improves over time' },
        { effect: 'Flatulence', frequency: '2-3%', management: 'Dietary modification; usually mild' }
      ],
      serious: [
        { effect: 'C. difficile-associated diarrhea', frequency: '1-2% with prolonged use', management: 'Stop PPI if possible; treat C. diff appropriately', warning: 'Risk increases with duration of PPI use' },
        { effect: 'Hypomagnesemia', frequency: '1-2% long-term', management: 'Monitor magnesium periodically; supplement if low; may need to discontinue PPI', warning: 'May cause tetany, arrhythmias, seizures if severe' },
        { effect: 'Bone fractures (hip, wrist, spine)', frequency: 'Increased risk with >1 year use', management: 'Use lowest effective dose; shortest duration; calcium/vitamin D supplementation', warning: 'FDA warning for long-term use' },
        { effect: 'Vitamin B12 deficiency', frequency: '10-25% with long-term use', management: 'Periodic B12 levels; supplement if deficient' }
      ],
      rare: [
        'Acute interstitial nephritis',
        'Cutaneous lupus erythematosus',
        'Fundic gland polyps (benign)',
        'Severe skin reactions (SJS, TEN)',
        'Anaphylaxis'
      ]
    },
    interactions: {
      major: [
        { drug: 'Rilpivirine', effect: 'Reduced rilpivirine absorption and potential HIV treatment failure', mechanism: 'Requires acidic pH for absorption', management: 'CONTRAINDICATED - use H2 blocker if acid suppression needed' },
        { drug: 'Atazanavir, nelfinavir', effect: 'Reduced protease inhibitor absorption', mechanism: 'Requires acidic pH for absorption', management: 'Avoid combination or use with caution; monitor HIV viral load' },
        { drug: 'Clopidogrel', effect: 'Reduced antiplatelet effect (controversial)', mechanism: 'CYP2C19 inhibition reduces conversion to active metabolite', management: 'Consider pantoprazole if PPI needed; clinical significance debated' }
      ],
      moderate: [
        { drug: 'Methotrexate', effect: 'Increased methotrexate levels and toxicity', management: 'Consider temporary PPI discontinuation with high-dose methotrexate' },
        { drug: 'Tacrolimus', effect: 'Increased tacrolimus levels', management: 'Monitor tacrolimus levels; may need dose reduction' },
        { drug: 'Digoxin', effect: 'Increased digoxin absorption', management: 'Monitor digoxin levels; may need dose adjustment' },
        { drug: 'Iron supplements, ketoconazole, itraconazole', effect: 'Reduced absorption of these drugs', management: 'Separate administration times or use alternative formulations' }
      ],
      food: [
        'Food decreases absorption - take before meals for optimal effect',
        'Avoid concurrent alcohol - may worsen GI symptoms'
      ]
    },
    pregnancy: {
      category: 'C',
      trimpiesterRisks: {
        first: 'Most data reassuring; no clear increase in major malformations; may use if indicated',
        second: 'Limited data suggest safety; use if benefits outweigh theoretical risks',
        third: 'Appears safe; no adverse fetal effects reported'
      },
      recommendation: 'May be used in pregnancy when indicated; extensive human data generally reassuring; ranitidine was historically preferred but is no longer available',
      lactation: { safe: true, notes: 'Excreted in breast milk in small amounts; unlikely to harm infant; considered compatible with breastfeeding' }
    },
    monitoring: {
      baseline: [
        { test: 'Endoscopy (if alarm symptoms)', reason: 'Rule out malignancy before chronic PPI therapy' },
        { test: 'Magnesium level', reason: 'Baseline for long-term therapy' }
      ],
      ongoing: [
        { test: 'Magnesium level', frequency: 'Annually with long-term use', target: 'Normal range' },
        { test: 'Vitamin B12', frequency: 'Every 1-3 years with long-term use', target: 'Normal range' },
        { test: 'Clinical response', frequency: 'After 4-8 weeks', target: 'Symptom resolution' },
        { test: 'Reassess need for continued therapy', frequency: 'Annually', target: 'Step-down to lowest effective dose or discontinue' }
      ],
      therapeutic: { range: 'Not applicable - clinical response used', timing: 'Not applicable' }
    },
    storage: {
      temperature: 'Store at 15-30°C (59-86°F)',
      light: 'Protect from light and moisture',
      humidity: 'Keep in original container; protect from humidity',
      specialInstructions: 'Do not remove from blister pack until ready to use',
      shelfLife: '2-3 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; available throughout Africa in urban and rural areas',
      affordability: 'AFFORDABLE - Generic widely available; approximately $2-5 USD per month; significantly cheaper than brand versions',
      supplyChainNotes: 'Stable supply; multiple generic manufacturers ensure availability; capsules more common than IV formulation',
      localManufacturing: 'Manufactured in Nigeria, South Africa, Kenya, Egypt, Morocco, Algeria; extensive generic production',
      alternatives: ['Ranitidine (if available)', 'Cimetidine', 'Antacids', 'Pantoprazole', 'Esomeprazole'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'High H. pylori prevalence in Africa (>70% in many regions) - important component of eradication therapy; peptic ulcer disease common; NSAID use for endemic diseases (malaria, infections) increases need for gastroprotection'
    },
    patientCounseling: {
      keyPoints: [
        'Take once daily, 30-60 minutes before breakfast for best effect',
        'Swallow capsules whole; do not crush or chew',
        'May take several days to feel full benefit',
        'Complete full course even if symptoms improve'
      ],
      commonMisconceptions: [
        'Misconception: "I can take this only when I have symptoms" - Truth: Works best when taken regularly; not for immediate symptom relief',
        'Misconception: "This is safe to take forever" - Truth: Long-term use has risks; periodic review with doctor recommended'
      ],
      adherenceTips: [
        'Take at same time each day, before first meal',
        'Keep next to toothbrush or coffee maker as reminder',
        'If you miss a dose, take it as soon as remembered unless close to next dose'
      ],
      warningSignsToReport: [
        'Difficulty swallowing, persistent vomiting, or blood in stool (alarm symptoms)',
        'Severe or persistent diarrhea (may indicate C. diff)',
        'Muscle cramps, tremors, or irregular heartbeat (low magnesium)',
        'Rash, joint pain, or skin changes'
      ]
    },
    clinicalPearls: [
      'PPIs are most effective when taken before first meal; parietal cells must be active for drug to work',
      'H. pylori eradication rates have declined due to clarithromycin resistance; consider bismuth quadruple therapy in high-resistance areas',
      'For suspected PPI non-response, ensure patient is taking medication correctly (before meals) before escalating therapy',
      'CYP2C19 poor metabolizers (more common in Asians) have higher omeprazole levels and better acid suppression',
      'In African settings, always consider H. pylori testing and treatment given high prevalence - treat the cause, not just symptoms'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'ACG Guidelines: Treatment of H. pylori Infection 2017',
      'NICE Guidelines: GERD and Dyspepsia 2014 (updated 2023)',
      'FDA Drug Safety Communication: PPI Long-term Risks 2017',
      'Maastricht V/Florence Consensus Report on H. pylori 2016'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 44. METOCLOPRAMIDE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'metoclopramide-044',
    genericName: 'Metoclopramide',
    brandNames: ['Reglan', 'Maxolon', 'Plasil', 'Primperan'],
    atcCode: 'A03FA01',
    therapeuticClass: 'Antiemetic / Prokinetic Agent',
    pharmacology: {
      mechanism: 'Dopamine D2 receptor antagonist in the chemoreceptor trigger zone (antiemetic effect) and GI tract (prokinetic effect). Also has peripheral 5-HT4 agonist activity (enhances acetylcholine release) and weak 5-HT3 antagonist activity. Increases lower esophageal sphincter tone, accelerates gastric emptying, and improves antroduodenal coordination.',
      pharmacokinetics: {
        absorption: 'Rapidly absorbed orally; bioavailability 80-85%; peak levels 1-2 hours; food delays but does not reduce absorption',
        distribution: 'Vd 2.2-3.4 L/kg; 30% protein bound; crosses blood-brain barrier (responsible for CNS effects); crosses placenta',
        metabolism: 'Hepatic glucuronidation and sulfation; minor CYP2D6 involvement; minimal first-pass metabolism',
        elimination: '85% renal excretion (unchanged drug and metabolites); 5% fecal',
        halfLife: '5-6 hours (extended to 14+ hours in severe renal impairment)'
      },
      pharmacodynamics: 'Antiemetic effect onset 30-60 minutes oral, 10-15 minutes IM, 1-3 minutes IV. Prokinetic effect begins within 30-60 minutes. Duration 1-2 hours for antiemetic; longer for prokinetic.'
    },
    indications: [
      { condition: 'Chemotherapy-induced nausea and vomiting', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: false, notes: 'Adjunct to 5-HT3 antagonists; more effective for delayed nausea' },
      { condition: 'Postoperative nausea and vomiting', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Second-line option; 5-HT3 antagonists often preferred' },
      { condition: 'Diabetic gastroparesis', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Most studied prokinetic; limit to 12 weeks due to tardive dyskinesia risk' },
      { condition: 'GERD (short-term)', evidenceLevel: 'B', recommendationStrength: 'Weak', firstLine: false, notes: 'Adjunct to PPIs for regurgitation; not first-line for GERD' },
      { condition: 'Nausea and vomiting (general)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Effective for multiple causes; short-term use preferred' },
      { condition: 'Migraine-associated nausea', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Improves absorption of oral analgesics during migraine' }
    ],
    dosing: {
      adults: {
        standard: '10 mg three times daily 30 minutes before meals and at bedtime; max 12 weeks continuous use',
        indicationSpecific: [
          { indication: 'Gastroparesis', dose: '10 mg four times daily (before meals and at bedtime) for 2-8 weeks; max 12 weeks' },
          { indication: 'CINV prophylaxis', dose: '10-20 mg IV 30 minutes before chemotherapy; repeat q4-6h PRN' },
          { indication: 'PONV prevention', dose: '10-20 mg IV at end of surgery' },
          { indication: 'GERD', dose: '10-15 mg four times daily before meals; short-term only' },
          { indication: 'Acute nausea/vomiting', dose: '10 mg oral/IV/IM q6-8h PRN' },
          { indication: 'Migraine adjunct', dose: '10 mg oral with analgesic' }
        ],
        maxDose: '40 mg/day (0.5 mg/kg/day); 30 mg/day in elderly; limit duration to 12 weeks',
        renalImpairment: {
          mild: 'No adjustment or reduce by 25%',
          moderate: 'Reduce dose by 50%',
          severe: 'Reduce dose by 50-75%; start 5 mg',
          dialysis: 'Give 5-10 mg after dialysis; poorly dialyzed'
        },
        hepaticImpairment: 'Reduce dose by 50% in moderate-severe impairment; monitor closely for side effects'
      },
      pediatric: {
        standard: '0.1-0.2 mg/kg/dose every 6-8 hours; max 0.5 mg/kg/day',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates', dose: 'Avoid if possible; if needed: 0.1 mg/kg q8h' },
          { age: 'Infants/Children <6 years', dose: '0.1 mg/kg/dose up to three times daily' },
          { age: 'Children 6-14 years', dose: '0.1-0.15 mg/kg/dose TID; max 0.5 mg/kg/day' },
          { age: 'Adolescents >14 years', dose: '10 mg TID; adult dosing' }
        ],
        maxDose: '0.5 mg/kg/day or 30 mg/day',
        neonatal: 'Use only when clearly needed; 0.1 mg/kg q8h; high risk of extrapyramidal effects'
      },
      elderly: 'Start 5 mg before meals; max 30 mg/day; avoid use >5 days if possible; very high risk of tardive dyskinesia and extrapyramidal symptoms',
      routes: ['Oral', 'IV', 'IM'],
      administration: [
        'Oral: Give 30 minutes before meals for gastroparesis/prokinetic effect',
        'IV: Give slowly over 1-2 minutes (rapid IV increases extrapyramidal risk); can dilute in 50 mL NS and infuse over 15 min',
        'IM: Deep intramuscular injection',
        'Orally disintegrating tablet: Place on tongue; dissolves without water'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to metoclopramide',
        'GI obstruction, perforation, or hemorrhage',
        'Pheochromocytoma (may cause hypertensive crisis)',
        'Epilepsy (lowers seizure threshold)',
        'History of tardive dyskinesia or neuroleptic malignant syndrome from any drug',
        'Concurrent use of drugs causing extrapyramidal symptoms'
      ],
      relative: [
        "Parkinson's disease (worsens symptoms)",
        'History of depression',
        'Renal impairment (dose adjustment needed)',
        'Elderly patients (high tardive dyskinesia risk)',
        'Children (higher extrapyramidal risk)'
      ],
      precautions: [
        'BLACK BOX WARNING: Tardive dyskinesia risk with prolonged use (>12 weeks)',
        'Limit treatment duration to shortest effective period',
        'Monitor for involuntary movements, especially in elderly and women',
        'May cause drowsiness and impair driving',
        'Risk of extrapyramidal symptoms increased with rapid IV administration',
        'Avoid in breast cancer if prolactin may stimulate tumor growth'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Drowsiness and fatigue', frequency: '10-20%', management: 'Dose at bedtime if once daily; reduce dose; usually improves' },
        { effect: 'Restlessness and anxiety', frequency: '10-15%', management: 'May be early akathisia; consider dose reduction or discontinuation' },
        { effect: 'Diarrhea', frequency: '5-10%', management: 'Usually mild; dose reduction if bothersome' },
        { effect: 'Headache', frequency: '5%', management: 'Usually transient; simple analgesics if needed' }
      ],
      serious: [
        { effect: 'Tardive dyskinesia', frequency: '1-10% with prolonged use', management: 'STOP drug immediately; may be irreversible; avoid future use', warning: 'BLACK BOX WARNING - Risk increases with duration >12 weeks and cumulative dose; higher risk in elderly, women, diabetics' },
        { effect: 'Acute dystonic reactions', frequency: '1-25% (higher in young patients)', management: 'Diphenhydramine 25-50 mg IV or benztropine 1-2 mg IV/IM; usually resolves within minutes' },
        { effect: 'Akathisia', frequency: '5-25%', management: 'Reduce dose or discontinue; propranolol or benzodiazepines may help' },
        { effect: 'Neuroleptic malignant syndrome', frequency: 'Rare', management: 'STOP drug; supportive care; dantrolene; ICU admission' },
        { effect: 'Depression', frequency: '1-5%', management: 'Discontinue if severe; monitor mood' }
      ],
      rare: [
        'Hyperprolactinemia (galactorrhea, gynecomastia, amenorrhea)',
        'Hypertensive crisis (with pheochromocytoma)',
        'Methemoglobinemia (neonates)',
        'Cardiac arrhythmias',
        'Fluid retention'
      ]
    },
    interactions: {
      major: [
        { drug: 'Dopamine agonists (levodopa, pramipexole, bromocriptine)', effect: 'Mutual antagonism - reduced efficacy of both', mechanism: 'Opposing dopamine receptor effects', management: 'Avoid combination; metoclopramide contraindicated in Parkinsons' },
        { drug: 'Antipsychotics and other dopamine antagonists', effect: 'Additive extrapyramidal and tardive dyskinesia risk', mechanism: 'Combined dopamine blockade', management: 'Avoid combination if possible; monitor closely' },
        { drug: 'MAOIs', effect: 'Enhanced catecholamine release and hypertensive potential', mechanism: 'Combined effect on catecholamines', management: 'Avoid combination' }
      ],
      moderate: [
        { drug: 'CNS depressants (alcohol, benzodiazepines, opioids)', effect: 'Enhanced sedation', management: 'Use lower doses of both; caution with driving' },
        { drug: 'Digoxin', effect: 'Reduced digoxin absorption due to faster GI transit', management: 'Monitor digoxin levels; may need dose adjustment' },
        { drug: 'Cyclosporine', effect: 'Increased cyclosporine absorption', management: 'Monitor cyclosporine levels; may need dose reduction' },
        { drug: 'Paracetamol/acetaminophen', effect: 'Faster absorption of paracetamol', management: 'Beneficial in migraine; monitor for faster effect' }
      ],
      food: [
        'Food delays absorption - take 30 minutes before meals for prokinetic effect',
        'Avoid alcohol - additive sedation'
      ]
    },
    pregnancy: {
      category: 'B',
      trimpiesterRisks: {
        first: 'No clear evidence of teratogenicity; extensive human data reassuring; may use for hyperemesis',
        second: 'Considered relatively safe; commonly used for nausea in pregnancy',
        third: 'May use; theoretical risk of extrapyramidal effects in neonate if used near delivery'
      },
      recommendation: 'One of the safer antiemetics in pregnancy; commonly used for hyperemesis gravidarum; no clear evidence of harm in extensive use',
      lactation: { safe: true, notes: 'Excreted in breast milk; has been used to stimulate lactation; monitor infant for GI effects and irritability' }
    },
    monitoring: {
      baseline: [
        { test: 'Movement disorder assessment', reason: 'Document baseline for TD monitoring' },
        { test: 'Renal function', reason: 'For dose adjustment' }
      ],
      ongoing: [
        { test: 'AIMS assessment (tardive dyskinesia)', frequency: 'Before starting, at 3 months, then every 6 months', target: 'Early detection of involuntary movements' },
        { test: 'Clinical efficacy', frequency: 'After 2 weeks', target: 'Symptom improvement' },
        { test: 'Duration of therapy review', frequency: 'At 12 weeks', target: 'Discontinue or justify continued use' }
      ],
      therapeutic: { range: 'Not routinely monitored', timing: 'Not applicable' }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F)',
      light: 'Protect from light',
      humidity: 'Store in dry place',
      specialInstructions: 'Injection may be light sensitive; inspect for discoloration',
      shelfLife: '2-3 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; available throughout Africa; both oral and injectable forms accessible',
      affordability: 'VERY AFFORDABLE - Generic widely available; approximately $1-3 USD per month; very low cost per dose',
      supplyChainNotes: 'Stable supply; multiple manufacturers; tablets and injection both available',
      localManufacturing: 'Manufactured in Nigeria, South Africa, Kenya, Egypt; extensive generic production',
      alternatives: ['Ondansetron', 'Domperidone (where available)', 'Promethazine'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'Important for nausea/vomiting in malaria, gastroenteritis, and other endemic infections; useful adjunct in migraine; diabetic gastroparesis common in Africa; cost-effective alternative where ondansetron unavailable'
    },
    patientCounseling: {
      keyPoints: [
        'Take 30 minutes before meals for best effect',
        'Limit use to shortest time needed (preferably less than 12 weeks)',
        'May cause drowsiness - avoid driving until you know how it affects you',
        'Report any involuntary movements of face, tongue, or limbs immediately'
      ],
      commonMisconceptions: [
        'Misconception: "This is safe for long-term use" - Truth: Risk of permanent movement disorder increases with duration; use short-term only',
        'Misconception: "This is just like other nausea medications" - Truth: Has unique risks not shared by all antiemetics'
      ],
      adherenceTips: [
        'Take consistently 30 minutes before meals',
        'Set reminders for doses if needed',
        'Keep track of how long you have been taking it'
      ],
      warningSignsToReport: [
        'Involuntary movements of tongue, face, or body (may be irreversible)',
        'Severe muscle stiffness or spasms, especially of neck and jaw',
        'Extreme restlessness or inability to sit still',
        'High fever with muscle rigidity (emergency)',
        'Worsening depression or mood changes'
      ]
    },
    clinicalPearls: [
      'Acute dystonia from metoclopramide responds rapidly (within minutes) to IV diphenhydramine or benztropine',
      'Young patients, especially young women, are at highest risk for acute dystonic reactions; elderly at highest risk for tardive dyskinesia',
      'Domperidone (where available) has fewer CNS effects as it does not cross BBB - preferred for gastroparesis if available',
      'For migraine, metoclopramide improves absorption of oral analgesics and has independent antiemetic and possibly antimigraine effects',
      'In African settings, metoclopramide is often the only prokinetic available - use with clear duration limits and patient education about movement disorder risks'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'FDA Black Box Warning: Metoclopramide and Tardive Dyskinesia 2009',
      'ACG Guidelines: Gastroparesis 2022',
      'ACOG Practice Bulletin: Nausea and Vomiting of Pregnancy 2018',
      'Cochrane Review: Metoclopramide for Nausea and Vomiting 2015'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 45. ORAL REHYDRATION SALTS (ORS)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'ors-045',
    genericName: 'Oral Rehydration Salts',
    brandNames: ['ORS', 'Pedialyte', 'Dioralyte', 'Hydralyte', 'Rehydrate'],
    atcCode: 'A07CA',
    therapeuticClass: 'Oral Rehydration Solution',
    pharmacology: {
      mechanism: 'Exploits coupled sodium-glucose transport mechanism in small intestine. Sodium and glucose are actively co-transported across enterocyte brush border via SGLT1 transporter, creating osmotic gradient that drives passive water absorption. This mechanism remains intact even when secretory diarrhea impairs other absorption pathways. WHO-ORS (reduced osmolarity) optimizes absorption while minimizing osmotic diarrhea.',
      pharmacokinetics: {
        absorption: 'Sodium-glucose cotransport is rapid and efficient; water follows passively; absorption occurs primarily in jejunum; impaired in severe malabsorption',
        distribution: 'Components distribute to ECF (sodium) and ICF (potassium, glucose); restore fluid compartments',
        metabolism: 'Glucose metabolized for energy; citrate converted to bicarbonate in liver',
        elimination: 'Water and electrolytes undergo normal renal homeostasis; excess excreted by kidneys',
        halfLife: 'Not applicable - physiological components'
      },
      pharmacodynamics: 'Rehydration begins immediately upon absorption; clinical improvement (skin turgor, urine output) typically within 2-4 hours in mild-moderate dehydration. WHO-ORS provides optimal ratio for absorption without increasing stool output.'
    },
    indications: [
      { condition: 'Acute diarrhea with dehydration', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Cornerstone of diarrhea treatment; suitable for all ages; life-saving intervention' },
      { condition: 'Cholera', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Essential treatment; can be sole therapy for mild-moderate cholera; IV for severe' },
      { condition: 'Prevention of dehydration during diarrhea', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Begin at onset of diarrhea to prevent dehydration' },
      { condition: 'Maintenance hydration after IV rehydration', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Transition from IV to ORS once patient can drink' },
      { condition: 'Dehydration from vomiting', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Small frequent sips; effective despite vomiting in most cases' },
      { condition: 'Heat-related dehydration and exercise', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'ORS superior to water for significant fluid/electrolyte losses' }
    ],
    dosing: {
      adults: {
        standard: 'Mild dehydration: 50-100 mL/kg over 4 hours; Moderate: 100 mL/kg over 4 hours; then replace ongoing losses',
        indicationSpecific: [
          { indication: 'Treatment of dehydration', dose: '75-100 mL/kg over 4 hours (WHO recommendation); then 10-20 mL/kg after each loose stool' },
          { indication: 'Prevention (no dehydration)', dose: 'As much as wanted + 10-20 mL/kg after each loose stool' },
          { indication: 'Maintenance after IV therapy', dose: 'Same as treatment doses; replace ongoing losses volume for volume' },
          { indication: 'Cholera', dose: 'Aggressive replacement: may need 200-350 mL/kg in first 24 hours' }
        ],
        maxDose: 'No maximum - guided by hydration status and ongoing losses; average adult may need 3-6 liters/day in severe diarrhea',
        renalImpairment: {
          mild: 'Use with caution; monitor for fluid overload',
          moderate: 'Use with caution; consider reduced sodium if oliguria',
          severe: 'Use with extreme caution; may need IV fluids with close monitoring instead',
          dialysis: 'Consider IV fluids; ORS can contribute to fluid overload'
        },
        hepaticImpairment: 'No adjustment needed; caution in severe liver failure with ascites'
      },
      pediatric: {
        standard: 'Mild: 50 mL/kg over 4h; Moderate: 100 mL/kg over 4h; plus ongoing losses',
        weightBased: true,
        ageSpecific: [
          { age: 'Neonates', dose: 'Severe dehydration needs IV; ORS 50-100 mL/kg over 4h for mild-moderate + breastfeeding' },
          { age: 'Infants <6 months', dose: '30-90 mL after each loose stool; continue breastfeeding; monitor closely' },
          { age: 'Children 6-24 months', dose: '50-100 mL after each loose stool (1/2 to 1 cup)' },
          { age: 'Children 2-10 years', dose: '100-200 mL after each loose stool (1-2 cups)' },
          { age: 'Adolescents/older children', dose: 'As much as wanted after each stool; similar to adult' }
        ],
        maxDose: 'No maximum - guided by hydration status; may need several liters daily',
        neonatal: 'Continue breastfeeding; ORS for mild-moderate dehydration; severe dehydration requires IV'
      },
      elderly: 'Same as adults; monitor closely for fluid overload; assess for underlying causes of diarrhea',
      routes: ['Oral', 'Nasogastric tube'],
      administration: [
        'Dissolve one sachet in correct volume of clean water (usually 200 mL or 1 liter depending on formulation)',
        'Use only clean, preferably boiled and cooled water',
        'Do not add sugar, salt, or other ingredients',
        'Give small frequent sips if vomiting (5-10 mL every 1-2 minutes)',
        'If severe vomiting, may give via NG tube',
        'Discard unused solution after 24 hours (or 1 hour if not refrigerated in hot climate)'
      ]
    },
    contraindications: {
      absolute: [
        'Intestinal obstruction',
        'Severe dehydration with shock (need IV first)',
        'Paralytic ileus'
      ],
      relative: [
        'Persistent severe vomiting (may need NG or IV)',
        'Altered consciousness (aspiration risk)',
        'Severe renal impairment with anuria',
        'Glucose malabsorption syndromes'
      ],
      precautions: [
        'Severe dehydration (>10% weight loss) requires initial IV rehydration',
        'Monitor for signs of over-hydration (edema, dyspnea)',
        'Ensure correct dilution - do not concentrate or dilute',
        'Continue breastfeeding in infants',
        'ORS does not stop diarrhea - treats/prevents dehydration only'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Vomiting (especially if given too fast)', frequency: '10-20%', management: 'Slow administration; give small amounts frequently; usually self-limiting' },
        { effect: 'Mild abdominal distension', frequency: '5-10%', management: 'Reduce volume temporarily; usually resolves' }
      ],
      serious: [
        { effect: 'Hypernatremia (if incorrectly prepared)', frequency: 'Rare with proper preparation', management: 'Ensure correct dilution; monitor sodium if prolonged use in renal impairment' },
        { effect: 'Hyponatremia (if over-diluted)', frequency: 'Rare', management: 'Use exact water volume specified; do not add extra water' },
        { effect: 'Fluid overload', frequency: 'Rare in healthy individuals', management: 'Monitor intake; watch for edema, dyspnea; reduce rate in renal/cardiac disease' }
      ],
      rare: [
        'Aspiration (if given to obtunded patient)',
        'Severe electrolyte imbalance (with improper preparation)'
      ]
    },
    interactions: {
      major: [
        { drug: 'None clinically significant', effect: 'ORS is physiological solution', mechanism: 'Not applicable', management: 'Not applicable' }
      ],
      moderate: [
        { drug: 'Diuretics', effect: 'May offset hydration if continued during diarrhea', management: 'Consider holding diuretics during acute diarrhea if safe' },
        { drug: 'ACE inhibitors/ARBs', effect: 'Risk of hyperkalemia with potassium in ORS if severe renal impairment', management: 'Monitor potassium in renal patients' }
      ],
      food: [
        'Continue feeding during diarrhea - does not worsen outcome',
        'Breastfeeding should continue alongside ORS',
        'BRAT diet (bananas, rice, applesauce, toast) not evidence-based but harmless'
      ]
    },
    pregnancy: {
      category: 'Safe',
      trimpiesterRisks: {
        first: 'Safe; physiological components; use as needed for dehydration',
        second: 'Safe; monitor hydration carefully; dehydration can cause contractions',
        third: 'Safe; important to maintain hydration; severe dehydration may induce labor'
      },
      recommendation: 'Safe and indicated in pregnancy; dehydration more dangerous than any theoretical risk; use freely',
      lactation: { safe: true, notes: 'Continue breastfeeding while using ORS; breast milk provides additional fluid and nutrients to infant' }
    },
    monitoring: {
      baseline: [
        { test: 'Clinical assessment of dehydration', reason: 'Determine severity (skin turgor, cap refill, mental status, urine output)' },
        { test: 'Weight (if available)', reason: 'Baseline for monitoring rehydration' }
      ],
      ongoing: [
        { test: 'Clinical signs', frequency: 'Every 1-4 hours during active rehydration', target: 'Improved skin turgor, mental status, urine output' },
        { test: 'Urine output', frequency: 'Ongoing', target: '>0.5 mL/kg/hour indicates adequate hydration' },
        { test: 'Weight', frequency: 'Every 4-6 hours if hospitalized', target: 'Return toward baseline weight' },
        { test: 'Stool output', frequency: 'Ongoing', target: 'Replace volume for volume' }
      ],
      therapeutic: { range: 'Not applicable', timing: 'Clinical hydration status is the guide' }
    },
    storage: {
      temperature: 'Store sachets at 15-30°C (59-86°F)',
      light: 'No special light protection needed',
      humidity: 'Protect from moisture; keep sachets sealed until use',
      specialInstructions: 'Once reconstituted: use within 24 hours if refrigerated, 1 hour at room temperature in hot climate',
      shelfLife: 'Sachets: 2-3 years; reconstituted solution: 24 hours maximum'
    },
    africanContext: {
      availability: 'UNIVERSALLY AVAILABLE - On WHO Essential Medicines List; available throughout Africa including remote areas; NGO distribution common',
      affordability: 'EXTREMELY AFFORDABLE - Often free through health programs; if purchased: <$0.50 USD per sachet; home-made solution possible if commercial unavailable',
      supplyChainNotes: 'Stable, lightweight, long shelf-life makes distribution feasible; UNICEF and WHO supply programs ensure availability',
      localManufacturing: 'Manufactured widely across Africa; local production in most countries; simple formulation suitable for local manufacturing',
      alternatives: ['Home-made ORS (1L water + 6 tsp sugar + 0.5 tsp salt)', 'Rice-based ORS', 'Coconut water with added salt'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'CRITICAL intervention for diarrheal diseases - leading cause of child death in Africa; essential for cholera outbreaks; key component of IMCI (Integrated Management of Childhood Illness); zinc supplementation should accompany ORS for childhood diarrhea'
    },
    patientCounseling: {
      keyPoints: [
        'Mix exactly as directed - do not add extra water or make it stronger',
        'Give small amounts frequently if child is vomiting - do not stop ORS',
        'Continue breastfeeding and feeding during diarrhea',
        'ORS prevents dehydration but does not stop diarrhea - this is normal'
      ],
      commonMisconceptions: [
        'Misconception: "I should stop feeding during diarrhea" - Truth: Continue feeding; food helps recovery',
        'Misconception: "ORS will stop the diarrhea" - Truth: ORS prevents dehydration; diarrhea may continue but child stays hydrated',
        'Misconception: "I can make it stronger/weaker" - Truth: Use exact proportions; incorrect mixing is dangerous'
      ],
      adherenceTips: [
        'Give ORS in a cup, not a bottle, for children',
        'Offer frequently even if child refuses initially',
        'Continue until diarrhea stops; keep spare sachets at home'
      ],
      warningSignsToReport: [
        'Signs of severe dehydration: very dry mouth, no tears, sunken eyes, very weak/lethargic',
        'Child unable to drink or refuses all fluids',
        'Persistent vomiting of everything',
        'Blood in stool',
        'Signs not improving after 4-6 hours of ORS'
      ]
    },
    clinicalPearls: [
      'ORS has saved more lives than any other medical intervention - estimated 50 million lives since introduction',
      'Reduced osmolarity WHO-ORS (245 mOsm/L) is superior to original formula (311 mOsm/L) - reduces stool output and vomiting',
      'Zinc supplementation (20 mg/day for 10-14 days) with ORS reduces diarrhea duration and prevents future episodes in children',
      'Home-made ORS (1L clean water + 6 level teaspoons sugar + 1/2 level teaspoon salt) is life-saving when commercial ORS unavailable',
      'Never withhold ORS because of vomiting - small frequent sips (5-10 mL/min by spoon) are absorbed even with vomiting'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO/UNICEF Joint Statement: Clinical Management of Acute Diarrhea 2004',
      'WHO Pocket Book of Hospital Care for Children 2nd ed. 2013',
      'Lancet: Oral Rehydration Salts - Medical Advance of the Century 1978',
      'UNICEF/WHO: Diarrhea Treatment Guidelines for Clinicians 2005'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }

,
  // ═══════════════════════════════════════════════════════════════════════════
  // 46. FERROUS SULFATE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'ferrous-sulfate-046',
    genericName: 'Ferrous Sulfate',
    brandNames: ['Fer-In-Sol', 'Feosol', 'Slow Fe', 'Fero-Gradumet'],
    atcCode: 'B03AA07',
    therapeuticClass: 'Iron Supplement (Oral)',
    pharmacology: {
      mechanism: 'Provides elemental iron (ferrous form, Fe2+) essential for hemoglobin synthesis, oxygen transport, and cellular respiration. Absorbed via DMT1 transporter in duodenum and proximal jejunum. Once absorbed, iron binds to transferrin for transport and ferritin for storage. Ferrous salts are more bioavailable than ferric (Fe3+) forms.',
      pharmacokinetics: {
        absorption: 'Variable absorption 10-35% depending on iron stores (increases in deficiency); best absorbed on empty stomach; ascorbic acid enhances absorption; food, antacids, tea reduce absorption',
        distribution: 'Bound to transferrin in plasma; distributed to bone marrow, liver, spleen; stored as ferritin and hemosiderin',
        metabolism: 'Iron recycled through reticuloendothelial system; minimal metabolic transformation',
        elimination: 'Minimal excretion (<1 mg/day via desquamated cells, sweat, urine, bile); no regulated excretion pathway - accumulates if excess',
        halfLife: 'Serum iron: 1-2 hours; transferrin-bound: 6 hours; total body iron turnover: ~10 days'
      },
      pharmacodynamics: 'Reticulocyte response begins in 3-7 days; hemoglobin rises 1-2 g/dL over 2-4 weeks with adequate replacement. Full correction of anemia typically 2-3 months; replenishment of stores requires 3-6 months of continued therapy.'
    },
    indications: [
      { condition: 'Iron deficiency anemia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line treatment; identify and treat underlying cause' },
      { condition: 'Iron deficiency without anemia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Treat symptomatic iron deficiency (fatigue, restless legs)' },
      { condition: 'Prevention of iron deficiency in pregnancy', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Routine supplementation recommended from first trimester' },
      { condition: 'Prophylaxis in high-risk groups', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Preterm infants, menstruating women with heavy periods, vegetarians' },
      { condition: 'Post-bariatric surgery supplementation', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'Lifelong supplementation often required after gastric bypass' }
    ],
    dosing: {
      adults: {
        standard: 'Treatment of deficiency: 150-200 mg elemental iron/day in 2-3 divided doses; Prevention: 30-60 mg elemental iron/day',
        indicationSpecific: [
          { indication: 'Iron deficiency anemia', dose: '325 mg ferrous sulfate (65 mg elemental iron) TID or 200 mg BID-TID' },
          { indication: 'Mild deficiency/prevention', dose: '325 mg ferrous sulfate once daily (65 mg elemental iron)' },
          { indication: 'Pregnancy prophylaxis', dose: '30-60 mg elemental iron daily (150-300 mg ferrous sulfate)' },
          { indication: 'Post-bariatric surgery', dose: '65-130 mg elemental iron daily with vitamin C' }
        ],
        maxDose: '200 mg elemental iron/day (approximately 1000 mg ferrous sulfate); higher doses increase side effects without proportional benefit',
        renalImpairment: {
          mild: 'No adjustment; monitor for iron overload in CKD',
          moderate: 'Use with caution; TSAT and ferritin monitoring; oral may be less effective',
          severe: 'IV iron often preferred in dialysis patients; oral absorption impaired',
          dialysis: 'IV iron typically required; oral absorption poor; monitor for overload'
        },
        hepaticImpairment: 'Caution in severe liver disease; iron accumulates; monitor ferritin'
      },
      pediatric: {
        standard: 'Treatment: 3-6 mg/kg/day elemental iron in 2-3 divided doses; Prevention: 1-2 mg/kg/day',
        weightBased: true,
        ageSpecific: [
          { age: 'Preterm infants', dose: '2-4 mg/kg/day elemental iron from 2 weeks to 12 months' },
          { age: 'Term infants 4-12 months', dose: '1 mg/kg/day elemental iron (if not iron-fortified formula or cereal)' },
          { age: 'Children 1-12 years (treatment)', dose: '3-6 mg/kg/day elemental iron in 2-3 divided doses; max 200 mg/day' },
          { age: 'Adolescents', dose: 'Adult dosing; menstruating females may need 30-60 mg elemental iron daily prophylaxis' }
        ],
        maxDose: '6 mg/kg/day elemental iron or 200 mg elemental iron/day',
        neonatal: 'Preterm: 2-4 mg/kg/day elemental iron starting at 2 weeks; term neonates rarely need supplementation'
      },
      elderly: 'Start with lower doses; GI side effects more problematic; consider every-other-day dosing or alternate formulations if not tolerated; investigate cause of deficiency (GI blood loss)',
      routes: ['Oral'],
      administration: [
        'Best absorbed on empty stomach 1 hour before or 2 hours after meals',
        'If GI upset occurs, may take with small amount of food (reduces absorption)',
        'Take with vitamin C (orange juice) to enhance absorption',
        'Avoid concurrent antacids, dairy, tea, coffee, whole grains - separate by 2 hours',
        'Liquid formulations available for children - may stain teeth (use straw/dropper)',
        'Extended-release formulations: do not crush or chew'
      ]
    },
    contraindications: {
      absolute: [
        'Iron overload states (hemochromatosis, hemosiderosis, repeated transfusions)',
        'Hemolytic anemias (may worsen iron overload)',
        'Known hypersensitivity to ferrous sulfate'
      ],
      relative: [
        'Peptic ulcer disease (may exacerbate)',
        'Inflammatory bowel disease (may worsen and reduce absorption)',
        'Chronic kidney disease (IV iron often preferred)',
        'Concurrent parenteral iron therapy'
      ],
      precautions: [
        'Keep out of reach of children - iron is leading cause of fatal poisoning in young children',
        'GI side effects common and limit adherence - counsel on management',
        'Dark/black stools are expected - distinguish from melena',
        'May worsen infection in malaria-endemic areas without treatment (controversial)',
        'Interferes with absorption of many medications - timing important',
        'Do not exceed recommended doses - toxicity risk'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Constipation', frequency: '20-40%', management: 'Increase fluids and fiber; consider stool softeners; reduce dose or switch formulation' },
        { effect: 'Nausea and epigastric pain', frequency: '15-30%', management: 'Take with food (reduces absorption); divide doses; try extended-release' },
        { effect: 'Dark/black stools', frequency: 'Nearly universal', management: 'Expected effect - reassure patient; distinguish from melena (dark stools not tarry)' },
        { effect: 'Diarrhea', frequency: '5-15%', management: 'Reduce dose; try different formulation; ensure adequate hydration' },
        { effect: 'Metallic taste', frequency: '5-10%', management: 'Usually transient; use coated tablets or liquid with straw' }
      ],
      serious: [
        { effect: 'Iron toxicity/overdose', frequency: 'Rare with therapeutic doses', management: 'Symptoms: vomiting, diarrhea, GI bleeding, shock; Treatment: whole bowel irrigation, deferoxamine', warning: 'Keep out of reach of children - leading cause of pediatric poisoning deaths' },
        { effect: 'Iron overload with chronic excess', frequency: 'Rare', management: 'Monitor ferritin periodically; stop when stores replete' }
      ],
      rare: [
        'Hypersensitivity reactions',
        'Esophageal ulceration (if tablet lodges)',
        'Tooth staining (liquid preparations)'
      ]
    },
    interactions: {
      major: [
        { drug: 'Levodopa and carbidopa', effect: 'Reduced levodopa absorption by 50%', mechanism: 'Chelation in GI tract', management: 'Separate by at least 2 hours; take iron at bedtime' },
        { drug: 'Fluoroquinolones (ciprofloxacin, levofloxacin)', effect: 'Reduced antibiotic absorption by 30-90%', mechanism: 'Chelation forming insoluble complexes', management: 'Separate by at least 2 hours (ideally 4-6 hours)' },
        { drug: 'Tetracyclines (doxycycline, tetracycline)', effect: 'Reduced absorption of both drugs', mechanism: 'Chelation forming insoluble complexes', management: 'Separate by at least 2-3 hours' },
        { drug: 'Thyroid hormones (levothyroxine)', effect: 'Reduced levothyroxine absorption', mechanism: 'Chelation in GI tract', management: 'Separate by at least 4 hours; take levothyroxine in morning, iron later' }
      ],
      moderate: [
        { drug: 'Proton pump inhibitors, H2 blockers, antacids', effect: 'Reduced iron absorption (requires acidic environment)', management: 'Separate by 2 hours; may need higher iron doses or IV iron' },
        { drug: 'Methyldopa', effect: 'Reduced methyldopa absorption', management: 'Separate by 2 hours' },
        { drug: 'Penicillamine', effect: 'Reduced penicillamine absorption', management: 'Separate by at least 2 hours' },
        { drug: 'Bisphosphonates', effect: 'Reduced bisphosphonate absorption', management: 'Take bisphosphonate first thing in morning; iron later in day' }
      ],
      food: [
        'Tea and coffee reduce absorption by 40-60% (tannins chelate iron) - avoid within 2 hours',
        'Dairy products reduce absorption (calcium competes)',
        'Whole grains and bran reduce absorption (phytates chelate iron)',
        'Vitamin C enhances absorption - take with orange juice or ascorbic acid supplement',
        'Meat enhances absorption of non-heme iron'
      ]
    },
    pregnancy: {
      category: 'A (at recommended doses)',
      trimpiesterRisks: {
        first: 'Safe and recommended; iron needs increase from early pregnancy',
        second: 'Safe and recommended; increased iron demands for fetal growth',
        third: 'Safe and essential; highest iron demands; prepare for delivery blood loss'
      },
      recommendation: 'Routine supplementation recommended throughout pregnancy (30-60 mg elemental iron daily); higher doses for treatment of anemia; essential for fetal development and maternal health',
      lactation: { safe: true, notes: 'Safe during breastfeeding; iron supplementation does not significantly affect breast milk iron; continue if indicated for maternal deficiency' }
    },
    monitoring: {
      baseline: [
        { test: 'CBC with reticulocytes', reason: 'Confirm iron deficiency anemia; baseline hemoglobin' },
        { test: 'Serum ferritin', reason: 'Assess iron stores; confirm deficiency (ferritin <30 ng/mL)' },
        { test: 'Serum iron, TIBC, transferrin saturation', reason: 'Confirm iron deficiency (low iron, high TIBC, low sat)' }
      ],
      ongoing: [
        { test: 'Reticulocyte count', frequency: 'After 1 week', target: 'Reticulocyte response indicates adequate absorption' },
        { test: 'Hemoglobin', frequency: 'Every 2-4 weeks during treatment', target: 'Rise 1-2 g/dL per month; normalize in 2-3 months' },
        { test: 'Ferritin', frequency: 'After 3 months', target: 'Ferritin >50-100 ng/mL indicates replete stores' }
      ],
      therapeutic: { range: 'Ferritin target 50-100 ng/mL for replete stores', timing: 'Continue 3-6 months after hemoglobin normalizes to replenish stores' }
    },
    storage: {
      temperature: 'Store at 15-30°C (59-86°F)',
      light: 'Protect from moisture; keep container tightly closed',
      humidity: 'Protect from humidity',
      specialInstructions: 'Keep out of reach of children in child-resistant container; store in original packaging',
      shelfLife: '2-3 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'UNIVERSALLY AVAILABLE - On WHO Essential Medicines List; available throughout Africa including remote areas; part of antenatal care packages',
      affordability: 'EXTREMELY AFFORDABLE - Approximately $1-3 USD per month of treatment; often free through public health programs',
      supplyChainNotes: 'Stable supply; included in essential medicines programs; lightweight and easy to distribute',
      localManufacturing: 'Manufactured widely across Africa; local production in most countries; simple formulation',
      alternatives: ['Ferrous gluconate', 'Ferrous fumarate', 'Iron polymaltose complex'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'Iron deficiency anemia extremely common in Africa (30-70% in pregnant women); hookworm and malaria contribute to anemia burden; WHO recommends intermittent iron supplementation in malaria-endemic areas rather than daily; deworming may improve iron status'
    },
    patientCounseling: {
      keyPoints: [
        'Take on empty stomach for best absorption; take with vitamin C (orange juice)',
        'Expect dark or black stools - this is normal and harmless',
        'Avoid tea, coffee, and milk within 2 hours of taking iron',
        'Continue treatment for 3-6 months even after feeling better to rebuild stores'
      ],
      commonMisconceptions: [
        'Misconception: "I can stop when I feel better" - Truth: Continue 3-6 months after hemoglobin normalizes to replenish stores',
        'Misconception: "Black stools mean bleeding" - Truth: Iron turns stools dark/black; this is expected and harmless',
        'Misconception: "More is better" - Truth: Higher doses cause more side effects without better results'
      ],
      adherenceTips: [
        'If stomach upset occurs, take with small snack (not dairy)',
        'Try taking at bedtime if daytime dosing causes problems',
        'Consider every-other-day dosing if side effects limit adherence',
        'Keep medication in a visible place to remember doses'
      ],
      warningSignsToReport: [
        'Bloody vomit or stool (not just dark stool)',
        'Severe abdominal pain',
        'Signs of allergic reaction (rash, swelling, difficulty breathing)',
        'No improvement in fatigue after 1-2 months'
      ]
    },
    clinicalPearls: [
      'Recent evidence supports every-other-day dosing - may improve absorption and reduce side effects without compromising efficacy',
      'Ferritin <30 ng/mL confirms iron deficiency; <12 ng/mL is specific but not sensitive; inflammation raises ferritin falsely',
      'If hemoglobin does not rise after 3-4 weeks, consider: non-adherence, malabsorption, ongoing blood loss, or wrong diagnosis',
      'In Africa, always consider parasitic causes of iron deficiency - hookworm, schistosomiasis - treat underlying cause',
      'Oral iron is ineffective in inflammatory states (hepcidin blocks absorption) - consider IV iron in chronic disease'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guideline: Daily Iron Supplementation in Infants and Children 2016',
      'WHO Guideline: Intermittent Iron Supplementation in Preschool and School-age Children 2011',
      'British Society of Gastroenterology: Guidelines on Management of Iron Deficiency Anaemia 2021',
      'Cochrane Review: Oral Iron Supplements for Anaemia 2015'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 47. FOLIC ACID
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'folic-acid-047',
    genericName: 'Folic Acid',
    brandNames: ['Folvite', 'Folacin', 'Folate', 'FA-8'],
    atcCode: 'B03BB01',
    therapeuticClass: 'Vitamin B9 / Hematopoietic Agent',
    pharmacology: {
      mechanism: 'Folic acid is converted to tetrahydrofolate (THF), the active coenzyme form essential for one-carbon transfer reactions in DNA synthesis (purine and thymidine), amino acid metabolism (methionine, serine, histidine), and neurotransmitter synthesis. Critical for rapidly dividing cells including bone marrow and fetal neural tube development.',
      pharmacokinetics: {
        absorption: 'Well absorbed in proximal jejunum; bioavailability 85-100% for synthetic folic acid vs 50% for dietary folate; peak 1-2 hours',
        distribution: 'Distributed to all tissues; concentrated in liver (primary storage) and CSF; crosses placenta; enters breast milk',
        metabolism: 'Reduced to THF by dihydrofolate reductase; methylated forms predominate in plasma and tissues',
        elimination: 'Excess excreted in urine as unchanged folic acid and metabolites; intact enterohepatic circulation',
        halfLife: 'Short plasma half-life (<1 hour) but tissue stores last 3-4 months'
      },
      pharmacodynamics: 'Megaloblastic anemia shows reticulocyte response in 3-5 days; hematologic recovery in 1-2 months. Neural tube closure occurs at 28 days post-conception - supplementation must begin before conception for prevention.'
    },
    indications: [
      { condition: 'Prevention of neural tube defects', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Start before conception; continue through first trimester minimum' },
      { condition: 'Megaloblastic anemia due to folate deficiency', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Exclude B12 deficiency before treating' },
      { condition: 'Methotrexate toxicity prevention/rescue', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Folic acid reduces toxicity; leucovorin for rescue after high-dose MTX' },
      { condition: 'Prevention of folate deficiency in pregnancy', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Routine supplementation recommended' },
      { condition: 'Chronic hemolytic anemias', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Increased folate requirements due to rapid RBC turnover' },
      { condition: 'Hyperhomocysteinemia', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: true, notes: 'Reduces homocysteine levels; cardiovascular benefit uncertain' }
    ],
    dosing: {
      adults: {
        standard: 'Deficiency treatment: 1-5 mg daily; NTD prevention: 400-800 mcg daily; with methotrexate: 1-5 mg weekly (not on MTX day)',
        indicationSpecific: [
          { indication: 'NTD prevention (no history)', dose: '400-800 mcg daily starting 1 month before conception through 12 weeks' },
          { indication: 'NTD prevention (previous affected pregnancy)', dose: '4-5 mg daily starting 3 months before conception through 12 weeks' },
          { indication: 'Megaloblastic anemia', dose: '1-5 mg daily for 1-4 months until hematologic recovery' },
          { indication: 'Methotrexate toxicity prevention (RA, psoriasis)', dose: '1-5 mg weekly, 24-48 hours after methotrexate' },
          { indication: 'Chronic hemolysis (sickle cell, thalassemia)', dose: '1-5 mg daily' }
        ],
        maxDose: '5 mg daily for most indications; higher doses do not provide additional benefit',
        renalImpairment: {
          mild: 'No adjustment needed',
          moderate: 'No adjustment; water-soluble, excreted renally',
          severe: 'May need lower doses; accumulation possible',
          dialysis: 'Removed by dialysis; supplement after dialysis if deficient'
        },
        hepaticImpairment: 'No adjustment typically needed; use with caution in severe liver disease'
      },
      pediatric: {
        standard: 'Deficiency: 0.1-0.4 mg daily; maintenance: 0.1 mg daily; premature infants: 0.05-0.1 mg daily',
        weightBased: false,
        ageSpecific: [
          { age: 'Premature infants', dose: '50-100 mcg (0.05-0.1 mg) daily' },
          { age: 'Infants 0-12 months', dose: '25-50 mcg daily (maintenance); 100 mcg daily (treatment)' },
          { age: 'Children 1-3 years', dose: '100-150 mcg daily (maintenance); 0.3 mg daily (treatment)' },
          { age: 'Children 4-13 years', dose: '200-400 mcg daily (maintenance); 0.4-1 mg daily (treatment)' },
          { age: 'Adolescents', dose: '400 mcg daily (maintenance); 1 mg daily (treatment)' }
        ],
        maxDose: '1 mg daily for children; 5 mg for specific conditions',
        neonatal: 'Premature infants: 50-100 mcg daily; full-term rarely need supplementation if breastfed by adequate mother'
      },
      elderly: 'No dose adjustment; ensure adequate B12 status before treating - folic acid can mask B12 deficiency',
      routes: ['Oral', 'IM', 'IV', 'Subcutaneous'],
      administration: [
        'Oral: May take with or without food; well absorbed',
        'Parenteral: Reserve for malabsorption, NPO status; rarely needed',
        'Prenatal vitamins: Ensure contain at least 400-800 mcg folic acid',
        'Fortified foods: Many countries mandate flour/grain fortification'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to folic acid',
        'Pernicious anemia or B12 deficiency (without concurrent B12 treatment) - may mask neurological damage'
      ],
      relative: [
        'Seizure disorders (high doses may reduce seizure threshold)',
        'Concurrent methotrexate for cancer (may reduce efficacy - leucovorin used instead)'
      ],
      precautions: [
        'MUST rule out B12 deficiency before treating megaloblastic anemia with folic acid alone - will correct hematologic picture but allow neurological damage to progress',
        'Doses >1 mg may mask B12 deficiency - always check B12 if using high doses',
        'May reduce effectiveness of anticonvulsants (phenytoin, phenobarbital) - monitor drug levels',
        'Fortified foods may contribute to total intake - consider when dosing'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'GI disturbance (nausea, bloating)', frequency: '5-10%', management: 'Usually mild and transient; take with food if needed' },
        { effect: 'Bitter taste', frequency: '5%', management: 'Take with juice or food; transient' }
      ],
      serious: [
        { effect: 'Masking of B12 deficiency neurological damage', frequency: 'Depends on vigilance', management: 'Always check B12 before treating megaloblastic anemia; monitor neurologically', warning: 'CRITICAL: Folic acid corrects hematologic findings of B12 deficiency but allows subacute combined degeneration to progress' },
        { effect: 'Hypersensitivity reactions', frequency: 'Very rare', management: 'Discontinue; standard allergy management' }
      ],
      rare: [
        'Bronchospasm',
        'Anaphylaxis (parenteral)',
        'Altered sleep patterns',
        'Irritability'
      ]
    },
    interactions: {
      major: [
        { drug: 'Methotrexate (cancer chemotherapy)', effect: 'Reduces methotrexate cytotoxic efficacy', mechanism: 'Folic acid bypasses methotrexate inhibition of dihydrofolate reductase', management: 'Do NOT give folic acid with high-dose methotrexate for cancer; use leucovorin rescue instead' }
      ],
      moderate: [
        { drug: 'Phenytoin, phenobarbital, primidone', effect: 'Mutual interaction - folic acid may reduce anticonvulsant levels; anticonvulsants deplete folate', management: 'Monitor anticonvulsant levels; supplement folate in long-term anticonvulsant users' },
        { drug: 'Methotrexate (low-dose for RA/psoriasis)', effect: 'Reduces methotrexate side effects without eliminating benefit', management: 'Intended beneficial interaction; give folic acid 24-48h after MTX' },
        { drug: 'Sulfasalazine', effect: 'Sulfasalazine impairs folate absorption', management: 'Supplement with folic acid 1 mg daily' },
        { drug: 'Trimethoprim-sulfamethoxazole', effect: 'TMP inhibits dihydrofolate reductase', management: 'May need supplementation with prolonged use' }
      ],
      food: [
        'No significant food interactions',
        'Alcohol impairs folate absorption and increases requirements',
        'Many grains are fortified with folic acid - contributes to total intake'
      ]
    },
    pregnancy: {
      category: 'A',
      trimpiesterRisks: {
        first: 'CRITICAL for neural tube closure at day 28; supplementation should begin before conception',
        second: 'Continued benefit for fetal growth and prevention of megaloblastic anemia',
        third: 'Continue supplementation; prepares for blood loss at delivery'
      },
      recommendation: 'All women of childbearing potential should take 400-800 mcg daily; women with history of NTD-affected pregnancy need 4-5 mg daily starting 3 months before conception',
      lactation: { safe: true, notes: 'Safe and recommended during breastfeeding; folate excreted in breast milk; continue supplementation to maintain maternal and infant status' }
    },
    monitoring: {
      baseline: [
        { test: 'Serum folate', reason: 'Confirm deficiency before treatment if uncertain' },
        { test: 'Serum B12', reason: 'CRITICAL: Rule out B12 deficiency before starting folic acid' },
        { test: 'CBC with MCV', reason: 'Baseline for monitoring response; elevated MCV in deficiency' }
      ],
      ongoing: [
        { test: 'Reticulocyte count', frequency: 'After 5-7 days of treatment', target: 'Reticulocyte response indicates adequate treatment' },
        { test: 'Hemoglobin', frequency: 'Monthly until normalized', target: 'Normalization of anemia' },
        { test: 'MCV', frequency: 'Monthly', target: 'Normalization of red cell size' }
      ],
      therapeutic: { range: 'Serum folate >4 ng/mL; RBC folate >150 ng/mL', timing: 'Check after 1-2 months if compliance uncertain' }
    },
    storage: {
      temperature: 'Store at 15-30°C (59-86°F)',
      light: 'Protect from light; folic acid is light sensitive',
      humidity: 'Store in dry place',
      specialInstructions: 'Keep in original container; avoid excessive heat',
      shelfLife: '2-3 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'UNIVERSALLY AVAILABLE - On WHO Essential Medicines List; part of antenatal care packages; available throughout Africa',
      affordability: 'EXTREMELY AFFORDABLE - <$1 USD per month; often free through maternal health programs',
      supplyChainNotes: 'Stable supply; lightweight; included in essential medicines kits; often combined with iron in antenatal supplements',
      localManufacturing: 'Manufactured across Africa; simple formulation; widely available generics',
      alternatives: ['Methylfolate (for MTHFR mutations - rarely available in Africa)', 'Dietary folate (leafy greens, legumes)'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'Neural tube defects have high incidence in Africa (varies by region); flour fortification programs expanding but incomplete; combined iron-folic acid supplements standard in antenatal care; sickle cell disease common - chronic hemolysis increases folate requirements'
    },
    patientCounseling: {
      keyPoints: [
        'Women planning pregnancy should start folic acid at least 1 month before conception',
        'Continue taking throughout pregnancy as directed',
        'Prevents serious birth defects of the brain and spine',
        'Eat folate-rich foods: green leafy vegetables, beans, fortified cereals'
      ],
      commonMisconceptions: [
        'Misconception: "I can get enough from diet alone" - Truth: Supplementation is recommended as dietary intake often insufficient',
        'Misconception: "I can start when I know Im pregnant" - Truth: Neural tube closes before most women know they are pregnant'
      ],
      adherenceTips: [
        'Take at the same time daily; consider pairing with another daily habit',
        'Prenatal vitamins contain folic acid - check the label',
        'Keep by toothbrush or bedside as reminder'
      ],
      warningSignsToReport: [
        'Signs of B12 deficiency: numbness/tingling in hands or feet, balance problems',
        'Allergic reactions (rare): rash, difficulty breathing',
        'No improvement in energy or anemia symptoms after 1-2 months'
      ]
    },
    clinicalPearls: [
      'ALWAYS check B12 before treating megaloblastic anemia - folic acid will correct blood counts but allow B12 neurological damage (subacute combined degeneration) to progress',
      '4-5 mg daily (10x normal dose) is required for women with previous NTD-affected pregnancy - standard prenatal vitamins are insufficient',
      'Methotrexate-folic acid timing is critical: for low-dose MTX (RA), give folic acid 24-48h AFTER MTX; for high-dose MTX (cancer), use leucovorin rescue, not folic acid',
      'MTHFR mutations reduce folate metabolism - methylfolate may be beneficial but is rarely available in resource-limited settings',
      'In Africa, prioritize ensuring all pregnant women receive folic acid supplementation - NTD prevention is highly cost-effective'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guideline: Optimal Serum and Red Blood Cell Folate Concentrations 2015',
      'US Preventive Services Task Force: Folic Acid for Prevention of Neural Tube Defects 2017',
      'Cochrane Review: Periconceptional Folate Supplementation to Prevent Neural Tube Defects 2015',
      'March of Dimes: Folic Acid and Birth Defects Prevention'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 48. VITAMIN A
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'vitamin-a-048',
    genericName: 'Vitamin A (Retinol)',
    brandNames: ['Aquasol A', 'Retinol', 'Vitamin A Palmitate', 'A-Mulsin'],
    atcCode: 'A11CA01',
    therapeuticClass: 'Fat-Soluble Vitamin / Retinoid',
    pharmacology: {
      mechanism: 'Vitamin A (retinol) is essential for visual cycle (retinal in rhodopsin), epithelial cell differentiation and maintenance, immune function, reproduction, and gene transcription. Retinoic acid (active metabolite) binds nuclear receptors (RAR, RXR) to regulate gene expression. Critical for integrity of mucosal surfaces and immune competence.',
      pharmacokinetics: {
        absorption: 'Fat-soluble; requires bile salts and dietary fat for absorption in small intestine; bioavailability 20-50% from supplements; provitamin A (beta-carotene) conversion is variable and inefficient',
        distribution: 'Stored primarily in liver (90%); transported by retinol-binding protein; stored as retinyl esters',
        metabolism: 'Metabolized in liver to retinal and retinoic acid (active forms); cytochrome P450 enzymes involved',
        elimination: 'Metabolites excreted in urine and feces; very slow turnover from liver stores',
        halfLife: 'Plasma half-life: several hours; liver stores can last months to years'
      },
      pharmacodynamics: 'In deficiency, night blindness improves within days of supplementation; xerophthalmia reverses over 1-2 weeks; full recovery of epithelial integrity takes weeks to months. High-dose supplementation in measles reduces mortality within days.'
    },
    indications: [
      { condition: 'Vitamin A deficiency treatment', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Life-saving in severe deficiency with xerophthalmia' },
      { condition: 'Measles treatment (WHO protocol)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Reduces measles mortality by 50%; standard of care' },
      { condition: 'Prevention of vitamin A deficiency', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'High-dose supplementation programs in endemic areas' },
      { condition: 'Night blindness (nyctalopia)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Earliest sign of deficiency; responds rapidly' },
      { condition: 'Xerophthalmia and keratomalacia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Medical emergency; can prevent blindness' },
      { condition: 'Acute diarrhea in children (deficient populations)', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Reduces severity in vitamin A deficient children' }
    ],
    dosing: {
      adults: {
        standard: 'Deficiency treatment: 200,000 IU (60 mg retinol) orally once; repeat on day 2 and at 2 weeks',
        indicationSpecific: [
          { indication: 'Severe deficiency with xerophthalmia', dose: '200,000 IU (60 mg) orally immediately, repeat next day, and at 2 weeks' },
          { indication: 'Measles (WHO)', dose: '200,000 IU orally once daily for 2 days' },
          { indication: 'Prophylaxis (deficient populations)', dose: '200,000 IU orally every 4-6 months' },
          { indication: 'Dietary supplementation', dose: '700-900 mcg RAE (2,300-3,000 IU) daily' }
        ],
        maxDose: '300,000 IU acute dose; chronic supplementation should not exceed 10,000 IU/day to avoid toxicity',
        renalImpairment: {
          mild: 'No adjustment needed',
          moderate: 'No adjustment; not renally eliminated',
          severe: 'Use with caution; reduced protein binding',
          dialysis: 'Use standard doses; not removed by dialysis'
        },
        hepaticImpairment: 'Use with extreme caution; vitamin A is hepatotoxic and stored in liver; reduce doses in liver disease'
      },
      pediatric: {
        standard: 'Age-based dosing per WHO; deficiency treatment and measles protocols differ by age',
        weightBased: false,
        ageSpecific: [
          { age: 'Infants <6 months', dose: '50,000 IU (15 mg) once; repeat next day and at 2 weeks for deficiency' },
          { age: 'Infants 6-11 months', dose: '100,000 IU (30 mg) once; repeat next day and at 2 weeks for deficiency/measles' },
          { age: 'Children 12 months and older', dose: '200,000 IU (60 mg) once; repeat next day and at 2 weeks for deficiency/measles' }
        ],
        maxDose: 'Follow WHO age-based recommendations; do not exceed for single high-dose supplementation',
        neonatal: 'Limited indications in neonates; maternal supplementation preferred; if needed: 50,000 IU'
      },
      elderly: 'Use standard adult doses for deficiency; lower supplementation doses (avoid >10,000 IU daily chronic use); increased risk of toxicity',
      routes: ['Oral', 'IM'],
      administration: [
        'Oral: Give with fatty meal to enhance absorption; capsules can be opened and contents given directly for children',
        'IM: Reserve for severe malabsorption or inability to take oral; use water-miscible preparation',
        'High-dose supplementation: Single dose preferred; directly observed in programs',
        'Prophylactic supplementation: Every 4-6 months in endemic areas'
      ]
    },
    contraindications: {
      absolute: [
        'Hypervitaminosis A',
        'Pregnancy or women who may become pregnant (high doses) - teratogenic',
        'Hypersensitivity to vitamin A or formulation components'
      ],
      relative: [
        'Liver disease (hepatotoxic)',
        'Alcoholism (increased hepatotoxicity risk)',
        'Malabsorption syndromes (reduced efficacy)',
        'Concurrent use of retinoids (isotretinoin, acitretin)'
      ],
      precautions: [
        'TERATOGENIC at high doses - do not give >10,000 IU/day to pregnant women',
        'Single high-dose supplementation generally safe in pregnancy in deficient populations',
        'Monitor for signs of toxicity with chronic supplementation',
        'Caution with other vitamin A sources (cod liver oil, fortified foods)',
        'Avoid concurrent isotretinoin or other retinoids'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Transient headache and nausea (high dose)', frequency: '5-15%', management: 'Self-limiting within 24-48 hours; reassurance' },
        { effect: 'Bulging fontanelle in infants (transient)', frequency: '1-5%', management: 'Resolves within 24-72 hours; no intervention needed' }
      ],
      serious: [
        { effect: 'Acute toxicity (headache, vomiting, visual changes)', frequency: 'Rare with appropriate dosing', management: 'Supportive care; discontinue vitamin A; monitor intracranial pressure' },
        { effect: 'Teratogenicity', frequency: 'Significant with high doses', management: 'CONTRAINDICATED high-dose in pregnancy; birth defects include craniofacial, cardiac, CNS', warning: 'Women of childbearing potential should avoid >10,000 IU/day; high-dose supplementation not in first trimester' },
        { effect: 'Chronic toxicity (hepatotoxicity, bone changes)', frequency: 'With prolonged excess', management: 'Stop supplementation; supportive care; liver recovery may take months' }
      ],
      rare: [
        'Pseudotumor cerebri (benign intracranial hypertension)',
        'Hepatomegaly and cirrhosis (chronic excess)',
        'Hypercalcemia',
        'Skin desquamation'
      ]
    },
    interactions: {
      major: [
        { drug: 'Oral retinoids (isotretinoin, acitretin, tretinoin)', effect: 'Additive vitamin A toxicity', mechanism: 'Combined vitamin A activity', management: 'Avoid combination; if necessary, use lowest doses and monitor closely' },
        { drug: 'Warfarin', effect: 'May enhance anticoagulant effect', mechanism: 'Unknown; possibly protein binding displacement', management: 'Monitor INR if starting vitamin A; may need warfarin adjustment' }
      ],
      moderate: [
        { drug: 'Orlistat and bile acid sequestrants', effect: 'Reduced vitamin A absorption', management: 'Separate administration by 2 hours; may need higher doses' },
        { drug: 'Tetracyclines', effect: 'Increased risk of pseudotumor cerebri', management: 'Avoid high-dose vitamin A with tetracyclines' },
        { drug: 'Mineral oil', effect: 'Reduced vitamin A absorption', management: 'Avoid chronic mineral oil use; separate by several hours' }
      ],
      food: [
        'Fat enhances absorption - give with fatty meal',
        'Beta-carotene (provitamin A) from plants is less bioavailable than retinol from animal sources',
        'Alcohol increases hepatotoxicity risk'
      ]
    },
    pregnancy: {
      category: 'X (high doses)',
      trimpiesterRisks: {
        first: 'HIGH RISK - doses >10,000 IU/day are teratogenic; causes craniofacial, cardiac, CNS malformations',
        second: 'High doses still risky; moderate doses likely safe',
        third: 'Lower teratogenic risk; avoid excessive doses'
      },
      recommendation: 'Do NOT exceed 10,000 IU (3,000 mcg) daily during pregnancy; however, single high-dose supplementation (up to 200,000 IU once) is considered safe in deficient populations after first trimester per WHO; postpartum supplementation preferred',
      lactation: { safe: true, notes: 'Vitamin A excreted in breast milk; postpartum supplementation (200,000 IU) improves infant status via breast milk; safe when following WHO guidelines' }
    },
    monitoring: {
      baseline: [
        { test: 'Serum retinol', reason: 'Confirm deficiency if uncertain (<0.7 µmol/L indicates deficiency)' },
        { test: 'Liver function tests', reason: 'Baseline before high-dose or chronic supplementation' }
      ],
      ongoing: [
        { test: 'Clinical response', frequency: 'Within days for night blindness; 1-2 weeks for xerophthalmia', target: 'Improvement in symptoms' },
        { test: 'Serum retinol', frequency: 'After 1-2 months if uncertain response', target: '>0.7 µmol/L' },
        { test: 'LFTs', frequency: 'If chronic supplementation or signs of toxicity', target: 'Normal liver function' }
      ],
      therapeutic: { range: 'Serum retinol 0.7-2.1 µmol/L (20-60 mcg/dL)', timing: 'Fasting sample preferred' }
    },
    storage: {
      temperature: 'Store at 15-25°C (59-77°F)',
      light: 'Protect from light - vitamin A is light sensitive',
      humidity: 'Protect from moisture',
      specialInstructions: 'Keep capsules in original container; do not transfer to pill organizers for prolonged periods',
      shelfLife: '2-3 years; check manufacturer labeling; observe for color changes'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; distributed through EPI (Expanded Programme on Immunization) and child health campaigns; high-dose capsules specifically for programs',
      affordability: 'EXTREMELY AFFORDABLE - Often free through supplementation programs; <$0.10 per high-dose capsule when purchased',
      supplyChainNotes: 'WHO/UNICEF supply programs ensure availability; integrated with immunization campaigns; capsules stable and easy to distribute',
      localManufacturing: 'Some local manufacturing in larger African countries; many imported through NGO and government programs',
      alternatives: ['Beta-carotene (from orange/yellow fruits and vegetables)', 'Vitamin A fortified foods (oils, flour)'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'Vitamin A deficiency is endemic in many African countries - leading cause of preventable childhood blindness; measles remains common - vitamin A supplementation is life-saving; integrated vitamin A supplementation with vaccination campaigns is highly effective'
    },
    patientCounseling: {
      keyPoints: [
        'High-dose vitamin A is given to prevent blindness and death from deficiency',
        'Transient side effects (headache, nausea) are normal and resolve within 1-2 days',
        'Bulging fontanelle in babies is temporary and not dangerous',
        'Bring children for routine vitamin A supplementation with vaccinations'
      ],
      commonMisconceptions: [
        'Misconception: "Vitamin A supplements are always safe" - Truth: High doses can be toxic, especially in pregnancy',
        'Misconception: "My child is sick from the vitamin A" - Truth: Transient symptoms are normal and protective benefits outweigh brief side effects'
      ],
      adherenceTips: [
        'Attend child health days and vaccination campaigns where vitamin A is distributed',
        'Eat orange and yellow fruits and vegetables (papaya, mango, carrots, sweet potato) and dark green leaves',
        'Include eggs, liver, and milk when affordable'
      ],
      warningSignsToReport: [
        'Persistent vomiting or severe headache lasting more than 48 hours',
        'Vision problems not improving after supplementation',
        'Severe skin peeling or hair loss (chronic toxicity)',
        'Pregnant women should report if given high-dose vitamin A'
      ]
    },
    clinicalPearls: [
      'Vitamin A supplementation during measles reduces mortality by 50% - absolutely essential intervention',
      'Night blindness is the earliest symptom of deficiency; Bitot spots (foamy patches on conjunctiva) indicate more severe deficiency',
      'Keratomalacia (corneal melting) is a medical emergency - can cause permanent blindness within days',
      'In African settings, assume vitamin A deficiency in any child with measles, severe diarrhea, or malnutrition - supplement presumptively',
      'Postpartum high-dose supplementation is preferred to supplementation during pregnancy - transfers to infant via breast milk'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Guideline: Vitamin A Supplementation in Infants and Children 2011',
      'WHO Guideline: Vitamin A Supplementation in Pregnant Women 2011',
      'Cochrane Review: Vitamin A Supplementation for Preventing Morbidity and Mortality in Children 2017',
      'WHO/UNICEF/IVACG: Vitamin A Supplements: A Guide to Their Use in Prevention and Treatment of Vitamin A Deficiency'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 49. OXYTOCIN
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'oxytocin-049',
    genericName: 'Oxytocin',
    brandNames: ['Pitocin', 'Syntocinon', 'Orasthin'],
    atcCode: 'H01BB02',
    therapeuticClass: 'Uterotonic / Posterior Pituitary Hormone',
    pharmacology: {
      mechanism: 'Synthetic nonapeptide identical to endogenous posterior pituitary hormone. Binds to oxytocin receptors (G-protein coupled) on uterine myometrium, increasing intracellular calcium and prostaglandin production, leading to rhythmic uterine contractions. Receptor density increases dramatically near term (100-200 fold), making the uterus highly sensitive to oxytocin. Also contracts myoepithelial cells in breast (milk letdown).',
      pharmacokinetics: {
        absorption: 'Not absorbed orally (peptide); IM onset 3-5 minutes; IV onset immediate',
        distribution: 'Vd 12-15 L; does not bind significantly to plasma proteins; distributed throughout ECF',
        metabolism: 'Rapidly metabolized by oxytocinase (pregnancy-associated enzyme) in plasma and tissue; also hepatic and renal degradation',
        elimination: 'Renal excretion of small amounts of unchanged drug and metabolites',
        halfLife: '3-5 minutes IV (very short); biological effect on uterus lasts 20-30 minutes after stopping infusion'
      },
      pharmacodynamics: 'Uterine contractions begin within 1 minute of IV administration; steady-state response in 20-40 minutes. At term, very low doses produce contractions; sensitivity increases through labor. Contractions continue for 20-30 minutes after stopping infusion due to sustained receptor activation.'
    },
    indications: [
      { condition: 'Prevention of postpartum hemorrhage', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'WHO recommends for all deliveries; single most effective intervention for PPH prevention' },
      { condition: 'Treatment of postpartum hemorrhage', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line uterotonic for PPH treatment' },
      { condition: 'Labor induction', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Most commonly used agent; requires cervical ripening if unfavorable' },
      { condition: 'Labor augmentation', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'For inadequate uterine contractions' },
      { condition: 'Incomplete abortion/miscarriage management', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Helps expel retained products; often combined with misoprostol' }
    ],
    dosing: {
      adults: {
        standard: 'PPH prevention: 10 IU IM after delivery of anterior shoulder or placenta; Labor induction: Start 0.5-2 mU/min IV, titrate',
        indicationSpecific: [
          { indication: 'PPH prevention (AMTSL)', dose: '10 IU IM immediately after delivery of baby (after anterior shoulder or after placenta delivery per local protocol); or 5 IU IV slow' },
          { indication: 'PPH treatment', dose: '20-40 IU in 1L NS/RL, run at 125-250 mL/h; or 10 IU IM; can give additional 10 IU doses' },
          { indication: 'Labor induction', dose: 'Start 0.5-2 mU/min IV; increase by 1-2 mU/min every 30-60 min; usual max 20-40 mU/min' },
          { indication: 'Labor augmentation', dose: 'Start 0.5-1 mU/min IV; lower incremental increases; usual max 20 mU/min' },
          { indication: 'Cesarean section', dose: '5-10 IU IV slow push after delivery; infusion may follow' }
        ],
        maxDose: '40 mU/min for induction/augmentation (some protocols up to 60 mU/min); PPH may require higher total doses',
        renalImpairment: {
          mild: 'No adjustment needed',
          moderate: 'No adjustment; monitor fluid balance',
          severe: 'No adjustment; watch for fluid retention',
          dialysis: 'No specific adjustment; rarely indicated in dialysis patients'
        },
        hepaticImpairment: 'No adjustment required; metabolized by tissue oxytocinases more than liver'
      },
      pediatric: {
        standard: 'Not indicated for pediatric patients',
        weightBased: false,
        ageSpecific: [
          { age: 'Pediatric', dose: 'Not applicable - oxytocin is used only in obstetric settings' }
        ],
        maxDose: 'Not applicable',
        neonatal: 'Not indicated'
      },
      elderly: 'Typically not applicable (post-reproductive); use standard doses if obstetric indication arises',
      routes: ['IV', 'IM'],
      administration: [
        'IM: 10 IU into deltoid or thigh; onset 3-5 minutes; ideal for PPH prevention in low-resource settings',
        'IV infusion (induction): Dilute in NS or RL; use infusion pump; start low, titrate carefully',
        'IV bolus (C-section/PPH): Give slowly over 1-2 minutes to avoid hypotension; never rapid push',
        'Never give undiluted IV bolus at high doses - causes severe hypotension'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to oxytocin',
        'Significant cephalopelvic disproportion',
        'Unfavorable fetal positions (transverse lie, brow/face presentation)',
        'Placenta previa or vasa previa',
        'Cord prolapse or presentation',
        'Fetal distress when delivery not imminent',
        'Uterine hypertonicity or hyperactive labor patterns'
      ],
      relative: [
        'Previous cesarean section or uterine surgery (risk of rupture)',
        'Grand multiparity (≥5 deliveries)',
        'Uterine overdistension (multiple gestation, polyhydramnios)',
        'Maternal cardiac disease',
        'Severe preeclampsia'
      ],
      precautions: [
        'Continuous fetal and uterine monitoring required during induction/augmentation',
        'Risk of uterine rupture, especially with prior uterine surgery',
        'Risk of water intoxication with prolonged infusion - use isotonic solutions, monitor I/O',
        'Hypotension with rapid IV administration',
        'May cause neonatal jaundice with prolonged use',
        'Avoid simultaneous use with prostaglandins (wait adequate interval)'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Uterine hyperstimulation', frequency: '5-10%', management: 'Reduce or stop infusion; lateral positioning; IV fluids; tocolytic (terbutaline) if severe' },
        { effect: 'Nausea and vomiting', frequency: '5-10%', management: 'Usually self-limiting; reduce infusion rate if problematic' },
        { effect: 'Transient hypotension', frequency: '5-10% with IV bolus', management: 'Give IV slowly; have fluids running; usually resolves quickly' }
      ],
      serious: [
        { effect: 'Uterine rupture', frequency: 'Rare (<1%)', management: 'Emergency cesarean section; resuscitation', warning: 'Risk increased with prior uterine surgery, high doses, prolonged use' },
        { effect: 'Water intoxication and hyponatremia', frequency: 'Rare with proper management', management: 'Use isotonic solutions; limit total fluid volume; monitor sodium if prolonged infusion', warning: 'Can cause seizures, coma, death if severe; avoid large volumes of hypotonic solutions' },
        { effect: 'Fetal distress from hyperstimulation', frequency: '2-5%', management: 'Stop oxytocin; lateral positioning; oxygen; IV fluids; tocolytic if needed' },
        { effect: 'Postpartum hemorrhage (from uterine atony if stopped too soon)', frequency: 'Variable', management: 'Continue oxytocin infusion until uterus firm; massage; additional uterotonics' }
      ],
      rare: [
        'Amniotic fluid embolism (association not causation)',
        'Arrhythmias',
        'Anaphylaxis',
        'Neonatal jaundice (with prolonged use)',
        'Afibrinogenemia'
      ]
    },
    interactions: {
      major: [
        { drug: 'Prostaglandins (misoprostol, dinoprostone)', effect: 'Additive uterine stimulation; risk of hyperstimulation and rupture', mechanism: 'Synergistic uterotonic effect', management: 'Allow adequate interval (30+ minutes for misoprostol; 6-12 hours for dinoprostone) before starting oxytocin' },
        { drug: 'Vasoconstrictors (ergot alkaloids)', effect: 'Severe hypertension', mechanism: 'Combined vasoconstrictor effect', management: 'Use with caution; ergot alkaloids generally avoided in PPH first-line' }
      ],
      moderate: [
        { drug: 'Inhaled anesthetics (halothane, sevoflurane)', effect: 'Reduced oxytocin effect; may need higher doses', management: 'May need to increase oxytocin dose for adequate uterine tone' },
        { drug: 'Sympathomimetics', effect: 'Enhanced hypertensive effect', management: 'Monitor blood pressure closely' },
        { drug: 'Magnesium sulfate', effect: 'Reduced oxytocin effect', management: 'May need higher oxytocin doses in eclampsia patients on magnesium' }
      ],
      food: [
        'Not applicable - parenteral administration only'
      ]
    },
    pregnancy: {
      category: 'C (but specifically indicated in pregnancy/labor)',
      trimpiesterRisks: {
        first: 'Contraindicated - do not use to induce abortion; no role in first trimester',
        second: 'Limited indications; cervix not responsive; risk of failed induction',
        third: 'Indicated when labor induction/augmentation or PPH prevention needed; monitor closely'
      },
      recommendation: 'Specifically indicated at term and peripartum; essential for PPH prevention; requires appropriate obstetric supervision',
      lactation: { safe: true, notes: 'Physiological hormone; promotes milk letdown; safe with breastfeeding; short half-life limits infant exposure' }
    },
    monitoring: {
      baseline: [
        { test: 'Fetal heart rate', reason: 'Baseline for continuous monitoring during labor' },
        { test: 'Cervical examination', reason: 'Assess Bishop score before induction' },
        { test: 'Maternal vital signs', reason: 'Baseline blood pressure especially important' }
      ],
      ongoing: [
        { test: 'Continuous fetal heart monitoring', frequency: 'Throughout labor when oxytocin used', target: 'Normal fetal heart rate pattern' },
        { test: 'Uterine contraction monitoring', frequency: 'Continuous with infusion', target: '3-5 contractions per 10 minutes; no hyperstimulation' },
        { test: 'Maternal vital signs', frequency: 'Every 15-30 minutes during active labor', target: 'Stable blood pressure' },
        { test: 'Fluid balance', frequency: 'Ongoing if prolonged infusion', target: 'Avoid fluid overload' }
      ],
      therapeutic: { range: 'Adequate labor pattern: 3-5 contractions per 10 minutes, lasting 60-90 seconds', timing: 'Titrate every 30-60 minutes until adequate pattern' }
    },
    storage: {
      temperature: 'Store at 2-8°C (refrigerated); stable at room temperature (15-25°C) for short periods',
      light: 'Protect from light',
      humidity: 'Keep ampules sealed until use',
      specialInstructions: 'Heat-sensitive - cold chain recommended in tropical climates; heat-stable formulations available for resource-limited settings',
      shelfLife: '2-3 years refrigerated; check manufacturer; shorter at room temperature'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; priority medicine for maternal health; available in most health facilities',
      affordability: 'VERY AFFORDABLE - <$0.50 per ampule; often provided free through maternal health programs',
      supplyChainNotes: 'Cold chain requirements can be challenging in Africa; heat-stable oxytocin (in carbetocin or Uniject prefilled devices) being introduced; UNFPA and WHO support supply',
      localManufacturing: 'Limited local manufacturing; mostly imported; quality assurance important - some substandard products identified',
      alternatives: ['Misoprostol (heat-stable, oral - preferred where cold chain unreliable)', 'Ergometrine (less preferred)', 'Carbetocin (heat-stable, longer acting, but more expensive)'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'PPH is leading cause of maternal death in Africa; oxytocin for AMTSL is single most important intervention; cold chain challenges mean misoprostol may be more practical in some settings; ensure quality-assured products'
    },
    patientCounseling: {
      keyPoints: [
        'Oxytocin is given after delivery to prevent heavy bleeding',
        'You may feel stronger cramping - this means the medicine is working to contract your uterus',
        'During labor induction, the medicine will make contractions regular and stronger',
        'The baby and contractions will be monitored continuously'
      ],
      commonMisconceptions: [
        'Misconception: "I dont need the injection after delivery" - Truth: It prevents dangerous bleeding that can happen to any woman',
        'Misconception: "The injection will harm my baby" - Truth: It is given after the baby is born and helps keep you safe'
      ],
      adherenceTips: [
        'Not applicable - healthcare provider administered',
        'Deliver at facility where oxytocin is available for PPH prevention'
      ],
      warningSignsToReport: [
        'During labor: severe pain, feeling like something is wrong, difficulty breathing',
        'After delivery: heavy bleeding (soaking more than 1 pad per hour), dizziness, feeling faint',
        'Severe headache or vision changes'
      ]
    },
    clinicalPearls: [
      'Active Management of Third Stage of Labor (AMTSL) with oxytocin reduces PPH by 60% - standard of care for all deliveries',
      'IM oxytocin is preferred for PPH prevention in low-resource settings - does not require IV or infusion equipment',
      '"Rule of 3s" for labor induction: assess every 3 hours, need 3-5 contractions per 10 minutes',
      'If oxytocin fails to control PPH, escalate rapidly to additional uterotonics, bimanual compression, and surgical intervention',
      'In African settings, ensure oxytocin quality and cold chain integrity - substandard oxytocin is a major problem; consider misoprostol backup'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Recommendation on Uterotonics for Prevention of Postpartum Haemorrhage 2018',
      'WHO Managing Complications in Pregnancy and Childbirth 2017',
      'ACOG Practice Bulletin: Induction of Labor 2009 (reaffirmed 2023)',
      'FIGO Guidelines: Prevention of Postpartum Hemorrhage 2022'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 50. MAGNESIUM SULFATE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: 'magnesium-sulfate-050',
    genericName: 'Magnesium Sulfate',
    brandNames: ['Epsom Salt (historical)', 'MgSO4', 'Magsulfate'],
    atcCode: 'A12CC02 / B05XA05 / N02BG / V04CC02',
    therapeuticClass: 'Anticonvulsant / Electrolyte / Tocolytic',
    pharmacology: {
      mechanism: 'Multiple mechanisms: (1) CNS depression via NMDA receptor antagonism and increased seizure threshold, (2) Vasodilation via calcium channel antagonism, (3) Reduces acetylcholine release at neuromuscular junction, (4) Reduces vascular endothelial dysfunction. In eclampsia, prevents/treats seizures likely through combination of cerebral vasodilation, reduced cerebral edema, and direct anticonvulsant effect.',
      pharmacokinetics: {
        absorption: 'Not orally absorbed for systemic effect (osmotic laxative); IV/IM administration for systemic effects',
        distribution: 'Distributed throughout ECF; crosses placenta (fetal levels ~75% of maternal); enters breast milk; does not significantly cross blood-brain barrier in normal states',
        metabolism: 'Not metabolized',
        elimination: 'Almost entirely renal excretion; clearance proportional to GFR; accumulates in renal impairment',
        halfLife: '4-5 hours (normal renal function); prolonged in renal impairment'
      },
      pharmacodynamics: 'Anticonvulsant effect onset within 15 minutes IV; therapeutic serum level 4-7 mEq/L (2-3.5 mmol/L) for eclampsia. Higher levels associated with toxicity: loss of reflexes 7-10 mEq/L, respiratory depression >10 mEq/L, cardiac arrest >25 mEq/L.'
    },
    indications: [
      { condition: 'Eclampsia treatment and seizure prevention', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Superior to diazepam and phenytoin for eclamptic seizures; standard of care' },
      { condition: 'Severe preeclampsia seizure prophylaxis', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'Prevents progression to eclampsia; continue 24-48h postpartum' },
      { condition: 'Fetal neuroprotection (preterm delivery)', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'When delivery <32 weeks anticipated; reduces cerebral palsy' },
      { condition: 'Severe asthma exacerbation', evidenceLevel: 'B', recommendationStrength: 'Moderate', firstLine: false, notes: 'Adjunct to standard therapy; bronchodilator effect' },
      { condition: 'Torsades de pointes', evidenceLevel: 'B', recommendationStrength: 'Strong', firstLine: true, notes: 'First-line treatment regardless of magnesium level' },
      { condition: 'Severe hypomagnesemia', evidenceLevel: 'A', recommendationStrength: 'Strong', firstLine: true, notes: 'IV replacement for symptomatic deficiency' }
    ],
    dosing: {
      adults: {
        standard: 'Eclampsia/severe preeclampsia: Loading 4-6 g IV over 15-20 min, then 1-2 g/h infusion; maintain 24-48 h postpartum',
        indicationSpecific: [
          { indication: 'Eclampsia/severe preeclampsia (Pritchard IM regimen)', dose: 'Loading: 4 g IV over 15-20 min + 10 g IM (5 g in each buttock); Maintenance: 5 g IM every 4 hours (alternate buttocks)' },
          { indication: 'Eclampsia/severe preeclampsia (Zuspan IV regimen)', dose: 'Loading: 4-6 g IV over 15-20 min; Maintenance: 1-2 g/h IV infusion for 24-48h' },
          { indication: 'Recurrent seizure in eclampsia', dose: 'Additional 2 g IV over 3-5 minutes' },
          { indication: 'Fetal neuroprotection (<32 weeks)', dose: '4-6 g IV loading over 20-30 min, then 1-2 g/h until delivery or max 12-24h' },
          { indication: 'Torsades de pointes', dose: '1-2 g IV over 5-15 minutes; may repeat' },
          { indication: 'Severe asthma', dose: '2 g IV over 20 minutes' },
          { indication: 'Severe hypomagnesemia', dose: '1-2 g IV over 15-60 min; repeat based on levels' }
        ],
        maxDose: 'Loading: 6 g; Maintenance: usually 2 g/h maximum; guided by clinical response and toxicity monitoring',
        renalImpairment: {
          mild: 'Standard loading dose; reduce maintenance to 1 g/h; monitor closely',
          moderate: 'Standard loading; reduce maintenance to 0.5-1 g/h; monitor magnesium levels',
          severe: 'Use with extreme caution; reduce maintenance; frequent monitoring; may need to stop if oliguric',
          dialysis: 'Removed by dialysis; use for acute indication with monitoring; replete after dialysis'
        },
        hepaticImpairment: 'No adjustment needed; not hepatically metabolized'
      },
      pediatric: {
        standard: 'Varies by indication; weight-based dosing',
        weightBased: true,
        ageSpecific: [
          { age: 'Hypomagnesemia', dose: '25-50 mg/kg IV/IM, max 2 g per dose' },
          { age: 'Asthma', dose: '25-75 mg/kg IV over 20 min, max 2 g' },
          { age: 'Torsades', dose: '25-50 mg/kg IV over 5-15 min, max 2 g' }
        ],
        maxDose: '2 g per dose for most pediatric indications',
        neonatal: 'Primarily consider maternal exposure effects; direct neonatal use limited'
      },
      elderly: 'Use standard doses for appropriate indications; more likely to have renal impairment - monitor closely',
      routes: ['IV', 'IM'],
      administration: [
        'IV loading: Dilute in 100-250 mL NS; infuse over 15-30 minutes (never rapid bolus)',
        'IV maintenance: Typically 40 g in 1L = 1 g/25 mL; run at 25-50 mL/h for 1-2 g/h',
        'IM: Use 50% solution (500 mg/mL); give deep gluteal injection; painful - add 1% lidocaine if available',
        'Pritchard regimen useful where IV infusion not practical (resource-limited settings)'
      ]
    },
    contraindications: {
      absolute: [
        'Hypersensitivity to magnesium sulfate',
        'Heart block',
        'Myocardial damage',
        'Myasthenia gravis (worsens weakness)'
      ],
      relative: [
        'Renal impairment (reduce dose and monitor)',
        'Respiratory depression or respiratory disease',
        'Severe hypotension',
        'Within 2 hours of delivery (risk of neonatal depression)'
      ],
      precautions: [
        'MUST monitor patellar reflexes, respiratory rate, and urine output continuously',
        'Calcium gluconate (1 g IV) should be immediately available as antidote',
        'Risk of magnesium toxicity: loss of reflexes → respiratory depression → cardiac arrest',
        'Reduce or stop if: reflexes absent, RR <12/min, urine output <30 mL/h',
        'Interacts with neuromuscular blocking agents',
        'May cause fetal/neonatal depression if given close to delivery'
      ]
    },
    adverseEffects: {
      common: [
        { effect: 'Flushing and warmth', frequency: '20-40%', management: 'Expected effect of vasodilation; reassurance; usually decreases with time' },
        { effect: 'Nausea and vomiting', frequency: '10-20%', management: 'Reduce infusion rate if severe; antiemetics if needed' },
        { effect: 'Headache', frequency: '10-15%', management: 'Distinguish from worsening preeclampsia; usually mild' },
        { effect: 'Muscle weakness', frequency: '10-15%', management: 'Expected at therapeutic levels; monitor for toxicity' },
        { effect: 'Injection site pain (IM)', frequency: 'Nearly universal', management: 'Add 1% lidocaine to IM injection; use deep injection' }
      ],
      serious: [
        { effect: 'Loss of deep tendon reflexes', frequency: 'At levels 7-10 mEq/L', management: 'STOP or reduce infusion; warning sign of toxicity', warning: 'Monitor patellar reflex every 1-4 hours - if absent, hold magnesium' },
        { effect: 'Respiratory depression/arrest', frequency: 'At levels >10-12 mEq/L', management: 'STOP infusion; give calcium gluconate 1 g IV over 3-5 min; ventilatory support', warning: 'Monitor RR every 1-4 hours - if <12/min, hold magnesium' },
        { effect: 'Cardiac arrest', frequency: 'At levels >25 mEq/L', management: 'STOP infusion; calcium gluconate; CPR; typically only with massive overdose or severe renal failure' },
        { effect: 'Neonatal depression', frequency: 'With high maternal levels near delivery', management: 'Support neonate; usually resolves as magnesium clears' }
      ],
      rare: [
        'Hypocalcemia',
        'Pulmonary edema',
        'Ileus',
        'Complete heart block'
      ]
    },
    interactions: {
      major: [
        { drug: 'Neuromuscular blocking agents (succinylcholine, rocuronium)', effect: 'Prolonged and enhanced neuromuscular blockade', mechanism: 'Additive inhibition of acetylcholine release', management: 'Reduce neuromuscular blocker dose; monitor neuromuscular function' },
        { drug: 'Calcium channel blockers (nifedipine)', effect: 'Enhanced hypotension and neuromuscular blockade; case reports of serious events', mechanism: 'Additive calcium antagonism', management: 'Use with caution; monitor BP closely; avoid concurrent use if possible' },
        { drug: 'Aminoglycosides', effect: 'Enhanced neuromuscular blockade and respiratory depression', mechanism: 'Additive neuromuscular junction effects', management: 'Avoid if possible; if necessary, monitor closely' }
      ],
      moderate: [
        { drug: 'CNS depressants', effect: 'Enhanced sedation', management: 'Monitor level of consciousness; reduce doses if necessary' },
        { drug: 'Digoxin', effect: 'Magnesium may enhance digoxin-induced cardiac conduction disturbances', management: 'Monitor ECG; caution in digitalized patients' },
        { drug: 'Potassium-sparing diuretics', effect: 'May increase serum magnesium', management: 'Monitor magnesium levels' }
      ],
      food: [
        'Not applicable - parenteral administration'
      ]
    },
    pregnancy: {
      category: 'D (prolonged use may cause fetal harm)',
      trimpiesterRisks: {
        first: 'Limited indications in first trimester; theoretical risk with prolonged use',
        second: 'For preterm labor, limit duration due to fetal bone effects; fetal neuroprotection benefit',
        third: 'Specifically indicated for preeclampsia/eclampsia; short-term use is safe'
      },
      recommendation: 'Essential for eclampsia/severe preeclampsia; benefits far outweigh risks for this indication; fetal neuroprotection benefit for preterm delivery; limit exposure to <5-7 days to avoid fetal bone effects',
      lactation: { safe: true, notes: 'Excreted in breast milk in small amounts; safe with breastfeeding; breastfeeding can continue during/after magnesium therapy' }
    },
    monitoring: {
      baseline: [
        { test: 'Deep tendon reflexes (patellar)', reason: 'Baseline before starting and key toxicity monitor' },
        { test: 'Respiratory rate', reason: 'Baseline; monitor continuously' },
        { test: 'Urine output', reason: 'Renal function determines clearance' },
        { test: 'Serum creatinine', reason: 'Assess renal function for dosing' }
      ],
      ongoing: [
        { test: 'Patellar reflex', frequency: 'Every 1-4 hours', target: 'Present (2+ normal); if absent HOLD magnesium' },
        { test: 'Respiratory rate', frequency: 'Every 1-4 hours', target: '≥12/min; if <12 HOLD magnesium' },
        { test: 'Urine output', frequency: 'Hourly', target: '≥30 mL/h (or 100 mL/4h); if oliguric reduce/stop' },
        { test: 'Serum magnesium (if available)', frequency: 'Every 4-6 hours or if concern for toxicity', target: '4-7 mEq/L (2-3.5 mmol/L) for eclampsia' }
      ],
      therapeutic: { range: '4-7 mEq/L (2-3.5 mmol/L) for eclampsia; 1.5-2.5 mEq/L normal; >7 mEq/L toxicity risk', timing: 'Steady state in ~6-8 hours with continuous infusion' }
    },
    storage: {
      temperature: 'Store at 20-25°C (68-77°F); do not freeze',
      light: 'No special light protection required',
      humidity: 'Protect from moisture',
      specialInstructions: 'Stable at room temperature; suitable for resource-limited settings; check for crystals before use',
      shelfLife: '2-3 years; check manufacturer labeling'
    },
    africanContext: {
      availability: 'WIDELY AVAILABLE - On WHO Essential Medicines List; priority medicine for maternal health; available in most hospitals and health centers',
      affordability: 'VERY AFFORDABLE - <$1-2 per treatment course; often provided free through maternal health programs',
      supplyChainNotes: 'Stable at room temperature; no cold chain required; easy to distribute; UNFPA and government programs ensure supply',
      localManufacturing: 'Manufactured locally in several African countries; generic widely available',
      alternatives: ['Diazepam (less effective for eclampsia but may be only option in emergencies)', 'Phenytoin (less effective than magnesium sulfate)'],
      resistancePatterns: 'Not applicable',
      endemicConsiderations: 'Eclampsia is major cause of maternal mortality in Africa; magnesium sulfate is life-saving; Pritchard IM regimen suitable for low-resource settings without IV infusion capability; ensure calcium gluconate antidote available; train midwives in monitoring'
    },
    patientCounseling: {
      keyPoints: [
        'This medicine prevents dangerous seizures from preeclampsia/eclampsia',
        'You will feel warm and flushed - this is normal',
        'Your reflexes, breathing, and urine output will be checked frequently',
        'Tell the nurse immediately if you feel very weak, short of breath, or drowsy'
      ],
      commonMisconceptions: [
        'Misconception: "The warm feeling means something is wrong" - Truth: Flushing is a normal effect; it means the medicine is working',
        'Misconception: "I dont need it if I feel fine" - Truth: Eclampsia can happen suddenly; prevention is critical'
      ],
      adherenceTips: [
        'Not applicable - administered by healthcare providers',
        'Allow healthcare staff to check your reflexes regularly'
      ],
      warningSignsToReport: [
        'Difficulty breathing or feeling very short of breath',
        'Severe drowsiness or confusion',
        'Inability to move or severe weakness',
        'Not passing urine'
      ]
    },
    clinicalPearls: [
      'The "Clinical Monitoring Triad" for magnesium: (1) Patellar reflex present, (2) RR ≥12/min, (3) UO ≥30 mL/h - if ANY absent, hold magnesium',
      'Pritchard IM regimen is as effective as IV and more practical in low-resource settings - do not avoid magnesium because IV unavailable',
      'Calcium gluconate 1 g IV is the antidote - must be immediately available wherever magnesium is used',
      'Magnesium reduces eclampsia risk by 50% compared to phenytoin and is superior to diazepam - it is the ONLY first-line choice',
      'In African settings, train all skilled birth attendants in magnesium sulfate administration and monitoring - this single intervention saves thousands of maternal lives'
    ],
    references: [
      'WHO Model List of Essential Medicines 2023',
      'WHO Recommendations for Prevention and Treatment of Pre-Eclampsia and Eclampsia 2011',
      'The Magpie Trial: Lancet 2002 - Landmark trial showing magnesium superiority',
      'ACOG Practice Bulletin: Gestational Hypertension and Preeclampsia 2020',
      'Cochrane Review: Magnesium Sulphate for Women with Eclampsia 2010'
    ],
    lastUpdated: '2025-01-20',
    medicalDisclaimer: 'CLINICAL DISCLAIMER: This information is for healthcare professional reference only and should not replace clinical judgment. Always verify dosing, check for contraindications, and consider local resistance patterns and guidelines. Individual patient factors must guide treatment decisions. OmniAlera Technologies Holdings Inc.'
  }

];

// Helper functions for medicine lookup and search
export function getDrugBySlug(slug: string): EnhancedMedicine | undefined {
  // Handle URL-encoded slugs and various formats
  const normalizedSlug = decodeURIComponent(slug).toLowerCase().replace(/[^a-z0-9]/g, '');
  
  return ENHANCED_MEDICINES.find(med => {
    // Check against ID
    const normalizedId = med.id.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedId === normalizedSlug) return true;
    
    // Check against generic name
    const normalizedGeneric = med.genericName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedGeneric === normalizedSlug) return true;
    
    // Check against brand names
    return med.brandNames.some(brand => {
      const normalizedBrand = brand.toLowerCase().replace(/[^a-z0-9]/g, '');
      return normalizedBrand === normalizedSlug;
    });
  });
}

export function searchDrugs(query: string): EnhancedMedicine[] {
  const normalizedQuery = query.toLowerCase().trim();
  
  if (!normalizedQuery) return ENHANCED_MEDICINES;
  
  return ENHANCED_MEDICINES.filter(med => {
    // Search in generic name
    if (med.genericName.toLowerCase().includes(normalizedQuery)) return true;
    
    // Search in brand names
    if (med.brandNames.some(brand => brand.toLowerCase().includes(normalizedQuery))) return true;
    
    // Search in therapeutic class
    if (med.therapeuticClass.toLowerCase().includes(normalizedQuery)) return true;
    
    // Search in indications
    if (med.indications.some(ind => ind.condition.toLowerCase().includes(normalizedQuery))) return true;
    
    return false;
  });
}

// Backward compatibility exports
export const enhancedMedicines = ENHANCED_MEDICINES;
export const drugs = ENHANCED_MEDICINES;
